// ============================================================
// SENTRY INIT (browser)
// ============================================================
// Fire-and-forget so app.js doesn't block on the network roundtrip. Sentry
// captures runtime errors that occur after init resolves — anything earlier
// would be a load-time failure visible in the user's console anyway. Skipped
// on dev (placeholder DSN). DSN + release are injected at deploy time by
// scripts/stamp-build.cjs.
//
// A tab left open across a deploy reloads into the new build the first time
// it needs a file that deploy removed (modules/stale-build.js). Installed
// before anything can lazily load.
installStaleBuildRecovery();
if (window.SENTRY_DSN_CRM && window.SENTRY_DSN_CRM !== 'dev') {
  import('https://cdn.jsdelivr.net/npm/@sentry/browser@8/+esm').then((Sentry) => {
    // Tag environment by hostname so staging events filter cleanly in Sentry.
    // Single Sentry project, two environments: 'production' for the live site,
    // 'staging' for staging.fitopspro.com (or the .netlify.app preview domain).
    const host = (location.hostname || '').toLowerCase();
    const env = host.includes('staging') ? 'staging' : 'production';
    Sentry.init({
      dsn: window.SENTRY_DSN_CRM,
      release: window.SENTRY_RELEASE || 'unknown',
      environment: env,
      // browserTracingIntegration is required for any transaction
      // capture in @sentry/browser v8 — without it, tracesSampleRate
      // has no effect because there's no transaction source.
      integrations: [Sentry.browserTracingIntegration()],
      // 100% transaction sample feeds the Operations paint-p95 card.
      // Was 0.10, raised 2026-08-11: at 10% the CRM produced ~11 transactions
      // a WEEK, and a p95 over 11 points is just the midpoint of the two
      // slowest loads — which is exactly the false alarm that sent the ops
      // board yellow ("p95 8511 ms" was one bad afternoon). Any honest sample
      // floor would have silenced the alert permanently at that volume, so
      // sample rate and sample floor are one decision (see MIN_BROWSER_SAMPLES
      // in netlify/functions/_shared/sentry-perf-browser.js).
      // Cost is negligible: 10% yielded 30 transactions in 30d, so 100% is
      // ~300/month for a dashboard a handful of coaches use.
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // A failed lazy load that the reload above fixes is not reported: it is
      // the expected cost of a deploy, not a fault (FITOPS-PRO-CRM-K). One that
      // a reload cannot fix (a dropped connection, a broken deploy) still is.
      beforeSend: unlessStaleBuildReload,
    });
    window._Sentry = Sentry;
  }).catch((e) => {
    console.warn('[sentry] init failed', e);
  });
}

// ============================================================
// SUPABASE IMPORT & INIT
// ============================================================
// ⚠️ A BARE SPECIFIER, BUNDLED — NOT THE CDN. The jsdelivr `+esm` build is not
// one file: it is a 12.5 KB entry that statically imports auth-js, storage-js,
// realtime-js, postgrest-js and functions-js, which import tslib, phoenix and
// iceberg-js. Nine requests, three hops, all of them before app.js can execute
// (measured 2026-09-09: requested at 428 ms, done at 551 ms).
//
// ⚠️ build-dashboard.cjs's `external: ['https://*']` STAYS. TipTap loads from a
// CDN with template-literal dynamic imports that esbuild cannot resolve. A bare
// package specifier is not matched by that pattern, so this one gets bundled
// and TipTap's still does not.
import { createClient } from '@supabase/supabase-js';
import { showCoachDashboard } from './modules/coach-dashboard/index.js';

// ── Module imports ────────────────────────────────────────────
import * as _state      from './modules/state.js';
import * as _config     from './modules/config.js';
import { wireClientAppTabsSave } from './modules/client-app-tabs-settings.js';
import { cancelSubscriptions } from './modules/cancel-client-subscriptions.js';
import { rosterDelete, bulkDelete, setClientDeleteDeps } from './modules/client-delete.js';
import { emailDomainVerified } from './modules/email-domain-status.js';
import { paintEmailCard } from './modules/email-card-status.js';
import * as _api        from './modules/api.js';
import * as _stripe     from './modules/stripe.js';
import { initStripeConnect } from './modules/stripe-connect.js';
import { initAutomationIntegrations } from './modules/integrations.js';
import { initMetaAdsCard } from './modules/meta-ads-card.js';
import { initScheduling as initSchedulingSettings } from './modules/scheduling-settings.js';
import { readCancellationSettings } from './modules/cancellation-settings.js';
import { saveTimeZone } from './modules/time-zone-setting.js';
import { showReviewLayout, pickedReviewLayout } from './modules/checkin-review/layout.js';
import { CI_PREDEFINED_CATS, CI_ANALYTICS_ROWS, CI_ANALYTICS_DEFAULTS } from './modules/checkin-category-settings.js';
import { clientStandingSlots } from './modules/coach-calendar/held-ghosts.js';
import { notifRowHtml, openNotifTarget } from './modules/notification-bell.js';
import { resolveStandingSlot, runStandingSlotMove, standingFillToastMessage } from './modules/coach-calendar/standing-slot-flow.js';
import { removeStandingSlot } from './modules/standing-slot-remove.js';
import { bulkSendOutcome } from './modules/bulk-send-outcome.js';
import { initJarvis } from './modules/jarvis/index.js';
// Installs window.openChargeOneoffModal and its four siblings — the modal's
// inline handlers in index.html are the call sites. Imported for that effect.
import { setChargeOneoffDeps } from './modules/charge-oneoff.js';
import { setRecordPaymentDeps } from './modules/record-payment.js';
import { setPaymentEntryDeps, showReceiptHtmlModal } from './modules/payment-entry-windows.js';
import { firstMonthAtSignUp } from './modules/add-client-sessions.js';
import { setContinuityAside, startMonthlyAfterProgram, notSetUpWords } from './modules/signup-continuity.js';
import { renderSessionsView, repaintSessionsOverview, setSessionsOverviewDeps } from './modules/sessions-overview.js';
import './modules/session-format.js';
import { ROSTER_FILTERS, RETIRED_PULSE_FILTER, ROSTER_OPS, ROSTER_PRESETS, asDateStr as _asDateStr,
         rosterFieldValue as _rosterFieldValue, matchRosterRule as _matchRosterRule,
         hasOpenCancellationRequest as _hasOpenCancellationRequest, missedCheckin as _missedCheckin,
         setRosterFilterDeps } from './modules/roster-filters.js';
// comms-settings.js (62 KB) is lazy — Settings → Communications is its only
// entry point, and most boots land on the Appearance tab. Both call sites
// discard the return value and the module carries its own _initialized guard,
// so a plain async wrapper is enough.
// ⚠️ The win is 62 KB, not 84 — its email-editor.js dependency stays in the
// boot graph either way, because modules/inbox.js imports it statically.
// Settings → Team is lazy for the same reason Communications is: most boots
// land on Appearance, and a solo coach may never open it at all. It carries its
// own _initialized guard, so a plain async wrapper is enough.
let _teamMod;
async function _loadTeam() {
  if (!_teamMod) _teamMod = await import('./modules/team.js');
  return _teamMod;
}
async function initTeam() { return (await _loadTeam()).initTeam(); }

let _commsSettingsMod;
async function _loadCommsSettings() {
  if (!_commsSettingsMod) _commsSettingsMod = await import('./modules/comms-settings.js');
  return _commsSettingsMod;
}
async function initCommsSettings(...args) { return (await _loadCommsSettings()).initCommsSettings(...args); }
import { initCalendarSync } from './modules/calendar-sync.js';
import { initSettingsDirtyTracking } from './modules/settings-dirty-tracking.js';
import { memberHasPerm } from './modules/member-permissions.js';
// The coach calendar, rebuilt as Layout A (S88). The old Google-Calendar-style
// module stays on disk until phase 1b lands its interactions; nothing imports
// it. Spec: docs/superpowers/specs/2026-08-13-calendar-rebuild-spec.md
// coach-calendar/index.js is lazy — 92 KB itself, ~319 KB with the 17 sibling
// files under coach-calendar/ plus session-origin.js, which nothing else
// imports. It loads on the first Calendar render.
//
// No callback cache is needed (there is no setCoachCalendarCallbacks), and the
// wrapper below is a hoisted function declaration, so the shorthand reference in
// setNavCallbacks still resolves at wiring time — same as renderBuilder, which
// has been a lazy wrapper in that same object literal for months.
let _coachCalMod;
async function _loadCoachCalendar() {
  if (!_coachCalMod) _coachCalMod = await import('./modules/coach-calendar/index.js');
  return _coachCalMod;
}
async function renderCalendar(...args) { return (await _loadCoachCalendar()).renderCoachCalendar(...args); }
import * as _clients    from './modules/clients.js';
import * as _modals     from './modules/modals.js';
import * as _navigation from './modules/navigation.js';
import { initStatusPage } from './modules/status.js';
import {
  setSettingsCallbacks, updateModeBadge, renderSettings, renderTagSettings, exportCSV, renderColumnPanel, initSidebarResize,
} from './modules/settings.js';
// Namespace import alongside the named one above so the retention-settings
// handler can call renderCancelAgreementPreview without adding it to every
// call site's named-import list.
import * as _settings from './modules/settings.js';
import {
  darkenHex, addMonths, getEndDate, getMonthsAsClient, getClientLifetimeSpend, getDaysUntilEnd, hasContinuity, continuityRateOf, getMonthlyRecurring, isRenewalNeeded, getPipelineNotes, parseCurrencyInput, getEffectivePipelineStage, isManualContinuity, getContinuityStartDate, getOverdueMonthCount, formatDate, formatDateObj, formatCurrency, getNotes, LABELS, label, escHtml, showToast, debounce, sessionDescription, calcAgreementEndDate, setCurrencySymbol, getCurrencySymbol, setDateFormat, showConfirm, keepPasswordManagersOut, upgradeSelects, syncUpgradedSelects,
} from './modules/utils.js';
import { highlightHtml } from './modules/html-escape.js';
import { cachedLeadSources, loadClientLeadSources, clientSourceName, paintClientSourceFields } from './modules/client-lead-source.js';

// 1Password was dropping its inline autofill button on top of the text being
// typed — reported on the workout builder's day name, but every text field in
// the CRM is the same shape. One observer covers the whole dashboard and keeps
// covering it as views re-render. Password and email inputs are deliberately
// left alone so a coach's manager still fills a real sign-in.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => keepPasswordManagersOut(document.body));
} else {
  keepPasswordManagersOut(document.body);
}
import {
  DEFAULT_COLORS, LEAD_STAGES, LEAD_STAGE_ORDER, PIPELINE_STAGES, COLUMNS, LOGO_KEYS, CURRENCIES, DATE_FORMATS, loadSettings, saveSettingsToStorage, getCurrentMode, loadTagDefs, saveTagDefs, getTagStyle, loadColumnPrefs, saveColumnPrefs, getAutoTheme, applyTheme, applyCustomColors, applyLogo, applyLogoFromStorage, syncConfig, _uploadLogoToStorage, _deleteLogoFromStorage, loadConfig, refreshLogoRasters, loadMemberSpeakEnabled, loadGoogleMapsApi, initAddressAutocomplete,
} from './modules/config.js';
import {
  startRosterFetch, settleRosterFetch, callFunction, refreshClients,
  verifyEmail, verifyPhone, clearVerifyStatus,
  stripeConfirmWithRetry,
} from './modules/api.js';
import {
  initStripe, preloadStripeAtIdle, refreshConnectedAccount, mountCardElement, mountRenewCardElement, updatePaymentSection,
} from './modules/stripe.js';
import {
  addClient, updateClient, deleteClient, fireNewClientEvent,
  addNoteToClient, deleteNoteFromClient,
  findPotentialDuplicates, showDuplicateWarning, updateInlineDupeBanner,
  closeDuplicateWarning, goToExistingClient, proceedDespiteDuplicate,
  _applyColOrder, _initColumnDrag,
} from './modules/clients.js';
import {
  openAddModal, closeModal,
  showConfirmDialog, confirmCancelClient, confirmDelete,
} from './modules/modals.js';
import {
  showView, showClientProfile, restoreLastView, renderCurrentView, renderAll, loginUrl,
} from './modules/navigation.js';
import { toggleSidebarCollapse } from './modules/side-menus.js';
import { initSidebar, setSidebarAccount } from './modules/sidebar.js';
import { initGlobalSearch } from './modules/global-search.js';
import { applyOwnerNav, scribeIsTheRestoreTarget } from './modules/nav-access.js';
import * as _sessions from './modules/sessions.js';
import './modules/mfa.js';
import './modules/password.js';
import './modules/two-step-gate.js';

// calendar.js (74 KB) is lazy — only loaded when the user first enters a
// client profile (where calendar appears as a tab) OR opens the legacy
// calendar path. Cached callback is applied on first load.
let _calendarMod;
let _calendarPendingCallbacks = null;
async function _loadCalendar() {
  if (!_calendarMod) {
    _calendarMod = await import('./modules/calendar.js');
    if (_calendarPendingCallbacks) _calendarMod.setCalendarCallbacks(_calendarPendingCallbacks);
  }
  return _calendarMod;
}
function setCalendarCallbacks(cbs) {
  if (_calendarMod) _calendarMod.setCalendarCallbacks(cbs);
  else _calendarPendingCallbacks = cbs;
}
async function initCalendar(...args) { return (await _loadCalendar()).initCalendar(...args); }
async function renderCalendarTab(...args) { return (await _loadCalendar()).renderCalendarTab(...args); }
import { renderInbox, setInboxClients, deactivateInbox } from './modules/inbox.js';
import { startMessaging, stopMessaging } from './modules/messages/boot.js';
// lead-engine/inbox.js (236 KB) + leads-virtual-table.js (8 KB) are lazy —
// nothing else imports either file, and the module has no import-time side
// effects: its realtime channel and both poll timers start inside
// renderLeadEngine.
//
// ⚠️ deactivateLeadEngine MUST NOT force the chunk to load. It is the view-swap
// teardown, so it fires when the coach navigates AWAY — including away from a
// view that never loaded Lead Engine at all. Awaiting an import there would
// pull 243 KB down to tear down something that was never built, which is the
// exact opposite of the point. If the module was never loaded there is nothing
// to deactivate, so it is a no-op.
// ⚠️ THE STYLESHEET IS LAZY TOO, AND BOTH DOORS MUST AWAIT IT. lead-engine.css
// used to sit in <head>, render-blocking on all 17 views, for a view most
// sessions never open — 32 KB brotli and one request off every first paint,
// measured 2026-09-09.
//
// ⚠️ .le-view IS `display: flex !important` and .le-view.hidden is
// `display: none !important`. Apply the sheet BEFORE the view is un-hidden or
// the Lead Engine paints on top of the CRM for a frame. Awaiting here (and in
// initLeadEngineSettingsCard, the OTHER door — Settings → Integrations) is
// what guarantees that.
//
// onload AND onerror resolve: a missing stylesheet must never wedge navigation.
let _leCss;
function _ensureLeadEngineCss() {
  if (_leCss) return _leCss;
  _leCss = new Promise((resolve) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = `/v/${window.BUILD || 'dev'}/lead-engine.css`;
    l.onload = l.onerror = () => resolve();
    document.head.appendChild(l);
  });
  return _leCss;
}

let _leMod;
async function _loadLeadEngine() {
  const cssReady = _ensureLeadEngineCss();
  if (!_leMod) _leMod = await import('./modules/lead-engine/inbox.js');
  await cssReady;
  return _leMod;
}
async function renderLeadEngine(...args) { return (await _loadLeadEngine()).renderLeadEngine(...args); }
function deactivateLeadEngine(...args) { return _leMod ? _leMod.deactivateLeadEngine(...args) : undefined; }
async function initLeadEngineSettingsCard(...args) {
  await _ensureLeadEngineCss();
  return (await _loadLeadEngine()).initLeadEngineSettingsCard(...args);
}
// Marks a lead for the Lead Engine's next draw to open (navigation.js openLead shows it right after).
async function openLeadById(...args) { return (await _loadLeadEngine()).openLeadById(...args); }

// ⚠️ THE RAIL IS PAINTED BEFORE THE CODE THAT HANDLES IT EXISTS. The Lead
// Engine's own navigation is static markup in index.html, so it is on screen and
// clickable about 200ms after load — while the 243 KB module that listens to it
// is still downloading. Every click in that window went nowhere, silently: the
// button did not even light up. Measured at 198ms on this machine, and it is the
// first thing a coach does when the page opens.
//
// The listener therefore lives HERE, in the always-loaded bundle, and only while
// the module has not wired the rail itself. It does not trigger a render —
// navigation has already started one — it records the intent and lets
// _wireSubsidebar pick it up when it runs, which it does before dispatching.
document.addEventListener('click', (e) => {
  const btn = e.target?.closest?.('#view-lead-engine .le-sidebar [data-le-section]');
  if (!btn) return;
  const rail = btn.closest('.le-sidebar');
  // `wired` is stamped by the module. Keyed on that rather than on whether the
  // import resolved, because there is a moment where the module is loaded and
  // the rail still is not.
  if (!rail || rail.dataset.wired === '1') return;
  rail.dataset.pendingSection = btn.dataset.leSection;
  // Light the button now, so the click reads as received rather than ignored.
  rail.querySelectorAll('.le-sidebar-btn').forEach((b) => b.classList.toggle('active', b === btn));
}, true);
// community.js (57 KB) is lazy — loads on the first Community render. Its only
// dependency is api.js, which is already in the boot graph, so the whole 57 KB
// is a clean cut. Nothing flows INTO the module, so no cached-callback trick.
let _communityMod;
async function _loadCommunity() {
  if (!_communityMod) _communityMod = await import('./modules/community.js');
  return _communityMod;
}
async function renderCommunity(...args) { return (await _loadCommunity()).renderCommunity(...args); }
import { setAutomationCallbacks, renderAutomations } from './modules/automations.js';
// meetings.js is the small shared core both owner tabs read: the one fetch of
// meetings-list, the one array, the isUpcoming predicate and the realtime
// channel. Kept lazy because the ONLY thing that needs it at boot is the
// Scribe nav badge, and that is deferred to idle (see _paintMeetingsBadge).
let _meetingsMod;
async function _loadMeetings() {
  if (!_meetingsMod) _meetingsMod = await import('./modules/meetings.js');
  return _meetingsMod;
}
// Booked is its own module directory now (modules/booked/), and it is the
// only importer of booking-question-editor.js (38 KB) and
// weekly-hours-editor.js — so this chunk, not the shared core, is what stays
// off the boot graph until the owner opens the tab.
let _bookedMod;
async function _loadBooked() {
  if (!_bookedMod) _bookedMod = await import('./modules/booked/index.js');
  return _bookedMod;
}
async function renderBooked(...args) { return (await _loadBooked()).renderBooked(...args); }
// ⚠️ MUST NOT FORCE THE CHUNK TO LOAD. This is called on every view swap, so
// importing here would pull Booked's chunk the first time the coach opens any
// other tab. Same guard shape as deactivateLeadEngine above.
function deactivateBooked(...args) { return _bookedMod ? _bookedMod.deactivateBooked(...args) : undefined; }
// Scribe is its own module too — meetings.js keeps only what they share.
let _scribeMod;
async function _loadScribe() {
  if (!_scribeMod) _scribeMod = await import('./modules/scribe/index.js');
  return _scribeMod;
}
async function renderScribe(...args) { return (await _loadScribe()).renderScribe(...args); }
// ⚠️ MUST NOT FORCE THE CHUNK TO LOAD. Same shape and same reason as
// deactivateBooked above: this is called on every view swap, so importing here
// would pull Scribe's chunk the first time the coach opens any other tab.
function deactivateScribe(...args) { return _scribeMod ? _scribeMod.deactivateScribe(...args) : undefined; }
async function refreshMeetingsList(...args) { return (await _loadMeetings()).refreshList(...args); }
import {
  setCheckinFormsUser, loadForms, initCheckinFormEvents, renderFormsList, getForms,
} from './modules/checkin-forms.js';
import {
  setCheckinReviewUser, setCheckinReviewClients, setCheckinReviewCoachSettings, loadCheckins,
  getCheckins, openCheckinView, initCheckinReviewEvents, initCheckinsRealtime, renderPendingList,
} from './modules/checkin-review.js';
import { initCoachTasks, setCoachTasksClients } from './modules/coach-tasks.js';
// Builder chain (builder, agreement-builder, workout-builder, exercise-library)
// is lazy — all 4 modules load together when the user first clicks the Builder
// tab OR the first time an app.js handler calls one of their functions
// (e.g. window._openSendAgreement → loadAgreementTemplates). ~234 KB chunk.
let _builderChain;
let _builderCachedUser = null;
async function _loadBuilderChain() {
  if (!_builderChain) {
    const [builder, agreement, workout, exercise] = await Promise.all([
      import('./modules/builder.js'),
      import('./modules/agreement-builder.js'),
      import('./modules/workout-builder.js'),
      import('./modules/exercise-library.js'),
    ]);
    if (_builderCachedUser) {
      builder.setBuilderUser(_builderCachedUser);
      agreement.setAgreementUser(_builderCachedUser);
      workout.setWorkoutBuilderUser(_builderCachedUser);
      exercise.setExerciseLibraryUser(_builderCachedUser);
    }
    builder.setBuilderCallbacks({ renderProgramList: workout.renderProgramList, renderAutomations });
    builder.initBuilderEvents();
    workout.initWorkoutBuilderEvents();
    exercise.initExerciseLibraryEvents();
    // Data preload — MUST await so that renderBuilder() below has programs
    // and exercises populated before it runs. Previously fire-and-forget,
    // which caused a blank Builder on first visit (data arrived after the
    // initial render). Switching tabs then back re-ran the render with
    // data loaded, which is why the "click away and back" workaround worked.
    await Promise.all([
      workout.loadPrograms(),
      exercise.loadExercises(),
    ]);
    _builderChain = { builder, agreement, workout, exercise };
  }
  return _builderChain;
}
async function renderBuilder(...args) { return (await _loadBuilderChain()).builder.renderBuilder(...args); }
async function loadAgreementTemplates(...args) { return (await _loadBuilderChain()).agreement.loadTemplates(...args); }
async function sendAgreement(...args) { return (await _loadBuilderChain()).agreement.sendAgreement(...args); }
async function loadClientAgreements(...args) { return (await _loadBuilderChain()).agreement.loadClientAgreements(...args); }
async function renderAgreementStatus(...args) { return (await _loadBuilderChain()).agreement.renderAgreementStatus(...args); }
async function loadPrograms(...args) { return (await _loadBuilderChain()).workout.loadPrograms(...args); }
async function loadExercises(...args) { return (await _loadBuilderChain()).exercise.loadExercises(...args); }
async function renderProgramList(...args) { return (await _loadBuilderChain()).workout.renderProgramList(...args); }
// Imported only for its idle preload of Chart.js, so the first client profile's charts open without a wait.
import './modules/chart-loader.js';

// client-profile.js (+ checkin-review, client-nutrition, save-history chain) is
// lazy — only loaded when the user first clicks a client. Biggest single chunk
// (~400KB across the chain). switchProfileTab is intentionally omitted from
// this lazy wrapper because the module itself attaches it to window on load,
// and the inline onclicks only exist after the profile has rendered.
let _cpMod;
let _cpPendingCallbacks = null;
async function _loadClientProfile() {
  if (!_cpMod) {
    _cpMod = await import('./modules/client-profile.js');
    if (_cpPendingCallbacks) _cpMod.setProfileCallbacks(_cpPendingCallbacks);
  }
  return _cpMod;
}
async function renderClientProfileV2(...args) {
  return (await _loadClientProfile()).renderClientProfile(...args);
}
// ── CRM 2.0 profile (the default since 2026-07-16) ───────────
// The old revfit_profile_next opt-IN flag is gone: Clayton is the only coach
// on the platform, he approved the rebuild, and the flag's last act was hiding
// finished work from the one person who wanted it. What remains is an opt-OUT
// escape hatch — the carry-over tabs (Check-Ins / Progress / Account) still
// mount classic renderers, and if one breaks in prod this is the way back:
//   localStorage.setItem('revfit_profile_classic','1')
let _cpNextMod = null;
function isClassicProfileForced() {
  try { return localStorage.getItem('revfit_profile_classic') === '1'; }
  catch (e) { return false; }
}
async function renderClientProfileNext() {
  // ⚠️ CHECK BEFORE IMPORTING, NOT AFTER. This used to await the
  // client-profile-next.js import and only then discover there
  // was no client open — client-profile-next.js:137-142 bails with "Client not
  // found" into a hidden element. That is why ~300 KB of profile chunks
  // (142 KB next + the 163 KB classic chunk it statically imports) downloaded
  // at ~900 ms on EVERY boot, on every view. Measured on production
  // 2026-09-09; the boot-trace report had it filed as lazy.
  if (!profileClientId) return;
  if (!_cpNextMod) _cpNextMod = await import('./modules/client-profile-next.js');
  // The Check-Ins and Account tabs are the classic renderers from
  // client-profile.js, which the next profile reaches through its OWN static
  // import — never through _loadClientProfile. So the callbacks registered
  // below (showView, getForms) never arrived: "Review" / "Open in Review" on
  // the Check-Ins tab opened the check-in on a hidden page and did nothing
  // visible, and check-in cards lost their form question labels. Same module
  // instance, already downloaded: this costs nothing.
  await _loadClientProfile();
  return _cpNextMod.renderClientProfileNext();
}
function setProfileCallbacks(cbs) {
  if (_cpMod) _cpMod.setProfileCallbacks(cbs);
  else _cpPendingCallbacks = cbs;
}
import { openSaveAttempt, closeSaveAttempt, initSaveAttemptListeners, setSaveAttemptCallbacks } from './modules/save-attempt.js';
import { renewBlockedWords } from './modules/change-billing/plan-answers.js';
import { emailDomainStep, emailReadyStep } from './modules/email-wizard-steps.js';
import { installFreshSession } from './modules/session-freshness.js';
import { installStaleBuildRecovery, unlessStaleBuildReload } from './modules/stale-build.js';
import { a2pPrefill } from './modules/a2p-prefill.js';
import { buildCoachSettingsSync } from './modules/coach-settings-sync.js';
import { businessNameOnFile, saveBusinessName } from './modules/coach-business-name.js';
import { renderCallerIdName, initCallerIdName } from './modules/caller-id-name.js';
import { applyMessagingSuspendSettings } from './modules/messaging-suspend-settings.js';
import { initNutritionToleranceCard } from './modules/nutrition-tolerance-card.js';
import { saveProgramStartDate } from './modules/client-profile/program-start-date.js';

const supabase = createClient(
  'https://vgnrfskpestbpvlygiua.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZnbnJmc2twZXN0YnB2bHlnaXVhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ5MDg5MDIsImV4cCI6MjA5MDQ4NDkwMn0.pgpi6ooHlXd8DVS0H7J9r6_CEJmwpKB_OFH2Ic1R7c4'
);

// ⚠️ INSTALL BEFORE ANYTHING READS A SESSION. Wraps getSession so a token
// that is expired (or about to be) is refreshed before it can reach an
// Authorization header. 94 fetches in this file and 114 more across the
// modules build that header by hand from their own getSession() — patching
// api.js alone on 2026-08-22 left 807 × `403 token is expired` in a day.
// Doing it on the shared client covers every one of them.

installFreshSession(supabase);

// Give modules access to the Supabase client immediately
_config.setSupabaseRef(supabase);

// Authenticated user — set by onAuthStateChange below
let currentUser = null;
let _appInitialized = false;

// ── Google Maps / Places Autocomplete ────────────────────────
window.onGoogleMapsLoaded = async function() {
  const { Autocomplete } = await google.maps.importLibrary('places');
  const inputEl = document.getElementById('f-addressLine1');
  if (!inputEl) return;

  initAddressAutocomplete(
    inputEl,
    document.getElementById('f-addressLine2'),
    document.getElementById('f-city'),
    document.getElementById('f-state'),
    document.getElementById('f-zipCode'),
    Autocomplete,
  );
};

// ============================================================
// STRIPE INIT (dynamic — re-runs when mode or keys change)
// ============================================================
let stripe = null;
let elements = null;
let cardElement = null;
let renewCardElement = null;
let _renewClientId   = null;

// ============================================================
// STATE
// ============================================================
let clients = [];
let _churnScores = {}; // { clientId: { score, risk, snoozed, scored } }
let _appCoachSettings = {};
let currentView    = 'dashboard';
let pipelineWindow = 90; // days shown in auto-populated pipeline stages
let editingLeadId   = null;
let profileClientId = null;
let confirmCallback = null;


let visibleColumns = loadColumnPrefs();
_state.setVisibleColumns(visibleColumns);

let leadsVolumeFilter = 'all';
let leadsRateFilter   = 'mtd';


// Sort state
//
// ⚠️ THE ROSTER OPENS ALPHABETICAL BY NAME. This was `null`, which meant no sort
// was applied at all and the table drew whatever order the API happened to
// return — which reads as random, and made finding a client a matter of
// remembering to click the Client header first, every time.
//
// `getSortValue` already lowercases the name, so this is a true A–Z rather than
// one where every lowercase name sorts after every uppercase one. Clicking the
// header still works exactly as before: the first click on Client now flips to
// Z–A, because that column is already the active sort.
let sortColumn    = 'name';
let sortDirection = 'asc'; // 'asc' | 'desc'
let _clientsViewFresh = false; // true when navigating TO clients view (triggers entrance anim)
let _selectedClients  = new Set(); // IDs of checked rows in clients table
let _pendingDelete    = new Set(); // IDs soft-deleted (hidden), awaiting undo-toast commit
let _rosterRules      = [];        // active filter-builder rules [{col, op, val}] (AND)
let _activeSmartList  = 'all';     // active smart-list id, or '__custom' for ad-hoc rules
let _bulkActionCallback = null;    // pending bulk modal confirm callback

// ============================================================
// CHURN DETECTION
// ============================================================

async function loadChurnScores() {
  try {
    const res = await _api.callFunction('churn-score-get', null, 'GET', { all: 'true' });
    // ⚠️ KEPT AFTER PULSE WAS SWITCHED OFF (10/20): each client's answer also carries lastCheckinAt, checkinAwayDays and
    // cancelRequestedAt, which the "Missed check-in" and "Cancellation requested" lists and the row badge read.
    if (res?.scores) _churnScores = res.scores;
  } catch (err) { console.warn('[churn] Failed to load scores:', err.message); }
}

function nameInitials(c) {
  const parts = String(c.name || c.firstName || '?').trim().split(/\s+/);
  return ((parts[0][0] || '?') + (parts[1] ? parts[1][0] : '')).toUpperCase();
}
const _AVATAR_COLORS = ['#4f7cee', '#8a63e8', '#d75f92', '#d9822f', '#28a8a2', '#b3893a', '#647fe8', '#4b9e56'];
function avatarColor(c) {
  const s = String(c.id || c.name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return _AVATAR_COLORS[h % _AVATAR_COLORS.length];
}

// The Pulse breakdown card (score ring, At Risk / Quiet / Healthy, signal bars, Snooze 14d) that used to sit here
// had no caller left; it went on 10/20 with Pulse and At Risk.

// ============================================================
// SUPABASE REAL-TIME LISTENERS
// ============================================================
// ── Session Helpers ──────────────────────────────────────────
// MOVED to modules/session-format.js (standing rule ③): window.updateSessionSection and window.setSessionFormat.

// ============================================================
// ============================================================
// CURRENCY
// ============================================================
function applyCurrencyToUI(code) {
  const entry = CURRENCIES.find(c => c.code === code) || CURRENCIES[0];
  setCurrencySymbol(entry.symbol);
  // Update every currency prefix span in the DOM
  document.querySelectorAll('.currency-prefix').forEach(el => {
    el.textContent = entry.symbol;
  });
}
window.saveCurrencySetting = function() {
  const code = document.getElementById('s-currency')?.value || 'USD';
  const settings = loadSettings();
  settings.currency = code;
  saveSettingsToStorage(settings);
  applyCurrencyToUI(code);
  renderAll();
  showToast('Currency updated.', 'success');
  document.getElementById('btn-save-currency')?._dirtyReset?.();
};

// ⚠️ '' MEANS "FOLLOW MY CURRENCY", NOT "MILES". Storing a concrete unit the
// coach never chose would freeze today's guess forever — change the currency
// to euros later and the distances would stay in miles with nothing on screen
// explaining why. An empty value keeps the setting derived, and the server
// derives it the same way (netlify/functions/_shared/distance-unit.js).
window.saveDistanceUnitSetting = function() {
  const unit = document.getElementById('s-distance-unit')?.value || '';
  const settings = loadSettings();
  if (unit) settings.distanceUnit = unit;
  else delete settings.distanceUnit;
  saveSettingsToStorage(settings);
  showToast(unit === 'mi' ? 'Distances shown in miles.'
    : unit === 'km' ? 'Distances shown in kilometres.'
    : 'Distances now follow your currency.', 'success');
  document.getElementById('btn-save-distance-unit')?._dirtyReset?.();
};

// ============================================================
// DATE FORMAT
// ============================================================
window.saveDateFormatSetting = function() {
  const fmt = document.getElementById('s-date-format')?.value || 'MMM D, YYYY';
  const settings = loadSettings();
  settings.dateFormat = fmt;
  saveSettingsToStorage(settings);
  setDateFormat(fmt);
  renderAll();
  showToast('Date format updated.', 'success');
  document.getElementById('btn-save-date-format')?._dirtyReset?.();
};

// ============================================================
// UNIT SYSTEM
// ============================================================
window.setUnitSystem = function(system) {
  const settings = loadSettings();
  settings.unitSystem = system;
  saveSettingsToStorage(settings);
  document.querySelectorAll('#unit-system-control .theme-seg-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.unitSystem === system);
  });
  showToast(`Units set to ${system === 'metric' ? 'Metric (kg, cm, km)' : 'Imperial (lbs, ft, miles)'}.`, 'success');
};

// ============================================================
// Verification
// ============================================================
window.verifyEmail = _api.verifyEmail;
window.verifyPhone = _api.verifyPhone;

window.clearVerifyStatus = _api.clearVerifyStatus;

window.onPaymentSourceChange = function(source) {
  const stripeSection = document.getElementById('stripe-payment-section');
  const btnSave       = document.getElementById('btn-save');
  if (stripeSection) stripeSection.style.display = source === 'stripe' ? '' : 'none';
  if (btnSave)       btnSave.style.display        = '';
};

window.toggleExistingClient = function(checked) {
  const existFields = document.getElementById('existing-client-fields');
  const progStart = document.getElementById('program-start-date-group');
  const stripeSection = document.getElementById('stripe-payment-section');
  const btnSave = document.getElementById('btn-save');
  if (existFields) existFields.classList.toggle('hidden', !checked);
  if (progStart) progStart.classList.toggle('hidden', !checked);
  if (checked) {
    // Hide payment section and set clean button text
    if (stripeSection) stripeSection.style.display = 'none';
    if (btnSave) { btnSave.textContent = 'Add Client'; btnSave.style.display = ''; }
  } else {
    // Restore payment section and button text based on current payment source
    const source = document.getElementById('f-paymentSource')?.value;
    if (source === 'stripe') {
      if (stripeSection) stripeSection.style.display = '';
      if (btnSave) btnSave.textContent = 'Charge & Add Client';
    } else {
      if (btnSave) { btnSave.textContent = 'Add Client'; btnSave.style.display = ''; }
    }
  }
};


// Returns "Mar 2026" from a YYYY-MM-DD string or Date object
function periodLabel(dateOrStr) {
  const d = typeof dateOrStr === 'string' ? new Date(dateOrStr + 'T00:00:00') : dateOrStr;
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

// getOverdueMonthCount MOVED to modules/utils.js (standing rule ③)


// ============================================================
// MODAL OPEN / CLOSE
// ============================================================

// ============================================================
// DUPLICATE CLIENT CHECK
// ============================================================
window.goToExistingClient = _clients.goToExistingClient;
window.proceedDespiteDuplicate = _clients.proceedDespiteDuplicate;

// ============================================================
// FORM SUBMIT
// ============================================================
async function handleFormSubmit(e) {
  e.preventDefault();
  const f = document.getElementById('client-form');
  const fd = new FormData(f);
  const g = k => (fd.get(k) || '').trim();
  const gn = k => { const v = parseFloat(g(k)); return isNaN(v) ? null : v; };
  const gi = k => { const v = parseInt(g(k)); return isNaN(v) ? 0 : v; };

  const firstName = g('firstName');
  const lastName  = g('lastName');
  const name = [firstName, lastName].filter(Boolean).join(' ');
  if (!firstName) { showToast('Please enter a first name.', 'error'); return; }

  // ⚠️ A CONVERSION IS A SALE, AND A SALE IS A PAYMENT. There are three routes
  // through this handler that end in the browser's own `addClient` insert — the
  // existing-client path, the Stripe path and the no-charge path — and only the
  // Stripe one has a server conversion behind it. Without this check, converting
  // a lead with the payment source set to anything else would quietly write the
  // client from the browser and never mark the lead sold, which is the exact
  // half-finished state the whole conversion exists to prevent. Refuse instead,
  // and say why.
  if (_state.convertingLeadId && (g('paymentSource') !== 'stripe' || g('billingStructure') === 'comped')) {
    showToast('Converting a lead needs a card payment. Add them as a client directly if there is no charge.', 'error');
    return;
  }

  // Amount validation
  const billingCheck = g('billingStructure') || 'MRR';
  const missingAmount =
    (billingCheck === 'MRR'          && !parseFloat(g('mrrAmount'))) ||
    (billingCheck === 'PIF'          && !parseFloat(g('agreementValue'))) ||
    (billingCheck === 'installments' && !parseFloat(g('agreementValue')));
  if (missingAmount) {
    const label = billingCheck === 'MRR' ? 'monthly amount' : 'agreement value';
    showToast(`Please enter an ${label} before saving.`, 'error');
    const fieldId = billingCheck === 'MRR' ? 'f-mrrAmount' : 'f-agreementValue';
    document.getElementById(fieldId)?.focus();
    return;
  }

  // Duplicate check, skipped when the coach clicked "Add Anyway". ⚠️ The warning's button sets the SHARED flag in
  // state.js (clients.js proceedDespiteDuplicate); a private copy here was never set, so Add Anyway never worked (10/9).
  if (!_state._skipDuplicateCheck) {
    const dupes = findPotentialDuplicates(firstName, lastName, g('email'), g('phone'));
    if (dupes.length > 0) {
      showDuplicateWarning(dupes);
      return;
    }
  }
  _state.setSkipDuplicateCheck(false); // reset after use

  const paymentSource = g('paymentSource') || 'stripe';
  const billingStructure = g('billingStructure') || 'MRR';

  const data = {
    firstName,
    lastName,
    name, // combined for display throughout the app
    // Status: new clients always start active
    status:                'active',
    serviceType:           g('serviceType')         || 'online',
    billingStructure,
    paymentSource,
    signUpDate:            g('signUpDate')          || null,
    programStartDate:      g('programStartDate')    || null,
    lengthOfAgreement:     gi('lengthOfAgreement'),
    bonusMonths:           gi('bonusMonths'),
    agreementValue:        gn('agreementValue'),
    moneyCollectedUpfront: null, // set programmatically below based on billing
    installmentCount:      gi('installmentCount'),
    mrrAmount:             gn('mrrAmount'),
    continuityRate:        gn('continuityRate'),
    _notesText:            g('notes'),
    phone:                 g('phone')               || null,
    email:                 g('email')               || null,
    addressLine1:          g('addressLine1')        || null,
    addressLine2:          g('addressLine2')        || null,
    city:                  g('city')                || null,
    state:                 g('state')               || null,
    zipCode:               g('zipCode')             || null,
    birthdate:             g('birthdate')           || null,
    instagramHandle:       g('instagramHandle')     || null,
    leadSourceId:          g('leadSourceId')        || null,
    referredByName:        g('referredByName')      || null,
    referredById:          g('referredById')        || null,
    sessionFormat:         g('sessionFormat')       || null,
    sessionCredits:        gi('sessionCredits')     || null,
    sessionsPerPeriod:     gi('sessionsPerPeriod')  || null,
    sessionPeriod:         g('sessionPeriod')       || null,
    billingStartDate:      g('signUpDate')          || null,
    // Onboarding automation
    onboardingAgreementTemplateId: g('agreementTemplate') || null,
    onboardingFormId:              g('onboardingForm')     || null,
    // Toggle off = the workflow parks at app_invite_held after the form and
    // the coach sends the app invite manually (Invite to App on the profile).
    onboardingAutoInvite: document.getElementById('f-onboardingAutoInvite')
      ? document.getElementById('f-onboardingAutoInvite').checked
      : true,
  };

  // Build notes array for new clients; strip the temp field
  const notesText = data._notesText;
  delete data._notesText;
  data.notes = notesText
    ? [{ id: 'note_' + Date.now(), text: notesText, createdAt: new Date().toISOString() }]
    : [];

  // For MRR, auto-calculate agreement value from length × monthly rate
  if (data.billingStructure === 'MRR') {
    const totalMo = (data.lengthOfAgreement || 0) + (data.bonusMonths || 0);
    data.agreementValue    = (data.mrrAmount || 0) * totalMo;
    data.continuityRate    = null; // not applicable for MRR
  }

  // Comped / free client (friends, family, trials) — no billing, no charge, no
  // amount required, and zero revenue contribution (excluded from PIF/MRR stats).
  if (data.billingStructure === 'comped') {
    data.agreementValue = 0;
    data.mrrAmount = 0;
    data.continuityRate = null;
  }

  // Set moneyCollectedUpfront based on billing structure
  if (data.billingStructure === 'PIF') {
    data.moneyCollectedUpfront = data.agreementValue; // full amount paid upfront
  } else {
    data.moneyCollectedUpfront = 0;
  }

  // Seed totalCollected — webhook will increment this as future payments arrive
  const isExisting = document.getElementById('f-existingClient')?.checked;
  const manualTotalCollected = parseFloat(document.getElementById('f-totalCollected')?.value) || 0;
  data.totalCollected = isExisting ? manualTotalCollected : (data.moneyCollectedUpfront || 0);
  data.paymentHistory = [];
  data.paymentFailure = null;

  // Existing client: store manually-entered Stripe IDs + program start date
  if (isExisting) {
    const manualCusId = (document.getElementById('f-stripeCustomerId')?.value || '').trim();
    const manualSubId = (document.getElementById('f-stripeSubscriptionId')?.value || '').trim();
    const progStartDate = document.getElementById('f-programStartDate')?.value || '';
    if (manualCusId) data.stripeCustomerId = manualCusId;
    if (manualSubId) data.stripeSubscriptionId = manualSubId;
    if (progStartDate) data.programStartDate = progStartDate;
    data.moneyCollectedUpfront = manualTotalCollected;
  }

  // ---- EXISTING CLIENT: SKIP PAYMENT FLOW ----
  const isExistingClient = document.getElementById('f-existingClient')?.checked;
  if (isExistingClient) {
    try {
      await addClient(data);
      closeModal();
      showToast(`${name} added as existing client.`, 'success');
    } catch (err) {
      showToast('Error adding client: ' + err.message, 'error');
    }
    return;
  }

  // ---- NEW CLIENT: STRIPE PAYMENT ---- (comped never charges → falls to no-charge path below)
  if (paymentSource === 'stripe' && billingStructure !== 'comped') {
    const stripe = _state.stripe;
    const cardElement = _state.cardElement;
    const btnSave = document.getElementById('btn-save');
    const errorsEl = document.getElementById('stripe-card-errors');

    btnSave.disabled = true;
    btnSave.textContent = 'Processing…';
    btnSave.classList.add('btn-save-loading');
    if (errorsEl) { errorsEl.style.display = 'none'; errorsEl.textContent = ''; }

    try {
      // ⚠️ 0. WHEN THIS IS A LEAD CONVERSION, CLAIM THE SALE BEFORE STRIPE.
      // A payment that lands without its record is invisible to this platform
      // forever — the webhook resolves clients by stripe_customer_id and
      // silently gives up when there is no row, and nothing reconciles orphaned
      // charges. The claim is the thing that could ever be found afterwards, and
      // its charge key is what stops a retry taking a second real payment.
      const convertingLeadId = _state.convertingLeadId;
      let leadClaim = null;
      /** @type {{ rate: number, cardId: string, paymentIntentId: string }|null} set up once the client is saved */
      let monthlyAfter = null;
      if (convertingLeadId) {
        leadClaim = await callFunction('leads-convert', {
          step: 'claim',
          leadId: convertingLeadId,
          amountCents: Math.round((data.billingStructure === 'MRR' ? (data.mrrAmount || 0) : (data.agreementValue || 0)) * 100)
            || Math.round((data.mrrAmount || data.agreementValue || 0) * 100),
          contractedCents: Math.round((data.agreementValue || data.mrrAmount || 0) * 100) || null,
          billingStructure: data.billingStructure || null,
        });
      }

      // 1. Create Stripe customer
      const { customerId } = await callFunction('create-customer', { name });

      if (data.billingStructure === 'PIF') {
        // 2a. PIF — one-time charge for full agreement value
        const { clientSecret } = await callFunction('create-payment-intent', {
          amount: data.agreementValue || 0,
          customerId,
          // Stamps the intent so leads-convert can refuse one belonging to a
          // different lead, and pins the idempotency key to this attempt.
          ...(convertingLeadId ? { leadId: convertingLeadId, chargeKey: leadClaim?.chargeKey } : {}),
        });
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: { name }
          }
        });
        if (error) throw new Error(error.message);
        data.__leadPaymentIntentId = paymentIntent?.id || null;

        // Log the PIF charge in payment history
        data.paymentHistory = [{
          id:          crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
          date:        _config.localToday(),
          amount:      data.agreementValue,
          description: 'PIF — Full Payment',
          source:      'stripe',
          recordedAt:  new Date().toISOString(),
        }];

        // Monthly after the program (continuity set at sign-up, 10/13): no Stripe free trial. The rate comes off the
        // record and Change billing sets it up once the client is saved (modules/signup-continuity.js).
        const contRate = setContinuityAside(data);
        if (contRate > 0 && paymentIntent.payment_method) {
          monthlyAfter = { rate: contRate, cardId: paymentIntent.payment_method, paymentIntentId: paymentIntent.id };
        }
      } else {
        // 2b. MRR or Installments — subscription
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: { name }
        });
        if (pmError) throw new Error(pmError.message);

        // MRR: no cancel_at (runs indefinitely)
        // Installments: cancel_at after N payments, charge = agreementValue / installmentCount
        let subAmount = data.mrrAmount || 0;
        let subMonths = 0; // 0 = no cancel_at (MRR)

        if (data.billingStructure === 'installments') {
          const instCount = parseInt(data.installmentCount) || 1;
          subAmount = data.agreementValue > 0 ? data.agreementValue / instCount : 0;
          subMonths = instCount;
          data.mrrAmount = subAmount; // store installment amount in mrrAmount field
        }

        const { clientSecret, subscriptionId } = await callFunction('create-subscription', {
          customerId,
          amount: subAmount,
          paymentMethodId: paymentMethod.id,
          agreementMonths: subMonths
        });
        const { error: confirmError } = await stripeConfirmWithRetry(clientSecret);
        if (confirmError) throw new Error(confirmError.message);
        data.stripeSubscriptionId = subscriptionId;
      }

      data.stripeCustomerId = customerId;

      // ⚠️ A LEAD CONVERSION NEVER TOUCHES addClient. That is the browser insert
      // this replaced: it writes the client from here, AFTER the money has
      // moved, and a failure leaves the charge with no record anywhere. The
      // server verifies the payment with Stripe and owns every write after it.
      let newClientId;
      if (convertingLeadId) {
        const done = await callFunction('leads-convert', {
          step: 'complete',
          conversionId: leadClaim.conversionId,
          paymentIntentId: data.__leadPaymentIntentId || null,
          subscriptionId: data.stripeSubscriptionId || null,
          clientData: data,
        });
        newClientId = done.clientId;
        _state.setConvertingLeadId(null);
      } else {
        newClientId = await addClient(data, { fireNewClient: !monthlyAfter });
      }
      // Monthly after the program, now the client exists; before onboarding starts and before a 'new client' flow
      // fires, so an agreement either sends states it
      let monthlyNotSetUp = null;
      if (monthlyAfter && newClientId) {
        const why = await startMonthlyAfterProgram({ clientId: newClientId, ...monthlyAfter });
        if (why) monthlyNotSetUp = notSetUpWords(name, why, monthlyAfter.rate);
        if (!convertingLeadId) fireNewClientEvent(newClientId, data.serviceType);
      }
      // Auto-issue first month's session credits for in-person/hybrid clients (modules/add-client-sessions.js)
      await firstMonthAtSignUp(data, newClientId, currentUser?.id);
      // Trigger onboarding automation (agreement → form → app account)
      if (newClientId && (data.onboardingAgreementTemplateId || data.onboardingFormId)) {
        fetch('/.netlify/functions/onboarding-advance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (await _config.getSupabase().auth.getSession()).data.session.access_token },
          body: JSON.stringify({ clientId: newClientId, action: 'start' }),
        }).catch(err => console.warn('[onboarding] trigger failed:', err.message));
      }
      closeModal();
      renderAll();
      showToast(`Payment successful! ${name} added.`, 'success');
      if (monthlyNotSetUp) showToast(monthlyNotSetUp, 'error', { duration: 20000 });
    } catch (err) {
      if (errorsEl) {
        errorsEl.textContent = err.message;
        errorsEl.style.display = '';
      }
    } finally {
      btnSave.disabled = false;
      btnSave.textContent = 'Charge & Add Client';
      btnSave.classList.remove('btn-save-loading');
    }
    return;
  }

  // ---- NEW CLIENT: NON-STRIPE ----
  // Money taken outside Stripe (cash, check, Venmo, a card run elsewhere) only
  // ever set moneyCollectedUpfront, which feeds Collected. Revenue is summed
  // from paymentHistory, so a PIF paid this way showed up as $6,000 Collected
  // and $0 Revenue side by side. Log the payment the Stripe PIF branch above
  // already logs for itself.
  //
  // Only reachable for a genuinely new, non-Stripe client: editing, existing
  // clients and the Stripe flow all return before this point. Existing clients
  // must NOT seed one — their upfront figure is money collected before they
  // joined, and dating it today would inflate this month's Revenue.
  if (Number(data.moneyCollectedUpfront) > 0 && !(data.paymentHistory || []).length) {
    data.paymentHistory = [{
      id:          crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      date:        _config.localToday(),
      amount:      Number(data.moneyCollectedUpfront),
      description: 'PIF — Full Payment',
      source:      'external',
      recordedAt:  new Date().toISOString(),
    }];
  }

  try {
    const newClientId = await addClient(data);
    // Auto-issue first period's session credits for in-person/hybrid clients (modules/add-client-sessions.js)
    await firstMonthAtSignUp(data, newClientId, currentUser?.id);
    // Trigger onboarding automation (agreement → form → app account)
    if (newClientId && (data.onboardingAgreementTemplateId || data.onboardingFormId)) {
      fetch('/.netlify/functions/onboarding-advance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (await _config.getSupabase().auth.getSession()).data.session.access_token },
        body: JSON.stringify({ clientId: newClientId, action: 'start' }),
      }).catch(err => console.warn('[onboarding] trigger failed:', err.message));
    }
    closeModal();
    renderAll();
    showToast(`${name} added.`, 'success');
  } catch (err) {
    showToast('Error adding client: ' + err.message, 'error');
  }
}

// ============================================================
// RENDER: DASHBOARD
// ============================================================
// The dashboard is modules/coach-dashboard/ (docs/superpowers/plans/2026-10-14-dashboard-build.md), worked out on the
// server. Clayton, 10/16: "there is no need to load anything from the old dashboard": the old one, its cards and its
// on/off switch are gone. Called by navigation (renderCurrentView) and at boot as soon as the view shows.
function renderDashboard() {
  const view = document.getElementById('view-dashboard');
  if (view) showCoachDashboard(view);
}


// ============================================================
// COHORT RETENTION ANALYSIS
// ============================================================


// ============================================================
// CHART DRILL-DOWN
// ============================================================


window.markContinuityCollected = function(clientId) {
  window.openRecordPaymentModal(clientId, 'continuity');
};

// ============================================================
// RENDER: CLIENTS TABLE
// ============================================================
/**
 * Has this client missed their check-in?
 *
 * The rule lives in roster-filters.js now, so a real test can import and run
 * it. This is a one-line adapter onto app.js's live _churnScores map, read
 * fresh on every call.
 */
function missedCheckin(c) { return _missedCheckin(c, _churnScores); }

/**
 * Has this client asked to cancel, and is that request still open?
 *
 * The decision itself — ignore status, read cancelRequestedAt, "not loaded"
 * means false, same rule as missedCheckin above — lives in roster-filters.js
 * now, so a real test can import and run it. This is a one-line adapter onto
 * app.js's live _churnScores map, read fresh on every call.
 */
function hasOpenCancellationRequest(c) { return _hasOpenCancellationRequest(c, _churnScores); }

function getFiltered() {
  const search = (document.getElementById('client-search')?.value || '').toLowerCase();
  const rules  = _rosterRules.filter(r => r.val !== '' && r.val != null);

  if (search) loadClientLeadSources(); // a client's source name is searchable once the list is in
  return clients.filter(c => {
    if (_pendingDelete.has(c.id)) return false; // soft-deleted, awaiting undo commit
    if (search) {
      const haystack = [
        c.name, c.firstName, c.lastName,
        c.phone, c.email,
        c.serviceType, c.billingStructure, c.status,
        c.paymentSource, clientSourceName(c, cachedLeadSources()),
        c.city, c.state, c.zipCode,
        c.instagramHandle, c.referredByName,
        c.signUpDate, c.programStartDate,
        c.checkinDay,
        ...(c.tags || []),
      ].map(v => (v || '').toLowerCase()).join(' ');
      const terms = search.split(',').map(t => t.trim()).filter(Boolean);
      if (!terms.every(term => haystack.includes(term))) return false;
    }
    for (const rule of rules) { if (!_matchRosterRule(c, rule)) return false; }
    return true;
  });
}

function getSortValue(c, key) {
  switch (key) {
    case 'name':                  return (c.name || '').toLowerCase();
    case 'status':                return c.status || '';
    case 'service':               return c.serviceType || '';
    case 'billing':               return c.billingStructure || '';
    case 'payment':               return c.paymentSource || '';
    case 'signUpDate':            return c.signUpDate || '';
    case 'programStartDate':      return c.programStartDate || '';
    case 'agreement':             return parseInt(c.lengthOfAgreement) || 0;
    case 'agreementValue':        return Number(c.agreementValue) || 0;
    case 'ltv':                   return getClientLifetimeSpend(c);
    case 'moneyCollectedUpfront': return Number(c.moneyCollectedUpfront) || 0;
    case 'mrrAmount':             return getMonthlyRecurring(c);
    case 'continuityRate':        return continuityRateOf(c);
    case 'months':                return getMonthsAsClient(c);
    case 'endDate': {
      const d = getEndDate(c);
      return d ? d.getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
    }
    case 'renewal': {
      const d = getDaysUntilEnd(c);
      return d === null ? (sortDirection === 'asc' ? Infinity : -Infinity) : d;
    }
    case 'installmentCount': return Number(c.installmentCount) || 0;
    case 'phone': return c.phone || '';
    case 'email': return c.email || '';
    case 'checkinDay': {
      const order = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
      const idx = order.indexOf((c.checkinDay || '').toLowerCase());
      return idx === -1 ? (sortDirection === 'asc' ? 999 : -1) : idx;
    }
    default: return '';
  }
}

function renderClientsSummary(filtered) {
  const el = document.getElementById('clients-summary');
  if (!el) return;

  const total     = filtered.length;
  const active    = filtered.filter(c => c.status === 'active').length;
  const cancelled = filtered.filter(c => c.status === 'cancelled').length;
  const mrr       = filtered
    .filter(c => c.status === 'active' && c.billingStructure === 'mrr')
    .reduce((s, c) => s + (c.mrrAmount || 0), 0);
  const pipeline  = filtered
    .filter(c => c.status === 'active')
    .reduce((s, c) => s + (c.agreementValue || 0), 0);
  const collected = filtered
    .filter(c => c.status === 'active')
    .reduce((s, c) => s + (c.moneyCollectedUpfront || 0), 0);

  const pill = (val, label, cls = '') =>
    `<span class="cs-pill"><span class="cs-pill-val${cls ? ' ' + cls : ''}">${val}</span>${label}</span>`;
  const div = `<span class="cs-divider">·</span>`;

  const parts = [
    pill(total, total === 1 ? ' client' : ' clients'),
    active < total ? pill(active, ' active', 'cs-pill-val--ok') : '',
    cancelled      ? pill(cancelled, ' cancelled', 'cs-pill-val--muted') : '',
    mrr            ? div + pill(formatCurrency(mrr), '/mo MRR') : '',
    pipeline       ? div + pill(formatCurrency(pipeline), ' pipeline') : '',
    collected      ? div + pill(formatCurrency(collected), ' collected') : '',
  ];

  el.innerHTML = parts.filter(Boolean).join(' ');
}

// Plain-language explanations shown on column-header hover (5th-grade voice).
const COLUMN_TIPS = {
  name:                  { t: "Client", b: "Everyone you coach. Click a row to open their full profile." },
  status:                { t: "Status", b: "Active, pending cancellation, or cancelled." },
  service:               { t: "Service", b: "How you deliver coaching - online, hybrid, or in-person." },
  billing:               { t: "Billing", b: "How they pay - monthly (MRR), paid in full (PIF), installments, or comped." },
  signUpDate:            { t: "Sign up", b: "The day they became a client." },
  agreementValue:        { t: "Value", b: "The total price of their current agreement." },
  moneyCollectedUpfront: { t: "Collected", b: "How much money you've collected from this client so far." },
  mrrAmount:             { t: "MRR", b: "What this client pays you every month: a monthly plan, or continuity once it starts. Paid in full and installments are not counted." },
  ltv:                   { t: "LTV", b: "Lifetime value - everything this client has ever paid you." },
  months:                { t: "Months", b: "How many months they've been a client." },
  renewal:               { t: "Renewal", b: "When their agreement renews. Shows a flag when action is needed soon, or “Auto-renews” for month-to-month clients." },
  checkinDay:            { t: "Check-in", b: "The weekday you review this client's check-in." },
  agreement:             { t: "Agreement", b: "The length of their agreement in months (plus any bonus months)." },
  continuityRate:        { t: "Continuity", b: "The monthly rate they roll onto after their current term ends." },
  programStartDate:      { t: "Start date", b: "The day their program started." },
  endDate:               { t: "End date", b: "The day their current agreement ends." },
  installmentCount:      { t: "Installments", b: "How many installment payments their plan has." },
  payment:               { t: "Payment", b: "How they pay you." },
  email:                 { t: "Email", b: "The client's email address." },
  phone:                 { t: "Phone", b: "The client's phone number." },
  notes:                 { t: "Notes", b: "Your latest note on this client." },
};

// ── Delete with undo: modules/client-delete.js (rosterDelete, bulkDelete; moved 2026-10-23) ──
setClientDeleteDeps({ getClients: () => clients, pendingDelete: _pendingDelete, selectedClients: _selectedClients, renderClientsTable });
window.rosterDelete = rosterDelete;
window.bulkDelete = bulkDelete;

function renderClientsTable() {
  const filtered = getFiltered();
  renderClientsSummary(filtered);

  // Sort
  if (sortColumn) {
    filtered.sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ?  1 : -1;
      return 0;
    });
  }

  // Determine which columns to show, in the coach's saved drag order
  const visCols = _applyColOrder(COLUMNS.filter(col => col.alwaysVisible || visibleColumns[col.key]));

  // Render thead
  const thead = document.getElementById('clients-thead');
  if (thead) {
    const allSelected = filtered.length > 0 && filtered.every(c => _selectedClients.has(c.id));
    const someSelected = !allSelected && filtered.some(c => _selectedClients.has(c.id));
    thead.innerHTML = `<tr><th class="col-check"><input type="checkbox" id="select-all-check" ${allSelected ? 'checked' : ''} onchange="window.toggleSelectAll(this.checked)" /></th>${visCols.map(col => {
      const isSorted = sortColumn === col.key;
      const arrow = col.sortable
        ? `<span class="sort-arrow${isSorted ? '' : ' sort-arrow--inactive'}">${isSorted ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}</span>`
        : '';
      const thAlignCls  = col.align === 'right' ? ' th-right' : col.align === 'center' ? ' th-center' : '';
      const thcAlignCls = col.align === 'right' ? ' r' : col.align === 'center' ? ' c' : '';
      const cls = `${col.sortable ? 'th-sortable ' : ''}col-${col.key}${thAlignCls}`;
      const heading = `<span class="thl">${escHtml(col.label)}</span>`;
      // Header alignment matches the column's content. Research-backed: for
      // right-aligned columns the sort arrow goes to the LEFT of the label so
      // the label stays flush with the numbers below it.
      const inner = col.align === 'right' ? `${arrow}${heading}` : `${heading}${arrow}`;
      const tip = COLUMN_TIPS[col.key];
      const tipAttrs = tip ? ` data-tip-title="${escHtml(tip.t)}" data-tip-body="${escHtml(tip.b)}"` : '';
      return `<th class="${cls}" data-colkey="${col.key}"${col.w ? ` style="width:${col.w}px"` : ''}${col.sortable ? ` data-sortcol="${col.key}"` : ''}${isSorted ? ' data-sorted="true"' : ''}${tipAttrs}><span class="thc${thcAlignCls}">${inner}</span></th>`;
    }).join('')}</tr>`;
    // Restore indeterminate state (can't be set via HTML attribute)
    const allCheck = document.getElementById('select-all-check');
    if (allCheck) allCheck.indeterminate = someSelected;

    // Enable drag-to-reorder on the header row (check + actions stay pinned).
    _initColumnDrag(thead);
  }

  // Render tbody
  const tbody = document.getElementById('clients-tbody');
  if (filtered.length === 0) {
    const search = (document.getElementById('client-search')?.value || '').trim();
    const isFiltered = !!search || _completeRules(_rosterRules).length > 0;
    const msg = isFiltered
      ? 'No clients match your current search or filters.'
      : 'No clients yet — add your first client to get started.';
    tbody.innerHTML = `<tr><td colspan="${visCols.length + 1}" class="empty-cell">${msg}</td></tr>`;
    return;
  }

  // Entrance animation — only when navigating to the view, not on filter/sort
  const viewEl = document.getElementById('view-clients');
  if (_clientsViewFresh && viewEl) {
    _clientsViewFresh = false;
    viewEl.classList.add('view-enter');
    setTimeout(() => viewEl.classList.remove('view-enter'), 1200);
  }

  tbody.innerHTML = filtered.map((c, i) => {
    const endDate  = getEndDate(c);
    const months   = getMonthsAsClient(c);
    const renewal  = isRenewalNeeded(c);
    const days     = getDaysUntilEnd(c);
    const agreeLbl = `${c.lengthOfAgreement || 0}mo${c.bonusMonths ? ` +${c.bonusMonths}` : ''}`;

    let renewalCell;
    const clientHasContinuity = hasContinuity(c);

    if (c.status === 'cancelled') {
      renewalCell = `<span class="dim">—</span>`;
    } else if (c.status === 'pending_cancellation') {
      const cancelMs   = c.inactiveDate ? new Date(c.inactiveDate + 'T12:00:00') - new Date(_config.localToday() + 'T12:00:00') : null;
      const cancelDays = cancelMs !== null ? Math.ceil(cancelMs / 86400000) : null;
      renewalCell = cancelDays !== null
        ? `<span class="renewal-flag cancels">Cancels in ${cancelDays}d</span>`
        : `<span class="renewal-flag cancels">Cancels soon</span>`;
    } else if (clientHasContinuity && days !== null && days <= 31 && days >= 0) {
      renewalCell = `<span class="renewal-flag continuity">Continuity in ${days}d</span>`;
    } else if (!clientHasContinuity && days !== null && days <= 31 && days >= 0) {
      renewalCell = `<span class="renewal-flag ${days <= 14 ? 'urgent' : ''}">Renew in ${days}d</span>`;
    } else {
      // Show the actual renewal/end date when there is one; auto-renewing
      // (continuity) clients have no fixed end date, so say that plainly
      // instead of a vague "on track".
      renewalCell = endDate
        ? `<span class="txt" style="font-variant-numeric:tabular-nums">${formatDateObj(endDate)}</span>`
        : (clientHasContinuity ? `<span class="dim">Auto-renews</span>` : `<span class="cell-empty">—</span>`);
    }

    const _e = '<span class="cell-empty">—</span>';
    // MRR column = what the client pays per month RIGHT NOW (flat MRR,
    // installment, or active continuity). Continuity column = the future
    // roll-to rate, shown only while the client is still in their term.
    const monthlyNow      = getMonthlyRecurring(c);
    const continuityFuture = continuityRateOf(c) > 0 && days !== null && days > 0;
    const svcCls = { online: 'online', hybrid: 'hybrid', 'in-person': 'in-person', 'in_person': 'in-person' }[c.serviceType] || '';
    // Identity-stable avatar colour (decorative — status reads from the pill). Photo when set; otherwise initials + a "no photo"
    // badge so coaches can see at a glance who to nudge.
    const _avatar = c.avatarUrl
      // ⚠️ 900 px OF PHONE CAMERA IN A 36 px CIRCLE. Sampled originals measured
      // 63,514 / 159,109 / 299,017 bytes (2026-09-09); the Clients list paints
      // 30 of them today and 300 at target scale, all eagerly, all at once —
      // 19 images took 2,940 ms to settle. The mobile app already crops avatars
      // to 300 pt (audit §9, E2 withdrawn); the real remaining fix is a
      // thumbnail derivative (E3), which is a write-site change and its own task.
      // width/height match the real rendered box: #view-clients .cl-avatar
      // (styles.css:29923) is 36px, an ID-scoped rule that overrides the base
      // .cl-avatar's 26px (styles.css:1804) — ruling PS4. They are overridden
      // by the img's own width:100%/height:100% — they exist to reserve the
      // box, not to size the image.
      ? `<span class="cl-avatar"><img class="cl-avatar-img" src="${escHtml(c.avatarUrl)}" alt="" loading="lazy" decoding="async" width="36" height="36"></span>`
      : `<span class="cl-avatar" style="background:${avatarColor(c)}">${escHtml(nameInitials(c))}<span class="cl-nophoto" title="No profile photo"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 5.5h2l1-1.3h4l1 1.3h2v7H3z"/><circle cx="8" cy="9" r="2"/></svg></span></span>`;
    const _money = (n) => n ? `<div class="cl-cin r">${formatCurrency(n)}</div>` : `<div class="cl-cin r">${_e}</div>`;
    const cellMap = {
      name:                  `<td class="col-name"><div class="cl-cin">${_avatar}<span class="client-name-link" data-client-id="${c.id}" onclick="showClientProfile('${c.id}')">${escHtml(c.name)}</span></div></td>`,
      phone:                 `<td class="col-phone"><div class="cl-cin">${c.phone ? `<a href="tel:${escHtml(c.phone)}" class="cell-link">${escHtml(c.phone)}</a>` : _e}</div></td>`,
      email:                 `<td class="col-email"><div class="cl-cin">${c.email ? `<a href="mailto:${escHtml(c.email)}" class="cell-link">${escHtml(c.email)}</a>` : _e}</div></td>`,
      status:                `<td class="col-status"><div class="cl-cin c"><span class="badge-status badge-${c.status}">${label(c.status)}</span>${hasOpenCancellationRequest(c) ? `<span class="renewal-flag" title="Open cancellation request. Their Billing card shows where it stands.">Cancellation requested</span>` : ''}</div></td>`,
      service:               `<td class="col-service"><div class="cl-cin c"><span class="badge-service badge-service--${svcCls}">${label(c.serviceType)}</span></div></td>`,
      billing:               `<td class="col-billing"><div class="cl-cin c"><span class="txt${c.billingStructure === 'comped' ? ' dim' : ''}">${label(c.billingStructure)}</span></div></td>`,
      installmentCount:      `<td class="col-installmentCount"><div class="cl-cin c">${c.billingStructure === 'installments' && c.installmentCount ? `${c.installmentCount} payments` : _e}</div></td>`,
      payment:               `<td class="col-payment"><div class="cl-cin c">${c.paymentSource ? label(c.paymentSource) : _e}</div></td>`,
      signUpDate:            `<td class="col-signUpDate"><div class="cl-cin c"><span class="txt" style="font-variant-numeric:tabular-nums">${formatDate(c.signUpDate)}</span></div></td>`,
      programStartDate:      `<td class="col-programStartDate"><div class="cl-cin c"><span class="txt" style="font-variant-numeric:tabular-nums">${formatDate(c.programStartDate)}</span></div></td>`,
      agreement:             `<td class="col-agreement"><div class="cl-cin c">${agreeLbl}</div></td>`,
      agreementValue:        `<td class="col-agreementValue">${_money(Number(c.agreementValue) || 0)}</td>`,
      moneyCollectedUpfront: `<td class="col-moneyCollectedUpfront">${_money(Number(c.moneyCollectedUpfront) || 0)}</td>`,
      mrrAmount:             `<td class="col-mrrAmount">${_money(monthlyNow > 0 ? monthlyNow : 0)}</td>`,
      ltv:                   `<td class="col-ltv">${_money(getClientLifetimeSpend(c) || 0)}</td>`,
      continuityRate:        `<td class="col-continuityRate"><div class="cl-cin r">${continuityFuture ? formatCurrency(continuityRateOf(c)) + '/mo' : _e}</div></td>`,
      months:                `<td class="col-months"><div class="cl-cin c">${months}</div></td>`,
      endDate:               `<td class="col-endDate"><div class="cl-cin c"><span class="txt" style="font-variant-numeric:tabular-nums">${endDate ? formatDateObj(endDate) : _e}</span></div></td>`,
      renewal:               `<td class="col-renewal"><div class="cl-cin c">${renewalCell}</div></td>`,
      checkinDay:            `<td class="col-checkinDay"><div class="cl-cin c">${c.checkinDay ? c.checkinDay.charAt(0).toUpperCase() + c.checkinDay.slice(1, 3) : _e}</div></td>`,
      notes: (() => {
        const nn = getNotes(c);
        if (nn.length === 0) return `<td class="col-notes"><div class="cl-cin">${_e}</div></td>`;
        const latest = nn[0];
        const preview = escHtml(latest.text.substring(0, 35)) + (latest.text.length > 35 ? '…' : '');
        return `<td class="col-notes"><div class="cl-cin"><span class="txt">${preview}</span> <span class="note-count-badge">${nn.length}</span></div></td>`;
      })(),
      actions: `<td class="col-actions"><div class="cl-acts">
        <button class="btn-action-icon" onclick="event.stopPropagation();window.openInlineChatFromList&&window.openInlineChatFromList('${c.id}')" title="Message"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></button>
        <button class="btn-action-icon btn-action-icon--danger" onclick="window.rosterDelete('${c.id}')" title="Delete"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
      </div></td>`,
    };

    const isChecked = _selectedClients.has(c.id);
    return `<tr class="client-row ${c.status === 'cancelled' ? 'row-cancelled' : ''}${isChecked ? ' row-selected' : ''}" style="--row-i:${Math.min(i, 16)}" onclick="handleRowClick(event,'${c.id}')"><td class="col-check" onclick="event.stopPropagation()"><input type="checkbox" class="row-check" ${isChecked ? 'checked' : ''} onchange="window.toggleClientSelect('${c.id}', this.checked)" /></td>${visCols.map(col => cellMap[col.key] || '<td>—</td>').join('')}</tr>`;
  }).join('');
  renderBulkBar();
  if (typeof window._applyColumnWidths === 'function') window._applyColumnWidths();

  // Delegated tooltips for churn dots and other [data-tip-title] elements in the table
  if (!window._clTableTipInit) {
    window._clTableTipInit = true;
    let _tipTimer = null;
    const _getTip = () => {
      let tip = document.getElementById('ph-metric-tooltip');
      if (!tip) { tip = document.createElement('div'); tip.id = 'ph-metric-tooltip'; tip.className = 'ph-metric-tip'; document.body.appendChild(tip); }
      return tip;
    };
    document.addEventListener('mouseover', (e) => {
      const el = e.target.closest('[data-tip-title]');
      if (!el || el.closest('.ph-metrics')) return; // ph-metrics handled separately
      clearTimeout(_tipTimer);
      _tipTimer = setTimeout(() => {
        const tip = _getTip();
        tip.innerHTML = `<div class="pmt-title">${escHtml(el.dataset.tipTitle)}</div><div class="pmt-body">${escHtml(el.dataset.tipBody || '')}</div>`;
        const rect = el.getBoundingClientRect();
        tip.style.top = (rect.bottom + 6) + 'px';
        tip.style.left = Math.max(8, Math.min(rect.left, window.innerWidth - 340)) + 'px';
        tip.classList.add('visible');
      }, 200);
    });
    document.addEventListener('mouseout', (e) => {
      const el = e.target.closest('[data-tip-title]');
      if (!el || el.closest('.ph-metrics')) return;
      clearTimeout(_tipTimer);
      const tip = document.getElementById('ph-metric-tooltip');
      if (tip) tip.classList.remove('visible');
    });
  }

  // Summary strip
  const stripEl = document.getElementById('cl-summary-strip');
  if (stripEl) {
    const activeCount = clients.filter(c => c.status === 'active').length;
    const totalCount = clients.length;
    const filtered = document.querySelectorAll('#clients-tbody tr.client-row').length;
    const showing = filtered < totalCount ? `Showing ${filtered} of ${totalCount}` : `${totalCount} clients`;
    stripEl.innerHTML = `<span class="cl-strip-count">${showing}</span><span class="cl-strip-dot">·</span><span class="cl-strip-active">${activeCount} active</span>`;
  }

  // Smart-list chips reflect the active filter + live counts on every render.
  if (typeof _renderSmartChips === 'function') _renderSmartChips();
}

window.openInlineChatFromList = function(clientId) {
  // Navigate to inbox and open this client's conversation with the full composer
  showView('inbox');
  setTimeout(() => {
    if (typeof window.openInboxConversation === 'function') {
      window.openInboxConversation(clientId);
    }
  }, 200);
};

// ============================================================
// CLIENT HOVER CARD
// ============================================================
(() => {
  let _hoverTimer = null;
  let _hoverCard = null;

  function _createHoverCard() {
    if (_hoverCard) return _hoverCard;
    _hoverCard = document.createElement('div');
    _hoverCard.className = 'client-hover-card';
    _hoverCard.id = 'client-hover-card';
    document.body.appendChild(_hoverCard);
    _hoverCard.addEventListener('mouseenter', () => clearTimeout(_hoverTimer));
    _hoverCard.addEventListener('mouseleave', () => _hideHoverCard());
    return _hoverCard;
  }

  function _hideHoverCard() {
    clearTimeout(_hoverTimer);
    if (_hoverCard) _hoverCard.classList.remove('visible');
  }

  function _showHoverCard(el, clientId) {
    const c = clients.find(x => x.id === clientId);
    if (!c) return;

    const card = _createHoverCard();
    const initials = (c.firstName || c.name || '?')[0].toUpperCase();
    const avatarColors = { active: 'var(--success)', cancelled: 'var(--danger)', paused: 'var(--info)', pending_cancellation: 'var(--warning)' };
    const avatarBg = avatarColors[c.status] || 'var(--text-muted)';
    const collected = Number(c.totalCollected ?? c.moneyCollectedUpfront) || 0;

    // Time as client
    const elapsedDays = c.programStartDate
      ? Math.max(0, Math.floor((Date.now() - new Date(c.programStartDate + 'T12:00:00').getTime()) / 86400000)) : 0;
    const timeLbl = elapsedDays > 0
      ? (elapsedDays < 30 ? elapsedDays + 'd' : elapsedDays < 365 ? Math.floor(elapsedDays/30.44) + 'mo' : Math.floor(elapsedDays/365) + 'yr ' + (Math.floor(elapsedDays/30.44)%12) + 'mo')
      : '—';

    card.innerHTML = `
      <div class="chc-header">
        <div class="chc-avatar" style="background:${avatarBg}">${initials}</div>
        <div class="chc-info">
          <div class="chc-name">${escHtml(c.name)}</div>
          <span class="badge-status badge-${c.status}" style="font-size:9px;">${label(c.status)}</span>
        </div>
      </div>
      <div class="chc-stats">
        <div class="chc-stat"><div class="chc-stat-val">${formatCurrency(collected)}</div><div class="chc-stat-lbl">LTV</div></div>
        <div class="chc-stat"><div class="chc-stat-val">${timeLbl}</div><div class="chc-stat-lbl">Time</div></div>
      </div>
      <div class="chc-meta">
        ${c.serviceType ? `<span>${label(c.serviceType)}</span>` : ''}
        ${c.checkinDay ? `<span>Check-in: ${c.checkinDay.charAt(0).toUpperCase() + c.checkinDay.slice(1)}</span>` : ''}
      </div>
    `;

    const rect = el.getBoundingClientRect();
    card.style.top = (rect.bottom + 8) + 'px';
    card.style.left = Math.max(8, Math.min(rect.left, window.innerWidth - 300)) + 'px';
    card.classList.add('visible');
  }

  // Delegate hover events on client name links
  document.addEventListener('mouseover', (e) => {
    const nameEl = e.target.closest('.client-name-link[data-client-id]');
    if (!nameEl) return;
    clearTimeout(_hoverTimer);
    _hoverTimer = setTimeout(() => _showHoverCard(nameEl, nameEl.dataset.clientId), 500);
  });
  document.addEventListener('mouseout', (e) => {
    const nameEl = e.target.closest('.client-name-link[data-client-id]');
    if (!nameEl) return;
    clearTimeout(_hoverTimer);
    _hoverTimer = setTimeout(_hideHoverCard, 200);
  });
})();

// ============================================================
// COLUMN RESIZE
// ============================================================
(() => {
  let _resizing = null;
  let _lastHandleDown = null; // { th, at } — set on every handle press; the dblclick matcher
  const WIDTHS_KEY = 'revfit_col_widths';

  function _loadWidths() {
    try { return JSON.parse(localStorage.getItem(WIDTHS_KEY)) || {}; } catch { return {}; }
  }
  function _saveWidths(widths) {
    localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths));
  }

  function _setColWidth(th, width) {
    th.style.width = width + 'px';
  }

  window._applyColumnWidths = function() {
    // Clear any inline table width left by a drag so the table re-fits the card
    // on re-render (saved per-column widths are re-applied below).
    const table = document.querySelector('#view-clients .clients-table');
    if (table) table.style.width = '';

    const widths = _loadWidths();
    if (Object.keys(widths).length === 0) return;

    document.querySelectorAll('#clients-thead th').forEach(th => {
      const key = th.dataset.sortcol;
      if (key && widths[key]) _setColWidth(th, widths[key]);
    });
  };

  // Auto-fit a column to its content (double-click the handle). Briefly lets the
  // column size to content under an auto layout, reads that width, then restores
  // the fixed layout with every OTHER column pinned — synchronous, so no flash.
  function _autofit(th) {
    const table = th.closest('table');
    if (!table) return;
    const ths = [...table.querySelectorAll('#clients-thead th')];
    const snap = ths.map(t => t.offsetWidth);
    table.style.tableLayout = 'auto';
    table.style.width = 'auto';
    th.style.width = 'auto';
    th.style.minWidth = '';
    const natural = Math.max(64, Math.min(480, th.offsetWidth + 4));
    table.style.tableLayout = 'fixed';
    ths.forEach((t, i) => { if (t !== th) t.style.width = snap[i] + 'px'; });
    th.style.width = natural + 'px';
    table.style.width = ths.reduce((sum, t, i) => sum + (t === th ? natural : snap[i]), 0) + 'px';
    const key = th.dataset.sortcol;
    if (key) { const w = _loadWidths(); w[key] = natural; _saveWidths(w); }
  }

  document.addEventListener('mousedown', (e) => {
    const th = e.target.closest('#clients-thead th');
    if (!th) return;
    const rect = th.getBoundingClientRect();
    if (e.clientX < rect.right - 8) return;

    e.preventDefault();
    e.stopPropagation();
    window._wasResizing = true;
    // The dblclick handler matches on THIS, not on re-measured geometry: by
    // the time the second click lands, the first press may have shifted the
    // edge a pixel or two, and "within 8px of rect.right" then rejects the
    // double-click — the autofit never ran and the two micro-drags were the
    // only visible result (Clayton, 2026-08-05).
    _lastHandleDown = { th, at: Date.now() };

    // Spreadsheet resize: freeze EVERY column to its current width and pin the
    // table's total width. Dragging one handle then changes ONLY that column and
    // grows/shrinks the whole table (adjusting the horizontal scroll) — the
    // neighbouring columns never move.
    // ⚠️ The freeze itself is a mutation (offsetWidth rounds; inline widths
    // land where there were none) — snapshot the prior inline styles so a
    // press that never becomes a DRAG can restore them byte-for-byte and
    // leave zero trace. That is what makes a double-click drift-free.
    const table = th.closest('table');
    const prevThWidths = [];
    document.querySelectorAll('#clients-thead th').forEach(t => {
      prevThWidths.push([t, t.style.width, t.style.minWidth]);
      t.style.width = t.offsetWidth + 'px';
      t.style.minWidth = '';
    });
    const prevTable = table ? { layout: table.style.tableLayout, width: table.style.width } : null;
    const tableStartW = table ? table.offsetWidth : 0;
    if (table) { table.style.tableLayout = 'fixed'; table.style.width = tableStartW + 'px'; }

    _resizing = {
      th, table, startX: e.clientX, startW: th.offsetWidth, tableStartW,
      key: th.dataset.sortcol || '', moved: false, prevThWidths, prevTable,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  });

  document.addEventListener('mousemove', (e) => {
    if (!_resizing) {
      const th = e.target.closest('#clients-thead th');
      if (th) {
        const rect = th.getBoundingClientRect();
        th.style.cursor = e.clientX >= rect.right - 8 ? 'col-resize' : '';
      }
      return;
    }
    const diff = e.clientX - _resizing.startX;
    // Click-vs-drag threshold: hand jitter inside a click (and inside each
    // half of a double-click) must not resize anything. Only once the
    // pointer commits to a real drag does the resize begin.
    if (!_resizing.moved && Math.abs(diff) < 3) return;
    _resizing.moved = true;
    const newW = Math.max(60, _resizing.startW + diff);
    _setColWidth(_resizing.th, newW);
    // Grow/shrink the table by the same delta so no neighbouring column reflows.
    if (_resizing.table) _resizing.table.style.width = (_resizing.tableStartW + (newW - _resizing.startW)) + 'px';
  });

  document.addEventListener('mouseup', () => {
    if (!_resizing) return;
    if (!_resizing.moved) {
      // A click, not a drag — undo the freeze exactly (restore every inline
      // style the mousedown replaced) and save nothing. A double-click's two
      // presses each pass through here leaving zero trace, so the edge is
      // still where the coach aimed when the dblclick handler fires.
      _resizing.prevThWidths.forEach(([t, w, mw]) => { t.style.width = w; t.style.minWidth = mw; });
      if (_resizing.table && _resizing.prevTable) {
        _resizing.table.style.tableLayout = _resizing.prevTable.layout;
        _resizing.table.style.width = _resizing.prevTable.width;
      }
    } else {
      const widths = _loadWidths();
      if (_resizing.key) {
        widths[_resizing.key] = _resizing.th.offsetWidth;
        _saveWidths(widths);
      }
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    _resizing = null;
  });

  // Double-click the right-edge handle → auto-fit the column to its content.
  // Matched by where the PRESS started (_lastHandleDown, set in mousedown),
  // not by re-measured geometry — the edge may have moved a pixel between
  // the two clicks, and the old rect check silently swallowed the autofit.
  // Geometry stays as the fallback for safety's sake.
  document.addEventListener('dblclick', (e) => {
    const th = e.target.closest('#clients-thead th');
    if (!th) return;
    const pressedHandle = _lastHandleDown
      && _lastHandleDown.th === th
      && (Date.now() - _lastHandleDown.at) < 600;
    const rect = th.getBoundingClientRect();
    if (!pressedHandle && e.clientX < rect.right - 8) return;
    e.preventDefault();
    _autofit(th);
  });
})();

// ============================================================
// FILTER BUILDER + SMART-LIST CHIPS
// ============================================================
// A rule builder (column · operator · value, AND) tucked inside the Filter
// popover, plus smart-list chips in the toolbar. Chips are usage-ranked
// (most-used first, per coach) and collapse into a "More" menu so they can
// never push the toolbar to a second row.

function _loadSmartLists() { try { return JSON.parse(localStorage.getItem('revfit_smart_lists')) || []; } catch { return []; } }
function _saveSmartLists(l) { try { localStorage.setItem('revfit_smart_lists', JSON.stringify(l)); } catch { /* ignore */ } }
function _loadListUse()  { try { return JSON.parse(localStorage.getItem('revfit_smartlist_use')) || {}; } catch { return {}; } }
function _saveListUse(u) { try { localStorage.setItem('revfit_smartlist_use', JSON.stringify(u)); } catch { /* ignore */ } }
function _bumpListUse(id) { const u = _loadListUse(); u[id] = (u[id] || 0) + 1; _saveListUse(u); }
function _allSmartLists() { return [...ROSTER_PRESETS, ..._loadSmartLists()]; }
function _rankedSmartLists() {
  const use = _loadListUse();
  const all = _allSmartLists();
  const pinned = all.filter(l => l.id === 'all');
  const rest = all.filter(l => l.id !== 'all').sort((a, b) => (use[b.id] || 0) - (use[a.id] || 0));
  return [...pinned, ...rest];
}
function _completeRules(rules) { return (rules || []).filter(r => r.val !== '' && r.val != null); }
function _countForRules(rules) {
  const rs = _completeRules(rules);
  return clients.filter(c => !_pendingDelete.has(c.id) && rs.every(r => _matchRosterRule(c, r))).length;
}

// ── Apply / save / refilter ─────────────────────────────────────
function _applySmartList(id) {
  const list = _allSmartLists().find(l => l.id === id);
  if (!list) return;
  _activeSmartList = id;
  _rosterRules = JSON.parse(JSON.stringify(list.rules || []));
  _bumpListUse(id);
  _afterFilterChange();
}
// Styled "name this smart list" prompt — replaces the raw window.prompt(). Reuses
// the shared .confirm-overlay / .confirm-dialog shell so it matches every other
// CRM dialog. Resolves to the trimmed name, or null if cancelled. Enter saves,
// Escape / backdrop / Cancel dismiss, Save is disabled until there's a name.
function _showSaveListDialog() {
  return new Promise((resolve) => {
    let overlay = document.getElementById('cl-save-list-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'cl-save-list-overlay';
      overlay.className = 'confirm-overlay hidden';
      overlay.innerHTML = `
        <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="cl-save-list-title">
          <div class="confirm-body">
            <h3 id="cl-save-list-title" class="cl-save-title">Name this smart list</h3>
            <p class="cl-save-sub">Save the current filters so you can pull up this group in one click.</p>
            <input type="text" id="cl-save-list-input" class="cl-save-input" placeholder="e.g. High value renewals" maxlength="40" autocomplete="off" spellcheck="false">
          </div>
          <div class="confirm-footer">
            <div class="confirm-actions">
              <button class="btn-secondary confirm-cancel" id="cl-save-list-cancel">Cancel</button>
              <button class="btn-primary confirm-ok" id="cl-save-list-ok">Save list</button>
            </div>
          </div>
        </div>`;
      document.body.appendChild(overlay);
    }
    const input  = overlay.querySelector('#cl-save-list-input');
    const okBtn  = overlay.querySelector('#cl-save-list-ok');
    const cancel = overlay.querySelector('#cl-save-list-cancel');
    input.value = '';
    okBtn.disabled = true;

    let done = false;
    const finish = (val) => {
      if (done) return; done = true;
      overlay.classList.add('hidden');
      input.removeEventListener('input', onInput);
      input.removeEventListener('keydown', onKey);
      okBtn.removeEventListener('click', onOk);
      cancel.removeEventListener('click', onCancel);
      overlay.removeEventListener('mousedown', onBackdrop);
      resolve(val);
    };
    const onInput    = () => { okBtn.disabled = !input.value.trim(); };
    const onOk       = () => { const v = input.value.trim(); if (v) finish(v); };
    const onCancel   = () => finish(null);
    const onKey      = (e) => { if (e.key === 'Enter') { e.preventDefault(); onOk(); } else if (e.key === 'Escape') { e.preventDefault(); finish(null); } };
    const onBackdrop = (e) => { if (e.target === overlay) finish(null); };
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKey);
    okBtn.addEventListener('click', onOk);
    cancel.addEventListener('click', onCancel);
    overlay.addEventListener('mousedown', onBackdrop);

    overlay.classList.remove('hidden');
    setTimeout(() => input.focus(), 30);
  });
}

async function _saveCurrentAsList() {
  const rules = _completeRules(_rosterRules);
  if (!rules.length) return;
  const name = await _showSaveListDialog();
  if (!name) return;
  const lists = _loadSmartLists();
  const id = 'sl_' + Date.now();
  lists.push({ id, name, rules });
  _saveSmartLists(lists);
  _activeSmartList = id;
  _bumpListUse(id);
  showToast(`Smart list "${name}" saved.`, 'success');
  _afterFilterChange();
}
function _updateFilterBadge() {
  const btn = document.getElementById('btn-filter-toggle');
  if (!btn) return;
  const n = _completeRules(_rosterRules).length;
  let badge = btn.querySelector('.cl-filter-badge');
  if (n > 0) {
    if (!badge) { badge = document.createElement('span'); badge.className = 'cl-filter-badge'; btn.appendChild(badge); }
    badge.textContent = String(n);
    btn.classList.add('cl-filter-active');
  } else { if (badge) badge.remove(); btn.classList.remove('cl-filter-active'); }
}
function _refilter() { renderClientsTable(); _updateFilterBadge(); }          // rows + chips + badge
function _afterFilterChange() { _renderFilterBuilder(); _refilter(); }         // + rebuild the builder

// ── Chips (State A/B) + pills (State C) ─────────────────────────
function _renderSmartChips() {
  const container = document.getElementById('cl-active-filters');
  if (!container) return;

  // State C — an ad-hoc custom filter is active → removable pills + "Save as list".
  if (_activeSmartList === '__custom' && _completeRules(_rosterRules).length) {
    container.className = 'cl-active-filters cl-af--pills';
    const pills = _completeRules(_rosterRules).map((r) => {
      const idx = _rosterRules.indexOf(r);
      const col = ROSTER_FILTERS.find(f => f.key === r.col) || (r.col === 'pulse' ? RETIRED_PULSE_FILTER : null);
      return `<span class="cl-pill"><b>${escHtml(col ? col.label : r.col)}</b> ${escHtml(r.op)} ${escHtml(String(r.val))}<button class="cl-pill-x" data-ri="${idx}" title="Remove">✕</button></span>`;
    }).join('');
    container.innerHTML = pills + `<button class="cl-chip cl-chip--dashed" id="cl-save-list-chip">Save as list</button>`;
    container.querySelectorAll('.cl-pill-x').forEach(x => x.addEventListener('click', () => {
      _rosterRules.splice(+x.dataset.ri, 1);
      if (!_rosterRules.length) _activeSmartList = 'all';
      _afterFilterChange();
    }));
    container.querySelector('#cl-save-list-chip')?.addEventListener('click', _saveCurrentAsList);
    return;
  }

  // State A/B — smart-list chips (usage-ranked, All pinned, overflow → More).
  container.className = 'cl-active-filters cl-af--chips';
  const lists = _rankedSmartLists();
  const counts = {};
  lists.forEach(l => { counts[l.id] = _countForRules(l.rules); });
  container.innerHTML = lists.map(l =>
    `<button class="cl-chip${l.id === _activeSmartList ? ' active' : ''}" data-list="${l.id}">${escHtml(l.name)} <span class="cnt">${counts[l.id]}</span></button>`
  ).join('') + `<button class="cl-chip cl-chip--more" id="cl-more-chip" hidden>More <span class="cnt" id="cl-more-cnt"></span></button>`;
  container.querySelectorAll('.cl-chip[data-list]').forEach(ch => ch.addEventListener('click', () => _applySmartList(ch.dataset.list)));
  _collapseChips(container);
}

// Priority-plus overflow: keep only the chips that fit on one line; the rest go
// into a "More" menu. 'All' + the active list are always kept visible.
function _collapseChips(container) {
  const more = container.querySelector('#cl-more-chip');
  if (!more) return;
  more.hidden = true;
  const chips = [...container.querySelectorAll('.cl-chip[data-list]')];
  chips.forEach(ch => { ch.hidden = false; });
  const avail = container.clientWidth;
  if (!avail) return;
  let total = 0;
  chips.forEach(ch => { total += ch.offsetWidth + 6; });
  if (total <= avail) return; // everything fits

  const moreW = 86;
  let used = 0;
  const overflow = [];
  for (const ch of chips) {
    const w = ch.offsetWidth + 6;
    const pin = ch.dataset.list === 'all' || ch.dataset.list === _activeSmartList;
    if (pin) { used += w; continue; }
    if (used + w > avail - moreW) overflow.push(ch);
    else used += w;
  }
  if (!overflow.length) return;
  overflow.forEach(ch => { ch.hidden = true; });
  more.hidden = false;
  const cnt = container.querySelector('#cl-more-cnt');
  if (cnt) cnt.textContent = String(overflow.length);
  more.onclick = (e) => { e.stopPropagation(); _openMoreMenu(more, overflow.map(ch => ch.dataset.list)); };
}

function _openMoreMenu(anchor, listIds) {
  document.getElementById('cl-more-menu')?.remove();
  const all = _allSmartLists();
  const menu = document.createElement('div');
  menu.id = 'cl-more-menu';
  menu.className = 'cl-more-menu';
  menu.innerHTML = listIds.map(id => {
    const l = all.find(x => x.id === id);
    return l ? `<button class="cl-more-item${id === _activeSmartList ? ' active' : ''}" data-list="${id}">${escHtml(l.name)} <span class="cnt">${_countForRules(l.rules)}</span></button>` : '';
  }).join('');
  document.body.appendChild(menu);
  const r = anchor.getBoundingClientRect();
  menu.style.top = (r.bottom + 4) + 'px';
  menu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - 220)) + 'px';
  menu.querySelectorAll('.cl-more-item').forEach(it => it.addEventListener('click', () => { _applySmartList(it.dataset.list); menu.remove(); }));
  const close = (e) => { if (!menu.contains(e.target) && e.target !== anchor) { menu.remove(); document.removeEventListener('click', close); } };
  setTimeout(() => document.addEventListener('click', close), 0);
}

// ── The rule builder (inside the Filter popover) ────────────────
function _renderFilterBuilder() {
  const panel = document.getElementById('cl-filter-panel');
  if (!panel) return;
  const rows = _rosterRules.map((r, i) => {
    const col = ROSTER_FILTERS.find(f => f.key === r.col) || (r.col === 'pulse' ? RETIRED_PULSE_FILTER : ROSTER_FILTERS[0]);
    // Field / operator / select-value use a styled trigger (native <select> popups
    // can't be themed); free-text + number values stay native inputs.
    const _ph = (r.val === '' || r.val == null);
    let valCtrl;
    if (col.ftype === 'select')      valCtrl = _rselTrigger('cl-rsel--val', i, 'val', _ph ? 'Choose value' : r.val, _ph);
    else if (col.ftype === 'number') valCtrl = `<input class="cl-rinp cl-rsel--val" type="number" data-ri="${i}" data-part="val" value="${escHtml(r.val ?? '')}" placeholder="0">`;
    else if (col.ftype === 'date')   valCtrl = _rdateTrigger(i, r.val);
    else                             valCtrl = `<input class="cl-rinp cl-rsel--val" type="text" data-ri="${i}" data-part="val" value="${escHtml(r.val ?? '')}" placeholder="value">`;
    return `<div class="cl-rule"><span class="cl-rlbl">${i === 0 ? 'Where' : 'And'}</span>`
      + _rselTrigger('cl-rsel--col', i, 'col', col.label, false)
      + _rselTrigger('cl-rsel--op', i, 'op', r.op, false)
      + valCtrl
      + `<button class="cl-rx" data-ri="${i}" title="Remove">✕</button></div>`;
  }).join('');
  // "Add rule" lives with the rules (it adds one), not jammed into the action
  // footer — that row was overrunning itself. The footer holds only Clear/Save,
  // and only when there's a rule to act on. The "match every rule" hint appears
  // once 2+ rules exist (it's meaningless before that), keeping the common case
  // uncluttered.
  const _addRuleBtn = `<button class="cl-addrule" id="cl-add-rule"><svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>Add rule</button>`;
  const _rulesBody = rows || '<div class="cl-builder-empty">No rules yet — add one to filter your clients.</div>';
  const _foot = _rosterRules.length
    ? `<div class="cl-builder-foot">`
      + (_rosterRules.length >= 2 ? `<span class="cl-builder-hint">Clients must match every rule</span>` : '')
      + `<span class="cl-foot-spacer"></span>`
      + `<button class="cl-btn-sm" id="cl-clear-rules">Clear</button>`
      + `<button class="cl-btn-sm cl-btn-primary" id="cl-save-list-btn">Save as list</button></div>`
    : '';
  panel.innerHTML = `<div class="cl-builder-rules">${_rulesBody}${_addRuleBtn}</div>${_foot}`;

  panel.querySelector('#cl-add-rule')?.addEventListener('click', () => {
    const f = ROSTER_FILTERS[0];
    // Empty value on purpose — a new row must NOT silently filter the roster by a
    // rule the coach never chose. It stays inert until they pick a value
    // (getFiltered / _completeRules skip val === '' rows).
    _rosterRules.push({ col: f.key, op: ROSTER_OPS[f.ftype][0], val: '' });
    _activeSmartList = '__custom';
    _afterFilterChange();
  });
  panel.querySelector('#cl-clear-rules')?.addEventListener('click', () => { _rosterRules = []; _activeSmartList = 'all'; _afterFilterChange(); });
  panel.querySelector('#cl-save-list-btn')?.addEventListener('click', _saveCurrentAsList);
  panel.querySelectorAll('.cl-rx').forEach(x => x.addEventListener('click', () => {
    _rosterRules.splice(+x.dataset.ri, 1);
    _activeSmartList = _rosterRules.length ? '__custom' : 'all';
    _afterFilterChange();
  }));
  // Styled dropdowns (field / operator / select-value) open a portaled menu;
  // date fields open the custom calendar instead.
  panel.querySelectorAll('.cl-rsel').forEach(t => t.addEventListener('click', (e) => {
    e.stopPropagation();
    if (t.classList.contains('cl-rdatetrig')) _openDateMenu(t); else _openSelMenu(t);
  }));
  // Free-text / number value fields stay native inputs — filter on change.
  panel.querySelectorAll('input[data-part]').forEach(el => el.addEventListener('change', () => {
    _rosterRules[+el.dataset.ri][el.dataset.part] = el.value;
    _activeSmartList = '__custom';
    _refilter();
  }));
}

// Styled trigger button standing in for a native <select> in the rule builder.
function _rselTrigger(mod, ri, part, display, isPlaceholder) {
  return `<button type="button" class="cl-rsel ${mod}" data-ri="${ri}" data-part="${part}">`
    + `<span class="cl-rsel-txt${isPlaceholder ? ' cl-rsel-ph' : ''}">${escHtml(display)}</span>`
    + `<svg class="cl-rsel-chev" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    + `</button>`;
}
// Date value control — same styled trigger, but a calendar glyph, and it opens
// the custom date picker (_openDateMenu) rather than a list menu.
function _fmtDateShort(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function _rdateTrigger(ri, val) {
  const disp = val ? _fmtDateShort(val) : 'Pick a date';
  return `<button type="button" class="cl-rsel cl-rsel--val cl-rdatetrig" data-ri="${ri}" data-part="val">`
    + `<span class="cl-rsel-txt${val ? '' : ' cl-rsel-ph'}">${escHtml(disp)}</span>`
    + `<svg class="cl-rsel-chev" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="2.5" y="3.5" width="11" height="10" rx="1.5"/><path d="M2.5 6.7h11M5.5 1.8v3M10.5 1.8v3" stroke-linecap="round"/></svg>`
    + `</button>`;
}

// The dropdown menu is portaled to <body> so it escapes the filter panel's
// overflow:hidden and can size/position freely — mirrors _openMoreMenu.
let _selMenuEl = null, _selMenuTrigger = null, _selMenuCleanup = null;
function _closeSelMenu() {
  if (_selMenuCleanup) { _selMenuCleanup(); _selMenuCleanup = null; }
  if (_selMenuEl) { _selMenuEl.remove(); _selMenuEl = null; }
  _selMenuTrigger = null;
  document.querySelectorAll('.cl-rsel.open').forEach(t => t.classList.remove('open'));
}
function _openSelMenu(trigger) {
  const reopen = _selMenuTrigger === trigger;
  _closeSelMenu();
  if (reopen) return; // clicking the same trigger again toggles it shut
  const ri = +trigger.dataset.ri, part = trigger.dataset.part;
  const rule = _rosterRules[ri];
  if (!rule) return;
  const col = ROSTER_FILTERS.find(f => f.key === rule.col) || (rule.col === 'pulse' ? RETIRED_PULSE_FILTER : ROSTER_FILTERS[0]);
  let items;
  if (part === 'col')     items = ROSTER_FILTERS.map(f => ({ v: f.key, label: f.label, active: f.key === rule.col }));
  else if (part === 'op') items = ROSTER_OPS[col.ftype].map(o => ({ v: o, label: o, active: o === rule.op }));
  else                    items = (col.options || []).map(o => ({ v: o, label: o, active: o === rule.val }));

  const menu = document.createElement('div');
  menu.className = 'cl-sel-menu';
  menu.style.minWidth = trigger.getBoundingClientRect().width + 'px';
  menu.innerHTML = items.map(it =>
    `<button type="button" class="cl-sel-opt${it.active ? ' active' : ''}" data-val="${escHtml(it.v)}"><span>${escHtml(it.label)}</span>${it.active ? '<svg class="cl-sel-check" width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7.4l3.2 3.2L12 3.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}</button>`
  ).join('');
  menu.addEventListener('click', (e) => {
    const opt = e.target.closest('.cl-sel-opt');
    if (opt) _applySelChange(ri, part, opt.dataset.val);
  });
  _mountSelMenu(menu, trigger);
}
// Position a portaled menu under its trigger (flips above if it would overflow).
function _positionSelMenu(menu, trigger) {
  const r = trigger.getBoundingClientRect();
  const mw = menu.offsetWidth, mh = menu.offsetHeight;
  let top = r.bottom + 4;
  if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
  menu.style.top = top + 'px';
  menu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - mw - 8)) + 'px';
}
// Append a portaled menu, position it, and wire dismissal (outside click /
// scroll / resize / Escape). Shared by the select menu and the calendar.
function _mountSelMenu(menu, trigger) {
  document.body.appendChild(menu);
  _positionSelMenu(menu, trigger);
  trigger.classList.add('open');
  _selMenuEl = menu;
  _selMenuTrigger = trigger;
  const onDoc = (e) => { if (!menu.contains(e.target) && !trigger.contains(e.target)) _closeSelMenu(); };
  // Capture-phase scroll fires for scrolls INSIDE the menu too — when the field
  // list is taller than its max-height and the user scrolls it. Only dismiss on
  // a page/ancestor scroll that would move the trigger out from under the menu.
  const onScroll = (e) => {
    if (e && e.target && e.target.nodeType && (menu === e.target || menu.contains(e.target))) return;
    _closeSelMenu();
  };
  const onResize = () => _closeSelMenu();
  const onKey = (e) => { if (e.key === 'Escape') _closeSelMenu(); };
  setTimeout(() => document.addEventListener('click', onDoc), 0);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);
  document.addEventListener('keydown', onKey);
  _selMenuCleanup = () => {
    document.removeEventListener('click', onDoc);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('keydown', onKey);
  };
}

// ── Custom date picker (fully styled — no native <input type=date>) ──────────
const _CAL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const _CAL_DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
function _openDateMenu(trigger) {
  const reopen = _selMenuTrigger === trigger;
  _closeSelMenu();
  if (reopen) return;
  const ri = +trigger.dataset.ri;
  const rule = _rosterRules[ri];
  if (!rule) return;
  const sel = /^\d{4}-\d{2}-\d{2}$/.test(rule.val || '') ? rule.val : '';
  const view = sel ? new Date(sel + 'T00:00:00') : new Date();
  view.setDate(1);
  const menu = document.createElement('div');
  menu.className = 'cl-cal';
  const render = () => {
    menu.innerHTML = _calHtml(view, sel);
    if (_selMenuEl === menu) _positionSelMenu(menu, trigger); // height changes per month
  };
  menu.addEventListener('click', (e) => {
    e.stopPropagation(); // keep every in-calendar click contained (nav re-renders detach nodes)
    if (e.target.closest('.cl-cal-prev')) { view.setMonth(view.getMonth() - 1); render(); return; }
    if (e.target.closest('.cl-cal-next')) { view.setMonth(view.getMonth() + 1); render(); return; }
    if (e.target.closest('.cl-cal-today')) { _pickDate(ri, _asDateStr(new Date())); return; }
    if (e.target.closest('.cl-cal-clear')) { _pickDate(ri, ''); return; }
    const day = e.target.closest('.cl-cal-day[data-d]');
    if (day) _pickDate(ri, day.dataset.d);
  });
  render();
  _mountSelMenu(menu, trigger);
}
function _calHtml(view, sel) {
  const y = view.getFullYear(), m = view.getMonth();
  const first = (new Date(y, m, 1).getDay() + 6) % 7;   // Monday-start
  const days = new Date(y, m + 1, 0).getDate();
  const today = _asDateStr(new Date());
  let cells = '';
  for (let i = 0; i < first; i++) cells += '<span class="cl-cal-day cl-cal-pad"></span>';
  for (let d = 1; d <= days; d++) {
    const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const cls = 'cl-cal-day' + (ds === sel ? ' sel' : '') + (ds === today ? ' today' : '');
    cells += `<button type="button" class="${cls}" data-d="${ds}">${d}</button>`;
  }
  return `<div class="cl-cal-head">`
    + `<button type="button" class="cl-cal-nav cl-cal-prev" aria-label="Previous month">‹</button>`
    + `<span class="cl-cal-title">${_CAL_MONTHS[m]} ${y}</span>`
    + `<button type="button" class="cl-cal-nav cl-cal-next" aria-label="Next month">›</button></div>`
    + `<div class="cl-cal-dow">${_CAL_DOW.map(d => `<span>${d}</span>`).join('')}</div>`
    + `<div class="cl-cal-grid">${cells}</div>`
    + `<div class="cl-cal-foot"><button type="button" class="cl-cal-clear">Clear</button><button type="button" class="cl-cal-today">Today</button></div>`;
}
function _pickDate(ri, val) {
  _closeSelMenu();
  if (!_rosterRules[ri]) return;
  _rosterRules[ri].val = val;
  _activeSmartList = '__custom';
  _afterFilterChange();
}
function _applySelChange(ri, part, val) {
  _closeSelMenu();
  const rule = _rosterRules[ri];
  if (!rule) return;
  if (part === 'col') {
    // Field change resets to an empty value (never auto-apply a value the coach
    // didn't pick) and rebuilds — the operator set + value control both change.
    const col = ROSTER_FILTERS.find(f => f.key === val) || ROSTER_FILTERS[0];
    _rosterRules[ri] = { col: val, op: ROSTER_OPS[col.ftype][0], val: '' };
  } else {
    _rosterRules[ri][part] = val;
  }
  _activeSmartList = '__custom';
  _afterFilterChange();
}

function _initFilterSystem() {
  const btn = document.getElementById('btn-filter-toggle');
  if (btn) btn.addEventListener('click', () => _renderFilterBuilder());
  _renderFilterBuilder();
  _renderSmartChips();
  _updateFilterBadge();
  let _rz;
  window.addEventListener('resize', () => {
    clearTimeout(_rz);
    _rz = setTimeout(() => { const c = document.getElementById('cl-active-filters'); if (c && c.classList.contains('cl-af--chips')) _renderSmartChips(); }, 120);
  });
}
_initFilterSystem();


// ============================================================
// RENDER: RENEWAL PIPELINE BOARD
// ============================================================

function buildPipelineCard(client, stage) {
  const days    = getDaysUntilEnd(client);
  const endDate = getEndDate(client);

  // Days badge
  let daysBadge = '';
  if (days !== null && !['renewed','not_renewing'].includes(stage)) {
    const cls = days <= 7 ? 'urgent' : days <= 14 ? 'warn' : days <= 30 ? 'caution' : 'ok';
    const lbl = days === 0 ? 'Today' : days < 0 ? 'Overdue' : `${days}d`;
    daysBadge = `<span class="pc-days pc-days--${cls}">${lbl}</span>`;
  }

  // Last contact
  const contactRow = client.renewalLastContact
    ? `<div class="pc-last-contact">📞 ${formatDate(client.renewalLastContact)}</div>` : '';

  // Note log — show count badge + latest snippet
  const notes = getPipelineNotes(client);
  const noteRow = notes.length > 0
    ? `<div class="pc-note">
         <span class="pc-note-count">${notes.length} note${notes.length > 1 ? 's' : ''}</span>
         ${notes[0].text ? escHtml(notes[0].text.slice(0, 80)) + (notes[0].text.length > 80 ? '…' : '') : ''}
       </div>` : '';

  // Note button (not on terminal stages)
  const noteBtn = !['renewed','not_renewing'].includes(stage)
    ? `<button class="pc-btn-note" title="Add note" onclick="window.openPipelineNote('${client.id}')">+ Note</button>` : '';

  // PIF opportunity: client is on PIF billing and rolling to continuity — needs re-sign pitch
  const isPifOpp = client.billingStructure === 'PIF' && hasContinuity(client);

  // PIF banner across the top of the card
  const pifBanner = isPifOpp ? `<div class="pc-pif-banner">PIF Opportunity</div>` : '';

  // Single info line: Program End Date · Service Type · Agreement Value
  const infoLine = [
    endDate ? formatDateObj(endDate) : null,
    label(client.serviceType),
    formatCurrency(client.agreementValue),
  ].filter(Boolean).join(' · ');

  // PIF-specific section: continuity info + pitch value input (aligned rows)
  const pifSection = isPifOpp
    ? `<div class="pc-pif-section">
         ${continuityRateOf(client) ? `
         <div class="pc-pif-row">
           <span class="pc-pif-row-label">Rolling to continuity</span>
           <span class="pc-pif-rate">${formatCurrency(continuityRateOf(client))}/mo</span>
         </div>` : ''}
         <div class="pc-pif-row">
           <span class="pc-pif-row-label">Pitch value</span>
           <span class="pc-pif-input-wrap">
             <span class="pc-pif-currency">${getCurrencySymbol()}</span>
             <input type="text" class="pc-pif-value-input"
                    value="${escHtml(client.renewalPotentialValue || '')}"
                    placeholder="0"
                    onclick="event.stopPropagation()"
                    onmousedown="event.stopPropagation()"
                    onblur="window.savePifPotentialValue('${client.id}', this.value)" />
           </span>
         </div>
       </div>`
    : '';

  // Action buttons per stage
  let actions = '';
  if (isPifOpp && (stage === 'reach_out' || stage === 'upcoming')) {
    actions = `<button class="pc-btn pc-btn--pif" onclick="window.pipelineLogContact('${client.id}')">Pitch PIF Re-Up</button>`;
  } else if (stage === 'upcoming') {
    actions = `<button class="pc-btn pc-btn--primary" onclick="window.pipelineLogContact('${client.id}')">Log Contact</button>`;
  } else if (stage === 'reach_out') {
    actions = `<button class="pc-btn pc-btn--urgent" onclick="window.pipelineLogContact('${client.id}')">Log Contact</button>`;
  } else if (stage === 'contacted') {
    actions = `<button class="pc-btn pc-btn--purple" onclick="window.pipelineMoveStage('${client.id}','in_talks')">In Talks →</button>
               <button class="pc-btn pc-btn--ghost"  onclick="window.pipelineMoveStage('${client.id}','not_renewing')">Not Renewing</button>`;
  } else if (stage === 'in_talks') {
    // PIF opportunity clients have a third outcome: staying on continuity (no new PIF, still a client)
    const isPifPath = isPifOpp;
    actions = isPifPath
      ? `<button class="pc-btn pc-btn--success" onclick="window.openRenewModal('${client.id}')">Re-Sign PIF ✓</button>
         <button class="pc-btn pc-btn--primary" onclick="window.pipelineClearStage('${client.id}')">Staying Continuity</button>
         <button class="pc-btn pc-btn--danger"  onclick="window.pipelineMoveStage('${client.id}','not_renewing')">Not Renewing</button>`
      : `<button class="pc-btn pc-btn--success" onclick="window.openRenewModal('${client.id}')">Renew ✓</button>
         <button class="pc-btn pc-btn--danger"  onclick="window.pipelineMoveStage('${client.id}','not_renewing')">Not Renewing</button>`;
  } else if (stage === 'renewed') {
    actions = `<button class="pc-btn pc-btn--ghost" onclick="window.openRenewModal('${client.id}')">Update Agreement</button>
               <button class="pc-btn pc-btn--ghost" onclick="window.pipelineClearStage('${client.id}')">Clear</button>`;
  } else if (stage === 'not_renewing') {
    actions = `<button class="pc-btn pc-btn--ghost" onclick="confirmCancelClient('${client.id}')">Schedule Cancel</button>
               <button class="pc-btn pc-btn--ghost" onclick="window.pipelineClearStage('${client.id}')">Clear</button>`;
  }

  const cardClass = isPifOpp ? 'pipeline-card--pif'
    : stage === 'reach_out' ? 'pipeline-card--reach-out'
    : stage === 'renewed' ? 'pipeline-card--renewed'
    : stage === 'not_renewing' ? 'pipeline-card--not-renewing' : '';

  return `
    <div class="pipeline-card ${cardClass}" draggable="true"
         data-client-id="${client.id}" data-stage="${stage}">
      ${pifBanner}
      <div class="pc-header">
        <button class="pc-name" onclick="showClientProfile('${client.id}')">${escHtml(client.name)}</button>
        <div class="pc-header-right">${noteBtn}${daysBadge}</div>
      </div>
      <div class="pc-info-line">${infoLine}</div>
      ${pifSection}${contactRow}${noteRow}
      <div class="pc-actions ${isPifOpp ? 'pc-actions--full' : ''}">${actions}</div>
    </div>`;
}

function renderRenewals() {
  // Build stage → clients map
  const stageMap = {};
  PIPELINE_STAGES.forEach(s => stageMap[s.key] = []);
  clients.forEach(c => {
    const stage = getEffectivePipelineStage(c);
    if (stage) stageMap[stage].push(c);
  });

  // Sort auto-stages by urgency (fewest days first)
  ['upcoming','reach_out','contacted','in_talks'].forEach(k => {
    stageMap[k].sort((a, b) => (getDaysUntilEnd(a) ?? 999) - (getDaysUntilEnd(b) ?? 999));
  });

  // Stats
  const activePipeline = ['upcoming','reach_out','contacted','in_talks'];
  const pipelineClients = activePipeline.flatMap(k => stageMap[k]);
  const atStake      = pipelineClients.reduce((s, c) => s + (Number(c.agreementValue) || 0), 0);
  const won          = stageMap['renewed'].length;
  const lost         = stageMap['not_renewing'].length;
  const rate         = (won + lost) > 0 ? Math.round(won / (won + lost) * 100) : null;
  const urgent       = stageMap['reach_out'].length;
  // PIF count/potential: PIF+continuity clients anywhere in the active pipeline
  const pifClients   = pipelineClients.filter(c => c.billingStructure === 'PIF' && hasContinuity(c));
  const pifCount     = pifClients.length;
  const pifPotential = pifClients.reduce((s, c) => s + parseCurrencyInput(c.renewalPotentialValue), 0);

  // Render window filter controls
  const controlsBar = document.getElementById('pipeline-controls-bar');
  if (controlsBar) {
    controlsBar.innerHTML = `
      <span class="pw-label">Show next:</span>
      ${[30,60,90].map(n => `<button class="pw-btn${pipelineWindow === n ? ' active' : ''}" onclick="window.setPipelineWindow(${n})">${n} days</button>`).join('')}`;
  }

  // Render stats bar
  const statsBar = document.getElementById('pipeline-stats-bar');
  if (statsBar) {
    statsBar.innerHTML = `
      <div class="pipeline-stat">
        <span class="ps-label">In pipeline</span>
        <span class="ps-value">${pipelineClients.length}</span>
      </div>
      <div class="pipeline-stat">
        <span class="ps-label">At stake</span>
        <span class="ps-value">${formatCurrency(atStake)}</span>
      </div>
      <div class="pipeline-stat">
        <span class="ps-label ${pifCount > 0 ? 'ps-label--pif' : ''}">PIF opportunities</span>
        <span class="ps-value ${pifCount > 0 ? 'ps-value--pif' : ''}">${pifCount}</span>
      </div>
      ${pifPotential > 0 ? `<div class="pipeline-stat">
        <span class="ps-label ps-label--pif">PIF potential</span>
        <span class="ps-value ps-value--pif">${formatCurrency(pifPotential)}</span>
      </div>` : ''}
      <div class="pipeline-stat">
        <span class="ps-label ${urgent > 0 ? 'ps-label--urgent' : ''}">Need action</span>
        <span class="ps-value ${urgent > 0 ? 'ps-value--urgent' : ''}">${urgent}</span>
      </div>
      <div class="pipeline-stat">
        <span class="ps-label">Renewal rate</span>
        <span class="ps-value">${rate !== null ? rate + '%' : '—'}</span>
      </div>
      <div class="pipeline-stat">
        <span class="ps-label">Renewed</span>
        <span class="ps-value">${won}</span>
      </div>`;
  }

  // Render board columns
  const board = document.getElementById('pipeline-board');
  if (!board) return;

  // ── Combined auto-queue column (always shows both sections) ──
  const reachCards    = stageMap['reach_out'];
  const upcomingCards = stageMap['upcoming'];
  const autoTotal     = reachCards.length + upcomingCards.length;
  const autoValue     = [...reachCards, ...upcomingCards]
    .reduce((s, c) => s + (Number(c.agreementValue) || 0), 0);

  // Header colour reflects most urgent group present
  const autoAccent = reachCards.length > 0 ? '#f59e0b' : '#64748b';
  const autoDim    = reachCards.length > 0 ? 'rgba(245,158,11,0.14)' : 'rgba(100,116,139,0.14)';

  const buildAutoGroup = (cards, stageKey, groupLabel, accent, dim) => `
    <div class="pipeline-group-hdr">
      <span class="pgh-label" style="color:${accent}">${groupLabel}</span>
      <span class="pgh-count" style="background:${dim};color:${accent}">${cards.length}</span>
    </div>
    ${cards.length === 0
      ? `<div class="pipeline-empty pipeline-empty--sm">None right now</div>`
      : cards.map(c => buildPipelineCard(c, stageKey)).join('')}`;

  const autoColHtml = `
    <div class="pipeline-col pipeline-col--auto" data-stage="auto"
         ondragover="event.preventDefault(); this.classList.add('drag-over')"
         ondragleave="this.classList.remove('drag-over')"
         ondrop="window.pipelineDrop(event,'auto'); this.classList.remove('drag-over')">
      <div class="pipeline-col-header" style="--stage-accent:${autoAccent}; --stage-dim:${autoDim}">
        <div class="pch-top">
          <span class="pch-title">Renewal queue</span>
          <span class="pch-count" style="background:${autoDim};color:${autoAccent}">${autoTotal}</span>
        </div>
        <div class="pch-sub">${autoTotal > 0 ? `${autoTotal} client${autoTotal !== 1 ? 's' : ''} · ${formatCurrency(autoValue)}` : 'No renewals in window'}</div>
      </div>
      <div class="pipeline-cards">
        ${buildAutoGroup(reachCards,    'reach_out', '≤ 30 days — Reach out', '#f59e0b', 'rgba(245,158,11,0.14)')}
        ${buildAutoGroup(upcomingCards, 'upcoming',  '31–90 days — Upcoming', '#64748b', 'rgba(100,116,139,0.14)')}
      </div>
    </div>`;

  // ── Manual stage columns (droppable) ──
  const manualStages = PIPELINE_STAGES.filter(s =>
    !['pif_opportunity', 'upcoming', 'reach_out'].includes(s.key)
  );

  const manualColsHtml = manualStages.map(s => {
    const cards   = stageMap[s.key];
    const colVal  = cards.reduce((sum, c) => sum + (Number(c.agreementValue) || 0), 0);
    const subText = cards.length > 0 ? `${s.sub} · ${formatCurrency(colVal)}` : s.sub;
    return `
      <div class="pipeline-col" data-stage="${s.key}"
           ondragover="event.preventDefault(); this.classList.add('drag-over')"
           ondragleave="this.classList.remove('drag-over')"
           ondrop="window.pipelineDrop(event,'${s.key}'); this.classList.remove('drag-over')">
        <div class="pipeline-col-header" style="--stage-accent:${s.accent}; --stage-dim:${s.dim}">
          <div class="pch-top">
            <span class="pch-title">${s.label}</span>
            <span class="pch-count" style="background:${s.dim};color:${s.accent}">${cards.length}</span>
          </div>
          <div class="pch-sub">${subText}</div>
        </div>
        <div class="pipeline-cards">
          ${cards.length === 0
            ? `<div class="pipeline-empty">Drop clients here</div>`
            : cards.map(c => buildPipelineCard(c, s.key)).join('')}
        </div>
      </div>`;
  }).join('');

  board.innerHTML = autoColHtml + manualColsHtml;

  // Attach drag events to cards
  board.querySelectorAll('.pipeline-card').forEach(card => {
    card.addEventListener('dragstart', e => {
      e.dataTransfer.setData('clientId', card.dataset.clientId);
      e.dataTransfer.setData('fromStage', card.dataset.stage);
      setTimeout(() => card.classList.add('dragging'), 0);
    });
    card.addEventListener('dragend', () => card.classList.remove('dragging'));
  });

  // Update nav badge
  updatePipelineBadge();
}

// ── Pipeline interaction functions ───────────────────────────
let _pipelineNoteClientId  = null;
let _pipelineNoteNewStage  = null;  // null = note-only, string = move to stage
let _selectedMethod        = null;

// Raw status values (lowercase, snake_case or SHOUTING) shown as words: "public_page" -> "Public page".
function wordsFromRaw(raw) {
  return String(raw).replace(/[_-]+/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

const CONTACT_METHODS = { call: 'Call', text: 'Text', email: 'Email', 'in-person': 'In-person', dm: 'DM' };

function initMethodPills() {
  const container = document.getElementById('pnm-methods');
  if (!container) return;
  container.querySelectorAll('.pnm-method-pill').forEach(btn => {
    btn.onclick = () => {
      container.querySelectorAll('.pnm-method-pill').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      _selectedMethod = btn.dataset.method;
    };
  });
}
function resetMethodPills() {
  _selectedMethod = null;
  const container = document.getElementById('pnm-methods');
  if (container) container.querySelectorAll('.pnm-method-pill').forEach(b => b.classList.remove('active'));
}
function resetTpPanel() {
  document.getElementById('pnm-tp-panel')?.classList.add('hidden');
  document.querySelector('.pipeline-note-modal')?.classList.remove('pnm-expanded');
  const toggle = document.getElementById('pnm-tp-toggle');
  if (toggle) toggle.innerHTML = 'View Talking Points &#9656;';
}

function renderPipelineNoteLog(notes) {
  const logEl = document.getElementById('pnm-log');
  if (!logEl) return;
  if (notes.length === 0) {
    logEl.classList.add('hidden');
    logEl.innerHTML = '';
    return;
  }
  logEl.classList.remove('hidden');
  logEl.innerHTML = `
    <div class="pnm-log-title">Previous entries (${notes.length})</div>
    ${notes.map(n => {
      const methodBadge = n.method
        ? `<span class="pnm-log-method">${escHtml(CONTACT_METHODS[n.method] || n.method)}</span>` : '';
      return `
      <div class="pnm-log-entry">
        <span class="pnm-log-date">${n.date ? formatDate(n.date) : '—'}</span>
        ${methodBadge}
        <span class="pnm-log-text">${n.text ? escHtml(n.text) : '<em style="color:var(--text-subtle)">No note</em>'}</span>
      </div>`;
    }).join('')}`;
}

window.pipelineLogContact = function(clientId) {
  _pipelineNoteClientId = clientId;
  _pipelineNoteNewStage = 'in_talks';   // primary action: full convo happened
  const overlay = document.getElementById('pipeline-note-overlay');
  if (!overlay) return;
  const client = clients.find(c => c.id === clientId);
  const isPifOpp = client && client.billingStructure === 'PIF' && hasContinuity(client);
  document.getElementById('pnm-title').textContent = isPifOpp ? 'Log PIF Pitch' : 'Log Contact';
  // Primary = move to In Talks; secondary = first-touch only → Contacted
  document.getElementById('pnm-save-btn').textContent = 'Save & Move to In Talks';
  const secondary = document.getElementById('pnm-save-secondary');
  if (secondary) secondary.classList.remove('hidden');
  document.getElementById('pnm-date').value = _config.localToday();
  document.getElementById('pnm-note').value = '';
  resetMethodPills();
  initMethodPills();
  resetTpPanel();
  const toggle = document.getElementById('pnm-tp-toggle');
  if (toggle) toggle.classList.toggle('hidden', !isPifOpp);
  renderPipelineNoteLog(client ? getPipelineNotes(client) : []);
  overlay.classList.remove('hidden');
};

window.openPipelineNote = function(clientId) {
  const client = clients.find(c => c.id === clientId);
  if (!client) return;
  _pipelineNoteClientId = clientId;
  _pipelineNoteNewStage = null;
  const overlay = document.getElementById('pipeline-note-overlay');
  if (!overlay) return;
  const isPifOpp = client.billingStructure === 'PIF' && hasContinuity(client);
  document.getElementById('pnm-title').textContent = 'Contact Log';
  // Note-only mode: single save button, no stage change, secondary hidden
  document.getElementById('pnm-save-btn').textContent = 'Save Note';
  const secondary = document.getElementById('pnm-save-secondary');
  if (secondary) secondary.classList.add('hidden');
  document.getElementById('pnm-date').value = _config.localToday();
  document.getElementById('pnm-note').value = '';
  resetMethodPills();
  initMethodPills();
  resetTpPanel();
  const toggle = document.getElementById('pnm-tp-toggle');
  if (toggle) toggle.classList.toggle('hidden', !isPifOpp);
  renderPipelineNoteLog(getPipelineNotes(client));
  overlay.classList.remove('hidden');
};

window.closePipelineNote = function() {
  document.getElementById('pipeline-note-overlay')?.classList.add('hidden');
  _pipelineNoteClientId = null;
  _pipelineNoteNewStage = null;
  _selectedMethod = null;
  resetTpPanel();
};

window.savePipelineNote = async function(stageOverride) {
  if (!_pipelineNoteClientId) return;
  const noteText = document.getElementById('pnm-note')?.value.trim() || '';
  const dateVal  = document.getElementById('pnm-date')?.value || _config.localToday();
  const client   = clients.find(c => c.id === _pipelineNoteClientId);
  const existing = client ? getPipelineNotes(client) : [];
  const newEntry = { date: dateVal, text: noteText, ts: Date.now(), method: _selectedMethod };
  // stageOverride (from secondary button) takes precedence over _pipelineNoteNewStage
  const targetStage = stageOverride ?? _pipelineNoteNewStage;
  const update = {
    renewalNotes:       [newEntry, ...existing],
    renewalLastContact: dateVal,
    renewalNote:        null   // clear legacy field
  };
  if (targetStage) {
    update.renewalStage = targetStage;
    update.renewalStageDate = dateVal;
  }
  const stageLabels = { contacted: 'Contacted', in_talks: 'In Talks', renewed: 'Renewed', not_renewing: 'Not Renewing' };
  const toastMsg = targetStage
    ? `Logged & moved to ${stageLabels[targetStage] || targetStage}.`
    : 'Note saved.';
  try {
    // Optimistic local update
    if (client) {
      client.renewalNotes = [newEntry, ...existing];
      client.renewalLastContact = dateVal;
      if (targetStage) { client.renewalStage = targetStage; client.renewalStageDate = dateVal; }
    }
    await updateClient(_pipelineNoteClientId, update);
    window.closePipelineNote();
    renderRenewals();
    showToast(toastMsg, 'success');
  } catch (err) {
    showToast('Error saving entry.', 'error');
  }
};

// ── Talking Points Panel ─────────────────────────────────────
window.toggleTalkingPoints = function() {
  const panel = document.getElementById('pnm-tp-panel');
  if (!panel) return;
  const modal = document.querySelector('.pipeline-note-modal');
  const isHidden = panel.classList.contains('hidden');
  if (isHidden) {
    const client = clients.find(c => c.id === _pipelineNoteClientId);
    if (client) populateTalkingPoints(client);
    panel.classList.remove('hidden');
    modal?.classList.add('pnm-expanded');
    document.getElementById('pnm-tp-toggle').innerHTML = 'Hide Talking Points &#9662;';
  } else {
    panel.classList.add('hidden');
    modal?.classList.remove('pnm-expanded');
    document.getElementById('pnm-tp-toggle').innerHTML = 'View Talking Points &#9656;';
  }
};

// Kept for backward compat; tabs use selectTpSection now
window.toggleTpSection = function(index) { window.selectTpSection(index); };

window.selectTpSection = function(index) {
  // Update tab active state
  document.querySelectorAll('.tp-tab').forEach((tab, i) => {
    tab.classList.toggle('active', i === index);
  });
  // Swap content
  const content = document.getElementById('tp-active-content');
  if (content && window._tpSections && window._tpSections[index]) {
    const s = window._tpSections[index];
    content.innerHTML = `<h3 class="tp-section-active-title">${s.letter} — ${s.title}</h3>${s.body}`;
  }
};

function populateTalkingPoints(client) {
  const name      = client.name || 'Client';
  const firstName = client.firstName || name.split(' ')[0];
  const pifAmount = Number(client.agreementValue) || 0;
  const contRate  = continuityRateOf(client);
  const months    = getMonthsAsClient(client);
  const daysLeft  = getDaysUntilEnd(client);
  const svcType   = label(client.serviceType);
  const contAnnual = contRate * 12;
  const pitchValue = Number(client.renewalPotentialValue) || pifAmount;
  const savings    = contAnnual - pitchValue;

  document.getElementById('pnm-tp-title').textContent = 'Renewal Playbook \u2014 ' + firstName;

  document.getElementById('pnm-tp-summary').innerHTML = `
    <div class="tp-stat"><span class="tp-stat-val">${formatCurrency(pifAmount)}</span><span class="tp-stat-lbl">Current PIF</span></div>
    <div class="tp-stat"><span class="tp-stat-val">${formatCurrency(contRate)}/mo</span><span class="tp-stat-lbl">Continuity</span></div>
    <div class="tp-stat"><span class="tp-stat-val">${months} mo</span><span class="tp-stat-lbl">As client</span></div>
    <div class="tp-stat"><span class="tp-stat-val">${daysLeft !== null ? daysLeft + 'd' : '\u2014'}</span><span class="tp-stat-lbl">Remaining</span></div>
    <div class="tp-stat"><span class="tp-stat-val">${svcType}</span><span class="tp-stat-lbl">Service</span></div>`;

  const sections = getTalkingPointsSections(firstName, pifAmount, contRate, contAnnual, pitchValue, savings, months, daysLeft);
  // Store globally so selectTpSection can access content without re-computing
  window._tpSections = sections;

  // Short tab labels (abbreviated to fit)
  const tabLabels = ['Celebrate', 'Label', 'Outline', 'Sell', 'Explain', 'Reinforce', 'Objections'];

  document.getElementById('pnm-tp-accordion').innerHTML = `
    <div class="tp-tab-bar">
      ${sections.map((s, i) => `
        <button class="tp-tab${i === 0 ? ' active' : ''}" onclick="window.selectTpSection(${i})">
          <span class="tp-tab-letter">${s.letter}</span>
          <span class="tp-tab-label">${tabLabels[i] || s.letter}</span>
        </button>`).join('')}
    </div>
    <div class="tp-active-content" id="tp-active-content"></div>`;

  // Show first section by default
  const firstContent = document.getElementById('tp-active-content');
  if (firstContent && sections[0]) {
    const s = sections[0];
    firstContent.innerHTML = `<h3 class="tp-section-active-title">${s.letter} — ${s.title}</h3>${s.body}`;
  }
}

function getTalkingPointsSections(firstName, pifAmount, contRate, contAnnual, pitchValue, savings, months, daysLeft) {
  const fc = formatCurrency;
  const perMonth = pitchValue > 0 ? Math.round(pitchValue / 12) : 0;
  return [
    { letter: 'C', title: 'Celebrate & Clarify', body: `
      <p class="tp-prompt">Review their journey. Acknowledge wins. Build on the relationship you already have.</p>
      <ul class="tp-points">
        <li>"${firstName}, you've been with us for ${months} months now. Before we talk about what's next, I want to hear from you \u2014 what have been your biggest wins?"</li>
        <li>"Where are you now compared to when you started? What's changed \u2014 physically, mentally, in your daily life?"</li>
        <li>"What part of the program has worked best for you?"</li>
        <li>"How has your confidence / energy / lifestyle shifted since we started?"</li>
      </ul>
      <p class="tp-prompt">Listen actively. Their own words become your selling tools in later sections.</p>`
    },
    { letter: 'L', title: 'Label What\'s Next', body: `
      <p class="tp-prompt">Transition from past wins to future goals. Identify the next chapter.</p>
      <ul class="tp-points">
        <li>"Now that you've accomplished [reference their wins], what does the next level look like for you?"</li>
        <li>"Have your goals evolved? Any new priorities or things you haven't tackled yet?"</li>
        <li>"Where do you want to be in another 6\u201312 months?"</li>
        <li>"If you could change one more thing about your health / body / performance \u2014 what would it be?"</li>
      </ul>
      <p class="tp-prompt">Get them to articulate a desired future state. This becomes the anchor for the rest of the conversation.</p>`
    },
    { letter: 'O', title: 'Outline the Risk', body: `
      <p class="tp-prompt">What happens if ${firstName} stops or drops to continuity only?</p>
      <ul class="tp-points">
        <li>"What do you think would happen to your progress if you went back to doing this on your own?"</li>
        <li>"Continuity at ${fc(contRate)}/mo gives you access, but not the same structure, programming attention, and accountability you have right now."</li>
        <li>"It took ${months} months to get here. Losing momentum means rebuilding, and that takes even longer the second time."</li>
        <li>"What typically happens when you try to go it alone?" \u2014 let them answer. Their own history is the strongest argument.</li>
        <li>"The gap isn't knowledge \u2014 it's accountability. That's what disappears without a dedicated program."</li>
      </ul>`
    },
    { letter: 'S', title: 'Sell the Next Chapter', body: `
      <p class="tp-prompt">Future-pace where ${firstName} will be with continued dedicated coaching.</p>
      <ul class="tp-points">
        <li>"You've done [X] in ${months} months. Imagine another 12 months at this pace \u2014 where does that put you?"</li>
        <li>Reference their progress trajectory and project it forward. Make it vivid and specific.</li>
        <li>Tie back to the goals they named in the "Label" section.</li>
        <li>"This isn't starting over \u2014 this is Chapter 2. You've built the foundation. Now we build on it."</li>
        <li>If applicable, mention programming changes, new features, or upgraded support for the next term.</li>
      </ul>`
    },
    { letter: 'E', title: 'Explain the Value', body: `
      <p class="tp-prompt">Show the math. PIF saves money vs. staying on continuity.</p>
      <div class="tp-math-box">
        <div class="tp-math-row"><span>Continuity cost (12 months)</span><strong>${fc(contRate)} &times; 12 = ${fc(contAnnual)}</strong></div>
        <div class="tp-math-row"><span>New PIF offer</span><strong>${fc(pitchValue)}</strong></div>
        ${savings > 0 ? `<div class="tp-math-row tp-math-savings"><span>Annual savings with PIF</span><strong>${fc(savings)}</strong></div>` : ''}
        ${perMonth > 0 ? `<div class="tp-math-row"><span>PIF broken down</span><strong>~${fc(perMonth)}/mo</strong></div>` : ''}
      </div>
      <ul class="tp-points">
        <li>"By re-upping PIF at ${fc(pitchValue)}, you're ${savings > 0 ? 'saving ' + fc(savings) + ' compared to paying monthly' : 'locking in your rate and getting dedicated programming'}."</li>
        <li>Guaranteed rate \u2014 no price increases. Locked-in access and priority attention.</li>
        <li>"${fc(pitchValue)} over 12 months is about ${fc(perMonth)}/mo \u2014 ${perMonth > 0 && contRate > 0 ? 'that\'s ' + fc(contRate - perMonth) + ' less per month than continuity' : 'less than most people spend on supplements'}."</li>
      </ul>`
    },
    { letter: 'R', title: 'Reinforce Commitment', body: `
      <p class="tp-prompt">Why renewing NOW matters. Urgency through momentum, not pressure.</p>
      <ul class="tp-points">
        <li>"The best time to commit is while you're in the groove. Taking a break makes it 10x harder to restart."</li>
        ${daysLeft !== null ? `<li>"With ${daysLeft} days left on your current program, now is the time to plan the next phase so there's zero gap."</li>` : ''}
        <li>"Consistency is the number one predictor of long-term results. A gap disrupts habits, accountability, and the relationship we've built."</li>
        <li>"You've already proven you can do this. Don't give yourself a reason to stop."</li>
        <li>Direct close: "Are you ready to lock in the next chapter?"</li>
      </ul>`
    },
    { letter: 'O', title: 'Overcome Objections', body: `
      <div class="tp-objection">
        <span class="tp-objection-label">Money / Budget</span>
        <ul class="tp-points">
          <li>Compare PIF to continuity annual cost: ${fc(pitchValue)} vs ${fc(contAnnual)} \u2014 ${savings > 0 ? 'PIF actually saves ' + fc(savings) : 'similar investment, but with more structure'}.</li>
          <li>"If this was free, would you keep going?" \u2192 "So it's not about value \u2014 it's about finding a way. Let's figure that out."</li>
          <li>Offer to split into installments if available.</li>
          <li>Frame the cost of NOT having structure: gym memberships, supplements, wasted time.</li>
        </ul>
      </div>
      <div class="tp-objection">
        <span class="tp-objection-label">Want to try on my own</span>
        <ul class="tp-points">
          <li>"What happened last time you went solo?" \u2014 let them answer.</li>
          <li>"Continuity IS the 'on your own' option \u2014 lighter support, no dedicated programming. Is that really what got you results?"</li>
          <li>"The best athletes in the world have coaches. Not because they can't train \u2014 because they know they perform better with one."</li>
        </ul>
      </div>
      <div class="tp-objection">
        <span class="tp-objection-label">Need to think about it</span>
        <ul class="tp-points">
          <li>"Totally understand. Help me out \u2014 is it the price, the timing, or something else?"</li>
          <li>Isolate the real objection and address it directly.</li>
          <li>"If you already knew this was the right move, what would you still need to think about?"</li>
          <li>Offer to send a summary and set a specific 48-hour follow-up.</li>
        </ul>
      </div>
      <div class="tp-objection">
        <span class="tp-objection-label">Need to talk to partner</span>
        <ul class="tp-points">
          <li>"What do you think they'd say if you told them: 'I found something that's been working and I want to keep going'?"</li>
          <li>Offer a breakdown they can share \u2014 results so far, cost comparison, what the next program includes.</li>
          <li>Offer a three-way call if helpful.</li>
          <li>Set a specific follow-up time: "When will you chat with them? I'll check in right after."</li>
        </ul>
      </div>`
    },
  ];
}

window.pipelineMoveStage = async function(clientId, newStage) {
  const today = _config.localToday();
  try {
    await updateClient(clientId, { renewalStage: newStage, renewalStageDate: today });
    if (newStage === 'renewed') {
      showToast('Marked as Renewed. Don\'t forget to update their agreement dates.', 'success');
    } else if (newStage === 'not_renewing') {
      showToast('Marked as Not Renewing.', 'info');
    } else {
      showToast('Stage updated.', 'success');
    }
  } catch (err) {
    showToast('Error updating stage.', 'error');
  }
};

window.pipelineClearStage = async function(clientId) {
  try {
    await updateClient(clientId, {
      renewalStage: null, renewalStageDate: null,
      renewalNote: null,  renewalNotes: null,
      renewalLastContact: null, renewalPotentialValue: null
    });
    showToast('Pipeline entry cleared.', 'success');
  } catch (err) {
    showToast('Error clearing stage.', 'error');
  }
};

window.pipelineDrop = async function(event, newStage) {
  event.preventDefault();
  const clientId  = event.dataTransfer.getData('clientId');
  const fromStage = event.dataTransfer.getData('fromStage');
  if (!clientId || fromStage === newStage) return;
  // Auto-stages (incl. the queue column itself): clear manual override so auto-compute places correctly
  const autoStages = ['auto', 'upcoming', 'reach_out'];
  const newRenewalStage = autoStages.includes(newStage) ? null : newStage;
  const update = { renewalStage: newRenewalStage };

  // Optimistic local update + immediate re-render
  const c = clients.find(x => x.id === clientId);
  const prevStage = c ? c.renewalStage : undefined;
  if (c) c.renewalStage = newRenewalStage;
  renderRenewals();

  try {
    await updateClient(clientId, update);
  } catch (err) {
    // Revert on failure
    if (c) c.renewalStage = prevStage;
    renderRenewals();
    showToast('Error moving client.', 'error');
  }
};

function updatePipelineBadge() {
  const redBadge  = document.getElementById('renewal-count');
  const blueBadge = document.getElementById('renewal-pif-count');
  const urgent    = clients.filter(c => getEffectivePipelineStage(c) === 'reach_out').length;
  const pifCount  = clients.filter(c => getEffectivePipelineStage(c) === 'pif_opportunity').length;
  if (redBadge)  { redBadge.textContent  = urgent;   redBadge.classList.toggle('hidden',  urgent === 0);   }
  if (blueBadge) { blueBadge.textContent = pifCount; blueBadge.classList.toggle('hidden', pifCount === 0); }
}

window.setPipelineWindow = function(days) {
  pipelineWindow = days;
  _state.setPipelineWindow(days);
  renderRenewals();
};

window.savePifPotentialValue = async function(clientId, value) {
  try {
    await updateClient(clientId, { renewalPotentialValue: value.trim() });
  } catch (err) {
    showToast('Error saving value.', 'error');
  }
};

// ============================================================
// RENEW CLIENT MODAL
// ============================================================
window.openRenewModal = function(clientId) {
  const c = clients.find(x => x.id === clientId);
  if (!c) return;
  // Change billing 4c part 3: what comes next is set up there already; this window would charge again (plan-answers.js)
  const renewWaits = renewBlockedWords(c); if (renewWaits) { showToast(renewWaits, 'info'); return; }
  _renewClientId = clientId;

  // Title
  document.getElementById('renew-modal-title').textContent = `Renew — ${c.name}`;

  // Context bar showing current agreement
  const endDate = getEndDate(c);
  const ctxBar = document.getElementById('renew-context-bar');
  if (ctxBar) {
    ctxBar.innerHTML = `
      <div class="rcb-item"><span class="rcb-label">Current end</span><span class="rcb-val">${endDate ? formatDateObj(endDate) : '—'}</span></div>
      <div class="rcb-item"><span class="rcb-label">Billing</span><span class="rcb-val">${label(c.billingStructure)}</span></div>
      <div class="rcb-item"><span class="rcb-label">Value</span><span class="rcb-val">${formatCurrency(c.agreementValue)}</span></div>
      ${continuityRateOf(c) ? `<div class="rcb-item"><span class="rcb-label">Continuity</span><span class="rcb-val">${formatCurrency(continuityRateOf(c))}/mo</span></div>` : ''}
    `;
  }

  // New start date defaults to current end date (the day it expires = new start)
  const defaultStart = endDate
    ? _config.localDateStr(endDate)
    : _config.localToday();
  document.getElementById('renew-start-date').value = defaultStart;

  // Pre-fill from current agreement (coach changes what differs)
  const billingSelect = document.getElementById('renew-billing');
  billingSelect.value = (c.billingStructure || 'PIF').toUpperCase() === 'MRR' ? 'MRR'
    : (c.billingStructure || 'PIF').toLowerCase() === 'installments' ? 'installments'
    : 'PIF';

  document.getElementById('renew-length').value      = c.lengthOfAgreement || '';
  document.getElementById('renew-bonus').value       = c.bonusMonths        || '';
  document.getElementById('renew-value').value       = '';  // intentionally blank — coach enters new deal value
  document.getElementById('renew-mrr').value         = c.mrrAmount          || '';
  document.getElementById('renew-installments').value = c.installmentCount  || '';
  document.getElementById('renew-continuity').value  = c.continuityRate     || '';

  // Payment source — match add client options
  const pmSrc = document.getElementById('renew-payment-source');
  const validSources = ['stripe', 'venmo', 'zelle', 'cash', 'check', 'affirm'];
  const savedSrc = (c.paymentSource || 'stripe').toLowerCase();
  pmSrc.value = validSources.includes(savedSrc) ? savedSrc : 'stripe';

  // Subscription cancel notice — only relevant for active/pending clients who still have live subs
  const notice = document.getElementById('renew-sub-cancel-notice');
  if (notice) {
    const hasStripe    = !!(c.stripeSubscriptionId || c.stripeContinuitySubscriptionId);
    const alreadyGone  = c.status === 'cancelled'; // subs already cancelled when status was set
    notice.classList.toggle('hidden', !hasStripe || alreadyGone || pmSrc.value !== 'stripe');
  }

  // Show/hide conditional field groups
  window.renewBillingChange();
  window.renewPaymentSourceChange();

  // Service type — pre-fill and trigger session section show/hide
  const renewServiceType = document.getElementById('renew-service-type');
  if (renewServiceType) renewServiceType.value = c.serviceType || 'online';

  // Session fields — show section if hybrid/in-person, pre-fill from client
  window.updateSessionSection('renew', c.serviceType || 'online');
  const renewFmt = c.sessionFormat || 'credits';
  window.setSessionFormat('renew', renewFmt);
  const renewCreditsEl = document.getElementById('renew-sessionCredits');
  const renewPerPeriodEl = document.getElementById('renew-sessionsPerPeriod');
  const renewPeriodEl = document.getElementById('renew-sessionPeriod');
  if (renewCreditsEl) renewCreditsEl.value = c.sessionCredits || '';
  if (renewPerPeriodEl) renewPerPeriodEl.value = c.sessionsPerPeriod || '';
  if (renewPeriodEl && c.sessionPeriod) renewPeriodEl.value = c.sessionPeriod;

  // Custom dropdowns — upgrade on first open, then re-read the values set above.
  const renewForm = document.getElementById('renew-form');
  upgradeSelects(renewForm);
  syncUpgradedSelects(renewForm);

  // Clear any previous errors and reset submit button
  const errEl = document.getElementById('renew-card-errors');
  if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
  const btn = document.getElementById('renew-submit-btn');
  if (btn) { btn.disabled = false; btn.textContent = 'Process Renewal'; }

  // Open overlay
  document.getElementById('renew-modal-overlay').classList.remove('hidden');

  // Build initial charge summary
  window.updateRenewChargeSummary();

  // Mount Stripe card after DOM is visible
  if (pmSrc.value === 'stripe') {
    setTimeout(() => mountRenewCardElement(), 50);
  }
};

window.closeRenewModal = function() {
  document.getElementById('renew-modal-overlay')?.classList.add('hidden');
  // Unmount card to allow clean remount next open
  if (renewCardElement) {
    try { renewCardElement.unmount(); } catch(e) {}
    renewCardElement = null;
  }
  _renewClientId = null;
};

// ============================================================
// RECORD MANUAL PAYMENT
// ============================================================
// ── Record Payment ─────────────────────────────────────────────
// MOVED to modules/record-payment.js (standing rule ③). It installs the same window.openRecordPaymentModal /
// closeRecordPaymentModal / handleRecordPayment; setRecordPaymentDeps hands it THIS file's clients and helpers.

// ── One-off charge on the card on file (Stripe clients) ──────
// MOVED to modules/charge-oneoff.js (standing rule ③). It still installs the
// same window.openChargeOneoffModal / oneoffAmountChanged /
// closeChargeOneoffModal / oneoffCardPicked / handleChargeOneoff that the
// modal's inline handlers in index.html call; the import at the top of this
// file is what runs it. setChargeOneoffDeps hands it THIS file's `clients`.


// MOVED to modules/record-payment.js (standing rule ③), with openRecordPaymentModal and closeRecordPaymentModal.

window.renewBillingChange = function() {
  const billing      = document.getElementById('renew-billing')?.value || 'PIF';
  const mrrGrp       = document.getElementById('renew-mrr-group');
  const instGrp      = document.getElementById('renew-installments-group');
  const valContRow   = document.getElementById('renew-value-continuity-row');
  const bonusGrp     = document.getElementById('renew-bonus-group');
  if (mrrGrp)       mrrGrp.classList.toggle('hidden',     billing !== 'MRR');
  if (instGrp)      instGrp.classList.toggle('hidden',    billing !== 'installments');
  // MRR: hide Agreement Value + Continuity Rate (value is auto-calculated; continuity is redundant)
  if (valContRow)   valContRow.classList.toggle('hidden', billing === 'MRR');
  // Bonus Months only offered on PIF — hide for MRR and Installments
  if (bonusGrp)     bonusGrp.classList.toggle('hidden',   billing !== 'PIF');
  if (billing !== 'PIF') {
    const bonusEl = document.getElementById('renew-bonus');
    if (bonusEl) bonusEl.value = '';
  }
  window.updateRenewChargeSummary();
};

window.updateRenewChargeSummary = function() {
  const summaryEl  = document.getElementById('renew-charge-summary');
  const testBanner = document.getElementById('renew-test-mode-banner');
  if (!summaryEl) return;

  const billing      = document.getElementById('renew-billing')?.value || 'PIF';
  const startDateVal = document.getElementById('renew-start-date')?.value || '';
  const agreeLen     = parseInt(document.getElementById('renew-length')?.value) || 0;
  const bonusMo      = parseInt(document.getElementById('renew-bonus')?.value) || 0;
  const mrrAmt       = parseFloat(document.getElementById('renew-mrr')?.value) || 0;
  const instCount    = parseInt(document.getElementById('renew-installments')?.value) || 0;
  const contRate     = parseFloat(document.getElementById('renew-continuity')?.value) || 0;
  // For MRR, agreement value is auto-calculated from length × monthly rate
  const agreeVal     = billing === 'MRR'
    ? mrrAmt * (agreeLen + bonusMo)
    : parseFloat(document.getElementById('renew-value')?.value) || 0;

  // Test mode banner
  if (testBanner) testBanner.classList.toggle('hidden', getCurrentMode() !== 'test');

  // Calculate new agreement end date
  const totalMo = agreeLen + bonusMo;
  let endDateStr = '';
  if (startDateVal && totalMo > 0) {
    const endDate = new Date(startDateVal + 'T00:00:00');
    endDate.setMonth(endDate.getMonth() + totalMo);
    endDateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const endLine = endDateStr
    ? `<div class="charge-continuity-note">New agreement runs through <strong>${endDateStr}</strong></div>`
    : '';

  let html = '';

  if (billing === 'PIF') {
    html = agreeVal > 0
      ? `One-time charge: <span class="amount">${formatCurrency(agreeVal)}</span>`
      : `Enter agreement value above`;
    if (agreeVal > 0) {
      html += endLine;
      if (contRate > 0 && totalMo > 0) {
        html += `<div class="charge-continuity-note">Then ${formatCurrency(contRate)}/mo continuity begins automatically in ${totalMo} month${totalMo !== 1 ? 's' : ''}</div>`;
      }
    }
  } else if (billing === 'installments') {
    const instAmt = instCount > 0 && agreeVal > 0 ? agreeVal / instCount : 0;
    html = (instCount > 0 && agreeVal > 0)
      ? `${instCount} monthly payments of <span class="amount">${formatCurrency(instAmt)}</span> <span style="color:var(--text-muted)">· ${formatCurrency(agreeVal)} total</span>${endLine}`
      : `Enter value and number of payments above`;
    if (instCount > 0 && agreeVal > 0 && contRate > 0 && totalMo > 0) {
      html += `<div class="charge-continuity-note">Then ${formatCurrency(contRate)}/mo continuity begins automatically in ${totalMo} month${totalMo !== 1 ? 's' : ''}</div>`;
    }
  } else {
    // MRR — value is auto-calculated, show total as context
    const totalMo = agreeLen + bonusMo;
    html = mrrAmt > 0
      ? `Monthly subscription: <span class="amount">${formatCurrency(mrrAmt)}/mo</span>`
        + (totalMo > 0 ? `<div class="charge-continuity-note">${totalMo} month agreement · ${formatCurrency(agreeVal)} total · continues monthly until cancelled or renewed</div>` : '')
        + (totalMo > 0 ? endLine : '')
      : `Enter monthly rate above`;
  }

  summaryEl.innerHTML = html;
};

window.renewPaymentSourceChange = function() {
  const src    = document.getElementById('renew-payment-source')?.value || 'stripe';
  const strSec = document.getElementById('renew-stripe-section');
  const notice = document.getElementById('renew-sub-cancel-notice');
  // Use style.display to match the inline style on the section element
  if (strSec) strSec.style.display = src === 'stripe' ? '' : 'none';
  // Mount/unmount card
  if (src === 'stripe') {
    setTimeout(() => mountRenewCardElement(), 50);
  } else {
    if (renewCardElement) {
      try { renewCardElement.unmount(); } catch(e) {}
      renewCardElement = null;
    }
  }
  // Sub cancel notice — only for Stripe renewals with existing subs
  if (notice && _renewClientId) {
    const c           = clients.find(x => x.id === _renewClientId);
    const hasStripe   = c && !!(c.stripeSubscriptionId || c.stripeContinuitySubscriptionId);
    const alreadyGone = c?.status === 'cancelled';
    notice.classList.toggle('hidden', !hasStripe || alreadyGone || src !== 'stripe');
  }
  window.updateRenewChargeSummary();
};

window.handleRenewSubmit = async function() {
  const clientId = _renewClientId;
  const c = clients.find(x => x.id === clientId);
  if (!c || !clientId) return;

  const fd     = new FormData(document.getElementById('renew-form'));
  const g      = k => (fd.get(k) || '').trim();
  const gn     = k => { const v = parseFloat(g(k)); return isNaN(v) ? null : v; };
  const gi     = k => { const v = parseInt(g(k));   return isNaN(v) ? 0 : v; };

  const newStartDate      = g('newStartDate');
  const billingStructure  = g('billingStructure');
  const lengthOfAgreement = gi('lengthOfAgreement');
  const bonusMonths       = gi('bonusMonths');
  // let — reassigned below for MRR (const here threw at runtime and killed renewals)
  let agreementValue      = gn('agreementValue');
  const mrrAmount         = gn('mrrAmount');
  const installmentCount  = gi('installmentCount');
  const continuityRate    = gn('continuityRate') || 0;
  const paymentSource     = g('paymentSource');
  const serviceType       = g('serviceType')      || c.serviceType || 'online';
  const sessionFormat     = g('sessionFormat')    || null;
  const sessionCredits    = gi('sessionCredits')  || null;
  const sessionsPerPeriod = gi('sessionsPerPeriod') || null;
  const sessionPeriod     = g('sessionPeriod')    || null;

  // For MRR, auto-calculate agreement value from length × monthly rate
  if (billingStructure === 'MRR') {
    agreementValue = mrrAmount * (lengthOfAgreement + bonusMonths);
  }

  // --- Validation ---
  if (!newStartDate)                                    { showToast('Please enter a new start date.', 'error');       return; }
  if (!lengthOfAgreement || lengthOfAgreement < 1)      { showToast('Please enter agreement length.', 'error');       return; }
  if (billingStructure === 'MRR' && !mrrAmount)         { showToast('Please enter the monthly rate.', 'error');       return; }
  if (billingStructure !== 'MRR' && (agreementValue === null || agreementValue < 0)) { showToast('Please enter agreement value.', 'error'); return; }
  if (billingStructure === 'installments' && installmentCount < 2) { showToast('Please enter installment count (min 2).', 'error'); return; }

  const btn   = document.getElementById('renew-submit-btn');
  const errEl = document.getElementById('renew-card-errors');
  btn.disabled = true;
  btn.textContent = 'Processing…';
  if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }

  const today = _config.localToday();

  // Computed MRR for installments
  const effectiveMrr = billingStructure === 'installments'
    ? (agreementValue / installmentCount)
    : (billingStructure === 'MRR' ? mrrAmount : null);

  // Build Supabase update
  const updateData = {
    programStartDate:      newStartDate,
    billingStructure:      billingStructure,
    lengthOfAgreement:     lengthOfAgreement,
    bonusMonths:           bonusMonths,
    agreementValue:        agreementValue,
    mrrAmount:             effectiveMrr,
    installmentCount:      billingStructure === 'installments' ? installmentCount : null,
    continuityRate:        billingStructure === 'MRR' ? null : (continuityRate || null),
    moneyCollectedUpfront: billingStructure === 'PIF' ? agreementValue : 0,
    status:                'active',
    // Pipeline — mark as renewed
    renewalStage:          'renewed',
    renewalStageDate:      today,
    // Append renewal log entry
    renewalNotes:          [
      { date: today, text: `Renewed — ${label(billingStructure)} · ${formatCurrency(agreementValue)}`, ts: Date.now() },
      ...getPipelineNotes(c)
    ],
    renewalLastContact:    today,
    renewalPotentialValue: null, // clear pitch value — deal is done
    serviceType:           serviceType,
    sessionFormat:         sessionFormat,
    sessionCredits:        sessionCredits,
    sessionsPerPeriod:     sessionsPerPeriod,
    sessionPeriod:         sessionPeriod,
  };

  // Track old sub IDs to cancel after new payment succeeds
  const oldSubId     = c.stripeSubscriptionId;
  const oldContSubId = c.stripeContinuitySubscriptionId;
  // The old plan's subscriptions Stripe would not cancel, with its reason
  // (modules/cancel-client-subscriptions.js).
  let oldStillBilling = [];
  /** @type {{ rate: number, cardId: string, paymentIntentId: string }|null} set up once the renewal is saved */
  let monthlyAfter = null;

  try {
    if (paymentSource === 'stripe') {
      const customerId = c.stripeCustomerId;
      if (!customerId) throw new Error('No Stripe customer on record for this client. Use External payment instead.');

      if (billingStructure === 'PIF') {
        // ── PIF: one-time charge ──────────────────────────────────
        const { clientSecret } = await callFunction('create-payment-intent', { amount: agreementValue, customerId });
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: renewCardElement, billing_details: { name: c.name } }
        });
        if (error) throw new Error(error.message);

        // Log the renewal PIF charge in payment history
        const existingHistory = Array.isArray(c.paymentHistory) ? c.paymentHistory : [];
        updateData.paymentHistory = [...existingHistory, {
          id:          crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
          date:        _config.localToday(),
          amount:      agreementValue,
          description: 'PIF Renewal — Full Payment',
          period:      periodLabel(newStartDate || _config.localToday()),
          source:      'stripe',
          recordedAt:  new Date().toISOString(),
        }];
        updateData.totalCollected = (Number(c.totalCollected ?? c.moneyCollectedUpfront) || 0) + agreementValue;

        // Monthly after the program (continuity set at sign-up, 10/13): no Stripe free trial. The rate comes off the
        // record and Change billing sets it up once the renewal is saved (modules/signup-continuity.js).
        if (continuityRate > 0 && paymentIntent.payment_method) {
          monthlyAfter = { rate: continuityRate, cardId: paymentIntent.payment_method, paymentIntentId: paymentIntent.id };
        }
        updateData.continuityRate = null;
        updateData.stripeContinuitySubscriptionId = null;
        updateData.continuityStartDate = null;
        // PIF doesn't use a recurring subscription
        updateData.stripeSubscriptionId = null;

      } else {
        // ── MRR / Installments: subscription ─────────────────────
        const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
          type: 'card', card: renewCardElement, billing_details: { name: c.name }
        });
        if (pmErr) throw new Error(pmErr.message);

        const subAmount = billingStructure === 'installments'
          ? (agreementValue / installmentCount)
          : (mrrAmount || 0);
        const subMonths = billingStructure === 'installments' ? installmentCount : 0;

        const { clientSecret, subscriptionId } = await callFunction('create-subscription', {
          customerId, amount: subAmount, paymentMethodId: paymentMethod.id, agreementMonths: subMonths
        });
        const { error: confirmErr } = await stripeConfirmWithRetry(clientSecret);
        if (confirmErr) throw new Error(confirmErr.message);

        updateData.stripeSubscriptionId = subscriptionId;
        // Clear continuity (MRR clients don't have a scheduled continuity sub)
        updateData.stripeContinuitySubscriptionId = null;
        updateData.continuityStartDate = null;
      }

      // Cancel old subscriptions now that new payment succeeded
      oldStillBilling = await cancelSubscriptions([oldSubId, oldContSubId]);

    } else {
      // ── External / Manual payment ─────────────────────────────
      // No Stripe calls — coach collected payment outside the system
      // Clear old Stripe sub IDs since those payments are no longer active
      updateData.stripeSubscriptionId           = null;
      updateData.stripeContinuitySubscriptionId = null;
      updateData.continuityStartDate            = null;
    }

    await updateClient(clientId, updateData);
    const why = monthlyAfter ? await startMonthlyAfterProgram({ clientId, ...monthlyAfter }) : null;
    window.closeRenewModal();
    showToast(`${c.name} renewed successfully!`, 'success');
    if (why) showToast(notSetUpWords(c.name, why, monthlyAfter.rate), 'error', { duration: 20000 });
    // ⚠️ The new plan is paid and live, so the renewal stands. But an old
    // subscription Stripe would not cancel keeps charging beside it, and it is
    // no longer on their record, so nothing else would ever show it.
    if (oldStillBilling.length) {
      showToast(`${c.name} renewed, but Stripe would not cancel their old subscription, so they will be charged twice until it is cancelled. Cancel it in your Stripe dashboard now. Stripe said: ${oldStillBilling[0].message}`, 'error', { duration: 30000 });
    }

  } catch (err) {
    if (errEl) { errEl.textContent = err.message; errEl.style.display = ''; }
    showToast('Renewal failed: ' + err.message, 'error');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Process Renewal';
  }
};

// ============================================================
// NAVIGATION
// ============================================================
// ---- Tag management ----
window.addClientTag = async function(clientId, tag) {
  tag = tag.trim();
  if (!tag) return;
  const c = clients.find(x => x.id === clientId);
  if (!c) return;
  const existing = Array.isArray(c.tags) ? c.tags : [];
  if (existing.includes(tag)) return;
  // Auto-register tag in definitions if it doesn't exist yet
  const defs = loadTagDefs();
  if (!defs.find(d => d.name.toLowerCase() === tag.toLowerCase())) {
    defs.push({ name: tag, color: '#64748b' });
    saveTagDefs(defs);
    renderTagSettings();
  }
  try {
    await updateClient(clientId, { tags: [...existing, tag] });
    // Automation trigger — fire-and-forget, never blocks the tag add.
    callFunction('automation-fire-client-event', { clientId, eventType: 'tag_added', context: { tag } }).catch(() => {});
  } catch (err) { showToast('Error adding tag.', 'error'); }
};

window.addCustomTag = function(clientId) {
  const input = document.getElementById(`tag-input-${clientId}`);
  if (!input) return;
  const val = input.value.trim();
  if (val) { window.addClientTag(clientId, val); input.value = ''; }
};

window.removeClientTag = async function(clientId, tag) {
  const c = clients.find(x => x.id === clientId);
  if (!c) return;
  const updated = (c.tags || []).filter(t => t !== tag);
  try {
    await updateClient(clientId, { tags: updated });
  } catch (err) { showToast('Error removing tag.', 'error'); }
};

window.toggleTagPicker = function(clientId) {
  const picker = document.getElementById(`tag-picker-${clientId}`);
  if (picker) picker.classList.toggle('hidden');
};

function renderClientProfile() {
  // CRM 2.0 profile IS the profile. Classic only renders if the escape hatch
  // was explicitly set (see isClassicProfileForced above).
  if (isClassicProfileForced()) { renderClientProfileV2(); return; }
  renderClientProfileNext();
}

window.updateTagDefColor = function(index, newColor) {
  const defs = loadTagDefs();
  if (!defs[index]) return;
  defs[index].color = newColor;
  saveTagDefs(defs);
  // Update the chip preview in place without full re-render (avoids losing color picker focus)
  const chip = document.getElementById(`tag-def-chip-${index}`);
  if (chip) {
    const { bg, color, border } = getTagStyle(newColor);
    chip.style.background   = bg;
    chip.style.color        = color;
    chip.style.borderColor  = border;
  }
};

window.removeTagDef = function(index) {
  const defs = loadTagDefs();
  const def  = defs[index];
  if (!def) return;

  const tagName    = def.name;
  const { bg, color, border } = getTagStyle(def.color);
  const affected   = clients.filter(c => (c.tags || []).includes(tagName));

  if (affected.length === 0) {
    // No clients have this tag — delete immediately, no confirmation needed
    defs.splice(index, 1);
    saveTagDefs(defs);
    renderTagSettings();
    showToast(`Tag "${tagName}" deleted.`);
    return;
  }

  const clientList = affected.map(c =>
    `<div class="tag-delete-client-row">
       <span class="tag-delete-client-avatar">${escHtml((c.name || '?').charAt(0).toUpperCase())}</span>
       <span class="tag-delete-client-name">${escHtml(c.name || '—')}</span>
       <span class="badge-status badge-${c.status}" style="font-size:10px;padding:2px 7px;">${label(c.status)}</span>
     </div>`
  ).join('');

  const bodyHtml = `
    <div class="tag-delete-warning">
      <div class="tag-delete-tag-preview">
        <span class="tag-chip" style="background:${bg};color:${color};border-color:${border}">${escHtml(tagName)}</span>
        is assigned to <strong>${affected.length} client${affected.length === 1 ? '' : 's'}</strong>.
        Deleting it will remove it from all of them.
      </div>
      <div class="tag-delete-client-list">${clientList}</div>
    </div>`;

  window.openBulkModal(
    'Delete Tag',
    bodyHtml,
    async () => {
      window.closeBulkModal();
      // Remove tag from all affected clients via fresh Supabase fetch
      const ids = affected.map(c => c.id);
      const { data: freshRows, error: fetchErr } = await supabase
        .from('clients')
        .select('id, data')
        .in('id', ids);
      if (fetchErr || !freshRows) { showToast('Failed to load client data.', 'error'); return; }
      const now = new Date().toISOString();
      await Promise.allSettled(freshRows.map(async row => {
        const tags    = Array.isArray(row.data?.tags) ? row.data.tags : [];
        const newData = { ...row.data, tags: tags.filter(t => t !== tagName), updatedAt: now };
        const { error } = await supabase.from('clients').update({ data: newData, updated_at: now }).eq('id', row.id);
        if (error) throw new Error(error.message);
      }));
      // Remove definition
      const updatedDefs = loadTagDefs();
      const i = updatedDefs.findIndex(d => d.name === tagName);
      if (i !== -1) updatedDefs.splice(i, 1);
      saveTagDefs(updatedDefs);
      renderTagSettings();
      showToast(`Tag "${tagName}" deleted and removed from ${affected.length} client${affected.length === 1 ? '' : 's'}.`);
    },
    'Delete Tag',
    true
  );
};

window.addTagDef = function() {
  const nameInput  = document.getElementById('new-tag-name');
  const colorInput = document.getElementById('new-tag-color');
  if (!nameInput || !colorInput) return;
  const name  = nameInput.value.trim();
  const color = colorInput.value || '#64748b';
  if (!name) { showToast('Enter a tag name.', 'error'); return; }
  const defs = loadTagDefs();
  if (defs.find(d => d.name.toLowerCase() === name.toLowerCase())) {
    showToast('A tag with that name already exists.', 'error');
    return;
  }
  defs.push({ name, color });
  saveTagDefs(defs);
  nameInput.value  = '';
  colorInput.value = '#64748b';
  renderTagSettings();
  showToast(`Tag "${name}" added.`);
};


// The main menu's edge arrow lives in modules/side-menus.js. This runs when this file loads, as the old function
// did, so the arrow works before sign-in finishes.
window.toggleSidebarCollapse = toggleSidebarCollapse;

// ============================================================
// EVENT BINDING
// ============================================================
function bindEvents() {
  // Nav buttons (sidebar + card links)
  document.querySelectorAll('[data-view]').forEach(btn => {
    btn.addEventListener('click', () => showView(btn.dataset.view));
  });

  // The sidebar (2026-10-12): the [ key, the narrow-menu tooltip, Search or
  // ask, the + menu (New client opens the Add Client form) and the account menu.
  initSidebar({ openAddModal, showView });


  // Modal close
  document.getElementById('modal-close').addEventListener('click', closeModal);
  document.getElementById('btn-cancel').addEventListener('click', closeModal);
  document.getElementById('modal-overlay').addEventListener('click', e => {
    if (e.target.id === 'modal-overlay') closeModal();
  });

  // Form submit
  document.getElementById('client-form').addEventListener('submit', handleFormSubmit);

  // Real-time duplicate detection — watch name, email, phone with debounce
  const debouncedDupeCheck = debounce(updateInlineDupeBanner, 400);
  ['f-firstName', 'f-lastName', 'f-email', 'f-phone'].forEach(id => {
    document.getElementById(id)?.addEventListener('input', debouncedDupeCheck);
  });

  // Payment source change → update payment section + button text
  document.getElementById('f-paymentSource').addEventListener('change', () => {
    updatePaymentSection();
  });

  // Billing structure change → update charge summary
  document.getElementById('f-billingStructure').addEventListener('change', () => {
    updatePaymentSection();
  });

  // Amount fields change → update charge summary
  document.getElementById('f-agreementValue').addEventListener('input', updatePaymentSection);
  document.getElementById('f-mrrAmount').addEventListener('input', updatePaymentSection);
  document.getElementById('f-lengthOfAgreement')?.addEventListener('input', updatePaymentSection);
  document.getElementById('f-bonusMonths')?.addEventListener('input', updatePaymentSection);
  document.getElementById('f-continuityRate')?.addEventListener('input', updatePaymentSection);

  // Installment count change → refresh charge summary
  document.getElementById('f-installmentCount')?.addEventListener('input', updatePaymentSection);
  document.getElementById('f-agreementValue')?.addEventListener('input', updatePaymentSection);

  // Phone number auto-formatter → (555) 123-4567
  document.getElementById('f-phone')?.addEventListener('input', function() {
    const digits = this.value.replace(/\D/g, '').substring(0, 10);
    if (digits.length === 0) { this.value = ''; return; }
    if (digits.length <= 3) { this.value = `(${digits}`; return; }
    if (digits.length <= 6) { this.value = `(${digits.slice(0,3)}) ${digits.slice(3)}`; return; }
    this.value = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  });

  // Filters
  // Search — show/hide clear button
  const _searchInput  = document.getElementById('client-search');
  const _searchClear  = document.getElementById('client-search-clear');
  const _clearFilters = document.getElementById('btn-clear-filters');

  function _updateFilterUI() {
    // Clear-search button visibility
    if (_searchClear) _searchClear.classList.toggle('hidden', !_searchInput.value);

    // Active class on filter selects
    ['status-filter','service-filter','tag-filter'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.classList.toggle('filter-active', !!el.value);
    });

    // Clear-filters button visibility
    const anyActive = ['status-filter','service-filter','tag-filter'].some(id => {
      const el = document.getElementById(id);
      return el && !!el.value;
    });
    if (_clearFilters) _clearFilters.classList.toggle('hidden', !anyActive);
  }

  _searchInput.addEventListener('input', () => { renderClientsTable(); _updateFilterUI(); });
  // status/service/tag selects were replaced by the filter builder + smart-list
  // chips (see the FILTER BUILDER section); their change-listeners are gone.

  // Clear search ×
  _searchClear?.addEventListener('click', () => {
    _searchInput.value = '';
    _searchInput.dispatchEvent(new Event('input'));
    _searchInput.focus();
  });

  // Clear all filters
  _clearFilters?.addEventListener('click', () => {
    ['status-filter','service-filter','tag-filter'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    renderClientsTable();
    _updateFilterUI();
    _renderFilterPills();
  });

  // Filter panel toggle
  const _filterToggle = document.getElementById('btn-filter-toggle');
  const _filterPanel = document.getElementById('cl-filter-panel');
  if (_filterToggle && _filterPanel) {
    _filterToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      _filterPanel.classList.toggle('hidden');
    });
    // Close on outside click. Use composedPath (captured at dispatch) rather than
    // e.target.closest — clicking a control inside the panel (Add rule, ✕, Clear)
    // re-renders the panel via innerHTML, which DETACHES the clicked node before
    // this bubble handler runs; closest() on a detached node returns null, so the
    // panel would wrongly close on every in-panel click. composedPath still
    // contains .cl-filter-wrap because the path is fixed when the event dispatches.
    document.addEventListener('click', (e) => {
      if (_filterPanel.classList.contains('hidden')) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      // .cl-sel-menu is the field/op/value dropdown, portaled to <body> — clicking
      // an option there is still "inside" the filter interaction, so don't close.
      const inside = path.some(n => n && n.nodeType === 1 && n.classList && (n.classList.contains('cl-filter-wrap') || n.classList.contains('cl-sel-menu')));
      if (!inside) _filterPanel.classList.add('hidden');
    });
    // Re-render pills when filters change inside the panel
    _filterPanel.querySelectorAll('select').forEach(sel => {
      sel.addEventListener('change', () => {
        renderClientsTable();
        _updateFilterUI();
        _renderFilterPills();
      });
    });
  }

  // Active filter pills
  function _renderFilterPills() {
    // Superseded by the smart-list chips + filter builder; delegate so any old
    // caller still refreshes the same #cl-active-filters container.
    if (typeof _renderSmartChips === 'function') _renderSmartChips();
  }
  _renderFilterPills();

  // Export / Import
  document.getElementById('btn-export').addEventListener('click', exportCSV);
  document.getElementById('btn-import-csv')?.addEventListener('click', () => window.openImportModal());

  // Sort on column header click (event delegation on thead)
  // Skip if user was resizing a column (flag set by resize handler)
  document.getElementById('clients-thead')?.addEventListener('click', e => {
    if (window._wasResizing) { window._wasResizing = false; return; }
    const th = e.target.closest('[data-sortcol]');
    if (!th) return;
    const col = th.dataset.sortcol;
    if (sortColumn === col) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortColumn = col;
      sortDirection = 'asc';
    }
    renderClientsTable();
  });

  // Column visibility panel toggle
  const btnCols = document.getElementById('btn-columns');
  const colPanel = document.getElementById('column-panel');
  btnCols?.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = colPanel?.classList.toggle('open');
    if (isOpen) renderColumnPanel();
  });
  document.addEventListener('click', (e) => {
    if (colPanel && colPanel.classList.contains('open') && !colPanel.contains(e.target) && e.target !== btnCols) {
      colPanel.classList.remove('open');
    }
  });

  // Prevent scroll from changing number inputs
  document.addEventListener('wheel', (e) => {
    if (e.target.type === 'number') e.target.blur();
  }, { passive: true });

  // Custom select dropdowns
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.custom-select-trigger');
    if (trigger) {
      const wrap = trigger.closest('.custom-select');
      const dd = wrap?.querySelector('.custom-select-dropdown');
      // Close all other open custom selects
      document.querySelectorAll('.custom-select-dropdown').forEach(d => {
        if (d !== dd) d.classList.add('hidden');
      });
      dd?.classList.toggle('hidden');
      // Mark current active
      const current = wrap?.dataset.value;
      dd?.querySelectorAll('.custom-select-option').forEach(opt => {
        opt.classList.toggle('active', opt.dataset.value === current);
      });
      return;
    }
    const option = e.target.closest('.custom-select-option');
    if (option) {
      const wrap = option.closest('.custom-select');
      wrap.dataset.value = option.dataset.value;
      wrap.querySelector('.custom-select-text').textContent = option.textContent;
      wrap.querySelector('.custom-select-dropdown')?.classList.add('hidden');
      wrap.dispatchEvent(new Event('change'));
      return;
    }
    // Close all if clicking outside
    document.querySelectorAll('.custom-select-dropdown').forEach(d => d.classList.add('hidden'));
  });

  // Confirm dialog (the LEGACY cancel-client one; showConfirm in utils.js
  // drives the separate `sc-` dialog).
  //
  // ⚠️ Clear the optional groups on CLOSE, not only on the next open. They used
  // to persist until another showConfirmModal call re-rendered them, so a
  // coach saw the previous modal's Email/Text choice checkboxes sitting on an
  // unrelated prompt (2026-08-13). Cheap here, and it means a dismissed dialog
  // leaves nothing behind for the next one to inherit.
  const resetLegacyConfirm = () => {
    document.getElementById('confirm-overlay').classList.add('hidden');
    const choices = document.getElementById('confirm-choices-group');
    if (choices) { choices.innerHTML = ''; choices.classList.add('hidden'); }
    choices?.closest('.confirm-dialog')?.classList.remove('has-choices');
    for (const id of ['confirm-canceltype-group', 'confirm-date-group', 'confirm-reason-group']) {
      document.getElementById(id)?.classList.add('hidden');
    }
    confirmCallback = null;
    _state.setConfirmCallback(null);
  };
  document.getElementById('confirm-cancel').addEventListener('click', resetLegacyConfirm);
  document.getElementById('confirm-ok').addEventListener('click', async () => {
    const cb = confirmCallback || _state.confirmCallback;
    // Null the callback BEFORE awaiting it: a slow handler otherwise leaves it
    // live, and a second click would run it twice.
    confirmCallback = null;
    _state.setConfirmCallback(null);
    if (cb) await cb();
    resetLegacyConfirm();
  });

  // Settings — theme segmented control
  document.getElementById('theme-seg-control')?.addEventListener('click', function(e) {
    const btn = e.target.closest('.theme-seg-btn');
    if (!btn) return;
    const settings = loadSettings();
    settings.theme = btn.dataset.themeVal;
    saveSettingsToStorage(settings);
    applyTheme(settings.theme);
    applyLogoFromStorage();
    renderSettings();
  });

  // Settings — brand and chart color pickers. Chart Primary colours the coach app's charts (coach-mobile-today/
  // -business brandColors.chart1); Chart Secondary's picker went with the old dashboard, its only drawn use (10/16)
  ['brand', 'chart1'].forEach(key => {
    const picker = document.getElementById(`color-${key}`);
    const hexIn  = document.getElementById(`color-${key}-hex`);

    const applyColor = (val) => {
      const s = loadSettings();
      if (!s.customColors) s.customColors = { ...DEFAULT_COLORS };
      s.customColors[key] = val;
      saveSettingsToStorage(s);
      applyCustomColors(s);
      // Keep the white-label top-level brandColor in sync with the brand swatch so
      // the App tab + the client app (which prefer config.brandColor) never diverge.
      if (key === 'brand' && typeof syncConfig === 'function') syncConfig({ brandColor: val });
    };

    picker?.addEventListener('input', function () {
      if (hexIn) hexIn.value = this.value.toUpperCase();
      applyColor(this.value);
    });

    hexIn?.addEventListener('change', function () {
      const val = this.value.trim();
      if (/^#[0-9a-fA-F]{6}$/.test(val)) {
        if (picker) picker.value = val.toLowerCase();
        applyColor(val.toLowerCase());
      }
    });
  });

  // Settings — reset colors
  document.getElementById('btn-reset-colors')?.addEventListener('click', () => {
    const s = loadSettings();
    s.customColors = { ...DEFAULT_COLORS };
    saveSettingsToStorage(s);
    // Reset the white-label top-level brandColor too, or readers that prefer it
    // (App Studio / client app) would keep the old custom color after a reset.
    if (typeof syncConfig === 'function') syncConfig({ brandColor: DEFAULT_COLORS.brand });
    applyCustomColors(s);
    renderSettings();
    showToast('Colors reset to brand defaults.', 'success');
  });

  // Settings — logo upload helper (uploads to Supabase Storage for cross-device persistence)
  function bindLogoUpload(btnId, inputId, storageKey, toastLabel) {
    document.getElementById(btnId)?.addEventListener('click', () => {
      document.getElementById(inputId)?.click();
    });
    document.getElementById(inputId)?.addEventListener('change', function() {
      const file = this.files?.[0];
      if (!file) return;
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowed.includes(file.type)) {
        showToast('Unsupported file type. Please use PNG, JPG, or SVG.', 'error');
        this.value = ''; return;
      }
      if (file.size > 2 * 1024 * 1024) {
        showToast('File too large. Please use an image under 2 MB.', 'error');
        this.value = ''; return;
      }
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUri = e.target.result;
        if (currentUser) {
          try {
            const url = await _uploadLogoToStorage(currentUser.id, storageKey, dataUri);
            const cacheBusted = `${url}?t=${Date.now()}`;
            localStorage.setItem(storageKey, cacheBusted);
            syncConfig({ logoUrls: { ...(_currentLogoUrls()), [storageKey]: cacheBusted } });
            // An SVG can't render in email — have the server derive PNG copies
            // so the coach never uploads the same mark in a second format.
            refreshLogoRasters();
          } catch (err) {
            console.warn('[Logo] Storage upload failed, using local only:', err.message);
            localStorage.setItem(storageKey, dataUri);
          }
        } else {
          localStorage.setItem(storageKey, dataUri);
        }
        applyLogoFromStorage();
        showToast(`${toastLabel} updated.`, 'success');
      };
      reader.readAsDataURL(file);
      this.value = '';
    });
  }
  function bindLogoRemove(btnId, storageKey, toastLabel) {
    document.getElementById(btnId)?.addEventListener('click', async () => {
      if (currentUser) {
        await _deleteLogoFromStorage(currentUser.id, storageKey);
        const urls = _currentLogoUrls();
        delete urls[storageKey];
        syncConfig({ logoUrls: urls });
      }
      localStorage.removeItem(storageKey);
      applyLogoFromStorage();
      showToast(`${toastLabel} removed.`, 'success');
    });
  }
  function _currentLogoUrls() {
    const urls = {};
    LOGO_KEYS.forEach(k => { const v = localStorage.getItem(k); if (v) urls[k] = v; });
    return urls;
  }

  bindLogoUpload('btn-upload-logo',           'logo-file-input',           'revfit_logo',            'Icon logo');
  bindLogoRemove('btn-remove-logo',                                         'revfit_logo',            'Icon logo');
  bindLogoUpload('btn-upload-logo-light',     'logo-light-file-input',     'revfit_logo_light',      'Light icon logo');
  bindLogoRemove('btn-remove-logo-light',                                   'revfit_logo_light',      'Light icon logo');
  bindLogoUpload('btn-upload-logo-full',      'logo-full-file-input',      'revfit_logo_full',       'Full logo');
  bindLogoRemove('btn-remove-logo-full',                                    'revfit_logo_full',       'Full logo');
  bindLogoUpload('btn-upload-logo-full-light','logo-full-light-file-input','revfit_logo_full_light', 'Light full logo');
  bindLogoRemove('btn-remove-logo-full-light',                              'revfit_logo_full_light', 'Light full logo');

  // Settings — save Client Receipt Branding (Payments tab)
  document.getElementById('btn-save-receipt-brand')?.addEventListener('click', async () => {
    const settings = loadSettings();
    settings.receiptLogoUrl       = (document.getElementById('s-receipt-logo-url')?.value || '').trim();
    settings.receiptAccentColor   = (document.getElementById('s-receipt-accent-color')?.value || '#7c3aed').trim();
    settings.receiptSupportEmail  = (document.getElementById('s-receipt-support-email')?.value || '').trim();
    settings.receiptFooterText    = (document.getElementById('s-receipt-footer-text')?.value || '').trim();
    saveSettingsToStorage(settings);
    // Sync to coaches.settings so serverless receipt sends can read them
    try {
      const supabase = _config.getSupabase();
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) {
        fetch('/.netlify/functions/save-coach-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
          body: JSON.stringify({ settings: {
            receiptLogoUrl: settings.receiptLogoUrl,
            receiptAccentColor: settings.receiptAccentColor,
            receiptSupportEmail: settings.receiptSupportEmail,
            receiptFooterText: settings.receiptFooterText,
          }}),
        }).catch(() => {});
      }
    } catch {}
    document.getElementById('btn-save-receipt-brand')?._dirtyReset?.();
    showToast('Receipt branding saved.', 'success');
  });

  // Settings — receipt delivery source (Payments tab). Saves immediately on
  // change — deliberately separate from the Save branding button, so it is
  // NOT in the settings dirty-tracking registry.
  document.querySelectorAll('input[name="s-receipt-source"]').forEach(radio => {
    radio.addEventListener('change', async () => {
      if (!radio.checked) return;
      const src = radio.value === 'stripe' ? 'stripe' : 'platform';
      document.getElementById('receipt-source-note-platform')?.classList.toggle('hidden', src !== 'platform');
      document.getElementById('receipt-source-note-stripe')?.classList.toggle('hidden', src !== 'stripe');
      const settings = loadSettings();
      settings.receiptSource = src;
      // 'platform' also re-enables emailSendReceipts — clears a stale `false`
      // left by the removed legacy Email toggle (server gates sends on it).
      if (src === 'platform') settings.emailSendReceipts = true;
      saveSettingsToStorage(settings);
      try {
        const supabase = _config.getSupabase();
        const { data: { session: s } } = await supabase.auth.getSession();
        if (s) {
          await fetch('/.netlify/functions/save-coach-settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
            body: JSON.stringify({ settings: src === 'platform'
              ? { receiptSource: src, emailSendReceipts: true }
              : { receiptSource: src } }),
          });
        }
      } catch {}
      showToast('Receipt delivery saved.', 'success');
    });
  });

  document.getElementById('btn-open-stripe-emails')?.addEventListener('click', () => {
    window.open('https://dashboard.stripe.com/settings/emails', '_blank');
  });

  // Unsaved branding-form edits ride along so preview/test show what the
  // coach is looking at, not just the saved settings row.
  const getReceiptBrandOverride = () => {
    const val = (id) => (document.getElementById(id)?.value || '').trim();
    return {
      logoUrl:      val('s-receipt-logo-url'),
      accentColor:  val('s-receipt-accent-color'),
      supportEmail: val('s-receipt-support-email'),
      footerText:   val('s-receipt-footer-text'),
    };
  };

  // Receipt logo — upload a file (reuses the brand-logo storage → public URL),
  // reuse the coach's existing brand logo, or remove. The resulting URL lives in
  // the hidden #s-receipt-logo-url that the Save branding handler persists.
  const setReceiptLogo = (url) => {
    const hidden  = document.getElementById('s-receipt-logo-url');
    const preview = document.getElementById('s-receipt-logo-preview');
    const removeBtn = document.getElementById('btn-receipt-logo-remove');
    // Dispatch 'input' so Save-branding dirty tracking notices the JS-set value.
    if (hidden)  { hidden.value = url || ''; hidden.dispatchEvent(new Event('input', { bubbles: true })); }
    if (preview) { preview.src = url || ''; preview.style.display = url ? '' : 'none'; }
    if (removeBtn) removeBtn.classList.toggle('hidden', !url);
  };
  document.getElementById('btn-receipt-logo-upload')?.addEventListener('click', () => {
    document.getElementById('s-receipt-logo-file')?.click();
  });
  document.getElementById('s-receipt-logo-file')?.addEventListener('change', function () {
    const file = this.files?.[0];
    if (!file) return;
    // Email clients don't reliably render SVG — PNG/JPG only for receipts.
    const allowed = ['image/png', 'image/jpeg'];
    if (!allowed.includes(file.type)) { showToast("Please use a PNG or JPG — SVG doesn't show in all email apps.", 'error'); this.value = ''; return; }
    if (file.size > 2 * 1024 * 1024) { showToast('File too large. Please use an image under 2 MB.', 'error'); this.value = ''; return; }
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        if (!currentUser) throw new Error('Not signed in');
        const url = await _uploadLogoToStorage(currentUser.id, 'receipt_logo', e.target.result);
        setReceiptLogo(`${url}?t=${Date.now()}`);
        showToast('Logo uploaded — click Save branding to apply.', 'success');
      } catch (err) {
        showToast('Logo upload failed: ' + err.message, 'error');
      }
    };
    reader.readAsDataURL(file);
    this.value = '';
  });
  document.getElementById('btn-receipt-logo-usebrand')?.addEventListener('click', () => {
    const brandLogo = localStorage.getItem('revfit_logo_full') || localStorage.getItem('revfit_logo_full_light') || localStorage.getItem('revfit_logo') || '';
    if (!brandLogo.startsWith('http')) { showToast('No usable brand logo found — upload one in Appearance settings first.', 'error'); return; }
    if (brandLogo.split('?')[0].toLowerCase().endsWith('.svg')) { showToast("Your brand logo is an SVG, which some email apps won't show. Upload a PNG or JPG here instead.", 'error'); return; }
    setReceiptLogo(brandLogo);
    document.getElementById('btn-receipt-logo-usebrand')?.classList.add('hidden');
    showToast('Using your brand logo — click Save branding to apply.', 'success');
  });
  document.getElementById('btn-receipt-logo-remove')?.addEventListener('click', () => {
    setReceiptLogo('');
    showToast('Logo removed — click Save branding to apply.', 'success');
  });

  // Settings — preview receipt (server-rendered by coach-test-send-email).
  // Uses its own overlay: #confirm-overlay's children are looked up by id
  // from showConfirmDialog, so its innerHTML must never be replaced.
  document.getElementById('btn-preview-receipt')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-preview-receipt');
    btn.disabled = true;
    btn.textContent = 'Loading preview…';
    try {
      const { html } = await callFunction('coach-test-send-email', {
        event_key: 'payment_receipt', preview: true, brandOverride: getReceiptBrandOverride(),
      });
      showReceiptHtmlModal(html, 'Receipt preview');
    } catch (err) {
      showToast('Preview failed: ' + err.message, 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Preview receipt';
    }
  });

  // Settings — email a [TEST] receipt to the coach's own address
  document.getElementById('btn-test-receipt')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-test-receipt');
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      await callFunction('coach-test-send-email', {
        event_key: 'payment_receipt', brandOverride: getReceiptBrandOverride(),
      });
      showToast('✓ Test receipt sent to your email', 'success');
    } catch (err) {
      showToast('Test send failed: ' + err.message, 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Email me a test receipt';
    }
  });

  // Settings — save Email Notifications
  document.getElementById('btn-save-email')?.addEventListener('click', async () => {
    const settings = loadSettings();
    settings.emailBusinessName = (document.getElementById('s-email-business-name')?.value || '').trim();
    settings.emailReplyTo = (document.getElementById('s-email-reply-to')?.value || '').trim();
    settings.emailSendReceipts = !!document.getElementById('s-email-send-receipts')?.checked;
    settings.emailSendFailureNotice = !!document.getElementById('s-email-send-failure')?.checked;
    saveSettingsToStorage(settings);
    // Also sync email settings to coaches.settings so serverless functions can read them
    try {
      const supabase = _config.getSupabase();
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) {
        fetch('/.netlify/functions/save-coach-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
          body: JSON.stringify({ settings: {
            emailBusinessName: settings.emailBusinessName,
            emailReplyTo: settings.emailReplyTo,
            emailSendReceipts: settings.emailSendReceipts,
            emailSendFailureNotice: settings.emailSendFailureNotice,
            emailDomain: settings.emailDomain || null,
            emailDomainStatus: settings.emailDomainStatus || null,
            emailDomainId: settings.emailDomainId || null,
          }}),
        }).catch(() => {});
      }
    } catch {}
    showToast('Email settings saved.', 'success');
    document.getElementById('btn-save-email')?._dirtyReset?.();
  });

  // ── Communications Wizard Modal ─────────────────────────────────────
  // Modal-based setup wizard. Each micro-step is rendered dynamically.
  let _commsWizIdx = 0;
  let _commsWizSteps = [];

  function getSelectedChannels() {
    const channels = [];
    document.querySelectorAll('.comms-channel-check').forEach(cb => {
      if (cb.checked) channels.push(cb.dataset.channel);
    });
    return channels;
  }

  // ── Email wizard — AWS SES backend ──────────────────────────────────
  //
  // 3 steps (2026-10-28, spec E2 PR 4). The domain and every DNS record now
  // live in Settings > Integrations > Your domain, so the wizard's own
  // domain and verify steps are gone:
  //
  // Step 1: Brand — business name + their personal reply-to email
  // Step 2: Your domain — one line that opens the Your domain card
  // Step 3: Done + Send test — where emails come from, the cancel address,
  //         and a sandboxed test email (ses-domain-setup.js test-email)

  // The email steps after the business name live in
  // modules/email-wizard-steps.js; this is what they use from here.
  const emailWizardDeps = { loadSettings, escHtml, _config, closeCommsWizard };

  const EMAIL_STEPS = [
    // ── Step 1 of 5: Business Name ──────────────────────────────
    // Typeform-style: one field per step, paired with enough context that
    // a non-technical coach knows exactly what to type and why it matters.
    { id: 'email-1', title: 'What\'s your business name?', render: () => `
      <p class="comms-modal-desc" style="margin-bottom:18px;">This is what your clients will see in the <strong>"From"</strong> field when they open your emails. It's the public-facing name on every message.</p>
      <div class="form-group" style="max-width:480px;">
        <label style="font-size:13px;font-weight:600;color:var(--text);display:block;margin-bottom:6px;">Business Name</label>
        <input type="text" id="wiz-email-biz" placeholder="e.g. Revolution Fitness" value="${escHtml(loadSettings().emailBusinessName || '')}" autofocus style="font-size:16px;padding:12px 14px;" />
        <p style="font-size:12px;color:var(--text-muted);margin-top:8px;line-height:1.5;">Example: clients will see <code style="background:var(--surface-2);padding:1px 6px;border-radius:4px;">Revolution Fitness &lt;notifications@mail.revfitcoaching.com&gt;</code> in their inbox.</p>
      </div>`,
      onMount: () => {
        // Pressing Enter advances to the next step — Typeform feel.
        document.getElementById('wiz-email-biz')?.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); document.getElementById('comms-modal-next')?.click(); }
        });
      },
      onNext: () => {
        const settings = loadSettings();
        settings.emailBusinessName = document.getElementById('wiz-email-biz')?.value?.trim() || '';
        saveSettingsToStorage(settings);
      },
    },
    // NOTE 2026-05-17: the "Contact email" step (former email-1b) was
    // dropped per coach feedback. Test sends + admin alerts default to
    // the coach's signup email (coaches.email). See mockups/email-setup-
    // wizard.html for the final 4-step flow.
    emailDomainStep(emailWizardDeps),
    emailReadyStep(emailWizardDeps),
  ];


  let _commsNumberMode = 'new'; // 'new' = buy, 'port' = port-in, 'existing' = adopt account number (owner only)
  let _commsAdoptedNumber = null; // set when an existing account number is adopted, gates the existing-number step's Next
  let _commsPortData = {}; // stores data across port steps

  const SMS_STEPS = [
    { id: 'sms-intro', title: 'SMS & Voice Number', render: () => {
      const channels = getSelectedChannels();
      const hasVoice = channels.includes('voice');
      const isOwner = !!window._appIsOwner;
      const choiceBtn = (mode, title, desc) => {
        const active = _commsNumberMode === mode;
        return `<button class="sms-intro-choice ${active ? 'active' : ''}" data-mode="${mode}" style="flex:1;padding:16px 14px;border-radius:6px;border:2px solid ${active ? 'var(--accent)' : 'var(--border)'};background:${active ? 'rgba(139,92,246,0.06)' : 'var(--bg-card)'};cursor:pointer;text-align:left;">
            <div style="font-size:14px;font-weight:700;color:var(--text);margin-bottom:4px;">${title}</div>
            <div style="font-size:12px;color:var(--text-muted);line-height:1.4;">${desc}</div>
          </button>`;
      };
      const choices = [
        choiceBtn('new', 'Get a New Number', 'Search and purchase a new local or toll-free number.'),
        choiceBtn('port', 'Port Existing Number', 'Bring your number from RingCentral, Twilio, or another provider.'),
      ];
      // Owner-only: assign a number that is already in our Twilio account
      // (e.g. one pre-ported in). Regular coaches never see this, so they can
      // never pick from the shared account number pool.
      if (isOwner) choices.push(choiceBtn('existing', 'Use a Number We Already Own', 'Owner only — assign a number already in the FitOps Pro Twilio account.'));
      return `
        <div style="text-align:center;padding:20px 0;">
          <div style="font-size:36px;margin-bottom:12px;">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
          </div>
          <p style="font-size:18px;font-weight:700;color:var(--text);margin-bottom:8px;">Get Your Business Number</p>
          <p class="comms-modal-desc" style="max-width:380px;margin:0 auto;">
            ${hasVoice
              ? 'Set up a dedicated phone number for <strong>SMS and voice calls</strong>. Your personal number stays private.'
              : 'Set up a dedicated phone number for <strong>SMS messaging</strong>. Your personal number stays private.'}
          </p>
        </div>
        <div style="display:flex;flex-direction:${isOwner ? 'column' : 'row'};gap:10px;margin-top:4px;">
          ${choices.join('')}
        </div>`;
    },
      onMount: () => {
        document.querySelectorAll('.sms-intro-choice').forEach(btn => {
          btn.addEventListener('click', () => {
            _commsNumberMode = btn.dataset.mode;
            // Update visual state
            document.querySelectorAll('.sms-intro-choice').forEach(b => {
              const isActive = b === btn;
              b.style.borderColor = isActive ? 'var(--accent)' : 'var(--border)';
              b.style.background = isActive ? 'rgba(139,92,246,0.06)' : 'var(--bg-card)';
            });
          });
        });
      },
      onNext: () => {
        // Rebuild steps based on choice
        _commsAdoptedNumber = null; // re-picking a number path clears any prior adoption flag
        const channels = getSelectedChannels();
        const currentIdx = _commsWizSteps.findIndex(s => s.id === 'sms-intro');
        // Remove everything after intro up to A2P/voice/done
        const keepIds = new Set(['sms-intro']);
        const afterSteps = [];
        let foundEnd = false;
        for (const s of _commsWizSteps) {
          if (keepIds.has(s.id)) continue;
          // Strip whichever number path was previously inserted so switching
          // modes never leaves a stale step. 'existing-' must be here too, or
          // an abandoned account-number step orphans into the A2P section.
          if (s.id?.startsWith('sms-') || s.id?.startsWith('port-') || s.id?.startsWith('existing-')) continue;
          afterSteps.push(s);
        }
        // Insert correct path after intro
        const pathSteps = _commsNumberMode === 'port' ? PORT_STEPS
          : _commsNumberMode === 'existing' ? EXISTING_NUMBER_STEPS
          : NEW_NUMBER_STEPS;
        _commsWizSteps = [_commsWizSteps[currentIdx], ...pathSteps, ...afterSteps];
      },
    },
  ];

  const NEW_NUMBER_STEPS = [
    { id: 'sms-1', title: 'Find a Number', render: () => `
      <p class="comms-modal-desc">Search for an available phone number. Use the filters to find the perfect number.</p>
      <div class="form-grid" style="margin-top:16px;">
        <div class="form-group form-col-2"><label>Type</label>
          <div class="custom-select" id="wiz-sms-type" data-value="local">
            <button class="custom-select-trigger" type="button"><span class="custom-select-text">Local</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="13" height="13"><polyline points="6 9 12 15 18 9"/></svg></button>
            <div class="custom-select-dropdown hidden"><div class="custom-select-option active" data-value="local">Local</div><div class="custom-select-option" data-value="tollfree">Toll-Free</div></div>
          </div>
        </div>
        <div class="form-group form-col-2"><label>Area Code</label><input type="text" id="wiz-sms-area" placeholder="e.g. 507" maxlength="3" style="text-align:center;" /></div>
      </div>
      <div class="form-grid" style="margin-top:8px;">
        <div class="form-group form-col-2"><label>City <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="wiz-sms-city" placeholder="e.g. Rochester" /></div>
        <div class="form-group form-col-2"><label>Contains <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="wiz-sms-contains" placeholder="e.g. 507412 or *888*" maxlength="12" style="text-align:center;" /></div>
      </div>
      <div class="form-grid" style="margin-top:8px;">
        <div class="form-group form-col-2"><label>Ends With <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="wiz-sms-ends" placeholder="e.g. 8100" maxlength="4" style="text-align:center;" /></div>
        <div class="form-group form-col-2"></div>
      </div>
      <p style="font-size:11px;color:var(--text-muted);margin-top:4px;">Use * as a wildcard. Example: 507*8100 finds 507-xxx-8100</p>
      <button class="btn-primary" id="wiz-search-nums" style="margin-top:12px;">Search Available Numbers</button>
      <div id="wiz-sms-results" style="margin-top:12px;"></div>`,
      onMount: () => {
        document.getElementById('wiz-search-nums')?.addEventListener('click', async () => {
          const area = document.getElementById('wiz-sms-area')?.value?.trim();
          const type = document.getElementById('wiz-sms-type')?.dataset?.value || 'local';
          const containsVal = document.getElementById('wiz-sms-contains')?.value?.trim();
          const endsVal = document.getElementById('wiz-sms-ends')?.value?.trim();
          const cityVal = document.getElementById('wiz-sms-city')?.value?.trim();
          let containsPattern = containsVal || '';
          if (endsVal && !containsPattern) {
            containsPattern = (area || '') + '***' + endsVal;
          }
          const resultsEl = document.getElementById('wiz-sms-results');
          resultsEl.innerHTML = '<p style="font-size:13px;color:var(--text-muted);">Searching...</p>';
          try {
            const supabase = _config.getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch('/.netlify/functions/twilio-search-numbers', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
              body: JSON.stringify({ type, areaCode: area || undefined, contains: containsPattern || undefined, inLocality: cityVal || undefined }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error);
            if (!json.numbers?.length) { resultsEl.innerHTML = '<p style="font-size:13px;color:var(--text-muted);">No numbers found. Try a different area code.</p>'; return; }
            resultsEl.innerHTML = json.numbers.map(n =>
              '<div class="save-msg-action" style="cursor:pointer;margin-bottom:4px;" data-num="' + n.phoneNumber + '">' +
              '<div><div style="font-size:14px;font-weight:700;color:var(--text);">' + n.friendlyName + '</div>' +
              '<div style="font-size:11px;color:var(--text-muted);">' + (n.locality || '') + (n.region ? ', ' + n.region : '') + '</div></div>' +
              '<span class="action-tag action-tag--discount">Select</span></div>'
            ).join('');
            resultsEl.querySelectorAll('.save-msg-action').forEach(el => {
              el.addEventListener('click', () => {
                const num = el.dataset.num;
                showConfirmDialog({
                  message: `Purchase <strong>${num}</strong>?<br><span style="font-size:12px;color:var(--text-muted);margin-top:6px;display:block;">This will be your dedicated business number for SMS and voice calls. $1.15/mo for local, $2.15/mo for toll-free.</span>`,
                  okLabel: 'Purchase Number',
                  okClass: 'btn-primary',
                  onConfirm: async () => {
                el.querySelector('.action-tag').textContent = 'Purchasing...';
                try {
                  const r2 = await fetch('/.netlify/functions/twilio-purchase-number', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
                    body: JSON.stringify({ phoneNumber: num }),
                  });
                  const j2 = await r2.json();
                  if (!r2.ok) throw new Error(j2.error);
                  showToast('Number ' + j2.phoneNumber + ' purchased', 'success');
                  const raw = j2.phoneNumber.replace(/\D/g, '').slice(-10);
                  const fmt = raw.length === 10 ? '(' + raw.slice(0,3) + ') ' + raw.slice(3,6) + '-' + raw.slice(6) : j2.phoneNumber;
                  resultsEl.innerHTML = '<div style="text-align:center;padding:16px 0;">' +
                    '<div style="font-size:32px;color:#22c55e;margin-bottom:8px;">&#10003;</div>' +
                    '<div style="font-size:22px;font-weight:700;color:var(--text);">' + fmt + '</div>' +
                    '<div style="font-size:13px;color:var(--success);font-weight:600;margin-top:4px;">Number purchased successfully</div>' +
                    '</div>';
                } catch (err2) { showToast(err2.message, 'error'); el.querySelector('.action-tag').textContent = 'Select'; }
                  },
                });
              });
            });
          } catch (err) { resultsEl.innerHTML = '<p style="color:var(--danger);font-size:13px;">' + err.message + '</p>'; }
        });
      },
    },
  ];

  const PORT_STEPS = [
    { id: 'port-check', title: 'Check Your Number', render: () => `
      <p class="comms-modal-desc">Enter the phone number you want to bring over. We'll check if it can be ported.</p>
      <div class="form-grid" style="margin-top:16px;">
        <div class="form-group form-col-2"><label>Phone Number</label><input type="tel" id="wiz-port-phone" placeholder="+1 (555) 123-4567" value="${escHtml(_commsPortData.phoneNumber || '')}" /></div>
      </div>
      <button class="btn-primary" id="wiz-port-check-btn" style="margin-top:12px;">Check Portability</button>
      <div id="wiz-port-result" style="margin-top:12px;"></div>`,
      onMount: () => {
        document.getElementById('wiz-port-check-btn')?.addEventListener('click', async () => {
          const phone = document.getElementById('wiz-port-phone')?.value?.trim();
          if (!phone) { showToast('Enter a phone number', 'error'); return; }
          const btn = document.getElementById('wiz-port-check-btn');
          const resultEl = document.getElementById('wiz-port-result');
          btn.disabled = true;
          btn.textContent = 'Checking...';
          try {
            const supabase = _config.getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch('/.netlify/functions/twilio-port-check', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
              body: JSON.stringify({ phoneNumber: phone }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error);
            _commsPortData.phoneNumber = json.phoneNumber || phone;
            _commsPortData.pinRequired = json.pinRequired;
            _commsPortData.numberType = json.numberType;
            if (json.portable) {
              resultEl.innerHTML = '<div style="padding:14px;background:rgba(34,197,94,0.06);border:1px solid rgba(34,197,94,0.2);border-radius:6px;">' +
                '<div style="font-size:14px;font-weight:700;color:#22c55e;margin-bottom:4px;">Number is portable</div>' +
                '<div style="font-size:12px;color:var(--text-muted);line-height:1.5;">' +
                'Type: <strong>' + (json.numberType || 'Unknown') + '</strong><br>' +
                (json.pinRequired ? 'Your carrier requires an <strong>account number and PIN</strong> to port this number.' : 'No account PIN required by your carrier.') +
                '</div></div>';
              _commsPortData.portable = true;
            } else {
              resultEl.innerHTML = '<div style="padding:14px;background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.2);border-radius:6px;">' +
                '<div style="font-size:14px;font-weight:700;color:var(--danger);margin-bottom:4px;">Number cannot be ported</div>' +
                '<div style="font-size:12px;color:var(--text-muted);">' + escHtml(json.notPortableReason || 'This number is not eligible for porting.') + '</div></div>';
              _commsPortData.portable = false;
            }
            btn.textContent = 'Check Another';
            btn.disabled = false;
          } catch (err) {
            resultEl.innerHTML = '<p style="color:var(--danger);font-size:13px;">' + escHtml(err.message) + '</p>';
            btn.textContent = 'Try Again';
            btn.disabled = false;
          }
        });
      },
      onNext: () => {
        if (!_commsPortData.portable) { showToast('This number cannot be ported. Check a different number or go back to get a new one.', 'error'); throw new Error('block'); }
      },
    },
    { id: 'port-info', title: 'Carrier Information', render: () => `
      <p class="comms-modal-desc">We need details from your current provider to transfer the number. Find this in your carrier account settings or on a recent bill.</p>
      <div class="form-grid" style="margin-top:16px;">
        <div class="form-group form-col-2"><label>Current Carrier / Provider</label><input type="text" id="wiz-port-carrier" placeholder="e.g. Twilio, RingCentral, Vonage" value="${escHtml(_commsPortData.carrierName || '')}" /></div>
        <div class="form-group form-col-2"><label>Account Holder Name</label><input type="text" id="wiz-port-holder" placeholder="Name on the account" value="${escHtml(_commsPortData.accountHolderName || '')}" /></div>
      </div>
      ${_commsPortData.pinRequired !== false ? `<div class="form-grid" style="margin-top:8px;">
        <div class="form-group form-col-2"><label>Account Number</label><input type="text" id="wiz-port-acct" placeholder="From your carrier account" value="${escHtml(_commsPortData.accountNumber || '')}" /></div>
        <div class="form-group form-col-2"><label>Account PIN</label><input type="text" id="wiz-port-pin" placeholder="Transfer PIN" value="${escHtml(_commsPortData.accountPin || '')}" /></div>
      </div>` : ''}
      <div style="margin-top:14px;padding:12px;background:var(--bg-hover);border-radius:6px;border:1px solid var(--border);">
        <p style="font-size:12px;font-weight:600;color:var(--text);margin-bottom:4px;">Where to find this</p>
        <p style="font-size:12px;color:var(--text-muted);line-height:1.5;margin:0;">
          <strong>Twilio:</strong> Console &gt; Phone Numbers &gt; Manage &gt; Active Numbers<br>
          <strong>RingCentral:</strong> Admin Portal &gt; Phone System &gt; Phone Numbers<br>
          <strong>Other providers:</strong> Contact support and ask for your account number and transfer PIN
        </p>
      </div>`,
      onNext: () => {
        _commsPortData.carrierName = document.getElementById('wiz-port-carrier')?.value?.trim() || '';
        _commsPortData.accountHolderName = document.getElementById('wiz-port-holder')?.value?.trim() || '';
        _commsPortData.accountNumber = document.getElementById('wiz-port-acct')?.value?.trim() || '';
        _commsPortData.accountPin = document.getElementById('wiz-port-pin')?.value?.trim() || '';
        if (!_commsPortData.accountHolderName) { showToast('Account holder name is required', 'error'); throw new Error('block'); }
      },
    },
    { id: 'port-address', title: 'Service Address', render: () => {
      const settings = loadSettings();
      return `
      <p class="comms-modal-desc">Enter the address associated with this phone number on your current carrier account. This must match exactly or the port will be rejected.</p>
      <div class="form-grid" style="margin-top:16px;">
        <div class="form-group form-col-2"><label>Street Address</label><input type="text" id="wiz-port-street" placeholder="123 Main St" value="${escHtml(_commsPortData.street || settings.a2pStreet || '')}" /></div>
      </div>
      <div class="form-grid" style="margin-top:8px;">
        <div class="form-group"><label>City</label><input type="text" id="wiz-port-city" placeholder="Rochester" value="${escHtml(_commsPortData.city || settings.a2pCity || '')}" /></div>
        <div class="form-group"><label>State</label><input type="text" id="wiz-port-state" placeholder="MN" maxlength="2" value="${escHtml(_commsPortData.state || settings.a2pState || '')}" style="text-transform:uppercase;" /></div>
        <div class="form-group"><label>ZIP</label><input type="text" id="wiz-port-zip" placeholder="55901" maxlength="10" value="${escHtml(_commsPortData.zip || settings.a2pZip || '')}" /></div>
      </div>`;
    },
      onNext: () => {
        _commsPortData.street = document.getElementById('wiz-port-street')?.value?.trim() || '';
        _commsPortData.city = document.getElementById('wiz-port-city')?.value?.trim() || '';
        _commsPortData.state = document.getElementById('wiz-port-state')?.value?.trim().toUpperCase() || '';
        _commsPortData.zip = document.getElementById('wiz-port-zip')?.value?.trim() || '';
        if (!_commsPortData.street || !_commsPortData.city || !_commsPortData.state || !_commsPortData.zip) {
          showToast('Full address is required for the port request', 'error'); throw new Error('block');
        }
      },
    },
    { id: 'port-review', title: 'Review & Submit', render: () => {
      const raw = (_commsPortData.phoneNumber || '').replace(/\D/g, '').slice(-10);
      const fmt = raw.length === 10 ? '(' + raw.slice(0,3) + ') ' + raw.slice(3,6) + '-' + raw.slice(6) : _commsPortData.phoneNumber;
      return `
      <p class="comms-modal-desc">Review your port request details. After submitting, ${window._appIsOwner ? 'Twilio' : 'we'} will email you a Letter of Authorization (LOA) to sign electronically.</p>
      <div style="margin-top:16px;border:1px solid var(--border);border-radius:6px;overflow:hidden;">
        <div style="display:flex;justify-content:space-between;padding:10px 14px;border-bottom:1px solid var(--border);">
          <span style="font-size:12px;color:var(--text-muted);font-weight:600;">Number</span>
          <span style="font-size:13px;font-weight:700;color:var(--text);">${fmt}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px 14px;border-bottom:1px solid var(--border);">
          <span style="font-size:12px;color:var(--text-muted);font-weight:600;">Carrier</span>
          <span style="font-size:13px;color:var(--text);">${escHtml(_commsPortData.carrierName || 'Not specified')}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px 14px;border-bottom:1px solid var(--border);">
          <span style="font-size:12px;color:var(--text-muted);font-weight:600;">Account holder</span>
          <span style="font-size:13px;color:var(--text);">${escHtml(_commsPortData.accountHolderName)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px 14px;border-bottom:1px solid var(--border);">
          <span style="font-size:12px;color:var(--text-muted);font-weight:600;">Address</span>
          <span style="font-size:13px;color:var(--text);text-align:right;">${escHtml(_commsPortData.street)}<br>${escHtml(_commsPortData.city)}, ${escHtml(_commsPortData.state)} ${escHtml(_commsPortData.zip)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px 14px;">
          <span style="font-size:12px;color:var(--text-muted);font-weight:600;">LOA email</span>
          <span style="font-size:13px;color:var(--text);" id="wiz-port-email-display"></span>
        </div>
      </div>
      <button class="btn-primary" id="wiz-port-submit" style="margin-top:16px;width:100%;">Submit Port Request</button>
      <div id="wiz-port-submit-result" style="margin-top:12px;"></div>
      <div style="margin-top:14px;padding:12px;background:var(--bg-hover);border-radius:6px;border:1px solid var(--border);">
        <p style="font-size:12px;font-weight:600;color:var(--text);margin-bottom:4px;">What happens next</p>
        <p style="font-size:12px;color:var(--text-muted);line-height:1.5;margin:0;">
          1. You'll receive an email with a Letter of Authorization (LOA) to sign<br>
          2. Sign the LOA within 30 days<br>
          3. ${window._appIsOwner ? 'Twilio submits' : 'We submit'} the port to your current carrier<br>
          4. Carrier processes the transfer (typically 1-2 weeks)<br>
          5. Number goes live on your FitOps Pro account
        </p>
      </div>`;
    },
      onMount: async () => {
        // Get coach email for LOA
        try {
          const supabase = _config.getSupabase();
          const { data: { session } } = await supabase.auth.getSession();
          const email = session?.user?.email || '';
          _commsPortData.email = email;
          const el = document.getElementById('wiz-port-email-display');
          if (el) el.textContent = email;
        } catch {}

        document.getElementById('wiz-port-submit')?.addEventListener('click', async () => {
          const btn = document.getElementById('wiz-port-submit');
          const resultEl = document.getElementById('wiz-port-submit-result');
          btn.disabled = true;
          btn.textContent = 'Submitting...';
          try {
            const supabase = _config.getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch('/.netlify/functions/twilio-port-request', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
              body: JSON.stringify({
                action: 'create',
                phoneNumber: _commsPortData.phoneNumber,
                email: _commsPortData.email,
                name: _commsPortData.accountHolderName,
                carrierName: _commsPortData.carrierName,
                accountNumber: _commsPortData.accountNumber,
                accountPin: _commsPortData.accountPin,
                accountHolderName: _commsPortData.accountHolderName,
                street: _commsPortData.street,
                city: _commsPortData.city,
                state: _commsPortData.state,
                zip: _commsPortData.zip,
              }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error);
            _commsPortData.portSid = json.portSid;
            resultEl.innerHTML = '<div style="text-align:center;padding:16px 0;">' +
              '<div style="font-size:32px;color:#22c55e;margin-bottom:8px;">&#10003;</div>' +
              '<div style="font-size:16px;font-weight:700;color:var(--text);margin-bottom:4px;">Port Request Submitted</div>' +
              '<div style="font-size:13px;color:var(--text-muted);">Check your email at <strong>' + escHtml(_commsPortData.email) + '</strong> to sign the LOA.</div>' +
              '</div>';
            btn.classList.add('hidden');
            showToast('Port request submitted', 'success');
          } catch (err) {
            resultEl.innerHTML = '<p style="color:var(--danger);font-size:13px;">' + escHtml(err.message) + '</p>';
            btn.textContent = 'Try Again';
            btn.disabled = false;
          }
        });
      },
    },
  ];

  // Owner-only path: assign a number already in the FitOps Pro Twilio account
  // (e.g. one pre-ported in) to this coach. Calls twilio-adopt-number, which
  // re-points the number's webhooks at our app and writes coach_channels so
  // the A2P brand + campaign steps that follow find the number.
  const EXISTING_NUMBER_STEPS = [
    { id: 'existing-1', title: 'Use an Account Number', render: () => `
      <p class="comms-modal-desc">Pick a number that's already in the FitOps Pro Twilio account. We'll point its texts and calls to FitOps Pro and assign it to this coach. Numbers already used by another coach (and the platform number) won't show up.</p>
      <div id="wiz-existing-results" style="margin-top:14px;"><p style="font-size:13px;color:var(--text-muted);">Loading numbers...</p></div>`,
      onMount: () => {
        const resultsEl = document.getElementById('wiz-existing-results');
        (async () => {
          try {
            const supabase = _config.getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch('/.netlify/functions/twilio-adopt-number', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
              body: JSON.stringify({ action: 'list' }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Could not load numbers');
            if (!json.numbers?.length) {
              resultsEl.innerHTML = '<p style="font-size:13px;color:var(--text-muted);">No unassigned numbers found in the account. Use "Get a New Number" or "Port Existing Number" instead.</p>';
              return;
            }
            resultsEl.innerHTML = json.numbers.map(n =>
              '<div class="save-msg-action" style="cursor:pointer;margin-bottom:4px;" data-num="' + escHtml(n.phoneNumber) + '">' +
              '<div><div style="font-size:14px;font-weight:700;color:var(--text);">' + escHtml(n.friendlyName || n.phoneNumber) + '</div>' +
              '<div style="font-size:11px;color:var(--text-muted);">' + escHtml(n.phoneNumber) + (n.voice ? ' &middot; Voice' : '') + (n.sms ? ' &middot; SMS' : '') + '</div></div>' +
              '<span class="action-tag action-tag--discount">Select</span></div>'
            ).join('');
            resultsEl.querySelectorAll('.save-msg-action').forEach(el => {
              el.addEventListener('click', () => {
                const num = el.dataset.num;
                showConfirmDialog({
                  message: `Assign <strong>${escHtml(num)}</strong> to this coach?<br><span style="font-size:12px;color:var(--text-muted);margin-top:6px;display:block;">We'll point this number's texts and calls to FitOps Pro. Only this coach will be able to use it.</span>`,
                  okLabel: 'Use This Number',
                  okClass: 'btn-primary',
                  onConfirm: async () => {
                    el.querySelector('.action-tag').textContent = 'Assigning...';
                    try {
                      const r2 = await fetch('/.netlify/functions/twilio-adopt-number', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
                        body: JSON.stringify({ action: 'adopt', phoneNumber: num }),
                      });
                      const j2 = await r2.json();
                      if (!r2.ok) throw new Error(j2.error || 'Could not assign number');
                      _commsAdoptedNumber = j2.phoneNumber;
                      const raw = (j2.phoneNumber || '').replace(/\D/g, '').slice(-10);
                      const fmt = raw.length === 10 ? '(' + raw.slice(0,3) + ') ' + raw.slice(3,6) + '-' + raw.slice(6) : j2.phoneNumber;
                      resultsEl.innerHTML = '<div style="text-align:center;padding:16px 0;">' +
                        '<div style="font-size:32px;color:#22c55e;margin-bottom:8px;">&#10003;</div>' +
                        '<div style="font-size:22px;font-weight:700;color:var(--text);">' + escHtml(fmt) + '</div>' +
                        '<div style="font-size:13px;color:var(--success);font-weight:600;margin-top:4px;">Number assigned &mdash; click Next to register it</div>' +
                        '</div>';
                      showToast('Number ' + j2.phoneNumber + ' assigned', 'success');
                    } catch (e2) { showToast(e2.message, 'error'); el.querySelector('.action-tag').textContent = 'Select'; }
                  },
                });
              });
            });
          } catch (err) {
            resultsEl.innerHTML = '<p style="color:var(--danger);font-size:13px;">' + escHtml(err.message) + '</p>';
          }
        })();
      },
      onNext: () => {
        if (!_commsAdoptedNumber) { showToast('Pick a number first, or go back to choose a different option.', 'error'); throw new Error('block'); }
      },
    },
  ];

  const A2P_STEPS = [
    { id: 'a2p-1', title: 'SMS Compliance Registration', render: () => `
      <p class="comms-modal-desc">US carriers require business registration (A2P 10DLC) to deliver SMS messages. Fill out your business info below — we'll handle the submission.</p>
      <div id="a2p-existing-status"></div>
      <div id="a2p-form-wrap">
        <div id="a2p-preflight-panel" style="margin-top:12px;margin-bottom:14px;background:rgba(59,130,246,0.04);border:1px solid rgba(59,130,246,0.15);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
            <div>
              <div style="font-size:12px;font-weight:700;color:var(--accent);">Compliance check</div>
              <div style="font-size:11px;color:var(--text-muted);margin-top:2px;">We check your business details + website automatically. Fix anything flagged, then re-check — it saves you a 10-15 day rejection.</div>
            </div>
            <button class="btn-secondary" id="wiz-a2p-preflight" type="button" style="font-size:11px;padding:6px 12px;flex-shrink:0;">Re-check</button>
          </div>
          <div id="a2p-preflight-results" style="margin-top:10px;display:none;"></div>
        </div>
        <div class="form-grid">
          <div class="form-group form-col-2"><label>Business Name <span style="color:var(--danger);">*</span></label><input type="text" id="wiz-a2p-biz" placeholder="e.g. Smith Fitness LLC" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Must match your website and legal business name exactly</span></div>
          <div class="form-group form-col-2"><label>EIN <span style="font-weight:400;color:var(--text-muted);">(optional for sole props)</span></label><input type="text" id="wiz-a2p-ein" placeholder="Type 9 digits — we add the dash" maxlength="10" inputmode="numeric" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Just type the 9 numbers — we format it for you (XX-XXXXXXX). Sole props can leave blank.</span></div>
        </div>
        <div class="form-grid" style="margin-top:8px;">
          <div class="form-group form-col-2"><label>Website <span style="color:var(--danger);">*</span></label><input type="text" id="wiz-a2p-website" placeholder="https://yoursite.com" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Must be live, accessible, and include Privacy Policy + ToS links</span></div>
          <div class="form-group form-col-2"><label>Business Email</label><input type="email" id="wiz-a2p-email" placeholder="you@yourdomain.com" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Use your domain email, not Gmail/Yahoo (reduces rejection risk)</span></div>
        </div>
        <div class="form-grid" style="margin-top:8px;">
          <div class="form-group form-col-2"><label>Your First Name <span style="color:var(--danger);">*</span></label><input type="text" id="wiz-a2p-first" placeholder="Clayton" /></div>
          <div class="form-group form-col-2"><label>Your Last Name <span style="color:var(--danger);">*</span></label><input type="text" id="wiz-a2p-last" placeholder="Smith" /></div>
        </div>
        <div class="form-grid" style="margin-top:8px;">
          <div class="form-group form-col-2"><label>Business Phone</label><input type="tel" id="wiz-a2p-phone" placeholder="+1XXXXXXXXXX" /></div>
          <div class="form-group form-col-2"><label>Industry</label>
            <div class="custom-select" id="wiz-a2p-vertical" data-value="HEALTH">
              <button class="custom-select-trigger" type="button"><span class="custom-select-text">Health & Fitness</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="13" height="13"><polyline points="6 9 12 15 18 9"/></svg></button>
              <div class="custom-select-dropdown hidden"><div class="custom-select-option active" data-value="HEALTH">Health & Fitness</div><div class="custom-select-option" data-value="PROFESSIONAL">Professional Services</div><div class="custom-select-option" data-value="ENTERTAINMENT">Entertainment</div><div class="custom-select-option" data-value="EDUCATION">Education</div></div>
            </div>
          </div>
        </div>
        <div class="form-grid" style="margin-top:8px;">
          <div class="form-group"><label>Street Address</label><input type="text" id="wiz-a2p-street" placeholder="123 Main St" /></div>
        </div>
        <div class="form-grid" style="margin-top:8px;">
          <div class="form-group form-col-3"><label>City</label><input type="text" id="wiz-a2p-city" placeholder="Rochester" /></div>
          <div class="form-group form-col-3"><label>State</label><input type="text" id="wiz-a2p-state" placeholder="MN" maxlength="2" style="text-transform:uppercase;" /></div>
          <div class="form-group form-col-3"><label>Zip</label><input type="text" id="wiz-a2p-zip" placeholder="55901" maxlength="10" /></div>
        </div>
        <div id="a2p-review-summary" class="hidden" style="margin-top:14px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="font-size:10px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:8px;">Review before submitting</div>
          <div id="a2p-review-rows" style="font-size:13px;color:var(--text);line-height:1.8;"></div>
          <div id="a2p-brand-fee-note" style="margin-top:10px;font-size:11px;color:var(--text-muted);background:rgba(59,130,246,0.04);border:1px solid rgba(59,130,246,0.15);border-radius:var(--radius-sm);padding:8px 10px;line-height:1.55;">&#128179; Your <strong>first</strong> business registration is on us. If the carriers reject it and you resubmit, it's <strong>$4.50</strong> each time after &mdash; a carrier fee that goes entirely to the carriers, not to us. Your number &amp; monthly fees are already in your plan.</div>
        </div>
        <button class="btn-secondary" id="wiz-a2p-review" style="margin-top:14px;width:100%;">Review & Submit</button>
        <button class="btn-primary hidden" id="wiz-a2p-submit" style="margin-top:8px;width:100%;">Confirm & Submit Registration</button>
        <div id="wiz-a2p-result" style="margin-top:12px;"></div>
      </div>`,
      onMount: async () => {
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();

        // Check if already registered
        try {
          const res = await fetch('/.netlify/functions/twilio-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({ action: 'status' }),
          });
          const json = await res.json();
          if (json.status && json.status !== 'not_registered') {
            const statusEl = document.getElementById('a2p-existing-status');
            const formEl = document.getElementById('a2p-form-wrap');

            // Handle two-phase flow statuses
            if (json.status === 'bundles_pending') {
              const cpS = json.bundleStatus?.customerProfile || 'unknown';
              const tpS = json.bundleStatus?.trustProduct || 'unknown';
              statusEl.innerHTML = `
                <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:16px;margin-top:14px;text-align:center;">
                  <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:6px;">A2P bundle status</div>
                  <div style="font-size:18px;font-weight:700;color:#f59e0b;">${window._appIsOwner ? 'Awaiting Twilio Approval' : 'Awaiting Carrier Approval'}</div>
                  <div style="margin-top:10px;font-size:12px;color:var(--text-muted);line-height:1.8;">
                    Customer Profile: <strong>${escHtml(cpS)}</strong><br>
                    A2P Trust Product: <strong>${escHtml(tpS)}</strong>
                  </div>
                  <p style="font-size:12px;color:var(--text-muted);margin-top:8px;">${window._appIsOwner ? 'Twilio reviews' : 'Carriers review'} A2P compliance bundles in 10-15 business days (Standard) or ~1 week (Sole Proprietor). Once both are approved, return here to register your brand with carriers.</p>
                  <button id="a2p-check-bundles" class="btn-primary" style="margin-top:12px;padding:8px 20px;font-size:12px;">Check Bundle Status</button>
                </div>`;
              formEl.classList.add('hidden');
              document.getElementById('a2p-check-bundles')?.addEventListener('click', () => location.reload());
            } else if (json.status === 'bundles_approved') {
              statusEl.innerHTML = `
                <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:16px;margin-top:14px;text-align:center;">
                  <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:6px;">A2P bundle status</div>
                  <div style="font-size:18px;font-weight:700;color:#22c55e;">Bundles Approved</div>
                  <p style="font-size:12px;color:var(--text-muted);margin-top:8px;">Your compliance bundles are approved. Click below to register your brand with carriers.</p>
                  <button id="a2p-register-brand" class="btn-primary" style="margin-top:12px;padding:8px 20px;font-size:12px;">Register Brand Now</button>
                </div>`;
              formEl.classList.add('hidden');
              document.getElementById('a2p-register-brand')?.addEventListener('click', async () => {
                const btn = document.getElementById('a2p-register-brand');
                btn.disabled = true; btn.textContent = 'Registering...';
                try {
                  const regRes = await fetch('/.netlify/functions/twilio-register-brand', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
                    body: JSON.stringify({ action: 'register-brand' }),
                  });
                  const regJson = await regRes.json();
                  if (!regRes.ok) throw new Error(regJson.error);
                  statusEl.innerHTML = `
                    <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:16px;margin-top:14px;text-align:center;">
                      <div style="font-size:28px;color:#22c55e;margin-bottom:6px;">&#10003;</div>
                      <div style="font-size:15px;font-weight:700;color:var(--text);">Brand Registration Submitted</div>
                      <p style="font-size:12px;color:var(--text-muted);margin-top:6px;">Your brand is now under carrier review (10-15 business days). Click <strong>Next</strong> to write your example texts.</p>
                    </div>`;
                } catch (err) {
                  btn.disabled = false; btn.textContent = 'Try Again';
                  showToast(err.message, 'error');
                }
              });
            } else {
              // Standard brand statuses: PENDING, APPROVED, FAILED, etc.
              const color = (json.status === 'approved' || json.status === 'APPROVED') ? '#22c55e'
                : (json.status === 'failed' || json.status === 'FAILED') ? 'var(--danger)' : '#f59e0b';
              const label = (json.status === 'approved' || json.status === 'APPROVED') ? 'Approved'
                : (json.status === 'failed' || json.status === 'FAILED') ? 'Rejected' : 'Pending Review';
              statusEl.innerHTML = `
                <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:16px;margin-top:14px;text-align:center;">
                  <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:6px;">A2P 10DLC status</div>
                  <div style="font-size:18px;font-weight:700;color:${color};">${label}</div>
                  ${json.status === 'PENDING' || json.status === 'pending' ? '<p style="font-size:12px;color:var(--text-muted);margin-top:8px;">Carrier review typically takes 10-15 business days.</p>' : ''}
                  ${(json.status === 'failed' || json.status === 'FAILED') && json.rejectionReasons?.length > 0 ?
                    '<div style="text-align:left;margin-top:12px;background:rgba(239,68,68,0.04);border:1px solid rgba(239,68,68,0.15);border-radius:var(--radius-sm);padding:10px 12px;">' +
                    '<div style="font-size:10px;font-weight:600;letter-spacing:0;color:var(--danger);margin-bottom:6px;">Rejection details</div>' +
                    json.rejectionReasons.map(r =>
                      '<div style="margin-bottom:4px;font-size:12px;line-height:1.5;">' +
                      (r.code ? '<strong>Error ' + escHtml(String(r.code)) + ':</strong> ' : '') +
                      escHtml(r.message) + '</div>'
                    ).join('') +
                    '<p style="font-size:11px;color:var(--text-muted);margin-top:6px;">Fix the issues above, then resubmit below.</p></div>'
                  : (json.status === 'failed' || json.status === 'FAILED') ? '<p style="font-size:12px;color:var(--text-muted);margin-top:8px;">Review the form below and resubmit with corrected information.</p>' : ''}
                </div>`;
              if (json.status !== 'failed' && json.status !== 'FAILED') formEl.classList.add('hidden');
            }
          }
        } catch {}

        // Pre-fill from settings
        const settings = loadSettings();
        const bizInput = document.getElementById('wiz-a2p-biz');
        if (bizInput && settings.emailBusinessName && !bizInput.value) bizInput.value = settings.emailBusinessName;

        // Collect the wizard payload — used by both preflight and submit.
        const _collectA2pPayload = () => ({
          businessName: document.getElementById('wiz-a2p-biz')?.value?.trim() || '',
          ein: document.getElementById('wiz-a2p-ein')?.value?.trim() || '',
          website: document.getElementById('wiz-a2p-website')?.value?.trim() || '',
          email: document.getElementById('wiz-a2p-email')?.value?.trim() || '',
          firstName: document.getElementById('wiz-a2p-first')?.value?.trim() || '',
          lastName: document.getElementById('wiz-a2p-last')?.value?.trim() || '',
          phone: document.getElementById('wiz-a2p-phone')?.value?.trim() || '',
          street: document.getElementById('wiz-a2p-street')?.value?.trim() || '',
          city: document.getElementById('wiz-a2p-city')?.value?.trim() || '',
          state: document.getElementById('wiz-a2p-state')?.value?.trim()?.toUpperCase() || '',
          postalCode: document.getElementById('wiz-a2p-zip')?.value?.trim() || '',
        });

        // Render preflight results into the panel. Returns { ready, fail, warn, pass }.
        const _renderPreflight = (data) => {
          const resultsEl = document.getElementById('a2p-preflight-results');
          if (!resultsEl) return { ready: false, fail: 0, warn: 0, pass: 0 };
          const checks = Array.isArray(data?.checks) ? data.checks : [];
          const summary = data?.summary || { fail: 0, warn: 0, pass: 0 };
          const dot = (status) => {
            if (status === 'pass') return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#22c55e;flex-shrink:0;margin-top:5px;"></span>';
            if (status === 'warn') return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#f59e0b;flex-shrink:0;margin-top:5px;"></span>';
            return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ef4444;flex-shrink:0;margin-top:5px;"></span>';
          };
          const rowsHtml = checks.map((c) => {
            const interactive = !!c.fieldId;
            const cursor = interactive ? 'cursor:pointer;' : '';
            const titleColor = c.status === 'fail' ? 'var(--danger)' : c.status === 'warn' ? '#f59e0b' : 'var(--text)';
            return `
              <div data-field="${escHtml(c.fieldId || '')}" data-status="${escHtml(c.status)}" style="display:flex;align-items:flex-start;gap:8px;padding:6px 0;${cursor}">
                ${dot(c.status)}
                <div style="flex:1;min-width:0;">
                  <div style="font-size:12px;font-weight:600;color:${titleColor};">${escHtml(c.label)}</div>
                  ${c.detail ? `<div style="font-size:11px;color:var(--text-muted);margin-top:2px;line-height:1.45;">${escHtml(c.detail)}</div>` : ''}
                </div>
              </div>`;
          }).join('');
          const headline = summary.fail > 0
            ? `<span style="color:var(--danger);font-weight:700;">${summary.fail} fix needed</span>`
            : summary.warn > 0
              ? `<span style="color:#f59e0b;font-weight:700;">Ready (${summary.warn} warning${summary.warn === 1 ? '' : 's'})</span>`
              : `<span style="color:#22c55e;font-weight:700;">All checks passed</span>`;
          resultsEl.innerHTML = `
            <div style="font-size:11px;color:var(--text-muted);margin-bottom:6px;">${headline} · ${summary.pass} pass · ${summary.warn} warn · ${summary.fail} fail</div>
            <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:6px 12px;">
              ${rowsHtml || '<div style="font-size:11px;color:var(--text-muted);padding:6px 0;">No checks ran.</div>'}
            </div>`;
          resultsEl.style.display = 'block';
          // Wire row clicks → focus the offending field
          resultsEl.querySelectorAll('[data-field]').forEach((row) => {
            const fieldId = row.getAttribute('data-field');
            const status = row.getAttribute('data-status');
            if (!fieldId || status === 'pass') return;
            row.addEventListener('click', () => {
              const el = document.getElementById(fieldId);
              if (!el) return;
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              if (typeof el.focus === 'function') {
                setTimeout(() => el.focus(), 250);
              }
            });
          });
          return { ready: !!data?.ready, fail: summary.fail, warn: summary.warn, pass: summary.pass };
        };

        // Run server-side preflight. Returns the result object or null on error.
        const _runPreflight = async () => {
          const sbLive = _config.getSupabase();
          const liveSession = (await sbLive.auth.getSession()).data.session
            || (await sbLive.auth.refreshSession()).data.session;
          if (!liveSession) throw new Error('Session expired. Refresh and sign in.');
          const res = await fetch('/.netlify/functions/twilio-preflight-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + liveSession.access_token },
            body: JSON.stringify(_collectA2pPayload()),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.error || 'Pre-flight check failed.');
          return json;
        };

        // Manual "Run check" button — informational, doesn't gate anything.
        document.getElementById('wiz-a2p-preflight')?.addEventListener('click', async () => {
          const btn = document.getElementById('wiz-a2p-preflight');
          const original = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Checking…';
          try {
            const data = await _runPreflight();
            _renderPreflight(data);
          } catch (err) {
            const resultsEl = document.getElementById('a2p-preflight-results');
            if (resultsEl) {
              resultsEl.style.display = 'block';
              resultsEl.innerHTML = `<div style="font-size:12px;color:var(--danger);">${escHtml(err.message)}</div>`;
            }
          } finally {
            btn.disabled = false;
            btn.textContent = original;
          }
        });

        // EIN auto-format: type 9 digits → XX-XXXXXXX (we add the dash).
        const einInput = document.getElementById('wiz-a2p-ein');
        einInput?.addEventListener('input', () => {
          const d = einInput.value.replace(/\D/g, '').slice(0, 9);
          einInput.value = d.length > 2 ? d.slice(0, 2) + '-' + d.slice(2) : d;
        });

        // Auto-run the compliance check on entry (the mockup's auto-gate) so the
        // coach immediately sees what to fix — no button to hunt for. Only when
        // the form is showing (not when we're displaying a pending/approved
        // status from the resume logic above).
        if (!document.getElementById('a2p-form-wrap')?.classList.contains('hidden')) {
          (async () => { try { _renderPreflight(await _runPreflight()); } catch {} })();
        }

        // Review button → run preflight as a gate, then show summary.
        document.getElementById('wiz-a2p-review')?.addEventListener('click', async () => {
          const biz = document.getElementById('wiz-a2p-biz')?.value?.trim();
          const website = document.getElementById('wiz-a2p-website')?.value?.trim();
          if (!biz) { showToast('Business name is required', 'error'); return; }
          if (!website) { showToast('Website is required', 'error'); return; }

          const firstName = document.getElementById('wiz-a2p-first')?.value?.trim();
          const lastName = document.getElementById('wiz-a2p-last')?.value?.trim();
          if (!firstName || !lastName) { showToast('First and last name are required', 'error'); return; }

          const reviewBtn = document.getElementById('wiz-a2p-review');
          const originalLabel = reviewBtn.textContent;
          reviewBtn.disabled = true;
          reviewBtn.textContent = 'Running pre-flight check…';

          let preflight;
          try {
            preflight = await _runPreflight();
          } catch (err) {
            reviewBtn.disabled = false;
            reviewBtn.textContent = originalLabel;
            showToast(err.message, 'error');
            return;
          }
          const result = _renderPreflight(preflight);

          if (!result.ready) {
            reviewBtn.disabled = false;
            reviewBtn.textContent = originalLabel;
            showToast(`Pre-flight found ${result.fail} item${result.fail === 1 ? '' : 's'} to fix before submitting.`, 'error');
            document.getElementById('a2p-preflight-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }

          // Passed (warnings allowed). Proceed to review summary.
          reviewBtn.disabled = false;
          reviewBtn.textContent = originalLabel;
          const ein = document.getElementById('wiz-a2p-ein')?.value?.trim();
          const email = document.getElementById('wiz-a2p-email')?.value?.trim();
          const phone = document.getElementById('wiz-a2p-phone')?.value?.trim();
          const vertical = document.getElementById('wiz-a2p-vertical')?.querySelector('.custom-select-text')?.textContent || 'Health & Fitness';
          const street = document.getElementById('wiz-a2p-street')?.value?.trim();
          const city = document.getElementById('wiz-a2p-city')?.value?.trim();
          const state = document.getElementById('wiz-a2p-state')?.value?.trim()?.toUpperCase();
          const zip = document.getElementById('wiz-a2p-zip')?.value?.trim();

          // Enhanced Submission Review — shows EXACTLY what will be sent
          // to Twilio across all 3 bundle objects (Customer Profile,
          // Trust Product, Brand) plus surfaces the registration mode
          // (LVS vs Standard vs Sole Prop) and any compliance warnings
          // BEFORE the coach commits. Modeled after GHL's Trust Center
          // Submission Review screen.
          const sectionStyle = 'margin-top:12px;padding-top:10px;border-top:1px solid var(--border);';
          const rowStyle = 'display:flex;justify-content:space-between;gap:10px;line-height:1.7;';
          const labelStyle = 'font-size:11px;color:var(--text-muted);min-width:140px;';
          const valStyle = 'font-size:12px;color:var(--text);text-align:right;flex:1;word-break:break-word;';
          const fmtRow = (label, val) => `<div style="${rowStyle}"><span style="${labelStyle}">${label}</span><span style="${valStyle}">${val}</span></div>`;

          // Block submission if missing EIN — we don't support Sole Prop
          // flow yet, so silent submission would fail at the backend.
          const einMissing = !ein;
          if (einMissing) {
            document.getElementById('a2p-review-rows').innerHTML = `
              <div style="background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.25);border-radius:var(--radius-sm);padding:12px 14px;">
                <div style="font-weight:700;color:var(--danger);margin-bottom:6px;">EIN required</div>
                <div style="font-size:12px;color:var(--text);line-height:1.5;">Brand registration currently requires a business EIN. The Sole Proprietor flow (verified via mobile OTP + photo ID) is not yet supported by this wizard. <br><br>If you have an EIN, scroll up and enter it. If you don't, you can obtain one free at <a href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" target="_blank" rel="noopener" style="color:var(--accent);">irs.gov</a> in about 15 minutes.</div>
              </div>`;
            document.getElementById('a2p-review-summary').classList.remove('hidden');
            // Don't reveal the Submit button — block here.
            return;
          }

          // Industry → API enum mapping (must match INDUSTRY_MAP in
          // twilio-register-brand.js).
          const verticalEnum = ({ 'Health & Fitness': 'HEALTHCARE', 'Professional Services': 'PROFESSIONAL_SERVICES', 'Entertainment': 'MEDIA', 'Education': 'EDUCATION' })[vertical] || 'HEALTHCARE';

          const rows = [
            // Section 1: business identity (Customer Profile bundle)
            `<div style="font-size:10px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:6px;">Customer profile bundle</div>`,
            fmtRow('Legal business name', escHtml(biz)),
            fmtRow('EIN', escHtml(ein)),
            fmtRow('Business type', 'Limited Liability Corporation'),
            fmtRow('Industry', `${escHtml(vertical)} <span style="font-size:10px;color:var(--text-muted);">(${verticalEnum})</span>`),
            fmtRow('Website', `<a href="${escHtml(website)}" target="_blank" rel="noopener" style="color:var(--accent);">${escHtml(website)}</a>`),
            email ? fmtRow('Contact email', escHtml(email)) : '',
            phone ? fmtRow('Contact phone', escHtml(phone)) : '',
            fmtRow('Authorized rep', `${escHtml(firstName)} ${escHtml(lastName)}`),
            street ? fmtRow('Business address', `${escHtml(street)}, ${escHtml(city || '')} ${escHtml(state || '')} ${escHtml(zip || '')}`) : fmtRow('Business address', '<span style="color:var(--text-muted);">Not provided</span>'),

            // Section 2: A2P Trust Product bundle
            `<div style="${sectionStyle}font-size:10px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:6px;">A2P trust product bundle</div>`,
            fmtRow('Company type', 'Private Corporation'),
            fmtRow('Brand contact email', escHtml(email || 'coach login email')),
            fmtRow('Attached to', '<span style="color:var(--text-muted);">Secondary Customer Profile (above)</span>'),

            // Section 3: Brand Registration
            `<div style="${sectionStyle}font-size:10px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:6px;">Brand registration (TCR)</div>`,
            fmtRow('Brand type', '<strong style="color:#22c55e;">Standard — Low Volume</strong>'),
            fmtRow('Secondary vetting', 'Skipped (LVS tier)'),
            fmtRow('One-time fee', '$4.50'),
            fmtRow('Daily message cap', '~6,000 segments/day across all campaigns'),
            fmtRow('Expected review', 'Hours to 1 business day'),

            // Compliance reminders
            `<div style="${sectionStyle}background:rgba(59,130,246,0.04);border:1px solid rgba(59,130,246,0.15);border-radius:var(--radius-sm);padding:10px 12px;margin-top:14px;">
               <div style="font-size:11px;font-weight:700;color:var(--accent);margin-bottom:4px;">Before you submit, confirm:</div>
               <ul style="margin:4px 0 0 16px;padding:0;font-size:11px;color:var(--text);line-height:1.65;">
                 <li>Your business name above matches your IRS records and your website EXACTLY (no DBA, no abbreviation differences).</li>
                 <li>Your website is live and includes a publicly accessible Privacy Policy and Terms of Service.</li>
                 <li>Your EIN was issued at least 30 days ago (newly-issued EINs need 30–90 days to propagate to D&B / Experian).</li>
                 <li>Your business address matches your state's official filing (Articles of Organization / Incorporation).</li>
               </ul>
             </div>`,
          ].filter(Boolean);

          document.getElementById('a2p-review-rows').innerHTML = rows.join('');
          document.getElementById('a2p-review-summary').classList.remove('hidden');
          document.getElementById('wiz-a2p-review').classList.add('hidden');
          document.getElementById('wiz-a2p-submit').classList.remove('hidden');
        });

        // Submit handler
        document.getElementById('wiz-a2p-submit')?.addEventListener('click', async () => {
          const biz = document.getElementById('wiz-a2p-biz')?.value?.trim();
          const website = document.getElementById('wiz-a2p-website')?.value?.trim();
          if (!biz) { showToast('Business name is required', 'error'); return; }
          if (!website) { showToast('Website is required', 'error'); return; }

          const btn = document.getElementById('wiz-a2p-submit');
          const resultEl = document.getElementById('wiz-a2p-result');
          btn.disabled = true;
          btn.textContent = 'Submitting...';

          try {
            // Re-fetch the session at submit time. The `session` captured at wizard-step
            // render can be up to an hour old — Supabase access tokens expire in 1hr,
            // which caused an "Invalid token" error when a user spent time reviewing
            // their form before clicking Submit.
            const sbLive = _config.getSupabase();
            const liveSession = (await sbLive.auth.getSession()).data.session
              || (await sbLive.auth.refreshSession()).data.session;
            if (!liveSession) throw new Error('Session expired. Refresh the page and sign in again.');

            const a2pPayload = {
              businessName: biz,
              firstName: document.getElementById('wiz-a2p-first')?.value?.trim() || '',
              lastName: document.getElementById('wiz-a2p-last')?.value?.trim() || '',
              ein: document.getElementById('wiz-a2p-ein')?.value?.trim() || '',
              website,
              email: document.getElementById('wiz-a2p-email')?.value?.trim() || '',
              phone: document.getElementById('wiz-a2p-phone')?.value?.trim() || '',
              vertical: document.getElementById('wiz-a2p-vertical')?.dataset?.value || 'HEALTH',
              street: document.getElementById('wiz-a2p-street')?.value?.trim() || '',
              city: document.getElementById('wiz-a2p-city')?.value?.trim() || '',
              state: document.getElementById('wiz-a2p-state')?.value?.trim()?.toUpperCase() || '',
              postalCode: document.getElementById('wiz-a2p-zip')?.value?.trim() || '',
            };

            // Step 0: Cache profile for auto-registration on future re-entry (chunk 2).
            // Best-effort — failure here is non-fatal to the registration itself.
            fetch('/.netlify/functions/twilio-a2p-profile', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + liveSession.access_token },
              body: JSON.stringify({ action: 'save', profile: a2pPayload }),
            }).catch(() => {});

            // Step 1: Register brand
            const brandRes = await fetch('/.netlify/functions/twilio-register-brand', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + liveSession.access_token },
              body: JSON.stringify(a2pPayload),
            });
            const brandJson = await brandRes.json();
            if (!brandRes.ok) throw new Error(brandJson.error);

            // Show appropriate message based on phase
            if (brandJson.phase === 'brand_registered') {
              resultEl.innerHTML = `
                <div style="text-align:center;padding:12px 0;">
                  <div style="font-size:28px;color:#22c55e;margin-bottom:6px;">&#10003;</div>
                  <div style="font-size:15px;font-weight:700;color:var(--text);">Brand Registration Submitted</div>
                  <p style="font-size:12px;color:var(--text-muted);margin-top:6px;">Your brand is now under carrier review (10-15 business days). Once approved, we'll set up your SMS campaign. Click <strong>Next</strong> to write your example texts while you wait.</p>
                </div>`;
            } else {
              resultEl.innerHTML = `
                <div style="text-align:center;padding:12px 0;">
                  <div style="font-size:28px;color:#22c55e;margin-bottom:6px;">&#10003;</div>
                  <div style="font-size:15px;font-weight:700;color:var(--text);">Compliance Bundles Submitted</div>
                  <p style="font-size:12px;color:var(--text-muted);margin-top:6px;">Your compliance bundles are under ${window._appIsOwner ? 'Twilio' : 'carrier'} review (typically 10-15 business days; Sole Proprietor ~1 week). Once approved, return to this step to register your brand with carriers.</p>
                  <div style="margin-top:10px;font-size:11px;color:var(--text-muted);">
                    Customer Profile: <strong>${escHtml(brandJson.customerProfileStatus || 'pending')}</strong><br>
                    A2P Trust Product: <strong>${escHtml(brandJson.trustProductStatus || 'pending')}</strong>
                  </div>
                  ${brandJson.tpRejectionDetails ? '<pre style="text-align:left;margin-top:10px;font-size:10px;color:var(--text-muted);white-space:pre-wrap;max-height:200px;overflow:auto;background:rgba(0,0,0,0.2);padding:8px;border-radius:4px;">' + escHtml(JSON.stringify(brandJson.tpRejectionDetails, null, 2)) + '</pre>' : ''}
                </div>`;
            }
            btn.classList.add('hidden');
          } catch (err) {
            resultEl.innerHTML = '<p style="color:var(--danger);font-size:13px;">' + escHtml(err.message) + '</p>';
            btn.disabled = false;
            btn.textContent = 'Try Again';
          }
        });
      },
    },
    { id: 'a2p-2', title: 'Your example texts', render: () => `
      <p class="comms-modal-desc">This is the carriers' final check: a few <strong>example texts</strong> you might send and how clients agreed to get them. <strong>Nothing is sent to anyone here.</strong></p>

      <!-- Terminal-state slot: finish-step-3 / already-live / under-review. When
           filled, the editor below stays hidden. -->
      <div id="a2p4-blocker"></div>

      <div id="a2p4-editor" class="hidden">
        <!-- Honest cost note (mockup 4b) -->
        <div style="margin-bottom:14px;background:rgba(59,130,246,0.04);border:1px solid rgba(59,130,246,0.15);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="font-size:12px;font-weight:700;color:var(--accent);">&#128179; How the carrier fee works — read this first</div>
          <p style="font-size:11.5px;color:var(--text-muted);margin:4px 0 0;line-height:1.55;">Your <strong>first</strong> carrier review is free (on us). After that, <strong>every review costs $15</strong> — resubmitting and starting over cost the same, and all of it goes to the carriers, not to us. That's why we make you pass the compliance check before every submit: it reads the carriers' real rejection rules against your actual messages + policies, so it's built to get you approved on the first try.</p>
        </div>

        <p style="font-size:11.5px;color:var(--text-muted);margin:0 0 14px;line-height:1.55;">These messages are <strong>fully editable</strong> — we pre-filled them with our AI compliance system to give you the best chance of approval on your first submission. Change anything you like; the compliance check below will flag (and tell you how to fix) anything that would get you rejected before you submit.</p>

        <!-- Default-ON: auto-submit the (free, first) campaign once the business approves -->
        <div style="margin-bottom:14px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:10px 12px;display:flex;align-items:flex-start;gap:10px;">
          <input type="checkbox" id="a2p4-autosubmit" checked style="margin-top:2px;flex-shrink:0;width:15px;height:15px;cursor:pointer;" />
          <label for="a2p4-autosubmit" style="font-size:11.5px;color:var(--text);line-height:1.5;cursor:pointer;"><strong>Submit my texts automatically once my business is approved.</strong> We’ll re-check compliance and send them to the carriers for you — no need to come back. Turn this off if you’d rather review and submit yourself.</label>
        </div>

        <!-- Non-destructive nudge when an EDITED draft mentions a different website -->
        <div id="a2p4-stale-nudge"></div>

        <!-- 4a: auto-draft spinner -->
        <div id="a2p4-generating" class="hidden" style="text-align:center;padding:24px 10px;color:var(--text-muted);font-size:13px;font-weight:600;">&#9997;&#65039; Writing your messages with AI…</div>

        <div id="a2p4-fields">
          <div class="form-group" style="margin-top:4px;">
            <label style="font-size:12px;font-weight:700;color:var(--text);display:block;margin-bottom:2px;">What you'll text about</label>
            <p style="font-size:11px;color:var(--text-muted);margin:0 0 6px;line-height:1.45;">A plain summary of the texts you send. The carriers read this.</p>
            <textarea id="wiz-camp-description" rows="3" maxlength="500" style="resize:vertical;"></textarea>
          </div>
          <div class="form-group" style="margin-top:12px;">
            <label style="font-size:12px;font-weight:700;color:var(--text);display:block;margin-bottom:2px;">How clients say yes (consent)</label>
            <p style="font-size:11px;color:var(--text-muted);margin:0 0 6px;line-height:1.45;">Written for you from your opt-in page and policy links — every claim in it is a link the carriers can click. It updates itself when your links change; if you edit it by hand, it's yours from then on.</p>
            <textarea id="wiz-camp-flow" rows="3" maxlength="2048" style="resize:vertical;"></textarea>
            <div id="a2p4-optin-url-row" class="hidden" style="margin-top:6px;background:rgba(34,197,94,0.05);border:1px solid rgba(34,197,94,0.2);border-radius:var(--radius-sm);padding:8px 10px;">
              <div style="font-size:10px;font-weight:600;letter-spacing:0;color:#22c55e;margin-bottom:2px;">Your opt-in page (created for you)</div>
              <a id="a2p4-optin-url" href="#" target="_blank" rel="noopener" style="font-size:12px;color:var(--accent);word-break:break-all;"></a>
              <p style="font-size:11px;color:var(--text-muted);margin:4px 0 0;line-height:1.5;">This live page shows the exact consent checkboxes your clients see. The carriers click it to verify your opt-in — it's also a real sign-up page you can share.</p>
            </div>
          </div>
          <div class="form-group" style="margin-top:12px;">
            <label style="font-size:12px;font-weight:700;color:var(--text);display:block;margin-bottom:2px;">Example texts</label>
            <p style="font-size:11px;color:var(--text-muted);margin:0 0 6px;line-height:1.45;">Real examples of what you'd send. No prices. Each one ends with "Reply STOP".</p>
            <textarea id="wiz-camp-sample-0" rows="2" maxlength="1024" style="resize:vertical;"></textarea>
            <textarea id="wiz-camp-sample-1" rows="2" maxlength="1024" style="resize:vertical;margin-top:6px;"></textarea>
            <textarea id="wiz-camp-sample-2" rows="2" maxlength="1024" style="resize:vertical;margin-top:6px;"></textarea>
          </div>
          <div class="form-group" style="margin-top:12px;">
            <label style="font-size:12px;font-weight:700;color:var(--text);display:block;margin-bottom:2px;">Welcome / STOP / HELP replies</label>
            <p style="font-size:11px;color:var(--text-muted);margin:0 0 6px;line-height:1.45;">Auto-replies for new sign-ups, and for when someone texts STOP or HELP.</p>
            <div class="form-grid">
              <div class="form-group form-col-3"><label style="font-size:10px;color:var(--text-muted);">Welcome (opt-in)</label><textarea id="wiz-camp-optin" rows="3" maxlength="320" style="resize:vertical;"></textarea></div>
              <div class="form-group form-col-3"><label style="font-size:10px;color:var(--text-muted);">STOP reply</label><textarea id="wiz-camp-optout" rows="3" maxlength="320" style="resize:vertical;"></textarea></div>
              <div class="form-group form-col-3"><label style="font-size:10px;color:var(--text-muted);">HELP reply</label><textarea id="wiz-camp-help" rows="3" maxlength="320" style="resize:vertical;"></textarea></div>
            </div>
          </div>
          <div class="form-group" style="margin-top:12px;">
            <label style="font-size:12px;font-weight:700;color:var(--text);display:block;margin-bottom:2px;">Privacy policy &amp; terms links</label>
            <p style="font-size:11px;color:var(--text-muted);margin:0 0 6px;line-height:1.45;">The carriers open these pages and read them. They must live on your <strong>own</strong> website.</p>
            <input type="url" id="wiz-camp-privacy" placeholder="https://yoursite.com/privacy" style="font-size:12px;" />
            <input type="url" id="wiz-camp-terms" placeholder="https://yoursite.com/terms" style="font-size:12px;margin-top:6px;" />
            <p style="font-size:11px;color:var(--text-muted);margin:6px 0 0;">No privacy or terms page yet? <a href="#" id="a2p4-policy-toggle" style="color:var(--accent);">We'll write the exact text for you to publish &rarr;</a></p>
          </div>

          <!-- 4b·: write-my-policy generator (hidden until toggled) -->
          <div id="a2p4-policy-gen" class="hidden" style="margin-top:6px;">
            <div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:var(--radius-sm);padding:12px 14px;">
              <div style="font-size:12px;font-weight:700;color:#f59e0b;">&#9888; Add these two pages to your site</div>
              <p style="font-size:11.5px;color:var(--text-muted);margin:4px 0 0;line-height:1.55;">We'll write the exact privacy policy &amp; terms wording for your business. You paste each onto a new page on your own site (e.g. yoursite.com/privacy), publish it, and drop the links back here. We don't host them on our domain because carriers reject that.</p>
            </div>
            <button class="btn-primary" id="a2p4-policy-write" type="button" style="margin-top:10px;width:100%;">Write my privacy &amp; terms text for me</button>
            <div id="a2p4-policy-output" style="margin-top:10px;"></div>
          </div>
        </div>

        <div id="a2p4-save-row" style="display:flex;align-items:center;justify-content:flex-end;gap:10px;margin-top:14px;">
          <span id="a2p4-save-status" style="font-size:11px;color:var(--text-muted);"></span>
          <button class="btn-secondary" id="a2p4-save" type="button" style="font-size:11px;padding:6px 14px;">Save draft</button>
        </div>

        <div style="display:flex;gap:10px;margin-top:12px;">
          <button class="btn-secondary" id="wiz-campaign-generate" type="button" style="flex:1;">Have AI rewrite</button>
          <button class="btn-primary" id="wiz-a2p-ai-precheck" type="button" style="flex:1;">Run compliance check</button>
        </div>

        <!-- Rejection banner (only when the campaign came back FAILED) -->
        <div id="a2p-ai-rejection-banner" class="hidden" style="margin-top:12px;background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.22);border-radius:var(--radius-sm);padding:10px 12px;">
          <div style="display:flex;align-items:flex-start;gap:10px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ef4444;flex-shrink:0;margin-top:6px;"></span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:12px;font-weight:700;color:var(--danger);">The carriers rejected this</div>
              <div id="a2p-ai-rejection-reason" style="font-size:11px;color:var(--text-muted);margin-top:4px;line-height:1.5;"></div>
              <button class="btn-secondary" id="wiz-a2p-ai-diagnose" type="button" style="margin-top:8px;font-size:11px;padding:5px 11px;background:rgba(239,68,68,0.12);border-color:rgba(239,68,68,0.3);color:var(--danger);">See exactly what to fix</button>
            </div>
          </div>
        </div>

        <div id="a2p-ai-results" style="margin-top:10px;display:none;"></div>

        <!-- Gate-driven submit area (rendered by onMount) -->
        <div id="a2p4-submit-area" style="margin-top:14px;"></div>
      </div>`,
      onMount: async () => {
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        // Access tokens expire after ~1h; re-fetch a fresh one at any
        // network call that follows the coach reading/editing the form.
        const _freshSession = async () => {
          const sb = _config.getSupabase();
          return (await sb.auth.getSession()).data.session || (await sb.auth.refreshSession()).data.session;
        };
        const _auth = (s) => ({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token });

        const blockerEl = document.getElementById('a2p4-blocker');
        const editorEl = document.getElementById('a2p4-editor');

        // Footer gate: the wizard's footer "Finish"/"Next" must NOT let the coach
        // skip past this step without acting — it would jump to the summary as if
        // setup were done, bypassing the compliance gate. Disable it during load
        // and in the locked editor states; re-enable only where leaving is
        // legitimate (business still under review, or already submitted/approved).
        // The shell resets it to enabled each render so this never leaks forward.
        const _syncFooter = (disabled, label) => {
          const n = document.getElementById('comms-modal-next');
          if (!n) return;
          n.disabled = !!disabled;
          n.style.opacity = disabled ? '0.5' : '';
          n.style.cursor = disabled ? 'not-allowed' : '';
          if (label) n.textContent = label;
        };
        _syncFooter(true); // locked until the state resolves below

        // ── 1. Brand status + campaign channel + saved profile (parallel) ──
        let brandStatus = null, channel = null, profile = null;
        try {
          const [bRes, ccRes, pRes] = await Promise.all([
            fetch('/.netlify/functions/twilio-register-brand', { method: 'POST', headers: _auth(session), body: JSON.stringify({ action: 'status' }) }),
            fetch('/.netlify/functions/coach-channels-get', { method: 'GET', headers: _auth(session) }),
            fetch('/.netlify/functions/twilio-a2p-profile', { method: 'POST', headers: _auth(session), body: JSON.stringify({ action: 'get' }) }),
          ]);
          brandStatus = await bRes.json().catch(() => null);
          const ccJson = await ccRes.json().catch(() => null);
          channel = (ccJson?.channels || []).find(c => c.channel === 'sms') || null;
          const pJson = await pRes.json().catch(() => null);
          profile = pJson?.profile || null;
        } catch (_) {}

        const brand = String(brandStatus?.status || '').toLowerCase();
        const brandApproved = brand === 'approved';
        const brandFailed = brand === 'failed';
        const brandNotRegistered = !brandStatus || brand === '' || brand === 'not_registered';
        const campSid = channel?.a2p_campaign_sid || brandStatus?.campaignSid || null;
        // Prefer the LIVE Twilio campaign status (brand-status action fetches it)
        // over the DB column, which lags until the status cron reconciles — so a
        // fast carrier rejection surfaces the resubmit path immediately instead of
        // a stale "under review".
        const campStatus = String(channel?.a2p_campaign_status || brandStatus?.campaignStatus || '').toUpperCase();
        const campFailed = !!campSid && (campStatus === 'FAILED' || campStatus === 'REJECTED');
        const campLive = campStatus === 'VERIFIED' || (brandApproved && campStatus === 'APPROVED');
        // Only a KNOWN non-terminal status is "pending". A stored SID with an
        // empty/unknown status (e.g. campaign deleted out-of-band at Twilio) falls
        // through to the editor so the coach can re-create — matching the backend,
        // which recreates a stale SID rather than dead-ending.
        const campPending = !!campSid && ['IN_PROGRESS', 'PENDING', 'SUBMITTED', 'PENDING_REVIEW'].includes(campStatus);
        const campReason = channel?.a2p_campaign_failure_reason || brandStatus?.rejectionReasons?.[0]?.message || '';

        const _blocker = (color, title, body) => {
          blockerEl.innerHTML = `
            <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:16px;text-align:center;">
              <div style="font-size:15px;font-weight:700;color:${color};">${title}</div>
              <p style="font-size:12px;color:var(--text-muted);margin-top:6px;line-height:1.55;">${body}</p>
            </div>`;
        };

        // ── 2. Terminal states bypass the editor (leaving here is fine → footer on) ──
        if (brandNotRegistered) {
          _blocker('var(--text-muted)', 'Finish step 3 first', 'Go back to the previous step to verify your business with the carriers. Once that’s submitted, you can add your example texts here.');
          _syncFooter(false);
          return;
        }
        if (brandFailed) {
          _blocker('var(--danger)', 'Your business registration was rejected', 'Go back to the previous step to fix the flagged items and resubmit. You can add your example texts once your business is back under review.');
          _syncFooter(false);
          return;
        }
        if (campLive) {
          _blocker('#22c55e', '✓ Your messages are approved', 'Your number is approved for full SMS delivery to all carriers. There’s nothing left to do here.');
          _syncFooter(false);
          return;
        }
        if (campPending) {
          _blocker('#f59e0b', 'Submitted — under carrier review', 'Your example texts are with the carriers now (typically 1–2 business days). We’ll email you the moment they’re approved — then sending turns on automatically.');
          _syncFooter(false);
          return;
        }

        // ── 3. Editor ──
        editorEl.classList.remove('hidden');
        const generatingEl = document.getElementById('a2p4-generating');
        const fieldsEl = document.getElementById('a2p4-fields');

        let _optInPageUrl = ''; // server-derived proof URL ('' until consent-ready)
        let _flowDirty = false; // true once the coach hand-edits the flow textarea
        const _populateDraft = (draft) => {
          const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val || ''; };
          set('wiz-camp-description', draft?.description);
          set('wiz-camp-flow', draft?.messageFlow);
          // Opt-in proof page — derived server-side once the coach has their
          // own policy URLs; shown so the coach can preview what the carrier
          // reviewer will click, and sent along to the AI compliance check.
          _optInPageUrl = draft?.optInPageUrl || '';
          const urlRow = document.getElementById('a2p4-optin-url-row');
          const urlLink = document.getElementById('a2p4-optin-url');
          if (urlRow && urlLink) {
            urlRow.classList.toggle('hidden', !_optInPageUrl);
            urlLink.textContent = _optInPageUrl;
            urlLink.href = _optInPageUrl || '#';
          }
          const samples = Array.isArray(draft?.messageSamples) ? draft.messageSamples : [];
          set('wiz-camp-sample-0', samples[0]);
          set('wiz-camp-sample-1', samples[1]);
          set('wiz-camp-sample-2', samples[2]);
          set('wiz-camp-optin', draft?.optInMessage);
          set('wiz-camp-optout', draft?.optOutMessage);
          set('wiz-camp-help', draft?.helpMessage);
          // Don't clobber the coach's own URLs with the platform fallback.
          const PLATFORM = { p: 'https://fitopspro.com/privacy', t: 'https://fitopspro.com/terms' };
          if (draft?.privacyPolicyUrl && draft.privacyPolicyUrl !== PLATFORM.p) set('wiz-camp-privacy', draft.privacyPolicyUrl);
          if (draft?.termsAndConditionsUrl && draft.termsAndConditionsUrl !== PLATFORM.t) set('wiz-camp-terms', draft.termsAndConditionsUrl);
          const auto = document.getElementById('a2p4-autosubmit');
          if (auto) auto.checked = draft?.autoSubmit !== false; // default ON
        };
        const _collectDraft = () => ({
          description: document.getElementById('wiz-camp-description')?.value?.trim() || '',
          messageFlow: document.getElementById('wiz-camp-flow')?.value?.trim() || '',
          messageSamples: [
            document.getElementById('wiz-camp-sample-0')?.value?.trim() || '',
            document.getElementById('wiz-camp-sample-1')?.value?.trim() || '',
            document.getElementById('wiz-camp-sample-2')?.value?.trim() || '',
          ],
          optInMessage: document.getElementById('wiz-camp-optin')?.value?.trim() || '',
          optOutMessage: document.getElementById('wiz-camp-optout')?.value?.trim() || '',
          helpMessage: document.getElementById('wiz-camp-help')?.value?.trim() || '',
          privacyPolicyUrl: document.getElementById('wiz-camp-privacy')?.value?.trim() || '',
          termsAndConditionsUrl: document.getElementById('wiz-camp-terms')?.value?.trim() || '',
          ...(document.getElementById('a2p4-autosubmit') ? { autoSubmit: document.getElementById('a2p4-autosubmit').checked } : {}),
        });

        // Load draft. Rule:
        //  - no draft → auto-generate (mockup 4a).
        //  - an UNTOUCHED draft (generatedBy 'ai'/'default') generated for a
        //    different / no website → auto-regenerate with the coach's real info.
        //  - a draft the coach EDITED ('manual') → load it as-is, NEVER clobber;
        //    if it's also stale, show a non-destructive "rewrite?" nudge instead.
        const _draftFail = 'Couldn’t draft your messages automatically — type them in, or tap “Have AI rewrite”.';
        const _runGenerate = async (fallbackDraft) => {
          generatingEl?.classList.remove('hidden');
          fieldsEl?.classList.add('hidden');
          try {
            const ls = await _freshSession();
            const gRes = await fetch('/.netlify/functions/twilio-campaign-draft', { method: 'POST', headers: _auth(ls), body: JSON.stringify({ action: 'generate' }) });
            const gJson = await gRes.json().catch(() => ({}));
            if (gRes.ok) { _populateDraft(gJson.draft); }
            else { _populateDraft(fallbackDraft); showToast(gJson.error || _draftFail, 'error'); }
          } catch (_) { _populateDraft(fallbackDraft); showToast(_draftFail, 'error'); }
          generatingEl?.classList.add('hidden');
          fieldsEl?.classList.remove('hidden');
        };
        const _showStaleNudge = () => {
          const el = document.getElementById('a2p4-stale-nudge');
          if (!el) return;
          el.innerHTML = `
            <div style="margin:0 0 12px;background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:var(--radius-sm);padding:10px 12px;">
              <div style="font-size:11.5px;color:var(--text);line-height:1.5;">Heads up — this draft mentions a different website than the one on file. Rewrite it with your current info, or just edit the fields below — <strong>we won't touch your changes</strong>.</div>
              <div style="display:flex;gap:8px;margin-top:8px;">
                <button type="button" id="a2p4-stale-rewrite" class="btn-secondary" style="font-size:11px;padding:5px 11px;">Rewrite with my current info</button>
                <button type="button" id="a2p4-stale-dismiss" class="btn-link" style="font-size:11px;color:var(--text-muted);background:none;border:none;cursor:pointer;">Keep mine</button>
              </div>
            </div>`;
          document.getElementById('a2p4-stale-rewrite')?.addEventListener('click', () => { el.innerHTML = ''; _runGenerate(_collectDraft()); });
          document.getElementById('a2p4-stale-dismiss')?.addEventListener('click', () => { el.innerHTML = ''; });
        };
        try {
          const res = await fetch('/.netlify/functions/twilio-campaign-draft', { method: 'POST', headers: _auth(session), body: JSON.stringify({ action: 'get' }) });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error('get failed');
          const isManual = json.draft?.generatedBy === 'manual';
          if (!json.hasDraft || (json.stale && !isManual)) {
            await _runGenerate(json.draft);
          } else {
            _populateDraft(json.draft);
            if (json.hasDraft && json.stale && isManual) _showStaleNudge();
          }
        } catch (_) {
          generatingEl?.classList.add('hidden');
          fieldsEl?.classList.remove('hidden');
          showToast(_draftFail, 'error');
        }

        // ── Compliance gate + fee quote ──
        let _compliancePassed = false;
        let _feeQuote = null;
        try {
          const fRes = await fetch('/.netlify/functions/a2p-fee-quote', { method: 'POST', headers: _auth(session) });
          if (fRes.ok) _feeQuote = await fRes.json();
        } catch (_) {}

        const _money = (cents) => { const n = Number(cents || 0) / 100; return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)); };
        const _reviewBox = (rows) => `<div style="border:1px solid var(--border);border-radius:var(--radius-sm);padding:2px 14px;margin:6px 0;">${rows.map(([k, v], i) => `<div style="display:flex;justify-content:space-between;gap:16px;padding:9px 0;${i < rows.length - 1 ? 'border-bottom:1px solid var(--border);' : ''}font-size:13px;"><span style="color:var(--text-muted);font-weight:600;">${k}</span><span style="text-align:right;color:var(--text);">${v}</span></div>`).join('')}</div>`;

        // The coach's OWN privacy + terms URLs (http/https) are a hard precondition
        // for submit — empty fields would let the backend fall back to the
        // platform-hosted fitopspro.com policy, which carriers reject (we never host
        // the coach's policy). The AI compliance check alone can't guarantee this.
        const _ownUrlsOk = () => {
          const p = (document.getElementById('wiz-camp-privacy')?.value || '').trim();
          const t = (document.getElementById('wiz-camp-terms')?.value || '').trim();
          return /^https?:\/\/\S+/i.test(p) && /^https?:\/\/\S+/i.test(t);
        };

        // Re-render the submit area for the current gate + business state.
        const _renderSubmitArea = () => {
          const el = document.getElementById('a2p4-submit-area');
          if (!el) return;
          const q = _feeQuote?.campaign || null;
          const billable = !!q?.billable;
          const feeStr = _money(q?.feeCents != null ? q.feeCents : 1500);
          const nextAttempt = q?.nextAttempt || (campFailed ? 2 : 1);
          const passNote = `<p style="font-size:11px;color:var(--text-muted);text-align:center;margin-top:8px;line-height:1.5;">This $15 goes straight to the carriers — we don’t profit on it. Charged to your card on file; you’ll confirm first.</p>`;
          const lock = (txt) => `<p style="font-size:12px;color:var(--text-muted);text-align:center;margin-top:10px;">&#128274; ${txt}</p>`;
          const greenPanel = (h, b) => `<div style="background:rgba(34,197,94,0.06);border:1px solid rgba(34,197,94,0.22);border-radius:var(--radius-sm);padding:12px 14px;margin-bottom:10px;"><div style="font-size:12px;font-weight:700;color:#22c55e;">✓ ${h}</div><p style="font-size:11.5px;color:var(--text-muted);margin:4px 0 0;line-height:1.5;">${b}</p></div>`;

          // ── RESUBMIT after a rejection (R1 / R2) ──
          if (campFailed) {
            if (!_compliancePassed) {
              // The fee warning only makes sense when this account will actually
              // be charged — the platform owner's account is fee-exempt (the
              // carrier fee hits the platform's Twilio balance directly), so a
              // "$0 goes to the carriers" banner would be nonsense.
              el.innerHTML = billable ? `
                <div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:var(--radius-sm);padding:12px 14px;">
                  <div style="font-size:12px;font-weight:700;color:#f59e0b;">&#128179; Resubmitting costs ${feeStr} — all of it goes to the carriers</div>
                  <p style="font-size:11.5px;color:var(--text-muted);margin:4px 0 0;line-height:1.55;">Your first review was on us. After that the carriers charge ${feeStr} per review — we pass it straight through and keep nothing. Fix the reason above and pass the compliance check first so this one goes through.</p>
                </div>
                ${lock(`The <strong>Resubmit (${feeStr})</strong> button turns on once the check passes.`)}`
              : lock('Fix the reason above, then pass the compliance check to unlock <strong>Resubmit</strong>. No fee is charged on this account.');
              _syncFooter(true);
              return;
            }
            if (!_ownUrlsOk()) {
              el.innerHTML = lock('<strong>Add your privacy &amp; terms links</strong> (on your own site) above, then re-run the compliance check to unlock <strong>Resubmit</strong>.');
              _syncFooter(true);
              return;
            }
            el.innerHTML = `
              ${greenPanel('Compliance check passed', 'The rejection reason is fixed. You’re clear to resubmit.')}
              ${_reviewBox([['Carrier review', `#${nextAttempt} (your first was free)`], ['Carrier review fee', billable ? `<strong>${feeStr}</strong> — straight pass-through` : 'Included']])}
              <button class="btn-primary" id="a2p4-submit" type="button" style="width:100%;margin-top:6px;">${billable ? `Resubmit &amp; pay ${feeStr}` : 'Resubmit messages'}</button>
              ${billable ? passNote : ''}`;
            document.getElementById('a2p4-submit')?.addEventListener('click', (e) => _doSubmit(e.currentTarget, true));
            _syncFooter(true); // resubmit is the in-body action — don't let the footer skip it
            return;
          }

          // ── FIRST submission ──
          if (!_compliancePassed) {
            el.innerHTML = lock('<strong>Submit unlocks after the compliance check passes.</strong>');
            _syncFooter(true);
            return;
          }
          if (!_ownUrlsOk()) {
            el.innerHTML = lock('<strong>Add your privacy &amp; terms links</strong> (on your own site) above, then run the compliance check to unlock submit.');
            _syncFooter(true);
            return;
          }
          if (!brandApproved) {
            // 4d: messages ready, business still under review.
            el.innerHTML = `
              ${greenPanel('Compliance check passed', 'Your messages are ready. Nothing left to fix here.')}
              <div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:var(--radius-sm);padding:12px 14px;">
                <div style="font-size:12px;font-weight:700;color:#f59e0b;">&#9203; Waiting on your business approval</div>
                <p style="font-size:11.5px;color:var(--text-muted);margin:4px 0 0;line-height:1.55;">You can submit your messages as soon as your business is approved (from step 3) — usually 1–2 business days. <strong>We’ll email you</strong>, and this turns on by itself.</p>
              </div>
              <button class="btn-secondary" type="button" disabled style="width:100%;margin-top:10px;opacity:0.6;cursor:not-allowed;">&#128274; Submit messages — unlocks when your business is approved</button>`;
            // Business still under review — submitting isn't possible yet, so let
            // the coach leave and come back (we email them on approval).
            _syncFooter(false, 'Done for now');
            return;
          }
          // 4e: both gates green.
          el.innerHTML = `
            ${greenPanel('Ready to submit', 'Compliance check passed and your business is approved. Send your messages to the carriers for final sign-off.')}
            ${_reviewBox([
              ['Compliance check', '<span style="color:#22c55e;font-weight:600;">Passed</span>'],
              ['Business', '<span style="color:#22c55e;font-weight:600;">Approved</span>'],
              ['Privacy &amp; terms', 'Your links &#10003;'],
              ['Carrier review fee', billable ? `<strong>${feeStr}</strong>` : 'Included — first one’s on us'],
            ])}
            <button class="btn-primary" id="a2p4-submit" type="button" style="width:100%;margin-top:6px;">${billable ? `Submit &amp; pay ${feeStr}` : 'Submit messages to carriers'}</button>
            ${billable ? passNote : `<p style="font-size:11px;color:var(--text-muted);text-align:center;margin-top:8px;line-height:1.5;">Your first carrier review is included in your plan. If it’s rejected and you resubmit, carriers charge $15 per review after the first — so the check above is there to get you approved on the first try.</p>`}`;
          document.getElementById('a2p4-submit')?.addEventListener('click', (e) => _doSubmit(e.currentTarget, false));
          _syncFooter(true); // submitting is the in-body action — don't let the footer skip it
        };

        const _setCompliance = (passed) => { _compliancePassed = passed; _renderSubmitArea(); };

        // Replace a now-stale check result so the on-screen evidence matches the
        // re-locked gate (otherwise a green "all passed" panel lingers next to the
        // lock note after an edit).
        const _staleAiResults = (msg) => {
          const r = document.getElementById('a2p-ai-results');
          if (!r) return;
          r.style.display = 'block';
          r.innerHTML = `<div style="font-size:11.5px;color:var(--text-muted);background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 10px;">&#9998; ${escHtml(msg)}</div>`;
        };

        // Save the draft. Persisting flips it to 'manual' server-side → it's now
        // the coach's own copy and is NEVER auto-rewritten on a later visit.
        const _setSaveStatus = (txt, color) => {
          const s = document.getElementById('a2p4-save-status');
          if (s) { s.textContent = txt || ''; s.style.color = color || 'var(--text-muted)'; }
        };
        const _saveDraft = async () => {
          try {
            const ls = await _freshSession();
            if (!ls) return false;
            // The message flow is template-managed server-side (rebuilt when the
            // policy URLs change). Only send the textarea's copy when the coach
            // actually typed in it — otherwise a stale rendering would be saved
            // back as a "manual edit" and freeze the template.
            const draft = _collectDraft();
            if (!_flowDirty) delete draft.messageFlow;
            const r = await fetch('/.netlify/functions/twilio-campaign-draft', { method: 'POST', headers: _auth(ls), body: JSON.stringify({ action: 'save', draft }) });
            if (r.ok) {
              _setSaveStatus('Saved ✓', '#22c55e');
              // Reflect the server's (possibly rebuilt) flow + proof URL — but
              // never while the coach is typing in the flow field itself.
              try {
                const json = await r.json();
                const flowEl = document.getElementById('wiz-camp-flow');
                if (json?.draft && !_flowDirty && flowEl && document.activeElement !== flowEl) {
                  flowEl.value = json.draft.messageFlow || '';
                  _optInPageUrl = json.draft.optInPageUrl || '';
                  const urlRow = document.getElementById('a2p4-optin-url-row');
                  const urlLink = document.getElementById('a2p4-optin-url');
                  if (urlRow && urlLink) {
                    urlRow.classList.toggle('hidden', !_optInPageUrl);
                    urlLink.textContent = _optInPageUrl;
                    urlLink.href = _optInPageUrl || '#';
                  }
                }
              } catch (_) {}
              return true;
            }
          } catch (_) {}
          _setSaveStatus('Couldn’t save — tap Save draft to retry', 'var(--danger)');
          return false;
        };
        // Auto-save (debounced) as the coach types so an accidental close / refresh
        // never loses their work.
        const _autoSaveDraft = debounce(_saveDraft, 900);
        document.getElementById('a2p4-save')?.addEventListener('click', async () => {
          const btn = document.getElementById('a2p4-save');
          btn.disabled = true; _setSaveStatus('Saving…');
          await _saveDraft();
          btn.disabled = false;
        });
        document.getElementById('a2p4-autosubmit')?.addEventListener('change', async (e) => {
          await _saveDraft();
          showToast(e.target.checked ? 'We’ll auto-submit your texts once your business is approved.' : 'Auto-submit off — you’ll submit your texts yourself.', 'success');
        });

        // Editing any message field re-locks the gate (Clayton: an edit that
        // breaks compliance must re-gate and explain why before submit) and
        // auto-saves the draft.
        ['wiz-camp-description', 'wiz-camp-flow', 'wiz-camp-sample-0', 'wiz-camp-sample-1', 'wiz-camp-sample-2', 'wiz-camp-optin', 'wiz-camp-optout', 'wiz-camp-help', 'wiz-camp-privacy', 'wiz-camp-terms'].forEach((id) => {
          document.getElementById(id)?.addEventListener('input', () => {
            if (id === 'wiz-camp-flow') _flowDirty = true; // coach takes the flow over
            if (_compliancePassed) { _setCompliance(false); _staleAiResults('You edited your messages — re-run the compliance check to unlock submit.'); }
            _setSaveStatus('Unsaved changes…');
            _autoSaveDraft();
          });
        });

        // Rejection banner (post-FAILED) → reveal + populate reason + diagnose button.
        if (campFailed && campReason) {
          document.getElementById('a2p-ai-rejection-banner')?.classList.remove('hidden');
          const re = document.getElementById('a2p-ai-rejection-reason');
          if (re) re.textContent = campReason;
        }

        // ── "Write my privacy & terms text" generator (mockup 4b·) ──
        const _renderPolicyOutput = (privacy, terms) => {
          const out = document.getElementById('a2p4-policy-output');
          if (!out) return;
          const block = (label, text) => `
            <div style="margin-top:10px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:10px 12px;">
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;">
                <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);">${label}</div>
                <button type="button" class="btn-secondary" data-copy-policy="${label}" style="font-size:11px;padding:4px 10px;">Copy</button>
              </div>
              <div data-policy-text style="font-size:12px;color:var(--text);line-height:1.55;white-space:pre-wrap;max-height:240px;overflow:auto;">${escHtml(text)}</div>
            </div>`;
          out.innerHTML = `
            <p style="font-size:11.5px;color:var(--text-muted);line-height:1.5;margin:0 0 4px;">Paste each onto a new page on your own site (e.g. yoursite.com/privacy &amp; /terms), publish, then paste the two links above.</p>
            ${block('Privacy policy', privacy)}
            ${block('SMS terms', terms)}`;
          out.querySelectorAll('[data-copy-policy]').forEach((b) => {
            b.addEventListener('click', async () => {
              const text = b.closest('div').parentElement.querySelector('[data-policy-text]')?.innerText || '';
              try { await navigator.clipboard.writeText(text); const o = b.textContent; b.textContent = 'Copied!'; setTimeout(() => { b.textContent = o; }, 1500); }
              catch { showToast('Copy failed — select the text manually.', 'error'); }
            });
          });
        };
        let _policyCacheChecked = false;
        document.getElementById('a2p4-policy-toggle')?.addEventListener('click', async (e) => {
          e.preventDefault();
          const gen = document.getElementById('a2p4-policy-gen');
          gen?.classList.toggle('hidden');
          // First open: pull any previously-generated text from cache so the coach
          // isn't forced into a fresh (paid) regeneration of text they already have.
          if (!_policyCacheChecked && gen && !gen.classList.contains('hidden')) {
            _policyCacheChecked = true;
            try {
              const res = await fetch('/.netlify/functions/a2p-policy-generate', { method: 'POST', headers: _auth(session), body: JSON.stringify({ action: 'get' }) });
              const json = await res.json().catch(() => ({}));
              if (res.ok && json.hasPolicy && json.privacy && json.terms) {
                _renderPolicyOutput(json.privacy, json.terms);
                const wb = document.getElementById('a2p4-policy-write');
                if (wb) wb.textContent = 'Re-write my privacy & terms text';
              }
            } catch (_) {}
          }
        });
        document.getElementById('a2p4-policy-write')?.addEventListener('click', async () => {
          const btn = document.getElementById('a2p4-policy-write');
          btn.disabled = true; btn.textContent = 'Writing your policy text…';
          try {
            const ls = await _freshSession();
            if (!ls) throw new Error('Session expired. Refresh and sign in.');
            const res = await fetch('/.netlify/functions/a2p-policy-generate', { method: 'POST', headers: _auth(ls), body: JSON.stringify({ action: 'generate' }) });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Could not write your policy text.');
            _renderPolicyOutput(json.privacy, json.terms);
          } catch (err) {
            showToast(err.message, 'error');
          } finally {
            btn.disabled = false; btn.textContent = 'Re-write my privacy & terms text';
          }
        });

        // ── Have AI rewrite (regenerate the whole draft) ──
        document.getElementById('wiz-campaign-generate')?.addEventListener('click', async () => {
          const btn = document.getElementById('wiz-campaign-generate');
          const original = btn.textContent;
          btn.disabled = true; btn.textContent = 'Rewriting…';
          try {
            const ls = await _freshSession();
            if (!ls) throw new Error('Session expired. Refresh and sign in.');
            const res = await fetch('/.netlify/functions/twilio-campaign-draft', { method: 'POST', headers: _auth(ls), body: JSON.stringify({ action: 'generate' }) });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Rewrite failed.');
            _populateDraft(json.draft);
            _flowDirty = false; // fresh template — server owns the flow again
            _setCompliance(false); // new copy must be re-checked
            _staleAiResults('New draft — run the compliance check on it to unlock submit.');
            showToast('Fresh draft ready — review it, then run the compliance check.', 'success');
          } catch (err) {
            showToast(err.message, 'error');
          } finally {
            btn.disabled = false; btn.textContent = original;
          }
        });

        // Write an AI fix straight into a message field, then fire `input` so the
        // gate re-locks + the draft auto-saves (identical to a manual edit).
        const _applyFieldFix = (fieldId, wording) => {
          const el = document.getElementById(fieldId);
          if (!el) return;
          el.value = wording;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        // ── AI compliance checks renderer (shared by pre-check + diagnose) ──
        const _renderAiChecks = (data) => {
          const resultsEl = document.getElementById('a2p-ai-results');
          if (!resultsEl) return;
          const checks = Array.isArray(data?.checks) ? data.checks : [];
          const summary = data?.summary || { fail: 0, warn: 0, pass: 0 };
          const recs = Array.isArray(data?.topRecommendations) ? data.topRecommendations : [];
          const dot = (status) => {
            if (status === 'pass') return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#22c55e;flex-shrink:0;margin-top:5px;"></span>';
            if (status === 'warn') return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#f59e0b;flex-shrink:0;margin-top:5px;"></span>';
            return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ef4444;flex-shrink:0;margin-top:5px;"></span>';
          };
          const headline = data?.notApplicable
            ? '<span style="color:var(--text-muted);font-weight:700;">Not applicable</span>'
            : summary.fail > 0
              ? `<span style="color:var(--danger);font-weight:700;">${summary.fail} to fix before you can submit</span>`
              : `<span style="color:#22c55e;font-weight:700;">&#10003; All checks passed — you're ready to submit</span>`;
          const recsHtml = recs.length
            ? `<div style="margin:8px 0 10px;background:rgba(139,92,246,0.06);border:1px solid rgba(139,92,246,0.2);border-radius:var(--radius-sm);padding:10px 12px;">
                 <div style="font-size:10px;font-weight:600;letter-spacing:0;color:#a78bfa;margin-bottom:6px;">Do this next</div>
                 <ol style="margin:0;padding-left:18px;font-size:12px;color:var(--text);line-height:1.65;">${recs.map(r => `<li>${escHtml(r)}</li>`).join('')}</ol>
               </div>`
            : '';
          // Show the items that need action (fail/warn) in full; collapse the
          // passing ones into a single count so the fixed 18-check list reads as
          // finite progress ("16 passed · 2 to fix"), not an overwhelming wall.
          const actionable = checks.filter(c => c.status !== 'pass');
          const passing = checks.filter(c => c.status === 'pass');
          const fieldFixes = []; // {idx, fieldId, wording} — fixes auto-applyable to a draft field
          const rowsHtml = actionable.map((c, i) => {
            const titleColor = c.status === 'fail' ? 'var(--danger)' : c.status === 'warn' ? '#f59e0b' : 'var(--text)';
            const hasFix = c.fix && (c.fix.wording || c.fix.where);
            const fixId = `ai-fix-${i}`;
            // Auto-applyable when the fix targets a known draft field and gives
            // replacement wording — write it straight in, no copy/paste. (Fixes
            // that target the website / policy pages have no fieldId → Copy only.)
            const canApply = !!(c.fix?.wording && /^wiz-camp-/.test(c.fieldId || '') && document.getElementById(c.fieldId));
            if (canApply) fieldFixes.push({ idx: i, fieldId: c.fieldId, wording: c.fix.wording });
            const fixBlock = hasFix ? `
              <div style="margin-top:6px;">
                <button type="button" class="btn-link" data-toggle-fix="${fixId}" style="font-size:11px;color:#a78bfa;background:none;border:none;cursor:pointer;padding:0;text-decoration:underline;">Show suggested fix &#9662;</button>
                <div id="${fixId}" class="hidden" style="margin-top:6px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 10px;">
                  ${c.fix?.where ? `<div style="font-size:10px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-bottom:4px;">&#128205; ${escHtml(c.fix.where)}</div>` : ''}
                  ${c.fix?.wording ? `<div data-fix-wording style="font-size:12px;color:var(--text);line-height:1.55;white-space:pre-wrap;">${escHtml(c.fix.wording)}</div>` : ''}
                  ${c.fix?.wording ? `<div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
                    ${canApply ? `<button type="button" class="btn-primary" data-apply-fix="${i}" style="font-size:11px;padding:4px 10px;">Apply this fix</button>` : ''}
                    <button type="button" class="btn-secondary" data-copy-fix="${fixId}" style="font-size:11px;padding:4px 10px;">Copy</button>
                  </div>` : ''}
                </div>
              </div>` : '';
            return `
              <div style="padding:8px 0;border-bottom:1px solid var(--border);">
                <div style="display:flex;align-items:flex-start;gap:8px;">
                  ${dot(c.status)}
                  <div style="flex:1;min-width:0;">
                    <div style="font-size:12px;font-weight:600;color:${titleColor};">${escHtml(c.label)}</div>
                    ${c.detail ? `<div style="font-size:11px;color:var(--text-muted);margin-top:2px;line-height:1.5;">${escHtml(c.detail)}</div>` : ''}
                    ${fixBlock}
                  </div>
                </div>
              </div>`;
          }).join('');
          const passingHtml = passing.length ? `
            <div style="padding:8px 0;${actionable.length ? 'border-top:1px solid var(--border);' : ''}">
              <button type="button" class="btn-link" id="ai-passing-toggle" style="font-size:11px;color:#22c55e;font-weight:600;background:none;border:none;cursor:pointer;padding:0;">&#10003; ${passing.length} check${passing.length === 1 ? '' : 's'} passed &#9662;</button>
              <div id="ai-passing-list" class="hidden" style="margin-top:6px;padding-left:14px;">
                ${passing.map(c => `<div style="font-size:11px;color:var(--text-muted);padding:3px 0;">&#10003; ${escHtml(c.label)}</div>`).join('')}
              </div>
            </div>` : '';
          const codesStr = (summary.criticalRejectionCodes || []).length
            ? `<div style="font-size:10px;color:var(--text-muted);margin-top:6px;">Rejection codes evaluated: ${(summary.criticalRejectionCodes || []).map(c => escHtml(c)).join(', ')}</div>`
            : '';
          // One-click: apply every fix that targets a message field, then re-check.
          const applyAllHtml = fieldFixes.length
            ? `<button type="button" id="ai-apply-all" class="btn-primary" style="width:100%;margin:8px 0 4px;font-size:12px;">Apply ${fieldFixes.length} fix${fieldFixes.length === 1 ? '' : 'es'} to your messages &amp; re-check</button>`
            : '';
          resultsEl.innerHTML = `
            <div style="font-size:11px;color:var(--text-muted);margin-bottom:6px;">${headline} · ${summary.pass} passed · ${summary.fail} to fix</div>
            ${applyAllHtml}
            ${recsHtml}
            <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:0 12px;">
              ${(rowsHtml + passingHtml) || '<div style="font-size:11px;color:var(--text-muted);padding:10px 0;">No checks returned.</div>'}
            </div>
            ${codesStr}
            <div style="margin-top:10px;font-size:10px;color:var(--text-muted);">AI: ${escHtml(data?.aiMeta?.model || 'unknown')} · ${data?.aiMeta?.ms || 0}ms · ${data?.aiMeta?.urlsFetched || 0} URL${data?.aiMeta?.urlsFetched === 1 ? '' : 's'} fetched${data?.aiMeta?.urlsFailed ? ` (${data.aiMeta.urlsFailed} failed)` : ''}</div>`;
          resultsEl.style.display = 'block';
          resultsEl.querySelectorAll('[data-toggle-fix]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const id = btn.getAttribute('data-toggle-fix');
              const el = document.getElementById(id);
              if (!el) return;
              const isHidden = el.classList.contains('hidden');
              el.classList.toggle('hidden');
              btn.innerHTML = isHidden ? 'Hide suggested fix &#9652;' : 'Show suggested fix &#9662;';
            });
          });
          resultsEl.querySelectorAll('[data-copy-fix]').forEach((btn) => {
            btn.addEventListener('click', async () => {
              const id = btn.getAttribute('data-copy-fix');
              const el = document.getElementById(id);
              if (!el) return;
              const wording = el.querySelector('[data-fix-wording]') || el;
              const text = (wording.innerText || wording.textContent || '').trim();
              try {
                await navigator.clipboard.writeText(text);
                const orig = btn.textContent;
                btn.textContent = 'Copied!';
                setTimeout(() => { btn.textContent = orig; }, 1500);
              } catch {
                showToast('Copy failed — select the text manually.', 'error');
              }
            });
          });
          resultsEl.querySelectorAll('[data-apply-fix]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const f = fieldFixes.find(x => x.idx === Number(btn.getAttribute('data-apply-fix')));
              if (!f) return;
              _applyFieldFix(f.fieldId, f.wording);
              btn.textContent = 'Applied ✓';
              btn.disabled = true;
            });
          });
          document.getElementById('ai-apply-all')?.addEventListener('click', () => {
            fieldFixes.forEach(f => _applyFieldFix(f.fieldId, f.wording));
            document.getElementById('wiz-a2p-ai-precheck')?.click(); // re-check the updated draft
          });
          document.getElementById('ai-passing-toggle')?.addEventListener('click', () => {
            const l = document.getElementById('ai-passing-list');
            const b = document.getElementById('ai-passing-toggle');
            if (!l || !b) return;
            const open = l.classList.toggle('hidden') === false;
            b.innerHTML = `&#10003; ${passing.length} check${passing.length === 1 ? '' : 's'} passed ${open ? '&#9652;' : '&#9662;'}`;
          });
        };

        // ── Run compliance check (the HARD GATE) ──
        let _aiPrecheckHasRun = false;
        document.getElementById('wiz-a2p-ai-precheck')?.addEventListener('click', async () => {
          const btn = document.getElementById('wiz-a2p-ai-precheck');
          const original = btn.textContent;
          btn.disabled = true; btn.textContent = 'Checking…';
          try {
            const ls = await _freshSession();
            if (!ls) throw new Error('Session expired. Refresh and sign in.');
            const body = {
              draft: _collectDraft(),
              brand: {
                legalName: profile?.businessName || '',
                ein: profile?.ein || '',
                businessType: 'Limited Liability Corporation',
              },
              websiteUrl: profile?.website || '',
              // The coach's opt-in proof page — the AI fetches it like a TCR
              // reviewer would and verifies the consent language is live.
              optInPageUrl: _optInPageUrl,
              bypassCache: _aiPrecheckHasRun,
            };
            const A2P_AI_BASE = 'https://fitops-pro-a2p-ai.clayton-a60.workers.dev';
            const res = await fetch(`${A2P_AI_BASE}/precheck`, { method: 'POST', headers: _auth(ls), body: JSON.stringify(body) });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Compliance check failed.');
            _renderAiChecks(json);
            _setCompliance(!!json.ready);
            _aiPrecheckHasRun = true;
            btn.textContent = 'Re-run compliance check';
            if (!json.ready) {
              showToast(`${json.summary?.fail || 0} item${(json.summary?.fail || 0) === 1 ? '' : 's'} to fix before you can submit.`, 'error');
            }
          } catch (err) {
            const resultsEl = document.getElementById('a2p-ai-results');
            if (resultsEl) { resultsEl.style.display = 'block'; resultsEl.innerHTML = `<div style="font-size:12px;color:var(--danger);">${escHtml(err.message)}</div>`; }
            btn.textContent = original;
          } finally {
            btn.disabled = false;
          }
        });

        // ── Diagnose (post-rejection helper; does NOT change the gate) ──
        document.getElementById('wiz-a2p-ai-diagnose')?.addEventListener('click', async () => {
          const btn = document.getElementById('wiz-a2p-ai-diagnose');
          const original = btn.textContent;
          btn.disabled = true; btn.textContent = 'Analyzing rejection…';
          try {
            const ls = await _freshSession();
            if (!ls) throw new Error('Session expired. Refresh and sign in.');
            const A2P_AI_BASE = 'https://fitops-pro-a2p-ai.clayton-a60.workers.dev';
            const res = await fetch(`${A2P_AI_BASE}/rejection-analyzer`, { method: 'POST', headers: _auth(ls), body: JSON.stringify({ bypassCache: false }) });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Diagnostic failed.');
            _renderAiChecks(json);
            btn.textContent = 'Re-analyze';
          } catch (err) {
            const resultsEl = document.getElementById('a2p-ai-results');
            if (resultsEl) { resultsEl.style.display = 'block'; resultsEl.innerHTML = `<div style="font-size:12px;color:var(--danger);">${escHtml(err.message)}</div>`; }
            btn.textContent = original;
          } finally {
            btn.disabled = false;
          }
        });

        // ── Submit / resubmit (save the edited draft, then register) ──
        async function _doSubmit(btn, isResubmit) {
          if (!_ownUrlsOk()) { showToast('Add your own privacy & terms links first.', 'error'); return; }
          const original = btn.textContent;
          btn.disabled = true; btn.textContent = isResubmit ? 'Resubmitting…' : 'Submitting…';
          try {
            const ls = await _freshSession();
            if (!ls) throw new Error('Session expired. Refresh and sign in.');
            // Persist THIS (possibly edited) copy first — the backend submits the
            // saved draft, not the form. Template-managed flow is server-owned,
            // so only send it when the coach hand-edited it (see _saveDraft).
            const submitDraft = _collectDraft();
            if (!_flowDirty) delete submitDraft.messageFlow;
            const saveRes = await fetch('/.netlify/functions/twilio-campaign-draft', { method: 'POST', headers: _auth(ls), body: JSON.stringify({ action: 'save', draft: submitDraft }) });
            if (!saveRes.ok) { const j = await saveRes.json().catch(() => ({})); throw new Error(j.error || 'Could not save your messages.'); }
            // Create (first, free) or edit-and-resubmit (billed) — same endpoint.
            const res = await fetch('/.netlify/functions/twilio-register-campaign', { method: 'POST', headers: _auth(ls), body: '{}' });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json.error || 'Submission failed.');
            // Submission succeeded — render the confirmation defensively so a DOM
            // hiccup here can't be caught below and mis-reported as a failure (the
            // campaign is already submitted, and a resubmit already spent the fee).
            const area = document.getElementById('a2p4-submit-area');
            if (area) area.innerHTML = `
              <div style="text-align:center;padding:14px 0;">
                <div style="font-size:30px;">&#9989;</div>
                <div style="font-size:15px;font-weight:700;color:var(--text);margin-top:4px;">${isResubmit ? 'Resubmitted for review' : 'Submitted for review'}</div>
                <p style="font-size:12px;color:var(--text-muted);margin-top:6px;line-height:1.55;">Your messages are with the carriers now (typically 1–2 business days). We’ll email you the moment they’re approved — then sending turns on automatically.</p>
              </div>`;
            _syncFooter(false, 'Finish'); // submitted — now they can advance/close
          } catch (err) {
            showToast(err.message, 'error');
            btn.disabled = false; btn.textContent = original;
          }
        }

        // Initial gate render (locked until the compliance check passes).
        _renderSubmitArea();

        // Auto-fill the privacy/terms URL fields from the coach's own website
        // (discovered server-side via homepage links → sitemap → common paths),
        // so they don't retype what they entered in step 3. Only fills empty
        // fields; if nothing resolves, they stay empty + required. Fire-and-forget.
        (async () => {
          const pEl = document.getElementById('wiz-camp-privacy');
          const tEl = document.getElementById('wiz-camp-terms');
          const needP = pEl && !pEl.value.trim();
          const needT = tEl && !tEl.value.trim();
          const site = (profile?.website || '').trim();
          if (!site || (!needP && !needT)) return;
          try {
            const ls = await _freshSession();
            if (!ls) return;
            const res = await fetch('/.netlify/functions/a2p-discover-urls', { method: 'POST', headers: _auth(ls), body: JSON.stringify({ website: site }) });
            const json = await res.json().catch(() => ({}));
            let filled = false;
            if (needP && json.privacyUrl && pEl && !pEl.value.trim()) { pEl.value = json.privacyUrl; filled = true; }
            if (needT && json.termsUrl && tEl && !tEl.value.trim()) { tEl.value = json.termsUrl; filled = true; }
            if (filled) {
              _autoSaveDraft();
              _renderSubmitArea(); // URLs may now satisfy the gate's link requirement
              showToast('Found your privacy & terms links on your site — confirm or edit them.', 'success');
            }
          } catch (_) {}
        })();
      },
    },
  ];

  let _commsChannelCache = null; // cached channel data from coach-channels-get
  let _bhToggledByCoach = false; // voice-3: whether the coach actually clicked "Enable business hours"

  async function getChannelCache(force) {
    if (_commsChannelCache && !force) return _commsChannelCache;
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/coach-channels-get', {
        headers: { Authorization: 'Bearer ' + session.access_token },
      });
      if (res.ok) {
        const { channels } = await res.json();
        _commsChannelCache = channels?.find(c => c.channel === 'sms') || {};
      }
    } catch {}
    return _commsChannelCache || {};
  }

  async function saveChannelUpdate(body) {
    const supabase = _config.getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/.netlify/functions/coach-channels-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
      body: JSON.stringify(body),
    });
    // A refused write (RLS, permission, constraint) must not be read as a
    // success: don't touch the cache, and tell the caller so it can stop
    // the wizard and tell the coach — see each VOICE_STEPS call site's catch.
    if (!res.ok) throw new Error('Could not save. Try again.');
    // Refresh from the server instead of discarding — the wizard's later
    // steps read this cache synchronously with no re-fetch of their own, so
    // nulling it here (the old behaviour) made every step after the first
    // render blank and then save those blanks straight back to the coach's
    // row. See voice-settings-investigation.md (2026-09-10).
    await getChannelCache(true);
  }

  // ── Signal House A2P Registration Wizard ─────────────────────────
  // Three-step flow that drives the SH provisioning pipeline:
  //   Step 1: Brand registration (subgroup auto-created if missing)
  //   Step 2: Phone number search + purchase
  //   Step 3: Campaign registration + submission
  //
  // Each step polls /signalhouse-status for current state and reflects
  // it inline. The Next button auto-enables when the current step's
  // backend state reaches a terminal "done" value (VERIFIED, READY,
  // PENDING_REVIEW respectively).
  let _shWizPollers = []; // setInterval handles, cleaned up on step change/unmount

  function _shClearPollers() {
    while (_shWizPollers.length) clearInterval(_shWizPollers.pop());
  }

  async function _shFetchStatus(session) {
    try {
      const res = await fetch('/.netlify/functions/signalhouse-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'status' }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }

  // Wizard step titles use generic labels for non-owners (white-label) and
  // provider-tagged labels for owners (debugging clarity). Helper picks one.
  const _shStepTitle = (generic, withProvider) =>
    window._appIsOwner ? withProvider : generic;
  // String-level swap: returns the owner term (e.g. "Signal House") for
  // owners, the generic term for non-owners. Use inside template literals
  // wherever provider names appear in coach-facing copy.
  const _shLabel = (genericTerm, ownerTerm) =>
    window._appIsOwner ? ownerTerm : genericTerm;

  const SH_A2P_STEPS = [
    {
      id: 'sh-brand',
      get title() { return _shStepTitle('Register Brand', 'Signal House — Register Brand'); },
      render: () => `
        <p class="comms-modal-desc">Register your business with carriers so you can send SMS. Brand verification is automated and typically completes in 1–5 minutes.</p>
        <div id="sh-brand-state"></div>
        <div id="sh-brand-form" class="hidden">
          <div class="form-grid">
            <div class="form-group form-col-2"><label>Business Name <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-biz" placeholder="e.g. Revolution Fitness LLC" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Must match your IRS-registered legal name exactly.</span></div>
            <div class="form-group form-col-2"><label>EIN <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-ein" placeholder="XX-XXXXXXX" maxlength="10" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">9-digit federal tax ID. Hyphen optional.</span></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-2"><label>Display Name <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-display" placeholder="e.g. Revolution Fitness" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Shorter, customer-facing version.</span></div>
            <div class="form-group form-col-2"><label>Vertical</label>
              <select id="sh-wiz-vertical" style="width:100%;height:38px;padding:0 12px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">
                <option value="HEALTHCARE">Healthcare / Wellness / Fitness</option>
                <option value="PROFESSIONAL">Professional Services</option>
                <option value="HOSPITALITY">Hospitality</option>
                <option value="EDUCATION">Education</option>
                <option value="RETAIL">Retail</option>
                <option value="TECHNOLOGY">Technology</option>
              </select>
            </div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-2"><label>Business Email <span style="color:var(--danger);">*</span></label><input type="email" id="sh-wiz-email" placeholder="you@yourdomain.com" /></div>
            <div class="form-group form-col-2"><label>Business Phone <span style="color:var(--danger);">*</span></label><input type="tel" id="sh-wiz-phone" placeholder="+15551234567" /></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group"><label>Street Address <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-street" placeholder="123 Main St" /></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-3"><label>City <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-city" placeholder="Owatonna" /></div>
            <div class="form-group form-col-3"><label>State <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-state" placeholder="MN" maxlength="2" style="text-transform:uppercase;" /></div>
            <div class="form-group form-col-3"><label>Zip <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-zip" placeholder="55060" maxlength="10" /></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-2"><label>Your First Name <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-first" placeholder="Clayton" /></div>
            <div class="form-group form-col-2"><label>Your Last Name <span style="color:var(--danger);">*</span></label><input type="text" id="sh-wiz-last" placeholder="Nicholson" /></div>
          </div>
          <button class="btn-primary" id="sh-wiz-submit-brand" style="margin-top:14px;width:100%;">Submit Brand Registration ($4.50)</button>
          <div id="sh-wiz-brand-result" style="margin-top:12px;"></div>
        </div>`,
      onMount: async () => {
        _shClearPollers();
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();

        const stateEl = document.getElementById('sh-brand-state');
        const formEl = document.getElementById('sh-brand-form');

        // Pre-fill from existing coach record if available
        try {
          const { data: { user } } = await supabase.auth.getUser();
          // ⚠️ `ein` AND `business_address` ARE NOT COLUMNS. Selecting them
          // rejected the whole query, so this pre-filled NOTHING — see
          // modules/a2p-prefill.js for the rest of the story.
          const { data: coach } = await supabase
            .from('coaches')
            .select('name, email, business_name, settings')
            .eq('id', user.id)
            .single();
          const pf = a2pPrefill(coach);
          const fillIfEmpty = (id, val) => {
            const el = document.getElementById(id);
            if (el && val && !el.value) el.value = val;
          };
          fillIfEmpty('sh-wiz-biz', pf.businessName);
          fillIfEmpty('sh-wiz-display', pf.displayName);
          fillIfEmpty('sh-wiz-ein', pf.ein);
          fillIfEmpty('sh-wiz-email', pf.email);
          fillIfEmpty('sh-wiz-phone', pf.phone);
          fillIfEmpty('sh-wiz-street', pf.street);
          fillIfEmpty('sh-wiz-city', pf.city);
          fillIfEmpty('sh-wiz-state', pf.state);
          fillIfEmpty('sh-wiz-zip', pf.zip);
          fillIfEmpty('sh-wiz-first', pf.firstName);
          fillIfEmpty('sh-wiz-last', pf.lastName);
        } catch (e) {
          console.warn('[sh-wizard] pre-fill failed:', e.message);
        }

        const status = await _shFetchStatus(session);
        const brandStage = status?.stages?.brand;
        const brandStatus = String(brandStage?.status || '').toUpperCase();

        const renderBrandStatus = (s) => {
          const id = brandStage?.id || '—';
          const st = String(s || '').toUpperCase();
          if (st === 'VERIFIED' || st === 'VETTED_VERIFIED') {
            stateEl.innerHTML = `<div style="background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#22c55e;margin-bottom:4px;">Brand verified</div><div style="font-size:13px;color:var(--text);">Brand <code>${id}</code> is verified. Click Next to purchase a phone number.</div></div>`;
            formEl.classList.add('hidden');
            const nextBtn = document.getElementById('comms-modal-next');
            if (nextBtn) nextBtn.disabled = false;
          } else if (st === 'UNVERIFIED' || st === 'FAILED') {
            stateEl.innerHTML = `<div style="background:rgba(239,68,68,0.08);border:1px solid rgba(239,68,68,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--danger);margin-bottom:4px;">Verification failed</div><div style="font-size:13px;color:var(--text);margin-bottom:8px;">${escHtml(status?.failureReason || 'Brand identity could not be verified. Most often this means the company name + EIN combination does not match what is registered with the IRS. You can submit an appeal with supporting tax documents (SS-4 letter).')}</div><div style="font-size:11px;color:var(--text-muted);">Brand ID: <code>${id}</code></div></div>`;
            formEl.classList.add('hidden');
          } else if (st === 'PENDING_CREATION' || st === 'PENDING_APPROVAL' || st === 'PENDING') {
            stateEl.innerHTML = `<div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#f59e0b;margin-bottom:4px;">Verification in progress</div><div style="font-size:13px;color:var(--text);">Brand <code>${id}</code> submitted to TCR. Typically verifies in 1–5 minutes. Polling for updates every 10 seconds…</div></div>`;
            formEl.classList.add('hidden');
            const poll = setInterval(async () => {
              const s2 = await _shFetchStatus(session);
              const next = String(s2?.stages?.brand?.status || '').toUpperCase();
              if (next && next !== st) {
                _shClearPollers();
                renderBrandStatus(next);
              }
            }, 10000);
            _shWizPollers.push(poll);
          } else {
            stateEl.innerHTML = '';
            formEl.classList.remove('hidden');
          }
        };
        renderBrandStatus(brandStatus);

        document.getElementById('sh-wiz-submit-brand')?.addEventListener('click', async () => {
          const get = (id) => document.getElementById(id)?.value?.trim();
          const businessName = get('sh-wiz-biz');
          const ein = get('sh-wiz-ein')?.replace(/\D/g, '');
          const displayName = get('sh-wiz-display');
          const vertical = get('sh-wiz-vertical');
          const email = get('sh-wiz-email');
          const phone = get('sh-wiz-phone');
          const street = get('sh-wiz-street');
          const city = get('sh-wiz-city');
          const state = get('sh-wiz-state')?.toUpperCase();
          const postalCode = get('sh-wiz-zip');
          const firstName = get('sh-wiz-first');
          const lastName = get('sh-wiz-last');

          const result = document.getElementById('sh-wiz-brand-result');
          if (!businessName || !ein || !email || !phone || !street || !city || !state || !postalCode || !firstName || !lastName) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">Please fill all required fields marked with *</div>`;
            return;
          }

          const submitBtn = document.getElementById('sh-wiz-submit-brand');
          submitBtn.disabled = true;
          submitBtn.textContent = 'Submitting…';

          const res = await fetch('/.netlify/functions/signalhouse-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({
              businessName, displayName, ein, vertical,
              email, phone, street, city, state, postalCode,
              firstName, lastName, country: 'US',
              entityType: 'PRIVATE_PROFIT',
            }),
          });
          const json = await res.json();
          if (!res.ok) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">${escHtml(json.error || 'Submission failed.')}</div>`;
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit Brand Registration ($4.50)';
            return;
          }
          result.innerHTML = `<div style="color:#22c55e;font-size:13px;">Submitted. Brand ID <code>${escHtml(json.brandId || '')}</code>. Verifying…</div>`;
          renderBrandStatus(json.status || 'PENDING_CREATION');
        });
      },
    },

    {
      id: 'sh-number',
      get title() { return _shStepTitle('Get Phone Number', 'Signal House — Get Phone Number'); },
      render: () => `
        <p class="comms-modal-desc">Get a phone number for SMS. Buy a brand-new number from SH's pool, or port your existing number from Twilio (or another carrier).</p>
        <div id="sh-number-state"></div>

        <!-- Mode toggle: Buy New vs Port Existing -->
        <div id="sh-number-modes" style="display:flex;gap:2px;padding:4px;background:var(--surface-alt,rgba(100,116,139,0.06));border:1px solid var(--border);border-radius:var(--radius-sm);margin-bottom:14px;max-width:fit-content;">
          <button type="button" class="sh-mode-btn active" data-mode="buy" style="padding:6px 14px;background:var(--surface);border:none;color:var(--text);font-size:0.82rem;font-weight:500;cursor:pointer;border-radius:4px;box-shadow:0 1px 2px rgba(0,0,0,0.08);">Buy New Number</button>
          <button type="button" class="sh-mode-btn" data-mode="port" style="padding:6px 14px;background:transparent;border:none;color:var(--text-muted);font-size:0.82rem;font-weight:500;cursor:pointer;border-radius:4px;">Port Existing Number</button>
        </div>

        <!-- Buy mode form -->
        <div id="sh-number-search" class="sh-number-mode" data-mode="buy">
          <div class="form-grid">
            <div class="form-group form-col-2"><label>Area Code <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="sh-wiz-areacode" placeholder="e.g. 507" maxlength="3" style="text-align:center;" /></div>
            <div class="form-group form-col-2"><label>State <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="sh-wiz-numstate" placeholder="e.g. MN" maxlength="2" style="text-transform:uppercase;text-align:center;" /></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-3"><label>Contains digits <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="sh-wiz-numcontains" placeholder="e.g. 8888 or 4128" maxlength="10" style="text-align:center;" /></div>
            <div class="form-group form-col-3"><label>Ends with <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><input type="text" id="sh-wiz-numends" placeholder="e.g. 00 or 8100" maxlength="4" style="text-align:center;" /></div>
            <div class="form-group form-col-3"><label>How many results</label>
              <select id="sh-wiz-numlimit" style="width:100%;height:38px;padding:0 12px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">
                <option value="10">10 numbers</option>
                <option value="25">25 numbers</option>
                <option value="50">50 numbers</option>
              </select>
            </div>
          </div>
          <div style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">
            <strong>Tip:</strong> SH's pool is capped at ~10 numbers per area code, so rare patterns like <code>00</code> are often unavailable. Try multiple area codes or port your existing number instead.
          </div>
          <button class="btn-secondary" id="sh-wiz-search-numbers" style="margin-top:12px;">Search Available Numbers</button>
          <div id="sh-wiz-number-results" style="margin-top:14px;"></div>
        </div>

        <!-- Port mode form -->
        <div id="sh-number-port" class="sh-number-mode hidden" data-mode="port">
          <p style="font-size:13px;color:var(--text-muted);margin-bottom:14px;line-height:1.5;">
            Port an existing number from another carrier into your SMS service. Manually processed by our carrier partner — typically <strong>48 hours</strong> for SMS-only ports, <strong>~7 days</strong> for full ports (SMS + voice). The number stays active on the old carrier until the cutover, so no SMS gap.
          </p>

          <div class="form-grid">
            <div class="form-group form-col-2"><label>Phone Number to Port <span style="color:var(--danger);">*</span></label><input type="tel" id="sh-port-num" placeholder="+15074128900" /></div>
            <div class="form-group form-col-2"><label>Number Type</label>
              <select id="sh-port-numtype" style="width:100%;height:38px;padding:0 12px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">
                <option value="Landline/VOIP">Landline / VOIP (Twilio, RingCentral, etc.)</option>
                <option value="Cell">Cell phone (carrier billed account)</option>
              </select>
            </div>
          </div>

          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-2"><label>Port Type</label>
              <select id="sh-port-service" style="width:100%;height:38px;padding:0 12px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">
                <option value="Messaging Service">Messaging only (SMS/MMS) — 48 hours</option>
                <option value="Entire Service">Entire service (SMS + voice) — 7 days</option>
              </select>
              <span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Messaging-only keeps voice on the old carrier.</span>
            </div>
            <div class="form-group form-col-2 sh-port-cell-only hidden"><label>Account Number <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-acctnum" placeholder="From your carrier bill" /></div>
            <div class="form-group form-col-2 sh-port-entire-only hidden"><label>PIN / Passcode <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-pin" placeholder="Your carrier transfer PIN" /></div>
          </div>

          <div class="form-grid sh-port-entire-only hidden" style="margin-top:8px;">
            <div class="form-group"><label>Latest Bill (PDF) <span style="color:var(--danger);">*</span></label><input type="file" id="sh-port-bill" accept="application/pdf" style="padding:6px 0;" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Required for entire-service ports. Max 5 MB. Used to verify ownership.</span></div>
          </div>

          <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-top:18px;margin-bottom:6px;">Owner info (matches your carrier bill)</div>
          <div class="form-grid">
            <div class="form-group form-col-2"><label>Owner Name (as on bill) <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-owner" placeholder="Revolution Fitness LLC" /></div>
            <div class="form-group form-col-2"><label>Owner Email <span style="color:var(--danger);">*</span></label><input type="email" id="sh-port-email" placeholder="you@yourdomain.com" /></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group"><label>Authorized Person Name <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-authperson" placeholder="Clayton Nicholson" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Person legally authorized to transfer the number — usually you.</span></div>
          </div>

          <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-top:18px;margin-bottom:6px;">Billing address (matches your carrier bill)</div>
          <div class="form-grid">
            <div class="form-group"><label>Street Address <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-street" placeholder="210 N Oak Ave Apt 207" /></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-3"><label>City <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-city" placeholder="Owatonna" /></div>
            <div class="form-group form-col-3"><label>State <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-state" placeholder="MN" maxlength="2" style="text-transform:uppercase;" /></div>
            <div class="form-group form-col-3"><label>Zip <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-zip" placeholder="55060" /></div>
          </div>

          <div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--text-muted);margin-top:18px;margin-bottom:6px;">Signature (electronic)</div>
          <div class="form-grid">
            <div class="form-group"><label>Type Your Full Name to Sign <span style="color:var(--danger);">*</span></label><input type="text" id="sh-port-signature" placeholder="Clayton Nicholson" style="font-family:cursive;font-style:italic;font-size:18px;" /><span style="font-size:10px;color:var(--text-muted);margin-top:2px;display:block;">Typing your name here serves as your electronic signature authorizing the port-in.</span></div>
          </div>

          <div class="form-grid" style="margin-top:14px;">
            <div class="form-group"><label>Comments <span style="font-weight:400;color:var(--text-muted);">(optional)</span></label><textarea id="sh-port-comments" rows="2" placeholder="Any additional info for SH staff..." style="width:100%;padding:8px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);"></textarea></div>
          </div>

          <button class="btn-primary" id="sh-wiz-submit-port" style="margin-top:14px;width:100%;">Submit Port-In Request</button>
          <div id="sh-wiz-port-result" style="margin-top:12px;"></div>
        </div>`,
      onMount: async () => {
        _shClearPollers();
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();

        const stateEl = document.getElementById('sh-number-state');
        const searchEl = document.getElementById('sh-number-search');
        const portEl = document.getElementById('sh-number-port');
        const modesEl = document.getElementById('sh-number-modes');

        const status = await _shFetchStatus(session);
        const num = status?.stages?.number;

        if (num?.phoneNumber) {
          const display = num.phoneNumber.replace(/(\d)(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
          stateEl.innerHTML = `<div style="background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#22c55e;margin-bottom:4px;">Number set</div><div style="font-size:14px;font-weight:600;color:var(--text);">${escHtml(display)}</div><div style="font-size:11px;color:var(--text-muted);margin-top:4px;">Status: ${escHtml(num.status || 'Pending campaign')}</div></div>`;
          searchEl?.classList.add('hidden');
          portEl?.classList.add('hidden');
          modesEl?.classList.add('hidden');
          const nextBtn = document.getElementById('comms-modal-next');
          if (nextBtn) nextBtn.disabled = false;
          return;
        }

        // ── Mode toggle (Buy New / Port Existing) ──
        document.querySelectorAll('.sh-mode-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            const mode = btn.dataset.mode;
            // Visual state
            document.querySelectorAll('.sh-mode-btn').forEach(b => {
              const active = b.dataset.mode === mode;
              b.style.background = active ? 'var(--surface)' : 'transparent';
              b.style.color = active ? 'var(--text)' : 'var(--text-muted)';
              b.style.boxShadow = active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none';
              b.classList.toggle('active', active);
            });
            // Show the matching panel
            document.querySelectorAll('.sh-number-mode').forEach(panel => {
              panel.classList.toggle('hidden', panel.dataset.mode !== mode);
            });
          });
        });

        // ── Port-in mode: pre-fill from coach record + sub-toggle for fields ──
        try {
          const { data: { user } } = await supabase.auth.getUser();
          // ⚠️ `business_address` IS NOT A COLUMN — same silent rejection as
          // the wizard pre-fill above.
          const { data: coach } = await supabase
            .from('coaches')
            .select('name, email, business_name, settings')
            .eq('id', user.id)
            .single();
          const pf = a2pPrefill(coach);
          const fillIfEmpty = (id, val) => {
            const el = document.getElementById(id);
            if (el && val && !el.value) el.value = val;
          };
          fillIfEmpty('sh-port-owner', pf.displayName);
          fillIfEmpty('sh-port-email', pf.email);
          fillIfEmpty('sh-port-authperson', pf.authPerson);
          fillIfEmpty('sh-port-street', pf.street);
          fillIfEmpty('sh-port-city', pf.city);
          fillIfEmpty('sh-port-state', pf.state);
          fillIfEmpty('sh-port-zip', pf.zip);
        } catch {}

        // Show/hide conditional fields based on number type + port type
        const updateConditionalPortFields = () => {
          const isCell = document.getElementById('sh-port-numtype')?.value === 'Cell';
          const isEntire = document.getElementById('sh-port-service')?.value === 'Entire Service';
          document.querySelectorAll('.sh-port-cell-only').forEach(el => el.classList.toggle('hidden', !isCell));
          document.querySelectorAll('.sh-port-entire-only').forEach(el => el.classList.toggle('hidden', !isEntire));
        };
        document.getElementById('sh-port-numtype')?.addEventListener('change', updateConditionalPortFields);
        document.getElementById('sh-port-service')?.addEventListener('change', updateConditionalPortFields);

        // ── Port submit handler ──
        document.getElementById('sh-wiz-submit-port')?.addEventListener('click', async () => {
          const get = (id) => document.getElementById(id)?.value?.trim();
          const phoneNumber = get('sh-port-num')?.replace(/\D/g, '');
          const cellNumberOrLandline = get('sh-port-numtype');
          const portingService = get('sh-port-service');
          const accountNumber = get('sh-port-acctnum');
          const pinOrPasscode = get('sh-port-pin');
          const ownersName = get('sh-port-owner');
          const ownersEmail = get('sh-port-email');
          const authorizedPersonsName = get('sh-port-authperson');
          const ownersStreetAddress = get('sh-port-street');
          const ownersCity = get('sh-port-city');
          const ownersState = get('sh-port-state')?.toUpperCase();
          const ownersPostalCode = get('sh-port-zip');
          const signatureName = get('sh-port-signature');
          const comments = get('sh-port-comments');

          const result = document.getElementById('sh-wiz-port-result');
          if (!phoneNumber || !ownersName || !ownersEmail || !authorizedPersonsName || !ownersStreetAddress || !ownersCity || !ownersState || !ownersPostalCode || !signatureName) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">Please fill all required fields marked with *</div>`;
            return;
          }
          if (cellNumberOrLandline === 'Cell' && !accountNumber) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">Account Number is required for cell phone ports.</div>`;
            return;
          }
          if (portingService === 'Entire Service' && !pinOrPasscode) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">PIN/Passcode is required for entire-service ports.</div>`;
            return;
          }

          // Render the typed signature into a small canvas → base64 PNG.
          // Cursive italic styling makes it look like a signature on paper.
          const sigCanvas = document.createElement('canvas');
          sigCanvas.width = 400; sigCanvas.height = 80;
          const sCtx = sigCanvas.getContext('2d');
          sCtx.fillStyle = '#fff'; sCtx.fillRect(0, 0, sigCanvas.width, sigCanvas.height);
          sCtx.fillStyle = '#000';
          sCtx.font = 'italic 32px cursive';
          sCtx.fillText(signatureName, 20, 50);
          const signatureUrl = sigCanvas.toDataURL('image/png');

          // For entire-service ports, read the bill PDF as base64
          let billingUrl;
          const billFile = document.getElementById('sh-port-bill')?.files?.[0];
          if (portingService === 'Entire Service') {
            if (!billFile) {
              result.innerHTML = `<div style="color:var(--danger);font-size:13px;">Latest bill PDF is required for entire-service ports.</div>`;
              return;
            }
            if (billFile.size > 5 * 1024 * 1024) {
              result.innerHTML = `<div style="color:var(--danger);font-size:13px;">Bill PDF must be under 5 MB.</div>`;
              return;
            }
            billingUrl = await new Promise((resolve, reject) => {
              const r = new FileReader();
              r.onload = () => resolve(r.result);
              r.onerror = () => reject(r.error);
              r.readAsDataURL(billFile);
            });
          }

          const submitBtn = document.getElementById('sh-wiz-submit-port');
          submitBtn.disabled = true;
          submitBtn.textContent = 'Submitting…';

          const res = await fetch('/.netlify/functions/signalhouse-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({
              action: 'port-number',
              phoneNumber,
              portingRequestType: 'Business',
              businessName: ownersName,
              portingService,
              cellNumberOrLandline,
              accountNumber,
              pinOrPasscode,
              billingUrl,
              ownersName,
              ownersEmail,
              authorizedPersonsName,
              ownersStreetAddress,
              ownersCountry: 'US',
              ownersState,
              ownersCity,
              ownersPostalCode,
              serviceAddress: ownersStreetAddress,
              serviceCountry: 'US',
              serviceState: ownersState,
              serviceCity: ownersCity,
              servicePostalCode: ownersPostalCode,
              signatureUrl,
              comments,
            }),
          });
          const json = await res.json();
          if (!res.ok) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">${escHtml(json.error || 'Port-in submission failed.')}</div>`;
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit Port-In Request';
            return;
          }
          const display = String(phoneNumber).replace(/(\d)(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
          const fallbackPortMsg = _shLabel('Carrier staff will process. SMS-only ports take ~48 hours.', 'Signal House staff will process. SMS-only ports take ~48 hours.');
          stateEl.innerHTML = `<div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#f59e0b;margin-bottom:4px;">Port-in submitted</div><div style="font-size:14px;font-weight:600;color:var(--text);">${escHtml(display)}</div><div style="font-size:12px;color:var(--text-muted);margin-top:6px;">Port request ID: <code>${escHtml(json.portRequestId || '—')}</code></div><div style="font-size:12px;color:var(--text-muted);margin-top:4px;">${escHtml(json.message || fallbackPortMsg)}</div></div>`;
          searchEl?.classList.add('hidden');
          portEl?.classList.add('hidden');
          modesEl?.classList.add('hidden');
          const nextBtn = document.getElementById('comms-modal-next');
          if (nextBtn) nextBtn.disabled = false;
        });

        document.getElementById('sh-wiz-search-numbers')?.addEventListener('click', async () => {
          const ac = document.getElementById('sh-wiz-areacode')?.value?.trim();
          const st = document.getElementById('sh-wiz-numstate')?.value?.trim()?.toUpperCase();
          const contains = document.getElementById('sh-wiz-numcontains')?.value?.trim()?.replace(/\D/g, '');
          const endsWith = document.getElementById('sh-wiz-numends')?.value?.trim()?.replace(/\D/g, '');
          const limit = parseInt(document.getElementById('sh-wiz-numlimit')?.value || '10', 10);
          const results = document.getElementById('sh-wiz-number-results');
          results.innerHTML = '<div style="font-size:13px;color:var(--text-muted);">Searching…</div>';

          const res = await fetch('/.netlify/functions/signalhouse-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({ action: 'search-numbers', areaCode: ac, state: st, contains, endsWith, limit }),
          });
          const json = await res.json();
          if (!res.ok) {
            results.innerHTML = `<div style="font-size:13px;color:var(--danger);">${escHtml(json.error || 'Search failed.')}</div>`;
            return;
          }
          if (!json.numbers?.length) {
            const hasFilter = !!(contains || endsWith);
            const msg = hasFilter
              ? `No numbers matching that pattern in the current pool. Available numbers per area code are limited, so rare patterns like "${escHtml(endsWith || contains)}" are often unavailable. Try a different area code, drop the pattern filter, or check back later — pools refresh periodically.`
              : 'No numbers available in that area code right now. Try a different area code or state.';
            results.innerHTML = `<div style="font-size:13px;color:var(--text-muted);">${msg}</div>`;
            return;
          }

          results.innerHTML = json.numbers.map(n => {
            const display = String(n.phoneNumber).replace(/(\d)(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
            const monthly = n.price?.pricePerMonth ? '$' + n.price.pricePerMonth + '/mo' : '$0.25/mo';
            return `<div style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius-sm);margin-bottom:6px;">
              <div><span style="font-weight:600;font-size:14px;">${escHtml(display)}</span> <span style="font-size:11px;color:var(--text-muted);margin-left:8px;">${(n.capabilities || []).join(', ')} • ${monthly}</span></div>
              <button class="btn-primary sh-buy-num" data-num="${escHtml(n.phoneNumber)}" style="padding:6px 14px;font-size:12px;">Buy</button>
            </div>`;
          }).join('');

          results.querySelectorAll('.sh-buy-num').forEach(btn => {
            btn.addEventListener('click', async () => {
              const phoneNumber = btn.dataset.num;
              btn.disabled = true;
              btn.textContent = 'Purchasing…';
              const buyRes = await fetch('/.netlify/functions/signalhouse-register-brand', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
                body: JSON.stringify({ action: 'purchase-number', phoneNumber }),
              });
              const buyJson = await buyRes.json();
              if (!buyRes.ok) {
                btn.disabled = false;
                btn.textContent = 'Buy';
                results.insertAdjacentHTML('afterbegin', `<div style="color:var(--danger);font-size:13px;margin-bottom:8px;">${escHtml(buyJson.error || 'Purchase failed')}</div>`);
                return;
              }
              const display = String(phoneNumber).replace(/(\d)(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
              stateEl.innerHTML = `<div style="background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#22c55e;margin-bottom:4px;">Number purchased</div><div style="font-size:14px;font-weight:600;color:var(--text);">${escHtml(display)}</div><div style="font-size:11px;color:var(--text-muted);margin-top:4px;">Webhook will confirm in &lt;1 min. Click Next to set up the campaign.</div></div>`;
              searchEl.classList.add('hidden');
              const nextBtn = document.getElementById('comms-modal-next');
              if (nextBtn) nextBtn.disabled = false;
            });
          });
        });
      },
    },

    {
      id: 'sh-campaign',
      get title() { return _shStepTitle('Set Up Messaging Campaign', 'Signal House — Set Up Messaging Campaign'); },
      render: () => `
        <p class="comms-modal-desc">A campaign tells carriers what kind of messages you'll send. Most fields are auto-filled — review and submit. Approval typically takes 2–3 business days.</p>
        <div id="sh-campaign-state"></div>
        <div id="sh-campaign-form">
          <div class="form-grid">
            <div class="form-group form-col-2"><label>Use Case</label>
              <select id="sh-wiz-usecase" style="width:100%;height:38px;padding:0 12px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">
                <option value="MIXED">Mixed (general business + marketing)</option>
                <option value="CUSTOMER_CARE">Customer Care</option>
                <option value="LOW_VOLUME">Low Volume (cheaper, &lt;6K msgs/day)</option>
                <option value="MARKETING">Marketing</option>
              </select>
            </div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group"><label>Campaign Description (40+ chars)</label><textarea id="sh-wiz-camp-desc" rows="3" style="width:100%;padding:8px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">Personal fitness coaching messages for clients enrolled in our program — workout reminders, check-in prompts, schedule confirmations, and 1:1 coach replies.</textarea></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group"><label>Message Flow (how clients opt in)</label><textarea id="sh-wiz-camp-flow" rows="3" style="width:100%;padding:8px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);">Clients sign our coaching agreement which includes explicit SMS consent. They confirm their phone number during onboarding through our app.</textarea></div>
          </div>
          <div class="form-grid" style="margin-top:8px;">
            <div class="form-group form-col-2"><label>Privacy Policy URL</label><input type="text" id="sh-wiz-camp-privacy" value="https://app.fitopspro.com/privacy" /></div>
            <div class="form-group form-col-2"><label>Terms of Service URL</label><input type="text" id="sh-wiz-camp-tos" value="https://app.fitopspro.com/terms" /></div>
          </div>
          <button class="btn-primary" id="sh-wiz-submit-campaign" style="margin-top:14px;width:100%;">Submit Campaign for Review ($15)</button>
          <div id="sh-wiz-campaign-result" style="margin-top:12px;"></div>
        </div>`,
      onMount: async () => {
        _shClearPollers();
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();

        const stateEl = document.getElementById('sh-campaign-state');
        const formEl = document.getElementById('sh-campaign-form');

        const renderCampaignStatus = (s) => {
          const st = String(s || '').toUpperCase();
          if (st === 'ACTIVE') {
            stateEl.innerHTML = `<div style="background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#22c55e;margin-bottom:4px;">Campaign approved</div><div style="font-size:13px;">Your number is now live for SMS.</div></div>`;
            formEl.classList.add('hidden');
            const nextBtn = document.getElementById('comms-modal-next');
            if (nextBtn) nextBtn.disabled = false;
          } else if (st === 'REJECTED') {
            stateEl.innerHTML = `<div style="background:rgba(239,68,68,0.08);border:1px solid rgba(239,68,68,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:var(--danger);margin-bottom:4px;">Campaign rejected</div><div style="font-size:13px;">Edit the campaign details and resubmit.</div></div>`;
            formEl.classList.remove('hidden');
          } else if (st === 'PENDING_REVIEW' || st === 'PENDING_DCA_APPROVAL' || st === 'PENDING') {
            stateEl.innerHTML = `<div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.3);border-radius:var(--radius-sm);padding:14px;margin-bottom:14px;"><div style="font-size:11px;font-weight:600;letter-spacing:0;color:#f59e0b;margin-bottom:4px;">Under review</div><div style="font-size:13px;">Campaign submitted to ${_shLabel('carriers', 'Signal House')} for approval. Typically 2–3 business days. You can close this wizard — we'll notify you when it's approved.</div></div>`;
            formEl.classList.add('hidden');
            const nextBtn = document.getElementById('comms-modal-next');
            if (nextBtn) nextBtn.disabled = false;
          } else {
            stateEl.innerHTML = '';
            formEl.classList.remove('hidden');
          }
        };

        const status = await _shFetchStatus(session);
        renderCampaignStatus(status?.stages?.campaign?.status);

        document.getElementById('sh-wiz-submit-campaign')?.addEventListener('click', async () => {
          const usecase = document.getElementById('sh-wiz-usecase')?.value;
          const description = document.getElementById('sh-wiz-camp-desc')?.value?.trim();
          const messageFlow = document.getElementById('sh-wiz-camp-flow')?.value?.trim();
          const privacyPolicyLink = document.getElementById('sh-wiz-camp-privacy')?.value?.trim();
          const termsAndConditionsLink = document.getElementById('sh-wiz-camp-tos')?.value?.trim();

          const result = document.getElementById('sh-wiz-campaign-result');
          if (description.length < 40 || messageFlow.length < 40) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">Description and Message Flow must each be at least 40 characters.</div>`;
            return;
          }
          const submitBtn = document.getElementById('sh-wiz-submit-campaign');
          submitBtn.disabled = true;
          submitBtn.textContent = 'Submitting…';

          const res = await fetch('/.netlify/functions/signalhouse-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({
              action: 'register-campaign',
              usecase, description, messageFlow,
              privacyPolicyLink, termsAndConditionsLink,
            }),
          });
          const json = await res.json();
          if (!res.ok) {
            result.innerHTML = `<div style="color:var(--danger);font-size:13px;">${escHtml(json.error || 'Submission failed.')}</div>`;
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit Campaign for Review ($15)';
            return;
          }
          renderCampaignStatus(json.status || 'PENDING_REVIEW');
        });
      },
    },
  ];

  const VOICE_STEPS = [
    { id: 'voice-intro', title: 'Voice Setup', render: () => `
      <div style="text-align:center;padding:20px 0;">
        <div style="font-size:36px;margin-bottom:12px;">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
        </div>
        <p style="font-size:18px;font-weight:700;color:var(--text);margin-bottom:8px;">Set Up Voice Calls</p>
        <p class="comms-modal-desc" style="max-width:380px;margin:0 auto;">
          Your business number is ready for voice calls. Tell us where to forward them and we'll handle the rest — including voicemail when you can't answer.
        </p>
      </div>`,
    },
    { id: 'voice-1', title: 'Call Forwarding', render: () => {
      const ch = _commsChannelCache || {};
      return `
      <p class="comms-modal-desc">When clients call your business number, we'll forward it to your personal phone.</p>
      <div class="form-grid" style="margin-top:16px;">
        <div class="form-group form-col-2"><label>Forward Calls To</label><input type="tel" id="wiz-fwd-number" placeholder="Your personal cell number" value="${escHtml(ch.forwarding_number || '')}" /></div>
      </div>
      <label style="display:flex;align-items:center;gap:8px;margin-top:14px;font-size:13px;color:var(--text-muted);cursor:pointer;">
        <input type="checkbox" id="wiz-call-record" ${ch.call_recording_enabled ? 'checked' : ''} /> Enable call recording
      </label>`;
    },
      onNext: async () => {
        const fwd = document.getElementById('wiz-fwd-number')?.value?.trim();
        const recording = !!document.getElementById('wiz-call-record')?.checked;
        if (fwd) {
          try { await saveChannelUpdate({ forwardingNumber: fwd, callRecordingEnabled: recording }); }
          catch { showToast('Could not save. Try again.', 'error'); throw new Error('block'); }
        }
      },
    },
    { id: 'voice-2', title: 'Recording Disclosure', render: () => {
      const ch = _commsChannelCache || {};
      // Default-ON for first-time setup: only treat explicit false as "off."
      // New rows get DB default true; existing coaches keep whatever they set.
      const discOn = ch.recording_disclosure !== false;
      return `
      <p class="comms-modal-desc">When call recording is enabled, play a disclosure message so callers know they're being recorded. Required in many states.</p>
      <label style="display:flex;align-items:center;gap:8px;margin-top:16px;font-size:13px;color:var(--text);cursor:pointer;">
        <input type="checkbox" id="wiz-disclosure-on" ${discOn ? 'checked' : ''} /> Enable recording disclosure
      </label>
      <div id="wiz-disclosure-fields" style="margin-top:14px;${discOn ? '' : 'display:none;'}">
        <label style="font-size:12px;font-weight:600;color:var(--text);letter-spacing:0;display:block;margin-bottom:6px;">Disclosure message</label>
        <textarea id="wiz-disclosure-msg" rows="3" style="width:100%;resize:vertical;font-size:13px;padding:10px;border-radius:6px;border:1px solid var(--border);background:var(--bg-card);color:var(--text);" placeholder="This call may be recorded for quality and training purposes.">${escHtml(ch.recording_disclosure_message || '')}</textarea>
        <p style="font-size:11px;color:var(--text-muted);margin-top:6px;">Leave blank for default: "This call may be recorded for quality and training purposes."</p>
      </div>
      <div style="margin-top:16px;padding:12px;background:var(--bg-hover);border-radius:6px;border:1px solid var(--border);">
        <p style="font-size:12px;font-weight:600;color:var(--text);margin-bottom:4px;">How it works</p>
        <p style="font-size:12px;color:var(--text-muted);line-height:1.5;margin:0;">
          <strong>Inbound calls:</strong> Message plays to the caller before they're connected to you.<br>
          <strong>Outbound calls:</strong> Message plays to the client when they pick up, before you're bridged together.
        </p>
      </div>`;
    },
      onMount: () => {
        document.getElementById('wiz-disclosure-on')?.addEventListener('change', async (e) => {
          const cb = e.target;
          // Warn before letting the coach turn disclosure OFF. Many US states
          // are two-party consent — recording without notice is a liability
          // risk we want to surface explicitly rather than letting them
          // toggle past it without thinking.
          if (!cb.checked) {
            const ok = await showConfirm(
              'Turn off the recording disclosure? Many US states require two-party consent — recording calls without notifying the caller can be a legal liability. Only disable this if you have another way to disclose (e.g. a written agreement in advance).',
              { okLabel: 'Turn off anyway', cancelLabel: 'Keep on', danger: true }
            );
            if (!ok) {
              cb.checked = true;
              return;
            }
          }
          document.getElementById('wiz-disclosure-fields').style.display = cb.checked ? '' : 'none';
        });
      },
      onNext: async () => {
        const on = !!document.getElementById('wiz-disclosure-on')?.checked;
        const msg = document.getElementById('wiz-disclosure-msg')?.value?.trim() || '';
        try { await saveChannelUpdate({ recordingDisclosure: on, recordingDisclosureMessage: msg }); }
        catch { showToast('Could not save. Try again.', 'error'); throw new Error('block'); }
      },
    },
    { id: 'voice-2-5', title: 'Voicemail Greeting', render: () => {
      const ch = _commsChannelCache || {};
      const placeholder = "Hi, you've reached us. Please leave a message after the tone and we'll get back to you as soon as we can.";
      return `
      <p class="comms-modal-desc">When callers reach your voicemail, this is what they'll hear. Leave blank for a generic default. We read it aloud using a natural-sounding voice — no recording required.</p>
      <div style="margin-top:16px;">
        <label style="font-size:12px;font-weight:600;color:var(--text);letter-spacing:0;display:block;margin-bottom:6px;">Greeting message</label>
        <textarea id="wiz-vm-greeting-text" rows="3" maxlength="500" style="width:100%;resize:vertical;font-size:13px;padding:10px;border-radius:6px;border:1px solid var(--border);background:var(--bg-card);color:var(--text);" placeholder="${escHtml(placeholder)}">${escHtml(ch.voicemail_greeting_text || '')}</textarea>
        <p style="font-size:11px;color:var(--text-muted);margin-top:6px;">Tip: short and warm works best. Up to 500 characters.</p>
      </div>
      <div style="margin-top:16px;padding:12px;background:var(--bg-hover);border-radius:6px;border:1px solid var(--border);">
        <p style="font-size:12px;font-weight:600;color:var(--text);margin-bottom:4px;">When voicemail plays</p>
        <p style="font-size:12px;color:var(--text-muted);line-height:1.5;margin:0;">
          When you don't pick up within 20 seconds, when you haven't set up a forwarding number yet, or when a call comes in outside your business hours.<br>
          Voicemails land in your inbox with the audio and a transcript.
        </p>
      </div>`;
    },
      onNext: async () => {
        const text = document.getElementById('wiz-vm-greeting-text')?.value?.trim() || '';
        try { await saveChannelUpdate({ voicemailGreetingText: text }); }
        catch { showToast('Could not save. Try again.', 'error'); throw new Error('block'); }
      },
    },
    { id: 'voice-3', title: 'Business Hours', render: () => {
      const ch = _commsChannelCache || {};
      const bh = ch.business_hours || {};
      const days = bh.days || [1, 2, 3, 4, 5];
      const start = bh.start ?? 9;
      const end = bh.end ?? 17;
      const tz = bh.timezone || _config.getCoachTimezone();
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const enabled = !!ch.business_hours;

      let hourOpts = '';
      for (let h = 0; h < 24; h++) {
        const label = h === 0 ? '12:00 AM' : h < 12 ? h + ':00 AM' : h === 12 ? '12:00 PM' : (h - 12) + ':00 PM';
        hourOpts += `<option value="${h}">${label}</option>`;
      }

      return `
      <p class="comms-modal-desc">Set when you're available for calls. Outside these hours, callers go straight to voicemail.</p>
      <label style="display:flex;align-items:center;gap:8px;margin-top:16px;font-size:13px;color:var(--text);cursor:pointer;">
        <input type="checkbox" id="wiz-bh-enabled" ${enabled ? 'checked' : ''} /> Enable business hours
      </label>
      <div id="wiz-bh-fields" style="margin-top:16px;${enabled ? '' : 'display:none;'}">
        <label style="font-size:12px;font-weight:600;color:var(--text);letter-spacing:0;display:block;margin-bottom:8px;">Available days</label>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          ${dayNames.map((d, i) => `<label style="display:flex;align-items:center;gap:4px;padding:6px 10px;border-radius:6px;border:1px solid var(--border);cursor:pointer;font-size:13px;color:var(--text);background:${days.includes(i) ? 'var(--accent)' : 'var(--bg-card)'};${days.includes(i) ? 'color:#fff;border-color:var(--accent);' : ''}"><input type="checkbox" class="wiz-bh-day" data-day="${i}" ${days.includes(i) ? 'checked' : ''} style="display:none;" />${d}</label>`).join('')}
        </div>
        <div class="form-grid" style="margin-top:14px;">
          <div class="form-group"><label>Start Time</label><select id="wiz-bh-start">${hourOpts}</select></div>
          <div class="form-group"><label>End Time</label><select id="wiz-bh-end">${hourOpts}</select></div>
        </div>
        <p style="font-size:12px;color:var(--text-muted);margin-top:8px;">Timezone: <strong>${escHtml(tz)}</strong> (change in Settings &gt; Appearance)</p>
      </div>
      <div id="wiz-bh-off-msg" style="margin-top:14px;padding:12px;background:var(--bg-hover);border-radius:6px;border:1px solid var(--border);${enabled ? '' : 'display:none;'}">
        <p style="font-size:12px;color:var(--text-muted);line-height:1.5;margin:0;">Calls outside business hours will hear your voicemail greeting or a default message.</p>
      </div>`;
    },
      onMount: () => {
        const ch = _commsChannelCache || {};
        const bh = ch.business_hours || {};
        const startSel = document.getElementById('wiz-bh-start');
        const endSel = document.getElementById('wiz-bh-end');
        if (startSel) startSel.value = String(bh.start ?? 9);
        if (endSel) endSel.value = String(bh.end ?? 17);

        // Only an actual click on the checkbox counts as the coach's own
        // decision — see onNext, which will not clear a real saved value
        // unless this fired. A checkbox that merely reads unchecked (e.g.
        // from a cache that hadn't loaded the coach's real value yet) never
        // sets this.
        _bhToggledByCoach = false;

        // Toggle
        document.getElementById('wiz-bh-enabled')?.addEventListener('change', (e) => {
          _bhToggledByCoach = true;
          document.getElementById('wiz-bh-fields').style.display = e.target.checked ? '' : 'none';
          document.getElementById('wiz-bh-off-msg').style.display = e.target.checked ? '' : 'none';
        });

        // Day pill toggle styling
        document.querySelectorAll('.wiz-bh-day').forEach(cb => {
          cb.addEventListener('change', () => {
            const lbl = cb.parentElement;
            if (cb.checked) {
              lbl.style.background = 'var(--accent)';
              lbl.style.color = '#fff';
              lbl.style.borderColor = 'var(--accent)';
            } else {
              lbl.style.background = 'var(--bg-card)';
              lbl.style.color = 'var(--text)';
              lbl.style.borderColor = 'var(--border)';
            }
          });
        });
      },
      onNext: async () => {
        if (_commsChannelCache === null) {
          // The initial, unforced load (openCommsWizard's own preload) never
          // succeeded this pass, so hadSavedHours below has nothing real to
          // read — it would see "no hours saved" whether or not that's
          // true, and could send businessHours: null and wipe a real value
          // we never actually saw. Refuse to guess instead.
          showToast('Could not save. Try again.', 'error');
          throw new Error('block');
        }
        const enabled = !!document.getElementById('wiz-bh-enabled')?.checked;
        if (!enabled) {
          // A real saved value only gets cleared when the coach actually
          // clicked the checkbox off — never just because it reads
          // unchecked right now. Without this, a stale or not-yet-loaded
          // cache renders the box unchecked and this line wipes real hours
          // nobody asked to remove.
          const hadSavedHours = !!(_commsChannelCache || {}).business_hours;
          if (hadSavedHours && !_bhToggledByCoach) return;
          try { await saveChannelUpdate({ businessHours: null }); }
          catch { showToast('Could not save. Try again.', 'error'); throw new Error('block'); }
          return;
        }
        const days = [];
        document.querySelectorAll('.wiz-bh-day').forEach(cb => {
          if (cb.checked) days.push(parseInt(cb.dataset.day));
        });
        const start = parseInt(document.getElementById('wiz-bh-start')?.value || '9');
        const end = parseInt(document.getElementById('wiz-bh-end')?.value || '17');
        const tz = _config.getCoachTimezone();
        try { await saveChannelUpdate({ businessHours: { days, start, end, timezone: tz } }); }
        catch { showToast('Could not save. Try again.', 'error'); throw new Error('block'); }
      },
    },
  ];

  let _a2pStatusCache = null; // populated by a2p steps or done step

  const DONE_STEP = { id: 'done', title: 'Setup Complete', render: () => {
    const settings = loadSettings();
    const emailOk = emailDomainVerified(settings);
    const dot = (color) => `<span style="color:${color};">${color === 'var(--text-muted)' ? '&#9675;' : '&#9679;'}</span>`;
    return `
      <div style="text-align:center;padding:20px 0;">
        <div style="font-size:40px;margin-bottom:12px;">&#10003;</div>
        <p id="done-headline" style="font-size:16px;font-weight:700;color:var(--text);margin-bottom:6px;">Setup saved.</p>
        <p id="done-sub" style="font-size:12.5px;color:var(--text-muted);margin:0 auto 18px;max-width:380px;line-height:1.5;"></p>
        <div style="text-align:left;max-width:360px;margin:0 auto;">
          <div style="display:flex;gap:10px;align-items:center;padding:8px 0;font-size:14px;">${dot(emailOk ? '#22c55e' : 'var(--text-muted)')} <span style="color:var(--text);">Email</span> <span style="font-size:12px;color:var(--text-muted);margin-left:auto;">${emailOk ? 'Verified' : 'Pending'}</span></div>
          <div style="display:flex;gap:10px;align-items:center;padding:8px 0;font-size:14px;"><span id="done-sms-dot">${dot('var(--text-muted)')}</span> <span style="color:var(--text);">SMS</span> <span id="done-a2p-status" style="font-size:12px;margin-left:auto;color:var(--text-muted);">Checking…</span></div>
          <div style="display:flex;gap:10px;align-items:center;padding:8px 0;font-size:14px;">${dot('#22c55e')} <span style="color:var(--text);">Voice</span> <span style="font-size:12px;color:var(--text-muted);margin-left:auto;">Active</span></div>
        </div>
        <div id="done-next" style="text-align:left;max-width:380px;margin:18px auto 0;"></div>
      </div>`;
  }, onMount: async () => {
    // The SMS row + headline must reflect the REAL A2P state — a number being
    // provisioned ("Active") does NOT mean texting works; that needs carrier A2P
    // approval. Showing green "Active" while bundles/brand are in review tells the
    // coach texting is live when it isn't.
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/twilio-register-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'status' }),
      });
      const json = await res.json();
      const st = String(json.status || '').toLowerCase();
      const campVerified = String(json.campaignStatus || '').toUpperCase() === 'VERIFIED';
      const statusEl = document.getElementById('done-a2p-status');
      const dotEl = document.getElementById('done-sms-dot');
      const headEl = document.getElementById('done-headline');
      const subEl = document.getElementById('done-sub');
      const nextEl = document.getElementById('done-next');
      const setDot = (c) => { if (dotEl) dotEl.innerHTML = `<span style="color:${c};">${c === 'var(--text-muted)' ? '&#9675;' : '&#9679;'}</span>`; };
      const setStatus = (txt, c) => { if (statusEl) { statusEl.textContent = txt; statusEl.style.color = c; } };
      const note = (color, bg, title, body) => `<div style="background:${bg};border:1px solid ${color}40;border-radius:var(--radius-sm);padding:12px 14px;"><div style="font-size:12px;font-weight:700;color:${color};">${title}</div><p style="font-size:11.5px;color:var(--text-muted);margin:4px 0 0;line-height:1.55;">${body}</p></div>`;
      const amber = '#f59e0b', green = '#22c55e', red = 'var(--danger)', muted = 'var(--text-muted)';

      if (st === 'approved' && campVerified) {
        setDot(green); setStatus('Active — texting enabled', green);
        if (headEl) headEl.textContent = "You're all set.";
        if (subEl) subEl.textContent = 'Your number can text and call your clients — replies land in your CRM inbox.';
      } else if (st === 'approved') {
        // Brand approved, campaign not yet verified → coach submits/awaits the texts.
        setDot(amber); setStatus('Business approved — one step left', amber);
        if (headEl) headEl.textContent = 'Almost there.';
        if (nextEl) nextEl.innerHTML = note(amber, 'rgba(245,158,11,0.08)', 'One step left', 'Your business is approved. Open the SMS card and submit your example texts to the carriers for final sign-off — texting turns on once they clear.');
      } else if (st === 'failed') {
        setDot(red); setStatus('Needs attention', red);
        if (headEl) headEl.textContent = 'Setup saved.';
        if (nextEl) nextEl.innerHTML = note(red, 'rgba(239,68,68,0.06)', 'The carriers rejected your registration', 'Open the SMS card to see exactly what to fix and resubmit. Your example texts are saved.');
      } else if (['bundles_pending', 'pending', 'pending-review', 'in_progress', 'bundles_approved'].includes(st)) {
        setDot(amber); setStatus('In carrier review', amber);
        if (headEl) headEl.textContent = 'Setup saved — carrier review in progress.';
        if (nextEl) nextEl.innerHTML = note(amber, 'rgba(245,158,11,0.08)', "What's next — nothing for you right now", "Your business is being verified by the phone carriers (about 1–2 business days), then your brand and texts get approved. <strong>We'll email you at each step</strong>, and texting to your clients turns on automatically once it's all approved — you don't need to wait here. Your example texts are saved; come back to submit them once your business is approved.");
      } else {
        // not_registered / unknown — likely a non-SMS flow; keep it quiet.
        setDot(muted); setStatus('Not set up', muted);
        if (headEl) headEl.textContent = "You're all set.";
      }
    } catch {}
  }};

  // openCommsWizard(channelOverride?)
  //
  // - With no arg, falls back to the legacy multi-channel flow (checkbox-based).
  //   Kept for backward compat in case anything still calls it that way.
  // - With a channel name ('email' | 'sms' | 'voice'), queues only that
  //   channel's steps + DONE_STEP. This is the new per-card "Set up X"
  //   button path — each channel has its own card with its own button,
  //   and they no longer share a "Communications" parent card.
  async function openCommsWizard(channelOverride) {
    const channels = channelOverride
      ? [channelOverride]
      : getSelectedChannels();
    if (channels.length === 0) { showToast('Select at least one channel', 'error'); return; }
    // Preload channel cache for voice prefill
    if (channels.includes('voice')) await getChannelCache();
    _commsWizSteps = [];
    let _smsHasNumber = false, _smsBrandApproved = false;
    if (channels.includes('email')) _commsWizSteps.push(...EMAIL_STEPS);
    if (channels.includes('sms')) {
      _commsNumberMode = 'new';
      _commsAdoptedNumber = null;
      _commsPortData = {};
      // Resume-aware: if the coach already has a texting number, skip the
      // number-acquisition steps entirely — re-running them dead-ends the
      // coach ("no unassigned numbers found", "pick a number first"). Drop
      // straight into the A2P flow, the same deep-link the card's "Register
      // Now" link uses.
      const _smsCh = await getChannelCache();
      _smsHasNumber = !!_smsCh?.twilio_phone_number;
      _smsBrandApproved = _smsCh?.a2p_status === 'approved';
      if (_smsHasNumber) {
        _commsWizSteps.push(...A2P_STEPS);
      } else {
        _commsWizSteps.push(...SMS_STEPS, ...NEW_NUMBER_STEPS, ...A2P_STEPS);
      }
    }
    if (channels.includes('voice')) _commsWizSteps.push(...VOICE_STEPS);
    // DONE_STEP is a multi-channel summary screen (shows Email/SMS/Voice
    // status side-by-side). For the email-only card flow, email-4 ("Email
    // Ready") already serves as the completion step — checkmark, business
    // email addresses, send-test. Appending DONE_STEP on top would show
    // two consecutive "you're done" screens and inflate step count from
    // 4 → 5. Skip it when the flow is email-only.
    const isEmailOnly = channels.length === 1 && channels[0] === 'email';
    if (!isEmailOnly) _commsWizSteps.push(DONE_STEP);
    _commsWizIdx = 0;
    // Resume an in-progress SMS-only flow at the step the coach left off on:
    // business verification (a2p-1) until the brand is approved, then the
    // campaign / example-texts step (a2p-2). Only for the sms-only "Continue
    // SMS setup" entry — a multi-channel flow still starts at its first step.
    if (channels.length === 1 && channels[0] === 'sms' && _smsHasNumber) {
      const _resumeId = _smsBrandApproved ? 'a2p-2' : 'a2p-1';
      const _idx = _commsWizSteps.findIndex(s => s.id === _resumeId);
      if (_idx >= 0) _commsWizIdx = _idx;
    }
    renderCommsWizStep();
    document.getElementById('comms-modal-overlay').classList.remove('hidden');
  }

  function closeCommsWizard() {
    document.getElementById('comms-modal-overlay').classList.add('hidden');
    _commsChannelCache = null;
    // Refresh channel card statuses
    loadCommsCardStatuses();
    // Invalidate composer-gating cache so newly-configured channels light up.
    window._invalidateCommsCaps?.();
  }

  async function loadCommsCardStatuses() {
    try {
      const supabase = _config.getSupabase();
      if (!supabase) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // A domain verified, or a cancellation address switched on, since this
      // tab loaded (the background check runs every 15 minutes) shows here
      // without a reload.
      await _config.refreshEmailDomainStatus();
      const settings = loadSettings();

      // Email status: where the emails come from, pointing to Your domain.
      paintEmailCard(settings);

      // Fetch SMS/Voice via authenticated function (RLS blocks direct query)
      const res = await fetch('/.netlify/functions/coach-channels-get', {
        headers: { Authorization: 'Bearer ' + session.access_token },
      });
      if (!res.ok) return;
      const { channels } = await res.json();
      const smsChannel = channels?.find(c => c.channel === 'sms') || null;

      const smsStatus = document.getElementById('comms-card-sms-status');
      const smsCheck = document.querySelector('.comms-channel-check[data-channel="sms"]');
      if (smsStatus) {
        if (smsChannel?.twilio_phone_number) {
          const raw = smsChannel.twilio_phone_number.replace(/\D/g, '').slice(-10);
          const fmt = raw.length === 10 ? '(' + raw.slice(0,3) + ') ' + raw.slice(3,6) + '-' + raw.slice(6) : smsChannel.twilio_phone_number;
          let a2pBadge = '';
          // Normalize case — the A2P poll stores Twilio's UPPERCASE status
          // (APPROVED/FAILED), but these comparisons were lowercase, so an
          // approved brand fell through to "Pending Review" / "Pending brand approval".
          const a2p = (smsChannel.a2p_status || '').toLowerCase();
          const needsA2p = !a2p || a2p === 'not_registered' || a2p === 'failed';
          if (a2p === 'approved') {
            a2pBadge = '<br><span style="color:#22c55e;font-size:10px;font-weight:600;">A2P: Approved</span>';
          } else if (a2p && a2p !== 'not_registered' && a2p !== 'failed') {
            a2pBadge = '<br><span style="color:#f59e0b;font-size:10px;font-weight:600;">A2P: Pending Review</span>';
          } else if (a2p === 'failed') {
            a2pBadge = '<br><span style="color:var(--danger);font-size:10px;font-weight:600;">A2P: Rejected — <a href="#" id="btn-a2p-direct" style="color:var(--accent);text-decoration:underline;">Resubmit</a></span>';
          } else {
            a2pBadge = '<br><span style="color:var(--text-muted);font-size:10px;font-weight:600;">A2P: Not Registered — <a href="#" id="btn-a2p-direct" style="color:var(--accent);text-decoration:underline;">Register Now</a></span>';
          }
          smsStatus.innerHTML = '<span style="color:#22c55e;font-size:11px;font-weight:700;">Connected — ' + fmt + '</span>' + a2pBadge;

          // Direct A2P registration button handler
          document.getElementById('btn-a2p-direct')?.addEventListener('click', (e) => {
            e.preventDefault();
            _commsWizSteps = [...A2P_STEPS, DONE_STEP];
            _commsWizIdx = 0;
            renderCommsWizStep();
            document.getElementById('comms-modal-overlay').classList.remove('hidden');
          });
          if (smsCheck) { smsCheck.checked = true; smsCheck.disabled = true; }
        } else if (smsChannel?.port_status && smsChannel?.port_in_request_sid) {
          // Show port-in status
          const portColors = { submitted: '#f59e0b', 'in-progress': '#f59e0b', completed: '#22c55e', rejected: 'var(--danger)', cancelled: 'var(--text-muted)' };
          const portColor = portColors[smsChannel.port_status] || '#f59e0b';
          smsStatus.innerHTML = '<span style="color:' + portColor + ';font-size:11px;font-weight:700;">Port-In: ' + escHtml(smsChannel.port_status.charAt(0).toUpperCase() + smsChannel.port_status.slice(1)) + '</span>';
        } else {
          smsStatus.innerHTML = '<span style="color:var(--text-muted);font-size:11px;font-weight:700;">Not connected</span>';
        }
      }

      // Voice status — "Connected" REQUIRES both a coach-owned business
      // number AND a forwarding number. Forwarding alone is just a saved
      // preference; without a number on this coach's row, voice can't
      // actually work. (Previous logic showed "Connected — forwarding to X"
      // even when the coach had no number, which was misleading. Fixed
      // 2026-05-19 after Clayton flagged it.)
      const voiceStatus = document.getElementById('comms-card-voice-status');
      const voiceCheck = document.querySelector('.comms-channel-check[data-channel="voice"]');
      const voiceDesc = document.getElementById('voice-card-desc');
      const voiceBtn = document.getElementById('btn-setup-voice');
      if (voiceStatus) {
        const hasNumber = !!smsChannel?.twilio_phone_number;
        const hasForwarding = !!smsChannel?.forwarding_number;
        if (hasNumber && hasForwarding) {
          voiceStatus.innerHTML = '<span style="color:#22c55e;font-size:11px;font-weight:700;">Connected — forwarding to ' + escHtml(smsChannel.forwarding_number) + '</span>';
          if (voiceDesc) voiceDesc.textContent = 'Calls to your business number forward to ' + smsChannel.forwarding_number + '. Voicemail catches anything you miss. You can also call any client straight from inside the CRM.';
          if (voiceBtn) voiceBtn.textContent = 'Manage voice';
          if (voiceCheck) { voiceCheck.checked = true; voiceCheck.disabled = true; }
        } else if (hasNumber && !hasForwarding) {
          voiceStatus.innerHTML = '<span style="color:#f59e0b;font-size:11px;font-weight:700;">Number ready — add forwarding number</span>';
          if (voiceBtn) voiceBtn.textContent = 'Finish voice setup';
        } else {
          // No coach-owned phone number yet — voice is just "not connected"
          // regardless of whether a forwarding preference was saved.
          voiceStatus.innerHTML = '<span style="color:var(--text-muted);font-size:11px;font-weight:700;">Not connected</span>';
        }
      }

      // ── Caller ID & Spam Protection (Phase 4) ──────────────────
      // CNAM status comes straight from the column (modules/caller-id-name.js).
      // SHAKEN/STIR is derived from A2P Brand approval — if the brand is
      // approved, outbound voice is auto-attested at the carrier level.
      renderCallerIdName(smsChannel);
      const shakenBadge = document.getElementById('shaken-status-badge');
      if (shakenBadge) {
        // SHAKEN/STIR is auto-derived from A2P brand approval status.
        // Twilio enrolls verified brands automatically — no separate sign-up.
        // Case-normalized: the poll stores UPPERCASE (APPROVED).
        const shakenA2p = (smsChannel?.a2p_status || '').toLowerCase();
        const a2pApproved = (shakenA2p === 'approved');
        if (a2pApproved) {
          shakenBadge.textContent = 'Active (A-attestation)';
          shakenBadge.className = 'email-domain-status email-domain-status--verified';
        } else if (shakenA2p && shakenA2p !== 'not_registered') {
          shakenBadge.textContent = 'Pending SMS brand approval';
          shakenBadge.className = 'email-domain-status email-domain-status--pending';
        } else {
          shakenBadge.textContent = 'Not active';
          shakenBadge.className = 'email-domain-status';
        }
      }
      // Stash on the channel cache so the modals can read latest values
      window._voiceComplianceChannel = smsChannel || null;

      // Swap Start Setup → Edit Settings if all channels are connected.
      // Must read emailDomainStatus_ses (the SES field the send path checks in
      // buildFromAddress) — the legacy emailDomainStatus is a SendGrid-era
      // leftover that no send path honors, so trusting it here showed migrated
      // coaches "connected" while their mail still went out from fitopspro.com.
      const allConnected = (settings.emailDomainStatus_ses === 'verified') && smsChannel?.twilio_phone_number;
      const startBtn = document.getElementById('btn-start-comms-wizard');
      const editBtn = document.getElementById('btn-edit-comms');
      if (startBtn && editBtn) {
        if (allConnected) {
          startBtn.classList.add('hidden');
          editBtn.classList.remove('hidden');
        } else {
          startBtn.classList.remove('hidden');
          editBtn.classList.add('hidden');
        }
      }
    } catch (err) {
      console.warn('[loadCommsCardStatuses] Error:', err.message);
    }
  }

  function renderCommsWizStep() {
    const step = _commsWizSteps[_commsWizIdx];
    const body = document.getElementById('comms-modal-body');
    const title = document.getElementById('comms-modal-title');
    const label = document.getElementById('comms-modal-step-label');
    const backBtn = document.getElementById('comms-modal-back');
    const nextBtn = document.getElementById('comms-modal-next');

    title.textContent = step.title;
    body.innerHTML = step.render();
    label.textContent = `${_commsWizIdx + 1} of ${_commsWizSteps.length}`;

    backBtn.classList.toggle('hidden', _commsWizIdx === 0);

    const isLast = step.id === 'done';
    nextBtn.textContent = isLast ? 'Close' : (_commsWizIdx === _commsWizSteps.length - 2 ? 'Finish' : 'Next');
    // Reset the footer Next to enabled on every render. Individual steps (the
    // a2p-2 example-texts step) re-disable it to gate advancement; without this
    // reset a step's disable would leak to later steps.
    nextBtn.disabled = false;
    nextBtn.style.opacity = '';
    nextBtn.style.cursor = '';

    nextBtn.classList.remove('hidden');

    // Show skip button if email is already set up and we're on an email step
    const settings = loadSettings();
    const emailDone = emailDomainVerified(settings);
    const isEmailStep = step.id?.startsWith('email-');
    const skipBtn = document.getElementById('comms-modal-skip');
    if (skipBtn) {
      // Only show the skip-section button if the next non-email step is an
      // actual setup section (SMS or Voice) — not the DONE wrap-up step.
      // Without this guard the button shows "Skip to Next" on every email
      // step when the user opened the email-only wizard.
      const nextSection = _commsWizSteps.find((s, i) =>
        i > _commsWizIdx && (s.id?.startsWith('sms') || s.id?.startsWith('voice'))
      );
      if (isEmailStep && nextSection) {
        skipBtn.classList.remove('hidden');
        skipBtn.textContent = 'Skip to ' + (nextSection.id?.startsWith('sms') ? 'SMS' : 'Voice');
      } else {
        skipBtn.classList.add('hidden');
      }
    }

    if (step.onMount) step.onMount();
  }

  document.getElementById('btn-start-comms-wizard')?.addEventListener('click', () => openCommsWizard());
  // New per-channel launchers (split Communications card → 3 standalone cards).
  // Backward compat: the legacy btn-start-comms-wizard above still works for
  // any path that hasn't been migrated.
  document.getElementById('btn-setup-email')?.addEventListener('click', () => openCommsWizard('email'));
  document.getElementById('btn-setup-sms')?.addEventListener('click', () => openCommsWizard('sms'));
  document.getElementById('btn-setup-voice')?.addEventListener('click', () => openCommsWizard('voice'));

  // ── Caller ID & Spam Protection modals (Phase 4) ────────────
  // The window starts from Coach Profile's business name, in the order
  // agreements read it (coach-business-name.js). The Coach Profile field holds
  // the coaches row's name too, once settings have loaded.
  initCallerIdName({
    callFunction,
    showToast,
    reload: () => loadCommsCardStatuses(),
    businessName: () => businessNameOnFile(loadSettings(), _appCoachSettings, {
      business_name: /** @type {HTMLInputElement|null} */ (document.getElementById('s-business-name'))?.value || null,
    }),
  });

  function openShakenModal() {
    const ch = window._voiceComplianceChannel || {};
    const detail = document.getElementById('shaken-status-detail');
    if (detail) {
      if (ch.a2p_status === 'approved') {
        detail.innerHTML = `<strong style="color:#22c55e;">Active</strong> — your outbound calls are getting A-level attestation through ${window._appIsOwner ? "Twilio's Voice Integrity program" : 'our carrier-trust program'}. Nothing else to do.`;
      } else if (ch.a2p_status && ch.a2p_status !== 'not_registered') {
        detail.innerHTML = `<strong style="color:#f59e0b;">Pending</strong> — SHAKEN/STIR will activate automatically once your SMS A2P Brand is approved. Current brand status: <strong>${escHtml(ch.a2p_status)}</strong>.`;
      } else {
        detail.innerHTML = `Finish your SMS A2P Brand registration first — that same brand profile is what enables SHAKEN/STIR for voice. Once approved, this turns on automatically.`;
      }
    }
    document.getElementById('shaken-modal-overlay')?.classList.remove('hidden');
  }
  function closeShakenModal() {
    document.getElementById('shaken-modal-overlay')?.classList.add('hidden');
  }
  document.getElementById('btn-shaken-info')?.addEventListener('click', openShakenModal);
  document.getElementById('shaken-modal-close')?.addEventListener('click', closeShakenModal);
  document.getElementById('btn-shaken-close')?.addEventListener('click', closeShakenModal);
  document.getElementById('shaken-modal-overlay')?.addEventListener('click', (e) => {
    if (e.target.id === 'shaken-modal-overlay') closeShakenModal();
  });
  document.getElementById('btn-edit-comms')?.addEventListener('click', () => {
    showConfirmDialog({
      message: 'Edit your communication settings? You can update your domain, phone number, and forwarding settings. Changes may affect active client communications.',
      okLabel: 'Edit Settings',
      okClass: 'btn-primary',
      onConfirm: () => {
        // Re-enable checkboxes for editing
        document.querySelectorAll('.comms-channel-check').forEach(cb => { cb.disabled = false; });
        openCommsWizard();
      },
    });
  });
  document.getElementById('comms-modal-close')?.addEventListener('click', closeCommsWizard);
  // Backdrop click intentionally does NOT close the wizard — it's a multi-field
  // setup flow and an accidental outside-click used to discard in-progress work.
  // Close is via the ✕ button only (and the campaign draft auto-saves as you
  // type, so nothing is lost regardless).

  document.getElementById('comms-modal-next')?.addEventListener('click', async () => {
    const step = _commsWizSteps[_commsWizIdx];
    if (step.onNext) {
      try { await step.onNext(); } catch (e) { if (e.message === 'block') return; throw e; }
    }
    if (step.id === 'done') { closeCommsWizard(); return; }
    if (_commsWizIdx < _commsWizSteps.length - 1) { _commsWizIdx++; renderCommsWizStep(); }
  });

  document.getElementById('comms-modal-back')?.addEventListener('click', () => {
    if (_commsWizIdx > 0) { _commsWizIdx--; renderCommsWizStep(); }
  });

  document.getElementById('comms-modal-skip')?.addEventListener('click', () => {
    // Jump to the next non-email step
    const nextIdx = _commsWizSteps.findIndex((s, i) => i > _commsWizIdx && !s.id?.startsWith('email-'));
    if (nextIdx >= 0) { _commsWizIdx = nextIdx; renderCommsWizStep(); }
  });

  // ── A2P per-stage status renderer ─────────────────────────────────
  // Produces the HTML shown under the A2P "Status" badge in Settings →
  // Integrations → SMS. Called after loadSmsSettings fetches the rich status
  // payload from `/.netlify/functions/twilio-register-brand` (action: 'status').
  function _renderA2pStages(s, hasNumber) {
    const stages = s?.stages || {};
    const reason = s?.brandFailureReason || '';
    const lastChecked = s?.lastCheckedAt ? new Date(s.lastCheckedAt).toLocaleString() : '';
    const bundlesStuck = !!s?.bundlesStuck;

    // Map a raw Twilio/TCR status string to a visual pill variant + display label.
    // Full status enum coverage:
    //   Customer Profile + Trust Product (Twilio Trust Hub):
    //     draft, pending-review, in-review, twilio-approved, twilio-rejected, not_found
    //   Brand Registration (TCR):
    //     PENDING, IN_REVIEW, APPROVED, FAILED, SUSPENDED,
    //     DELETION_PENDING, DELETION_FAILED
    //   Campaign (TCR):
    //     PENDING, IN_PROGRESS, VERIFIED, FAILED, SUSPENDED
    const pill = (raw) => {
      if (!raw) return { cls: 'a2p-stage-pill--empty', label: 'Not started' };
      const r = String(raw).toLowerCase();
      if (r === 'twilio-approved' || r === 'approved' || r === 'verified') return { cls: 'a2p-stage-pill--done', label: 'Approved' };
      if (r === 'twilio-rejected' || r === 'failed' || r === 'rejected') return { cls: 'a2p-stage-pill--failed', label: 'Rejected' };
      if (r === 'suspended') return { cls: 'a2p-stage-pill--failed', label: 'Suspended' };
      if (r === 'deletion_pending') return { cls: 'a2p-stage-pill--failed', label: 'Deleting' };
      if (r === 'deletion_failed') return { cls: 'a2p-stage-pill--failed', label: 'Delete failed' };
      if (r === 'not_found') return { cls: 'a2p-stage-pill--failed', label: 'Deleted' };
      if (r === 'in-review' || r === 'in_review' || r === 'in_progress') return { cls: 'a2p-stage-pill--review', label: 'In review' };
      if (r === 'pending-review' || r === 'pending') return { cls: 'a2p-stage-pill--pending', label: 'Pending review' };
      return { cls: 'a2p-stage-pill--pending', label: wordsFromRaw(raw) };
    };

    // Twilio A2P review windows (in business days, weekends excluded):
    //   Standard:        10–15 biz days typical, escalate at 20
    //   Sole Proprietor: ~5 biz days typical, escalate at 10
    //   Fast Track:      ~2 biz days
    // We use the Standard thresholds in the UI (the conservative default).
    // Uses UTC throughout to avoid shifting near-midnight timestamps to the
    // wrong day in the local timezone.
    const businessDaysSince = (createdISO) => {
      if (!createdISO) return null;
      const start = new Date(createdISO);
      start.setUTCHours(0, 0, 0, 0);
      const end = new Date();
      end.setUTCHours(0, 0, 0, 0);
      let count = 0;
      const cur = new Date(start);
      while (cur < end) {
        cur.setUTCDate(cur.getUTCDate() + 1);
        const d = cur.getUTCDay();
        if (d !== 0 && d !== 6) count++;
      }
      return count;
    };

    const row = (label, stage, isActive) => {
      const sid = stage?.sid || '';
      const p = pill(stage?.status);
      const rawStatus = String(stage?.status || '').toLowerCase();
      const inReview = rawStatus === 'in-review' || rawStatus === 'pending-review';
      const bizDays = inReview ? businessDaysSince(stage?.dateCreated) : null;
      // Only surface a row-level warning once review is past the 20-business-day
      // mark — that's when Twilio's own docs say to open a support ticket.
      const stuckHint = bizDays != null && bizDays > 20
        ? `<div class="a2p-stage-sub" style="color:#ef4444;margin-top:2px;">Past 20 business days — contact our support team so we can escalate</div>`
        : '';
      // The "active" stage is the one currently blocking go-live — it gets a
      // loud amber treatment (3px left bar, warm row tint, filled pill with
      // halo, bolder title color) plus a "You are here" chip so the eye lands
      // on the answer without scanning every row. See styles.css
      // `.a2p-stage-row.is-active`.
      return `
        <div class="a2p-stage-row${isActive ? ' is-active' : ''}">
          <div>
            <div class="a2p-stage-label">${label}${isActive ? '<span class="a2p-stage-here">You are here</span>' : ''}</div>
            ${sid ? `<div class="a2p-stage-sub">${sid}</div>` : ''}
            ${stuckHint}
          </div>
          <span class="a2p-stage-pill ${p.cls}">${p.label}</span>
        </div>`;
    };

    // Identify the currently-blocking stage so we can mark it with the
    // loud "You are here" treatment. Order matters: first non-done, non-failed
    // stage in the registration sequence is "where you are." Failed stages
    // are handled separately by the failBox below, not marked active.
    const _stageIsDone   = (st) => /^(twilio-approved|approved|verified)$/i.test(String(st || ''));
    const _stageIsFailed = (st) => /^(twilio-rejected|failed|rejected|suspended)$/i.test(String(st || ''));
    const activeStage = (() => {
      const order = [
        ['customerProfile', stages.customerProfile?.status],
        ['trustProduct',    stages.trustProduct?.status],
        ['brand',           stages.brand?.status],
        ['campaign',        stages.campaign?.status],
      ];
      for (const [key, status] of order) {
        if (_stageIsFailed(status)) return null;
        if (!_stageIsDone(status)) return key;
      }
      return null;
    })();

    const numberRow = `
      <div class="a2p-stage-row">
        <div>
          <div class="a2p-stage-label">5. Your texting number</div>
        </div>
        <span class="a2p-stage-pill ${hasNumber ? 'a2p-stage-pill--done' : 'a2p-stage-pill--empty'}">${hasNumber ? 'Active' : 'Not yet purchased'}</span>
      </div>`;

    // Inline detail block under the Trust Product row when it's in-review,
    // so coaches can see day-of-window + compliance evaluation results
    // without having to log into Twilio's Trust Hub UI.
    const escapeHtml = (str) => String(str ?? '').replace(/[&<>"']/g, (c) => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    })[c]);
    const trustProductDetail = (() => {
      const tp = stages.trustProduct;
      if (!tp || !tp.sid) return '';
      const st = String(tp.status || '').toLowerCase();
      const inReview = (st === 'in-review' || st === 'pending-review');
      if (!inReview) return '';

      const lines = [];

      // Day count interpreted against Twilio's actual A2P SLA:
      //   10–15 business days = standard, 16–20 = edge of normal,
      //   > 20 = open a support ticket. Sole Prop runs faster (~5 biz days).
      if (tp.dateCreated) {
        const bizDays = businessDaysSince(tp.dateCreated);
        let dayLine, dayColor;
        if (bizDays <= 10) {
          dayLine = `Day ${bizDays} of the typical 10–15 business day compliance review window.`;
          dayColor = 'var(--text-muted)';
        } else if (bizDays <= 15) {
          dayLine = `Day ${bizDays} — within the typical 10–15 business day window.`;
          dayColor = 'var(--text-muted)';
        } else if (bizDays <= 20) {
          dayLine = `Day ${bizDays} — at the edge of the typical review window. Usually resolves on its own within a few days.`;
          dayColor = '#f59e0b';
        } else {
          dayLine = `Day ${bizDays} — past 20 business days. Contact our support team so we can escalate. Do NOT reset the registration while in review — that puts you at the back of the queue.`;
          dayColor = '#ef4444';
        }
        lines.push(`<div style="font-size:0.78rem;color:${dayColor};">${dayLine}</div>`);
      }

      // Compliance evaluation summary
      const ev = tp.evaluation;
      if (ev && ev.status) {
        if (ev.status === 'compliant') {
          lines.push(`<div style="font-size:0.78rem;color:#10b981;">✓ All ${ev.checksTotal} compliance checks passed — only the final manual review is left.</div>`);
        } else if (ev.failures && ev.failures.length) {
          const items = ev.failures.map(f =>
            `<li>${escapeHtml(f.requirement)} → <strong>${escapeHtml(f.field)}</strong>${f.reason ? `: ${escapeHtml(f.reason)}` : ''}</li>`
          ).join('');
          lines.push(`<div style="font-size:0.78rem;color:#ef4444;">${ev.checksTotal - ev.checksPassed} of ${ev.checksTotal} checks failed:<ul style="margin:4px 0 0 16px;padding:0;">${items}</ul></div>`);
        } else {
          lines.push(`<div style="font-size:0.78rem;color:#f59e0b;">Compliance returned non-compliant but no field-level details available.</div>`);
        }
      }

      if (!lines.length) return '';
      return `<div style="padding:8px 12px;background:rgba(255,255,255,0.02);border-left:2px solid var(--border);margin:-2px 0 6px 16px;border-radius:0 4px 4px 0;display:flex;flex-direction:column;gap:4px;">${lines.join('')}</div>`;
    })();

    // Brand detail panel — shown after the Brand row when the brand
    // is in a terminal status (APPROVED or FAILED). Surfaces the
    // TCR-issued identifiers + trust signals so coaches don't have to
    // log into Twilio Console to see how their brand actually landed.
    const brandDetail = (() => {
      const br = stages.brand;
      if (!br || !br.sid) return '';
      const st = String(br.status || '').toUpperCase();
      if (st !== 'APPROVED' && st !== 'FAILED') return '';

      const lines = [];
      if (br.tcrId) {
        lines.push(`<div style="display:flex;justify-content:space-between;gap:10px;"><span style="font-size:11px;color:var(--text-muted);">TCR Brand ID</span><span style="font-size:11px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--text);">${escapeHtml(br.tcrId)}</span></div>`);
      }
      if (br.brandType) {
        const isLvs = br.brandType === 'STANDARD' && br.skipAutomaticSecVet === true;
        const label = isLvs ? 'Standard (Low Volume)' : br.brandType.replace(/_/g, ' ');
        lines.push(`<div style="display:flex;justify-content:space-between;gap:10px;"><span style="font-size:11px;color:var(--text-muted);">Brand type</span><span style="font-size:11px;color:var(--text);">${escapeHtml(label)}</span></div>`);
      }
      if (br.identityStatus) {
        const statusLabel = br.identityStatus.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        const statusColor = br.identityStatus === 'VERIFIED' || br.identityStatus === 'VETTED_VERIFIED' ? '#22c55e' : 'var(--text)';
        lines.push(`<div style="display:flex;justify-content:space-between;gap:10px;"><span style="font-size:11px;color:var(--text-muted);">Identity verification</span><span style="font-size:11px;color:${statusColor};font-weight:600;">${escapeHtml(statusLabel)}</span></div>`);
      }
      // Trust score (0-100) is only populated when secondary vetting ran.
      // Low Volume Standard skips that vetting, so brandScore stays null —
      // show "N/A — Low Volume" instead of just hiding the row.
      if (br.brandScore != null) {
        const score = Number(br.brandScore);
        const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : 'var(--danger)';
        lines.push(`<div style="display:flex;justify-content:space-between;gap:10px;"><span style="font-size:11px;color:var(--text-muted);">Trust score</span><span style="font-size:11px;color:${color};font-weight:700;font-variant-numeric:tabular-nums;">${score}/100</span></div>`);
      } else if (br.skipAutomaticSecVet === true) {
        lines.push(`<div style="display:flex;justify-content:space-between;gap:10px;"><span style="font-size:11px;color:var(--text-muted);">Trust score</span><span style="font-size:11px;color:var(--text-muted);">Not scored (Low Volume)</span></div>`);
      }
      if (br.dateCreated) {
        const d = new Date(br.dateCreated);
        lines.push(`<div style="display:flex;justify-content:space-between;gap:10px;"><span style="font-size:11px;color:var(--text-muted);">Registered</span><span style="font-size:11px;color:var(--text);">${d.toLocaleDateString()}</span></div>`);
      }

      // Throughput hint based on brand type — actionable info coaches
      // can use to gauge if they need to upgrade.
      let throughputHint = '';
      if (br.brandType === 'STANDARD' && br.skipAutomaticSecVet === true) {
        throughputHint = `<div style="font-size:10px;color:var(--text-muted);margin-top:6px;line-height:1.4;font-style:italic;">Low Volume tier supports up to ~6,000 message segments/day across all campaigns. If you grow past that, upgrading to full Standard (with secondary vetting) raises the cap.</div>`;
      } else if (br.brandType === 'STANDARD' && br.brandScore != null) {
        throughputHint = `<div style="font-size:10px;color:var(--text-muted);margin-top:6px;line-height:1.4;font-style:italic;">Trust score drives daily message limits to T-Mobile and overall throughput. Higher = more headroom.</div>`;
      } else if (br.brandType === 'SOLE_PROPRIETOR') {
        throughputHint = `<div style="font-size:10px;color:var(--text-muted);margin-top:6px;line-height:1.4;font-style:italic;">Sole Proprietor brands have lower throughput caps than Standard. If volume becomes a concern, upgrading via an EIN-backed Standard brand is the path.</div>`;
      }

      if (!lines.length && !throughputHint) return '';
      return `<div style="padding:10px 12px;background:rgba(34,197,94,0.04);border-left:2px solid #22c55e;margin:-2px 0 6px 16px;border-radius:0 4px 4px 0;display:flex;flex-direction:column;gap:5px;">${lines.join('')}${throughputHint}</div>`;
    })();

    // Stage labels rewritten 2026-05-19 for non-technical coaches.
    // Old: "Customer Profile" / "A2P Trust Product" / "Brand (TCR)" /
    // "Campaign (TCR)" / "Phone Number" — these read as jargon to a coach.
    // New labels describe what's actually happening in plain language.
    const list = `
      <div class="a2p-stages-list">
        ${row('1. Verify your business', stages.customerProfile, activeStage === 'customerProfile')}
        ${row('2. Compliance bundle', stages.trustProduct, activeStage === 'trustProduct')}
        ${trustProductDetail}
        ${row('3. Carrier brand registration', stages.brand, activeStage === 'brand')}
        ${brandDetail}
        ${row('4. Carrier approval for texting', stages.campaign, activeStage === 'campaign')}
        ${numberRow}
      </div>`;

    let failBox = '';
    const brandStatus = stages.brand?.status;
    const brandFailed = brandStatus && (String(brandStatus).toLowerCase() === 'failed');
    const cpStatus = String(stages.customerProfile?.status || '').toLowerCase();
    const tpStatus = String(stages.trustProduct?.status || '').toLowerCase();
    const cpDeleted = cpStatus === 'not_found';
    const tpDeleted = tpStatus === 'not_found';

    if (brandFailed || reason) {
      failBox = `
        <div class="a2p-failure-box">
          <strong>Brand registration was rejected.</strong>
          ${reason ? `<div class="a2p-failure-reason">${String(reason).replace(/</g, '&lt;')}</div>` : '<div class="a2p-failure-reason">No specific reason surfaced yet. Contact our support team so we can pull the rejection detail, or use Reset registration to start over.</div>'}
          <div style="margin-top:10px;"><button class="btn-secondary" id="btn-retry-brand" style="font-size:0.8rem;">Retry with corrected data</button></div>
        </div>`;
    } else if (cpDeleted || tpDeleted) {
      failBox = `
        <div class="a2p-failure-box">
          <strong>A compliance bundle was removed.</strong>
          <div class="a2p-failure-reason">
            ${cpDeleted ? 'Customer Profile is gone. ' : ''}${tpDeleted ? 'Trust Product is gone. ' : ''}
            Click <em>Reset registration</em> (below) and then re-open the wizard to create fresh bundles.
          </div>
        </div>`;
    } else if (bundlesStuck) {
      failBox = `
        <div class="a2p-failure-box" style="background:rgba(245,158,11,0.08);border-color:rgba(245,158,11,0.3);color:#f59e0b;">
          <strong>Review is running long.</strong>
          <div class="a2p-failure-reason" style="color:var(--text);">
            The typical compliance review window is 10–15 business days for Standard registrations and about a week for Sole Proprietor. Past 20 business days, contact our support team to escalate — don't reset the registration.
            <br><br>
            <strong>What to do:</strong>
            <ol style="margin:8px 0 0 20px;padding:0;line-height:1.7;">
              <li>Email support so we can confirm the bundle isn't actually rejected on the carrier side.</li>
              <li>We'll open an escalation ticket on your behalf with the compliance team.</li>
              <li>Only use <em>Reset registration</em> if support confirms the bundle was rejected or deleted — resetting a pending bundle puts you at the back of the queue.</li>
            </ol>
          </div>
        </div>`;
    } else if (!stages.customerProfile && !stages.trustProduct && !stages.brand) {
      failBox = `<div style="margin-top:10px;"><button class="btn-secondary" id="btn-start-a2p-stages" style="font-size:0.8rem;">Get started with SMS setup</button></div>`;
    }

    const footer = lastChecked
      ? `<div style="font-size:0.7rem;color:var(--text-muted);margin-top:6px;">Last checked ${lastChecked}</div>`
      : '';

    return list + failBox + footer;
  }

  // ── Action banner ─────────────────────────────────────────────────
  // Inline alert above the stage list. Renders only when something is
  // actively blocking go-live (in-review / pending / failed). Hidden
  // when everything is clear, so the page is calm in steady state.
  // Pairs with the "You are here" treatment on the stage row below.
  function _renderA2pActionBanner(s, hasNumber) {
    const stages = s?.stages || {};
    const reason = s?.brandFailureReason || '';

    const isDone   = (st) => /^(twilio-approved|approved|verified)$/i.test(String(st || ''));
    const isFailed = (st) => /^(twilio-rejected|failed|rejected|suspended)$/i.test(String(st || ''));
    const isReview = (st) => /^(in-review|pending-review|in_progress|pending)$/i.test(String(st || ''));

    const items = [];
    const checks = [
      { key: 'customerProfile', label: 'Customer Profile',    stage: stages.customerProfile },
      { key: 'trustProduct',    label: 'Trust Product',       stage: stages.trustProduct },
      { key: 'brand',           label: 'Brand registration',  stage: stages.brand },
      { key: 'campaign',        label: 'Campaign',            stage: stages.campaign },
    ];
    for (const { label, stage } of checks) {
      const st = stage?.status;
      if (isFailed(st)) {
        items.push({
          title: `${label} was rejected`,
          sub: reason || 'Click the matching step below for the exact rejection reason and a retry button.',
          kind: 'fail',
        });
        break; // failed stage stops the chain — no need to surface later ones
      }
      if (isReview(st)) {
        items.push({
          title: `${label} is in compliance review`,
          sub: 'Carrier review typically lands in 1–3 business days. No action needed from you — the review team is working through the queue.',
          kind: 'review',
        });
        break;
      }
      // If status is empty / not started, keep looking — the wizard will
      // surface this elsewhere via the "Continue SMS setup" button.
    }

    // After all four stages are done, surface the phone number purchase if missing.
    if (items.length === 0 && !hasNumber) {
      const allDone = checks.every(c => isDone(c.stage?.status));
      if (allDone) {
        items.push({
          title: 'No phone number purchased yet',
          sub: 'Compliance is approved — click "Open registration wizard" below to buy your business number.',
          kind: 'pending',
        });
      }
    }

    if (items.length === 0) return ''; // all clear — banner stays hidden

    const count = items.length;
    return `
      <div class="a2p-action-banner-head">
        <div class="a2p-action-banner-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
        </div>
        <div>
          <div class="a2p-action-banner-title">${count} item${count > 1 ? 's' : ''} need${count > 1 ? '' : 's'} attention</div>
          <div class="a2p-action-banner-sub">SMS go-live estimated 3–7 days once these clear</div>
        </div>
      </div>
      <div class="a2p-action-banner-list">
        ${items.map(it => `
          <div class="a2p-action-banner-item">
            <div>
              <strong>${it.title}</strong>
              <div class="item-sub">${it.sub}</div>
            </div>
          </div>
        `).join('')}
      </div>`;
  }

  // ── Signal House A2P stages renderer ──────────────────────────────
  // Parallel to _renderA2pStages (Twilio). Renders the four-stage SH
  // pipeline: Subgroup → Brand → Campaign → Phone Number.
  // Used by the "SMS Compliance — via Signal House" coach-side card.
  function _renderShA2pStages(s) {
    const stages = s?.stages || {};
    const lastChecked = s?.lastCheckedAt ? new Date(s.lastCheckedAt).toLocaleString() : '';

    const pill = (raw) => {
      if (!raw) return { cls: 'a2p-stage-pill--empty', label: 'Not started' };
      const r = String(raw).toUpperCase();
      if (r === 'VERIFIED' || r === 'VETTED_VERIFIED' || r === 'ACTIVE' || r === 'READY') {
        return { cls: 'a2p-stage-pill--done', label: r === 'ACTIVE' ? 'Active' : 'Verified' };
      }
      if (r === 'UNVERIFIED' || r === 'REJECTED' || r === 'FAILED') {
        return { cls: 'a2p-stage-pill--failed', label: r === 'UNVERIFIED' ? 'Unverified' : 'Rejected' };
      }
      if (r === 'PENDING_CREATION' || r === 'PENDING_APPROVAL' || r === 'PENDING_REVIEW' || r === 'IN_PROGRESS' || r === 'PENDING' || r === 'PENDING_DCA_APPROVAL') {
        return { cls: 'a2p-stage-pill--review', label: 'In review' };
      }
      return { cls: 'a2p-stage-pill--pending', label: wordsFromRaw(raw) };
    };

    const row = (label, stage) => {
      const id = stage?.id || '';
      const p = pill(stage?.status);
      return `
        <div class="a2p-stage-row">
          <div>
            <div class="a2p-stage-label">${label}</div>
            ${id ? `<div class="a2p-stage-sub">${id}</div>` : ''}
          </div>
          <span class="a2p-stage-pill ${p.cls}">${p.label}</span>
        </div>`;
    };

    const numberPurchased = !!stages.number?.id || !!stages.number?.phoneNumber;
    const list = `
      <div class="a2p-stages-list">
        ${row('1. Subgroup', stages.subgroup)}
        ${row('2. Brand', stages.brand)}
        ${row('3. Campaign', stages.campaign)}
        <div class="a2p-stage-row">
          <div>
            <div class="a2p-stage-label">4. Phone Number</div>
            ${stages.number?.phoneNumber ? `<div class="a2p-stage-sub">${stages.number.phoneNumber}</div>` : ''}
          </div>
          <span class="a2p-stage-pill ${numberPurchased ? 'a2p-stage-pill--done' : 'a2p-stage-pill--empty'}">${numberPurchased ? 'Purchased' : 'Not purchased'}</span>
        </div>
      </div>`;

    const failureBox = s?.failureReason
      ? `<div class="a2p-failure-box" style="margin-top:12px;background:rgba(239,68,68,0.08);border-color:rgba(239,68,68,0.3);"><strong>Verification issue.</strong><div class="a2p-failure-reason">${String(s.failureReason).replace(/</g, '&lt;')}</div></div>`
      : '';

    const footer = lastChecked
      ? `<div style="font-size:0.7rem;color:var(--text-muted);margin-top:6px;">Last checked ${lastChecked}</div>`
      : '';

    return list + failureBox + footer;
  }

  // ── SMS & Voice Settings ──────────────────────────────────────────
  async function loadSmsSettings() {
    const supabase = _config.getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Refresh the inbox composer's channel-gating cache — A2P / number /
    // forwarding state may have changed since the last invalidation.
    window._invalidateCommsCaps?.();

    // coach_channels has no RLS/grants — must go through an authenticated Netlify
    // function (service role) or the query returns empty. See feedback memory
    // entry "platform_config is service-role-only" for the same pattern.
    let channel = null;
    let smsProvider = 'twilio';
    try {
      const res = await fetch('/.netlify/functions/coach-channels-get', {
        headers: { Authorization: 'Bearer ' + session.access_token },
      });
      if (res.ok) {
        const data = await res.json();
        channel = (data.channels || []).find(c => c.channel === 'sms') || null;
        smsProvider = data.smsProvider || 'twilio';
      }
    } catch {}

    // ── White-label visibility (Level 1) ──
    // Non-owner coaches should never see provider names ("Twilio" / "Signal House").
    // For owners (admin), keep both cards + provider tags visible — useful for
    // managing both providers during migration. For non-owners, hide the inactive
    // provider's card and strip provider branding from the active card.
    const isOwner = !!window._appIsOwner;
    const twilioCard = document.getElementById('a2p-status-card');
    const shCard = document.getElementById('a2p-sh-status-card');
    if (!isOwner) {
      if (smsProvider === 'signalhouse') {
        twilioCard?.classList.add('hidden');
        shCard?.classList.remove('hidden');
      } else {
        twilioCard?.classList.remove('hidden');
        shCard?.classList.add('hidden');
      }
      // Strip provider branding from titles for non-owners
      const twilioTitle = twilioCard?.querySelector('.card-header h2');
      if (twilioTitle) twilioTitle.textContent = 'SMS Compliance';
      const shTitle = shCard?.querySelector('.card-header h2');
      if (shTitle) shTitle.innerHTML = 'SMS Compliance';
      // Hide the "Open Signal House portal" link (provider-named external link)
      shCard?.querySelectorAll('a[href*="signalhouse.io"]').forEach(a => a.classList.add('hidden'));
    }

    // (The old pre-wizard status shims were written here — removed 2026-08-13
    // with the dead handlers below; the comms wizard owns this UI now.)
    const hasNumber = !!channel?.twilio_phone_number;

    // ── Signal House A2P card (owner-visible during migration testing) ──
    // Renders a parallel status card for SH brand/campaign/number provisioning.
    // The card itself is shown/hidden via the is_owner check in app init.
    // This block fetches + renders status whenever SMS settings load.
    (async () => {
      const shCard = document.getElementById('a2p-sh-status-card');
      if (!shCard || shCard.classList.contains('hidden')) return;
      const badge = document.getElementById('a2p-sh-status-badge');
      const target = document.getElementById('a2p-sh-stages');
      if (!target) return;
      try {
        const res = await fetch('/.netlify/functions/signalhouse-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ action: 'status' }),
        });
        if (!res.ok) {
          if (badge) {
            badge.textContent = 'Status check failed';
            badge.className = 'email-domain-status email-domain-status--error';
          }
          target.innerHTML = `<p style="font-size:13px;color:var(--danger);">HTTP ${res.status}: ${(await res.text()).slice(0, 200)}</p>`;
          return;
        }
        const json = await res.json();
        if (badge) {
          const st = json?.status;
          if (st === 'active') {
            badge.textContent = 'Active';
            badge.className = 'email-domain-status email-domain-status--verified';
          } else if (st === 'rejected') {
            badge.textContent = 'Rejected';
            badge.className = 'email-domain-status email-domain-status--error';
          } else if (st === 'pending') {
            badge.textContent = 'In review';
            badge.className = 'email-domain-status email-domain-status--pending';
          } else {
            badge.textContent = 'Not started';
            badge.className = 'email-domain-status';
          }
        }
        target.innerHTML = _renderShA2pStages(json);
      } catch (err) {
        if (badge) {
          badge.textContent = 'Status check failed';
          badge.className = 'email-domain-status email-domain-status--error';
        }
        target.innerHTML = `<p style="font-size:13px;color:var(--danger);">Status check failed: ${err.message}</p>`;
      }
    })();
    document.getElementById('btn-refresh-a2p-sh-status')?.addEventListener('click', () => {
      loadSmsSettings();
    });

    // A2P per-stage status — always render (registration can start BEFORE buying a
    // number). The "Phone Number" stage row is the last step and shows whether
    // a number has been purchased yet.
    const a2pBadge = document.getElementById('a2p-status-badge');
    const a2pContent = document.getElementById('a2p-content');
    if (a2pBadge && a2pContent) {
      const status = channel?.a2p_status;
      // Reveal the "Send test SMS" button only once SMS is actually live
      // end-to-end (number purchased + brand approved). Twilio still allows
      // SMS to verified caller IDs without A2P, but the button on the card
      // is meant for *real* delivery confirmation, so we gate strictly.
      const testBtn = document.getElementById('btn-send-test-sms');
      if (testBtn) {
        const liveForTest = !!channel?.twilio_phone_number && (status === 'APPROVED' || status === 'approved');
        testBtn.classList.toggle('hidden', !liveForTest);
      }
      // Reveal the inline appeal panel when registration is rejected.
      // Hidden by default; populated with the latest rejection reason
      // and ready to capture the coach's "what changed" note.
      const appealPanel = document.getElementById('a2p-appeal-panel');
      const appealReasonEl = document.getElementById('a2p-appeal-reason');
      const isRejected = status === 'FAILED' || status === 'failed' || status === 'twilio-rejected';
      if (appealPanel && isRejected) {
        appealPanel.classList.remove('hidden');
        if (appealReasonEl) {
          const reason = channel?.a2p_failure_reason || '';
          appealReasonEl.innerHTML = reason
            ? `<div style="background:rgba(0,0,0,0.04);border:1px solid var(--border);border-radius:var(--radius-sm);padding:10px 12px;font-size:11px;color:var(--text);line-height:1.55;"><div style="font-weight:600;color:var(--danger);letter-spacing:0;font-size:10px;margin-bottom:4px;">Twilio's reason</div>${escHtml(reason)}</div>`
            : '<div style="font-size:11px;color:var(--text-muted);font-style:italic;">No reason text was returned by Twilio. Reviewers will still see your appeal note.</div>';
        }
      } else if (appealPanel) {
        appealPanel.classList.add('hidden');
      }
      if (status === 'APPROVED' || status === 'approved') {
        a2pBadge.textContent = 'Approved';
        a2pBadge.className = 'email-domain-status email-domain-status--verified';
      } else if (status === 'FAILED' || status === 'twilio-rejected') {
        a2pBadge.textContent = 'Rejected';
        a2pBadge.className = 'email-domain-status email-domain-status--error';
      } else if (channel?.a2p_brand_sid) {
        a2pBadge.textContent = status || 'Pending';
        a2pBadge.className = 'email-domain-status email-domain-status--pending';
      } else if (channel?.a2p_customer_profile_sid || channel?.a2p_trust_product_sid) {
        a2pBadge.textContent = 'Bundles submitted';
        a2pBadge.className = 'email-domain-status email-domain-status--pending';
      } else {
        a2pBadge.textContent = 'Not Registered';
        a2pBadge.className = 'email-domain-status';
      }
      a2pContent.innerHTML = `<div id="a2p-stages" style="margin-top:10px;font-size:13px;color:var(--text-muted);">Loading status…</div>`;

      // Fetch rich per-stage breakdown from the server
      (async () => {
        try {
          const res = await fetch('/.netlify/functions/twilio-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({ action: 'status' }),
          });
          const target = document.getElementById('a2p-stages');
          if (!target) return;
          // Check res.ok BEFORE parsing — a 401 with `{error: 'Invalid token'}`
          // would otherwise be passed to _renderA2pStages, whose `stages = s?.stages || {}`
          // fallback silently renders every row as "Not started" (since the error
          // payload has no .stages field). The SH card right below does this
          // check and surfaces the actual HTTP error; mirror that pattern here
          // so users in a stale session see "401 Invalid token" instead of a
          // misleading row of NOT STARTED pills.
          if (!res.ok) {
            const errBody = (await res.text()).slice(0, 200);
            target.innerHTML = `<p style="font-size:13px;color:var(--danger);">HTTP ${res.status}: ${errBody}${res.status === 401 ? ' — refresh the page or log out and back in.' : ''}</p>`;
            return;
          }
          const json = await res.json();
          target.innerHTML = _renderA2pStages(json, hasNumber);

          // Render the action banner above the stage list. Only shows when
          // there's an active blocker; hides cleanly when everything is OK.
          const bannerEl = document.getElementById('a2p-action-banner');
          if (bannerEl) {
            const bannerHtml = _renderA2pActionBanner(json, hasNumber);
            if (bannerHtml) {
              bannerEl.innerHTML = bannerHtml;
              bannerEl.classList.remove('hidden');
            } else {
              bannerEl.innerHTML = '';
              bannerEl.classList.add('hidden');
            }
          }

          // ⚠️ BOTH OF THESE CLICKED #btn-start-a2p, a button from the old
          // one-click registration that no longer exists, so they did nothing.
          // They open the SMS setup wizard, which picks the registration up
          // where it stands, like "Continue SMS setup".
          document.getElementById('btn-start-a2p-stages')?.addEventListener('click', () => openCommsWizard('sms'));
          document.getElementById('btn-retry-brand')?.addEventListener('click', () => openCommsWizard('sms'));

          // ── Auto-register on first visit if profile is saved (chunk 2) ──
          // Conditions: status is explicitly 'not_registered' AND coach
          // has saved profile data AND auto-status hasn't already been
          // attempted. The explicit 'not_registered' guard is critical —
          // any in-flight bundle/brand/campaign must not re-trigger.
          const isUnregistered = json.status === 'not_registered'
            && !json.brandSid
            && !json.stages?.customerProfile?.sid
            && !json.stages?.trustProduct?.sid;
          if (isUnregistered) {
            try {
              const profRes = await fetch('/.netlify/functions/twilio-a2p-profile', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
                body: JSON.stringify({ action: 'get' }),
              });
              const profJson = await profRes.json();
              if (profJson.hasProfile && !profJson.autoStatus) {
                // Optimistic UI — auto-fire registration in the background.
                target.innerHTML = `
                  <div style="background:rgba(59,130,246,0.06);border:1px solid rgba(59,130,246,0.2);border-radius:var(--radius-sm);padding:12px 14px;">
                    <div style="display:flex;align-items:center;gap:10px;">
                      <div style="width:8px;height:8px;border-radius:50%;background:#3b82f6;flex-shrink:0;"></div>
                      <div style="font-size:13px;font-weight:700;color:var(--text);">Starting auto-registration…</div>
                    </div>
                    <p style="font-size:11px;color:var(--text-muted);margin:6px 0 0;">We're using your saved profile to register your A2P brand. This takes a few seconds.</p>
                  </div>`;
                try {
                  const autoRes = await fetch('/.netlify/functions/twilio-auto-register', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
                    body: JSON.stringify({}),
                  });
                  const autoJson = await autoRes.json();
                  if (autoJson.status === 'submitted') {
                    target.innerHTML = `
                      <div style="background:rgba(34,197,94,0.06);border:1px solid rgba(34,197,94,0.2);border-radius:var(--radius-sm);padding:12px 14px;">
                        <div style="display:flex;align-items:center;gap:10px;">
                          <div style="width:8px;height:8px;border-radius:50%;background:#22c55e;flex-shrink:0;"></div>
                          <div style="font-size:13px;font-weight:700;color:var(--text);">Registration in progress (~10–15 business days)</div>
                        </div>
                        <p style="font-size:11px;color:var(--text-muted);margin:6px 0 8px;">Carriers are now reviewing your A2P brand. We'll update you here when the status changes.</p>
                        <button class="btn-secondary" id="btn-auto-refresh" style="font-size:11px;padding:6px 12px;">Refresh status</button>
                      </div>`;
                    document.getElementById('btn-auto-refresh')?.addEventListener('click', () => loadSmsSettings());
                  } else if (autoJson.status === 'preflight_blocked') {
                    const failCount = autoJson.preflight?.summary?.fail || (autoJson.missing?.length || 0);
                    target.innerHTML = `
                      <div style="background:rgba(245,158,11,0.06);border:1px solid rgba(245,158,11,0.25);border-radius:var(--radius-sm);padding:12px 14px;">
                        <div style="display:flex;align-items:center;gap:10px;">
                          <div style="width:8px;height:8px;border-radius:50%;background:#f59e0b;flex-shrink:0;"></div>
                          <div style="font-size:13px;font-weight:700;color:var(--text);">${failCount} item${failCount === 1 ? '' : 's'} blocking auto-registration</div>
                        </div>
                        <p style="font-size:11px;color:var(--text-muted);margin:6px 0 8px;">Open the registration wizard to fix and resubmit.</p>
                        <button class="btn-primary" id="btn-auto-fix" style="font-size:11px;padding:6px 12px;">Open wizard</button>
                      </div>`;
                    document.getElementById('btn-auto-fix')?.addEventListener('click', () => document.getElementById('btn-open-a2p-wizard')?.click());
                  } else {
                    target.innerHTML = `
                      <div style="background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.2);border-radius:var(--radius-sm);padding:12px 14px;">
                        <div style="display:flex;align-items:center;gap:10px;">
                          <div style="width:8px;height:8px;border-radius:50%;background:#ef4444;flex-shrink:0;"></div>
                          <div style="font-size:13px;font-weight:700;color:var(--text);">Auto-registration failed</div>
                        </div>
                        <p style="font-size:11px;color:var(--text-muted);margin:6px 0 8px;">${escHtml(autoJson.error || 'Unknown error')}</p>
                        <button class="btn-primary" id="btn-auto-fallback" style="font-size:11px;padding:6px 12px;">Open wizard manually</button>
                      </div>`;
                    document.getElementById('btn-auto-fallback')?.addEventListener('click', () => document.getElementById('btn-open-a2p-wizard')?.click());
                  }
                } catch (err) {
                  target.innerHTML = `<p style="font-size:13px;color:var(--danger);">Auto-registration error: ${escHtml(err.message)}. Open the wizard to register manually.</p>`;
                }
              } else if (profJson.autoStatus === 'submitted') {
                // Show the in-progress banner without re-firing.
                target.innerHTML = `
                  <div style="background:rgba(34,197,94,0.06);border:1px solid rgba(34,197,94,0.2);border-radius:var(--radius-sm);padding:12px 14px;">
                    <div style="display:flex;align-items:center;gap:10px;">
                      <div style="width:8px;height:8px;border-radius:50%;background:#22c55e;flex-shrink:0;"></div>
                      <div style="font-size:13px;font-weight:700;color:var(--text);">Registration in progress (~10–15 business days)</div>
                    </div>
                    <p style="font-size:11px;color:var(--text-muted);margin:6px 0 0;">Carriers are reviewing your A2P brand. We'll update you here when the status changes.</p>
                  </div>`;
              }
            } catch (_) { /* ignore — falls back to normal stages view */ }
          }
        } catch (err) {
          const target = document.getElementById('a2p-stages');
          if (target) target.innerHTML = `<p style="font-size:13px;color:var(--danger);">Status check failed: ${err.message}</p>`;
        }
      })();
    }
  }

  // A2P registration (delegated click)
  document.addEventListener('click', (e) => {
    if (e.target.id === 'btn-start-a2p') {
      (async () => {
        try {
          const supabase = _config.getSupabase();
          const { data: { session } } = await supabase.auth.getSession();
          const settings = loadSettings();
          const res = await fetch('/.netlify/functions/twilio-register-brand', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({
              businessName: settings.emailBusinessName || 'Coaching Business',
              website: 'https://fitopspro.com',
              email: session.user.email,
            }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.error);
          showToast('10DLC brand registration submitted. Review takes 10-15 days.', 'success');
          // Auto-create campaign
          const campRes = await fetch('/.netlify/functions/twilio-register-campaign', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          });
          const campJson = await campRes.json();
          if (campRes.ok) showToast('Campaign created successfully.', 'success');
          loadSmsSettings();
        } catch (err) {
          showToast(err.message, 'error');
        }
      })();
    }
  });

  // Load SMS settings when integrations tab is shown
  document.querySelectorAll('.settings-tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.dataset.tab === 'integrations') {
        loadCommsCardStatuses();
        // Populate the new "SMS Compliance (A2P 10DLC)" card with 5-stage status
        loadSmsSettings();
        // Automation integrations (Slack / Notion / Google) connect rows
        initAutomationIntegrations();
      }
    });
  });

  // Populate the A2P card on initial load in case user lands on Integrations directly
  if (document.getElementById('a2p-status-card')) {
    setTimeout(() => loadSmsSettings(), 300);
  }

  // ── A2P card action buttons ───────────────────────────────────────
  document.getElementById('btn-refresh-a2p-status')?.addEventListener('click', () => {
    const stages = document.getElementById('a2p-stages');
    if (stages) stages.innerHTML = '<div style="color:var(--text-muted);font-size:13px;">Refreshing…</div>';
    loadSmsSettings();
  });
  // "Continue SMS setup" opens the Twilio SMS wizard directly. (It used to
  // forward a click to #btn-start-comms-wizard, which no longer exists in the
  // markup, so the button silently did nothing.)
  document.getElementById('btn-open-a2p-wizard')?.addEventListener('click', () => openCommsWizard('sms'));

  // ── Test SMS button (chunk 3) ─────────────────────────────────────
  // Toggles the inline panel; sends via the existing twilio-verify-caller
  // endpoint's send-test action. With A2P approved, Twilio doesn't require
  // the destination to be a verified caller ID.
  document.getElementById('btn-send-test-sms')?.addEventListener('click', () => {
    const panel = document.getElementById('a2p-test-sms-panel');
    if (!panel) return;
    panel.classList.toggle('hidden');
    if (!panel.classList.contains('hidden')) {
      const phoneEl = document.getElementById('a2p-test-sms-phone');
      // Pre-fill with the coach's auth-user phone if we know it (otherwise
      // leave blank — they'll usually want to send to their personal cell).
      if (phoneEl && !phoneEl.value) {
        const settings = loadSettings();
        if (settings.a2pPhone) phoneEl.value = settings.a2pPhone;
      }
      phoneEl?.focus();
    }
  });
  document.getElementById('btn-test-sms-cancel')?.addEventListener('click', () => {
    document.getElementById('a2p-test-sms-panel')?.classList.add('hidden');
    const r = document.getElementById('a2p-test-sms-result');
    if (r) r.innerHTML = '';
  });
  // ── SMS analytics (chunk 7) ───────────────────────────────────────
  let _smsAnalyticsPeriod = '30d';
  async function _renderSmsAnalytics() {
    const bodyEl = document.getElementById('sms-analytics-body');
    if (!bodyEl) return;
    bodyEl.innerHTML = '<div style="font-size:11px;color:var(--text-muted);">Loading…</div>';
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/coach-sms-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ period: _smsAnalyticsPeriod }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Load failed');
      const c = json.counts || {};
      const cost = json.cost || {};
      const segs = json.segments || {};
      const card = (label, value, sub) => `
        <div style="background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-sm);padding:12px 14px;">
          <div style="font-size:10px;font-weight:600;letter-spacing:0;color:var(--text-muted);">${escHtml(label)}</div>
          <div style="font-size:22px;font-weight:700;color:var(--text);font-variant-numeric:tabular-nums;margin-top:4px;line-height:1;">${escHtml(String(value))}</div>
          ${sub ? `<div style="font-size:11px;color:var(--text-muted);margin-top:4px;">${sub}</div>` : ''}
        </div>`;
      const dollar = (n) => '$' + (Number(n) || 0).toFixed(2);
      const truncWarning = json.meta?.fallbackTruncated
        ? `<div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:var(--radius-sm);padding:8px 12px;margin-bottom:10px;font-size:11px;color:#92400e;">Showing the most recent 5,000 messages — older traffic in this period isn't included. Run the analytics RPC migration to remove this cap.</div>`
        : '';
      bodyEl.innerHTML = `
        ${truncWarning}
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;">
          ${card('Sent', c.sent || 0, segs.outbound ? `${segs.outbound} segments` : '')}
          ${card('Delivered', c.delivered || 0, c.sent > 0 ? `${c.deliveryRate}% delivery rate` : '')}
          ${card('Failed', c.failed || 0, '')}
          ${card('Replied', c.replied || 0, segs.inbound ? `${segs.inbound} inbound segments` : '')}
          ${card('Opted out', c.optedOut || 0, c.optOutsFromStop ? `${c.optOutsFromStop} from STOP replies` : '')}
          ${card('Cost (est.)', dollar(cost.estimateUsd), `${dollar(cost.pricing?.smsOutbound || 0)}/seg out · ${dollar(cost.pricing?.smsInbound || 0)}/seg in`)}
        </div>
        ${json.bounds?.firstSent ? `<div style="font-size:11px;color:var(--text-muted);margin-top:10px;">First send: ${new Date(json.bounds.firstSent).toLocaleString()} · Last send: ${new Date(json.bounds.lastSent).toLocaleString()}</div>` : ''}
        <div style="font-size:10px;color:var(--text-muted);margin-top:10px;font-style:italic;">Cost is an estimate based on Twilio US local pricing. Actual billing reflects per-carrier surcharges + A2P fees.</div>`;
    } catch (err) {
      bodyEl.innerHTML = `<div style="color:var(--danger);">${escHtml(err.message)}</div>`;
    }
  }
  document.getElementById('btn-open-sms-analytics')?.addEventListener('click', () => {
    const panel = document.getElementById('a2p-sms-analytics-panel');
    if (!panel) return;
    panel.classList.toggle('hidden');
    if (!panel.classList.contains('hidden')) _renderSmsAnalytics();
  });
  document.getElementById('btn-close-sms-analytics')?.addEventListener('click', () => {
    document.getElementById('a2p-sms-analytics-panel')?.classList.add('hidden');
  });
  document.querySelectorAll('#sms-analytics-period .seg-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const period = btn.getAttribute('data-period');
      if (!period || period === _smsAnalyticsPeriod) return;
      _smsAnalyticsPeriod = period;
      document.querySelectorAll('#sms-analytics-period .seg-btn').forEach(b => {
        const isActive = b.getAttribute('data-period') === period;
        b.classList.toggle('active', isActive);
        b.style.background = isActive ? 'var(--accent)' : 'var(--surface)';
        b.style.color = isActive ? '#fff' : 'var(--text-muted)';
      });
      _renderSmsAnalytics();
    });
  });

  // ── Opt-out list (chunk 6) ────────────────────────────────────────
  async function _renderOptOuts() {
    const listEl = document.getElementById('opt-out-list');
    if (!listEl) return;
    listEl.innerHTML = '<div style="font-size:11px;color:var(--text-muted);">Loading…</div>';
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/coach-opt-outs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'list' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Load failed');
      const rows = json.optOuts || [];
      if (rows.length === 0) {
        listEl.innerHTML = '<div style="font-size:12px;color:var(--text-muted);font-style:italic;padding:8px 0;">No opt-outs yet. Anyone who texts STOP will appear here automatically.</div>';
        return;
      }
      const sourceBadge = (s) => {
        if (s === 'inbound_stop') return '<span style="display:inline-block;font-size:10px;font-weight:600;letter-spacing:0;padding:2px 8px;border-radius:99px;background:rgba(239,68,68,0.08);color:var(--danger);">STOP reply</span>';
        if (s === 'manual') return '<span style="display:inline-block;font-size:10px;font-weight:600;letter-spacing:0;padding:2px 8px;border-radius:99px;background:rgba(0,0,0,0.05);color:var(--text-muted);">Manual</span>';
        return '<span style="display:inline-block;font-size:10px;font-weight:600;letter-spacing:0;padding:2px 8px;border-radius:99px;background:rgba(59,130,246,0.08);color:var(--accent);">' + escHtml(wordsFromRaw(s)) + '</span>';
      };
      listEl.innerHTML = '<div style="display:flex;flex-direction:column;border:1px solid var(--border);border-radius:var(--radius-sm);overflow:hidden;">' +
        rows.map((r, i) => `
          <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;${i > 0 ? 'border-top:1px solid var(--border);' : ''}background:var(--surface);">
            <div style="flex:1;min-width:0;">
              <div style="font-size:13px;font-weight:600;color:var(--text);font-variant-numeric:tabular-nums;">${escHtml(r.phone_number)}</div>
              <div style="font-size:11px;color:var(--text-muted);margin-top:2px;">${sourceBadge(r.source)} <span style="margin-left:6px;">${r.set_by_user_id ? 'Blocked by ' + escHtml(r.set_by_name || 'a team member') + ' · ' : ''}${new Date(r.opted_out_at).toLocaleDateString()}</span>${r.note ? ' · ' + escHtml(r.note) : ''}</div>
            </div>
            <button class="btn-secondary" data-phone="${escHtml(r.phone_number)}" style="font-size:11px;padding:6px 10px;">Remove</button>
          </div>
        `).join('') + '</div>';
      listEl.querySelectorAll('button[data-phone]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const phone = btn.getAttribute('data-phone');
          const attestation = prompt(
            `Re-add ${phone} to your SMS list?\n\n` +
            `Twilio compliance requires you to record HOW the recipient re-opted-in.\n` +
            `Type the source of re-consent (e.g. "verbal at consult 5/3", "signed waiver"). Min 10 chars:`
          );
          if (!attestation || attestation.trim().length < 10) {
            if (attestation !== null) showToast('Re-consent record must be at least 10 characters.', 'error');
            return;
          }
          btn.disabled = true; btn.textContent = 'Removing…';
          try {
            const sb = _config.getSupabase();
            const { data: { session: s } } = await sb.auth.getSession();
            const res = await fetch('/.netlify/functions/coach-opt-outs', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
              body: JSON.stringify({ action: 'remove', phoneNumber: phone, attestation: attestation.trim() }),
            });
            const j = await res.json();
            if (!res.ok) throw new Error(j.error || 'Remove failed');
            showToast(`${phone} removed from opt-out list.`, 'success');
            _renderOptOuts();
          } catch (err) {
            showToast(err.message, 'error');
            btn.disabled = false; btn.textContent = 'Remove';
          }
        });
      });
    } catch (err) {
      listEl.innerHTML = `<div style="font-size:12px;color:var(--danger);">${escHtml(err.message)}</div>`;
    }
  }
  document.getElementById('btn-open-opt-outs')?.addEventListener('click', () => {
    const panel = document.getElementById('a2p-opt-outs-panel');
    if (!panel) return;
    panel.classList.toggle('hidden');
    if (!panel.classList.contains('hidden')) _renderOptOuts();
  });
  document.getElementById('btn-close-opt-outs')?.addEventListener('click', () => {
    document.getElementById('a2p-opt-outs-panel')?.classList.add('hidden');
  });
  document.getElementById('btn-opt-out-add-submit')?.addEventListener('click', async () => {
    const phoneEl = document.getElementById('opt-out-add-phone');
    const noteEl = document.getElementById('opt-out-add-note');
    const phone = phoneEl?.value?.trim() || '';
    if (!phone) { showToast('Enter a phone number', 'error'); return; }
    if (!/^\+?\d{10,15}$/.test(phone.replace(/[^\d+]/g, ''))) {
      showToast('Phone must be 10-15 digits, optionally prefixed with +', 'error');
      return;
    }
    const btn = document.getElementById('btn-opt-out-add-submit');
    const original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/coach-opt-outs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'add', phoneNumber: phone, note: noteEl?.value?.trim() || '' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Add failed');
      phoneEl.value = '';
      if (noteEl) noteEl.value = '';
      showToast('Number blocked.', 'success');
      _renderOptOuts();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      btn.disabled = false; btn.textContent = original;
    }
  });

  // ── Appeal & Resubmit (chunk 5) ───────────────────────────────────
  document.getElementById('btn-appeal-cancel')?.addEventListener('click', () => {
    document.getElementById('a2p-appeal-panel')?.classList.add('hidden');
    const r = document.getElementById('a2p-appeal-result');
    if (r) r.innerHTML = '';
  });
  document.getElementById('btn-appeal-edit-data')?.addEventListener('click', () => {
    // Open the registration wizard so the coach can edit fields before resubmitting.
    document.getElementById('btn-open-a2p-wizard')?.click();
  });
  document.getElementById('btn-appeal-submit')?.addEventListener('click', async () => {
    const noteEl = document.getElementById('a2p-appeal-note');
    const note = noteEl?.value?.trim() || '';
    if (note.length < 20) {
      showToast('Tell us what changed in at least 20 characters — reviewers want specifics.', 'error');
      noteEl?.focus();
      return;
    }
    const btn = document.getElementById('btn-appeal-submit');
    const resultEl = document.getElementById('a2p-appeal-result');
    const original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Resubmitting…';
    if (resultEl) resultEl.innerHTML = '';

    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      // Fetch the latest reason so we can attach it to the appeal record.
      let previousReason = '';
      try {
        const sRes = await fetch('/.netlify/functions/twilio-register-brand', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ action: 'status' }),
        });
        const sJson = await sRes.json();
        previousReason = sJson?.brandFailureReason
          || (Array.isArray(sJson?.rejectionReasons) && sJson.rejectionReasons.map(r => r.message).join('; '))
          || '';
      } catch (_) {}

      const res = await fetch('/.netlify/functions/twilio-a2p-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ appealNote: note, previousReason }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Appeal failed');
      if (resultEl) {
        resultEl.innerHTML = '<div style="color:#22c55e;font-weight:600;">Appeal submitted. Twilio review starts again — typically 10-15 business days.</div>';
      }
      noteEl.value = '';
      // Refresh the card so the new pending state shows
      setTimeout(() => loadSmsSettings(), 800);
    } catch (err) {
      if (resultEl) resultEl.innerHTML = `<div style="color:var(--danger);">${escHtml(err.message)}</div>`;
    } finally {
      btn.disabled = false; btn.textContent = original;
    }
  });

  document.getElementById('btn-test-sms-send')?.addEventListener('click', async () => {
    const phoneEl = document.getElementById('a2p-test-sms-phone');
    const bodyEl = document.getElementById('a2p-test-sms-body');
    const resultEl = document.getElementById('a2p-test-sms-result');
    const btn = document.getElementById('btn-test-sms-send');
    const phone = phoneEl?.value?.trim() || '';
    const body = bodyEl?.value?.trim() || '';
    if (!phone) { showToast('Enter a phone number', 'error'); return; }
    if (!/^\+?\d{10,15}$/.test(phone.replace(/[^\d+]/g, ''))) {
      showToast('Phone must be 10-15 digits, optionally prefixed with +', 'error');
      return;
    }
    const original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Sending…';
    if (resultEl) resultEl.innerHTML = '';
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/twilio-verify-caller', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'send-test', phoneNumber: phone, message: body || undefined }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Send failed');
      if (resultEl) {
        resultEl.innerHTML = `<div style="color:#22c55e;font-weight:600;">Sent — Twilio status: ${escHtml(json.status || 'queued')}.</div><div style="color:var(--text-muted);font-size:11px;margin-top:4px;">Message SID: ${escHtml(json.messageSid || '')}. Check the destination phone in a few seconds.</div>`;
      }
    } catch (err) {
      if (resultEl) resultEl.innerHTML = `<div style="color:var(--danger);">${escHtml(err.message)}</div>`;
    } finally {
      btn.disabled = false; btn.textContent = original;
    }
  });
  // Signal House registration wizard launcher — uses the same comms-modal
  // shell as the Twilio wizard but loads SH_A2P_STEPS.
  document.getElementById('btn-open-a2p-sh-wizard')?.addEventListener('click', () => {
    _commsWizSteps = [...SH_A2P_STEPS, DONE_STEP];
    _commsWizIdx = 0;
    renderCommsWizStep();
    document.getElementById('comms-modal-overlay').classList.remove('hidden');
  });
  document.getElementById('btn-reset-a2p-registration')?.addEventListener('click', async () => {
    const confirmed = confirm(window._appIsOwner
      ? 'Reset A2P registration?\n\n' +
        'This clears the saved registration from FitOps Pro so you can start fresh. ' +
        'It does NOT delete anything in Twilio — any existing approved bundles in your Twilio account ' +
        'will be reused automatically on your next submission.\n\n' +
        'Use this when a bundle is stuck mid-review or you need to re-submit with corrected data.'
      : 'Reset SMS registration?\n\n' +
        'This clears your saved registration so you can start fresh.\n\n' +
        'Use this when registration is stuck mid-review or you need to re-submit with corrected data.'
    );
    if (!confirmed) return;
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/twilio-register-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'reset' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Reset failed');
      showToast('Registration cleared. Open the wizard to submit again.', 'success');
      loadSmsSettings();
    } catch (err) {
      showToast('Reset failed: ' + err.message, 'error');
    }
  });

  // Signal House reset registration — same pattern as Twilio's. Clears
  // sh_subgroup_id / sh_brand_id / etc. from coach_channels so the wizard
  // starts fresh. Does NOT delete the actual subgroup/brand on SH's side
  // (those persist forever — admin can manually clean them up via SH
  // portal if needed). Use when a brand failed verification or you need
  // to re-submit with corrected data.
  document.getElementById('btn-reset-a2p-sh-registration')?.addEventListener('click', async () => {
    const confirmed = confirm(window._appIsOwner
      ? 'Reset Signal House registration?\n\n' +
        'This clears the saved SH registration from FitOps Pro so you can start fresh. ' +
        'It does NOT delete the subgroup or brand on Signal House — those persist on their side and you can clean them up manually via the SH portal if needed.\n\n' +
        'Use this when a brand failed verification or you need to re-submit with corrected data.'
      : 'Reset SMS registration?\n\n' +
        'This clears your saved registration so you can start fresh.\n\n' +
        'Use this when registration failed or you need to re-submit with corrected data.'
    );
    if (!confirmed) return;
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/signalhouse-register-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'reset' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Reset failed');
      showToast(_shLabel('Registration cleared. Open the wizard to start fresh.', 'Signal House registration cleared. Open the wizard to start fresh.'), 'success');
      loadSmsSettings();
    } catch (err) {
      showToast('Reset failed: ' + err.message, 'error');
    }
  });

  // Toast listener from inbox module
  document.addEventListener('show-toast', (e) => {
    showToast(e.detail.message, e.detail.type);
  });

  // ── Retention Settings ────────────────────────────────────────────
  const DEFAULT_FOLLOWUP_STEPS = [
    { dayOffset: 7,  body: "Hey {name}, just wanted to say it's been great working with you. The door is always open if you decide to come back. Wishing you the best.", sendEmail: false },
    { dayOffset: 30, body: "Hey {name}, hope you're doing well. Just checking in \u2014 how's everything going?", sendEmail: false },
    { dayOffset: 60, body: "Hey {name}, wanted to share that we've been making some great updates to the program. Miss having you in here.", sendEmail: true },
    { dayOffset: 90, body: "Hey {name}, I've been thinking about our time working together. If you ever want to jump back in, I'd love to work something out. Just reply and we'll get you set up.", sendEmail: true },
  ];

  window.renderRetentionFollowups = renderRetentionFollowups;
  window.loadCommsCardStatuses = loadCommsCardStatuses;
  function renderRetentionFollowups() {
    const settings = loadSettings();
    const steps = settings.retentionFollowupSteps || DEFAULT_FOLLOWUP_STEPS;
    const list = document.getElementById('retention-followup-list');
    if (!list) return;
    list.innerHTML = steps.map((step, i) => `
      <div class="save-action-config" style="margin-bottom:8px;" data-idx="${i}">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
          <div style="display:flex;align-items:center;gap:8px;">
            <label style="font-size:11px;font-weight:600;color:var(--text-muted);">Day</label>
            <input type="number" class="fu-day-input" value="${step.dayOffset}" min="1" max="365" style="width:60px;padding:4px 8px;font-size:13px;border:1px solid var(--border);border-radius:var(--radius-sm);background:var(--surface);color:var(--text);" />
            <label style="font-size:12px;color:var(--text-muted);display:flex;align-items:center;gap:4px;cursor:pointer;">
              <input type="checkbox" class="fu-email-check" ${step.sendEmail ? 'checked' : ''} /> Also email
            </label>
          </div>
          <button class="fu-remove-btn" style="background:none;border:none;color:var(--text-muted);cursor:pointer;font-size:16px;" title="Remove">&times;</button>
        </div>
        <textarea class="fu-body-input" rows="2" style="width:100%;resize:none;font-size:12px;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius-sm);background:var(--surface);color:var(--text);line-height:1.4;">${escHtml(step.body)}</textarea>
        <div style="margin-top:4px;font-size:10px;color:var(--text-muted);">Variables: {name}, {coach_name}</div>
      </div>
    `).join('');
  }

  function collectRetentionFollowups() {
    const items = document.querySelectorAll('#retention-followup-list .save-action-config');
    return Array.from(items).map(item => ({
      dayOffset: parseInt(item.querySelector('.fu-day-input')?.value) || 7,
      body: item.querySelector('.fu-body-input')?.value || '',
      sendEmail: !!item.querySelector('.fu-email-check')?.checked,
    })).filter(s => s.body.trim());
  }

  document.getElementById('btn-add-followup')?.addEventListener('click', () => {
    const settings = loadSettings();
    const steps = settings.retentionFollowupSteps || [...DEFAULT_FOLLOWUP_STEPS];
    const lastDay = steps.length > 0 ? steps[steps.length - 1].dayOffset : 0;
    steps.push({ dayOffset: lastDay + 30, body: 'Hey {name}, just wanted to check in. The door is always open.', sendEmail: false });
    settings.retentionFollowupSteps = steps;
    saveSettingsToStorage(settings);
    renderRetentionFollowups();
  });

  document.getElementById('retention-followup-list')?.addEventListener('click', (e) => {
    if (e.target.closest('.fu-remove-btn')) {
      const idx = parseInt(e.target.closest('.save-action-config')?.dataset.idx);
      const settings = loadSettings();
      const steps = settings.retentionFollowupSteps || [...DEFAULT_FOLLOWUP_STEPS];
      if (!isNaN(idx) && steps.length > 1) {
        steps.splice(idx, 1);
        settings.retentionFollowupSteps = steps;
        saveSettingsToStorage(settings);
        renderRetentionFollowups();
      }
    }
  });

  document.getElementById('btn-save-retention')?.addEventListener('click', async () => {
    const settings = loadSettings();
    settings.defaultSaveApproach = document.getElementById('s-retention-default-approach')?.dataset?.value || 'ask';
    settings.retentionMaxPauseDays = parseInt(document.getElementById('s-retention-max-pause-days')?.value) || 90;
    settings.retentionMaxPausesPerYear = parseInt(document.getElementById('s-retention-max-pauses-year')?.value) || 1;
    settings.retentionFollowupsEnabled = !!document.getElementById('s-retention-followups-enabled')?.checked;
    settings.retentionFollowupSteps = collectRetentionFollowups();
    // ⚠️ churnDetectionEnabled and churnAlertPreference are no longer on the page (10/20): left as they are saved, so
    // a Retention save never switches the nightly scoring off by reading a switch that is gone.
    settings.churnBookingLink = (document.getElementById('s-churn-booking-link')?.value || '').trim();
    settings.cancelAgreementTemplate = (document.getElementById('s-cancel-agreement-template')?.value || '').trim();
    saveSettingsToStorage(settings);
    _settings.renderCancelAgreementPreview(settings);
    // Update global reference for save attempt modal
    window._coachRetentionSettings = {
      defaultSaveApproach: settings.defaultSaveApproach,
      retentionMaxPauseDays: settings.retentionMaxPauseDays,
      retentionMaxPausesPerYear: settings.retentionMaxPausesPerYear,
      churnBookingLink: settings.churnBookingLink,
    };
    showToast('Retention settings saved.', 'success');
    document.getElementById('btn-save-retention')?._dirtyReset?.();
  });

  document.getElementById('s-cancel-agreement-template')?.addEventListener('input', () => {
    _settings.renderCancelAgreementPreview(loadSettings());
  });
  document.getElementById('btn-copy-cancel-agreement')?.addEventListener('click', () => {
    const text = document.getElementById('s-cancel-agreement-preview')?.textContent || '';
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => showToast('Cancellation language copied.', 'success'))
      .catch(() => showToast('Could not copy. Select the text and copy it by hand.', 'error'));
  });

  // ── Template Editor Modal ───────────────────────────────────────
  const TPL_CONFIGS = {
    fail_msg: {
      title: 'In-App Message — Payment Failure',
      desc: 'This message is sent in the app when a client\'s payment fails.',
      variables: [
        { key: '{amount}', label: 'Dollar amount (e.g. $297.00)' },
        { key: '{link}', label: 'Payment update link' },
      ],
      fields: [
        { id: 'tpl-fail-msg', label: 'Message', type: 'textarea', settingsKey: 'tplFailMsg',
          placeholder: 'Your payment of {amount} was unsuccessful. Please update your payment method to avoid any interruption to your coaching access.\n\nUpdate here: {link}' },
      ],
    },
    fail_email: {
      title: 'Email — Payment Failure',
      desc: 'This email is sent when a client\'s payment fails (if enabled in email settings).',
      variables: [
        { key: '{amount}', label: 'Dollar amount' },
        { key: '{name}', label: 'Client first name' },
        { key: '{reason}', label: 'Failure reason' },
        { key: '{link}', label: 'Payment update link' },
      ],
      fields: [
        { id: 'tpl-fail-email-subject', label: 'Subject line', type: 'input', settingsKey: 'tplFailEmailSubject',
          placeholder: 'Action needed: payment failed' },
        { id: 'tpl-fail-email-body', label: 'Email body', type: 'textarea', settingsKey: 'tplFailEmailBody',
          placeholder: 'Hey {name}, your payment of {amount} was unsuccessful.\n{reason}\n\nPlease update your payment method to avoid any interruption to your coaching access.' },
      ],
    },
  };

  let _activeTplKey = null;

  window.openTemplateEditor = (key) => {
    const config = TPL_CONFIGS[key];
    if (!config) return;
    _activeTplKey = key;
    const settings = loadSettings();

    document.getElementById('tpl-editor-title').textContent = config.title;
    const body = document.getElementById('tpl-editor-body');

    let html = `<p class="settings-note" style="margin-bottom:4px;">${config.desc}</p>`;

    // Variable chips
    html += `<div style="margin:10px 0 4px;"><span style="font-size:11px;font-weight:600;color:var(--text-muted);letter-spacing:0;">Available variables</span> <span style="font-size:10px;color:var(--text-muted);">— click to copy</span></div>`;
    html += `<div class="tpl-var-chips">`;
    for (const v of config.variables) {
      html += `<span class="tpl-var-chip" onclick="window.copyTplVar(this,'${v.key}')" title="${v.label}">${v.key}</span>`;
    }
    html += `</div>`;

    // Fields
    for (const f of config.fields) {
      const val = settings[f.settingsKey] || '';
      html += `<div class="tpl-editor-section">`;
      html += `<label for="${f.id}">${f.label}</label>`;
      if (f.type === 'textarea') {
        html += `<textarea id="${f.id}" placeholder="${f.placeholder.replace(/"/g, '&quot;')}">${val}</textarea>`;
      } else {
        html += `<input type="text" id="${f.id}" value="${val.replace(/"/g, '&quot;')}" placeholder="${f.placeholder.replace(/"/g, '&quot;')}" />`;
      }
      html += `</div>`;
    }

    // Actions
    html += `<div class="tpl-editor-actions">`;
    html += `<button class="btn-secondary btn-test" onclick="window.sendTestTemplate('${key}')"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg> Send Test</button>`;
    html += `<button class="btn-primary" onclick="window.saveTemplate('${key}')">Save Template</button>`;
    html += `</div>`;

    body.innerHTML = html;
    document.getElementById('tpl-editor-overlay').classList.remove('hidden');
  };

  window.closeTemplateEditor = () => {
    document.getElementById('tpl-editor-overlay').classList.add('hidden');
    _activeTplKey = null;
  };

  window.copyTplVar = (el, text) => {
    navigator.clipboard.writeText(text).then(() => {
      el.classList.add('copied');
      setTimeout(() => el.classList.remove('copied'), 1200);
    });
  };

  window.saveTemplate = (key) => {
    const config = TPL_CONFIGS[key];
    if (!config) return;
    const settings = loadSettings();
    for (const f of config.fields) {
      settings[f.settingsKey] = (document.getElementById(f.id)?.value || '').trim();
    }
    saveSettingsToStorage(settings);
    showToast('Template saved.', 'success');
    window.closeTemplateEditor();
  };

  window.sendTestTemplate = async (key) => {
    const config = TPL_CONFIGS[key];
    if (!config) return;
    const settings = loadSettings();
    const replyTo = settings.emailReplyTo;

    if (key === 'fail_msg') {
      // Preview in-app message with sample data
      const tpl = document.getElementById('tpl-fail-msg')?.value || config.fields[0].placeholder;
      const preview = tpl.replace(/\{amount\}/g, '$297.00').replace(/\{link\}/g, 'https://billing.stripe.com/example');
      showConfirmDialog({ message: `<div style="text-align:left;"><strong>Preview: In-App Message</strong><div style="margin-top:12px;padding:14px;background:var(--surface-2);border-radius:8px;font-size:13px;line-height:1.6;white-space:pre-line;color:var(--text);">${preview.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div></div>`, okLabel: 'Close', onConfirm: () => {} });
    } else if (key === 'fail_email') {
      // Send real test email to coach's reply-to address
      if (!replyTo) { showToast('Set a Reply-To Email above first.', 'error'); return; }
      const subject = (document.getElementById('tpl-fail-email-subject')?.value || 'Action needed: payment failed')
        .replace(/\{amount\}/g, '$297.00').replace(/\{name\}/g, 'Test Client');
      const bodyText = (document.getElementById('tpl-fail-email-body')?.value || config.fields[1].placeholder)
        .replace(/\{amount\}/g, '$297.00').replace(/\{name\}/g, 'Test Client')
        .replace(/\{reason\}/g, 'Card declined (test)').replace(/\{link\}/g, 'https://billing.stripe.com/example');

      try {
        showToast('Sending test email...', 'info');
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch('/.netlify/functions/ses-domain-setup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'test-email', subject, bodyText, to: replyTo }),
        });
        if (res.ok) {
          showToast(`Test email sent to ${replyTo}`, 'success');
        } else {
          const data = await res.json();
          showToast(data.error || 'Failed to send test', 'error');
        }
      } catch (err) { showToast(err.message, 'error'); }
    }
  };

  // ── Coach Notifications ─────────────────────────────────────────
  let _notifCache = [];
  let _notifUnread = 0;

  async function loadNotifications() {
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch('/.netlify/functions/coach-notifications-list?limit=20', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      _notifCache = data.notifications || [];
      _notifUnread = data.unreadCount || 0;
      const badge = document.getElementById('notif-badge');
      if (badge) {
        badge.textContent = _notifUnread;
        badge.classList.toggle('hidden', _notifUnread === 0);
      }
    } catch {}
  }

  function renderNotifPanel() {
    const panel = document.getElementById('notif-panel');
    if (!panel) return;
    // Position panel to the right of the sidebar (handle collapsed state)
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      // The sidebar floats with a gap around it (2026-10-12): its right edge, not its width.
      const sw = sidebar.getBoundingClientRect().right;
      panel.style.left = (sw + 8) + 'px';
    }
    if (_notifCache.length === 0) {
      panel.innerHTML = `<div class="notif-panel-header"><span>Notifications</span><button class="notif-panel-close" onclick="window.closeNotifPanel()" title="Close">&times;</button></div><div class="notif-empty">No notifications yet</div>`;
      return;
    }
    panel.innerHTML = `
      <div class="notif-panel-header">
        <span>Notifications</span>
        <div style="display:flex;align-items:center;gap:8px;">
          ${_notifUnread > 0 ? `<button onclick="window.markAllNotifsRead()">Mark all read</button>` : ''}
          ${_notifCache.length > 0 ? `<button onclick="window.clearAllNotifs()" style="color:var(--danger)">Clear all</button>` : ''}
          <button class="notif-panel-close" onclick="window.closeNotifPanel()" title="Close">&times;</button>
        </div>
      </div>
      ${_notifCache.map((n) => notifRowHtml(n)).join('')}`;
  }

  window.toggleNotifPanel = () => {
    const panel = document.getElementById('notif-panel');
    if (!panel) return;
    const isHidden = panel.classList.contains('hidden');
    if (isHidden) {
      panel.classList.remove('hidden');
      loadNotifications().then(renderNotifPanel);
      // Add click-outside overlay
      let overlay = document.getElementById('notif-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'notif-overlay';
        overlay.className = 'notif-overlay';
        overlay.addEventListener('click', () => window.closeNotifPanel());
        document.body.appendChild(overlay);
      }
      overlay.classList.remove('hidden');
    } else {
      window.closeNotifPanel();
    }
  };

  window.closeNotifPanel = () => {
    const panel = document.getElementById('notif-panel');
    if (panel) panel.classList.add('hidden');
    const overlay = document.getElementById('notif-overlay');
    if (overlay) overlay.classList.add('hidden');
  };

  window.markAllNotifsRead = async () => {
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch('/.netlify/functions/coach-notifications-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ all: true }),
      });
      _notifCache.forEach(n => n.read = true);
      _notifUnread = 0;
      const badge = document.getElementById('notif-badge');
      if (badge) { badge.textContent = '0'; badge.classList.add('hidden'); }
      renderNotifPanel();
    } catch {}
  };

  window.clearAllNotifs = async () => {
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch('/.netlify/functions/coach-notifications-clear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      });
      _notifCache = [];
      _notifUnread = 0;
      const badge = document.getElementById('notif-badge');
      if (badge) { badge.textContent = '0'; badge.classList.add('hidden'); }
      renderNotifPanel();
    } catch {}
  };

  window.handleNotifClick = async (notifId, clientId, type) => {
    // Mark as read
    try {
      const supabase = _config.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        fetch('/.netlify/functions/coach-notifications-read', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ id: notifId }),
        });
      }
    } catch {}
    // The cached row rides along: a Scribe row's call id is not in the inline click.
    openNotifTarget(clientId, type, _notifCache.find((x) => x.id === notifId));
  };

  // Poll notifications every 2 min — safety net only. Notifications are
  // push-delivered via realtime elsewhere; this catches anything missed.
  loadNotifications();
  setInterval(loadNotifications, 120_000);

  // ── "What's new" sidebar indicator ──────────────────────────
  // Polls changelog-unread; lights up the dot next to /changelog when
  // the latest public entry is newer than this coach's last-seen date.
  async function refreshWhatsNew() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch('/.netlify/functions/changelog-unread', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const { hasUnread } = await res.json();
      const dot = document.getElementById('whats-new-dot');
      if (!dot) return;
      dot.classList.toggle('hidden', !hasUnread);
    } catch {}
  }
  // Dim the dot immediately on click — mark-read fires server-side from
  // the /changelog page, but this stops the poll from briefly re-lighting
  // it if the user tabs back to the CRM before the next poll cycle.
  const whatsNewLink = document.getElementById('btn-whats-new');
  if (whatsNewLink) {
    whatsNewLink.addEventListener('click', () => {
      const dot = document.getElementById('whats-new-dot');
      if (dot) dot.classList.add('hidden');
    });
  }
  refreshWhatsNew();
  setInterval(refreshWhatsNew, 300_000); // every 5 min

  // Settings — tab switching
  const savedSettingsTab = localStorage.getItem('revfit_settings_tab') || 'appearance';
  document.querySelectorAll('.settings-tab-btn').forEach(btn => {
    const isActive = btn.dataset.tab === savedSettingsTab;
    btn.classList.toggle('active', isActive);
    document.getElementById(`settings-tab-${btn.dataset.tab}`)?.classList.toggle('active', isActive);
    btn.addEventListener('click', () => {
      // The Zoom review login has one tab; Booked's links that click another from code open nothing.
      if (window._reviewSettingsTab && btn.dataset.tab !== window._reviewSettingsTab) return;
      document.querySelectorAll('.settings-tab-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.settings-tab-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById(`settings-tab-${btn.dataset.tab}`)?.classList.add('active');
      localStorage.setItem('revfit_settings_tab', btn.dataset.tab);
      if (btn.dataset.tab === 'integrations') {
        loadCommsCardStatuses();
        // Lead Engine's connect card lives on this tab — see the note at boot.
        initLeadEngineSettingsCard().catch((e) => console.warn('[lead-engine] settings card init:', e?.message));
        // The Facebook and Instagram ads card sits right under it (hidden unless this account may connect one).
        initMetaAdsCard().catch((e) => console.warn('[meta-ads] settings card init:', e?.message));
      }
      if (btn.dataset.tab === 'team') {
        initTeam().catch((e) => console.warn('[team] init:', e?.message));
      }
      if (btn.dataset.tab === 'payments') {
        initStripeConnect(supabase);
      }
      if (btn.dataset.tab === 'sessions') {
        initSchedulingSettings();
        initCalendarSync();
      }
      if (btn.dataset.tab === 'communications') initCommsSettings();
    });
  });

  // ⚠️ THE REMEMBERED TAB INITIALISES WHEN SETTINGS OPENS, NOT 500 ms INTO
  // EVERY BOOT. This used to be five setTimeout(…, 500) blocks (plus a sixth
  // for App Studio further down) that ran no matter which view was being
  // restored — because restoreLastView (navigation.js:129) never clears
  // revfit_settings_tab; only showView does (:77). Measured 2026-09-09 with
  // Integrations remembered: three parallel coach-channels-get calls and the
  // 199 KB Lead Engine chunk, on a load that landed on the dashboard.
  //
  // ⚠️ THE PANELS MUST STILL FILL. The timers existed because landing straight
  // on a remembered tab — which is what happens the second time you open
  // Settings — showed an empty panel; the click handler above is what normally
  // initialises them. _initActiveSettingsTab is that same work, moved to the
  // Settings entry path where it belongs (renderSettings() in
  // modules/settings.js, called only when currentView === 'settings').
  window._initActiveSettingsTab = _initActiveSettingsTab;
  let _settingsTabInited = '';
  function _initActiveSettingsTab() {
    const tab = localStorage.getItem('revfit_settings_tab') || 'appearance';
    if (_settingsTabInited === tab) return;
    _settingsTabInited = tab;
    if (tab === 'integrations') {
      loadCommsCardStatuses();
      initAutomationIntegrations();
      initLeadEngineSettingsCard().catch((e) => console.warn('[lead-engine] settings card init:', e?.message));
      initMetaAdsCard().catch((e) => console.warn('[meta-ads] settings card init:', e?.message));
    }
    if (tab === 'team') initTeam().catch((e) => console.warn('[team] init:', e?.message));
    if (tab === 'payments') initStripeConnect(supabase);
    if (tab === 'sessions') { initSchedulingSettings(); initCalendarSync(); }
    if (tab === 'communications') initCommsSettings();
    // _awWire/_awInit live inside the App Studio IIFE (app.js, closes further
    // down) — outside this function's scope — so they're reached through
    // window, exposed from inside that IIFE (see the click listener there).
    if (tab === 'app') { window._awWire(); window._awInit(); }
  }

  // Stripe Connect return — if the OAuth callback redirected us back with
  // ?stripeConnect=... in the URL, stash it in sessionStorage and jump to
  // settings → payments so the coach sees the flash message.
  (function handleStripeConnectReturn() {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('stripeConnect');
    if (!status) return;
    // Persist params for the module to flash once the Payments tab renders.
    try { sessionStorage.setItem('stripeConnectReturn', window.location.search); } catch {}
    // Clean the URL so a refresh doesn't re-trigger navigation.
    window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    // Auto-navigate: switch to Settings view + Payments tab.
    setTimeout(() => {
      const settingsNav = document.querySelector('.nav-btn[data-view="settings"]');
      if (settingsNav) settingsNav.click();
      setTimeout(() => {
        const paymentsTab = document.querySelector('.settings-tab-btn[data-tab="payments"]');
        if (paymentsTab) paymentsTab.click();
        setTimeout(() => initStripeConnect(supabase), 200);
      }, 200);
    }, 300);
  })();


  // Coach IG handle — saves to coaches.settings.igHandle so the mobile
  // transformation post page reads the same value. Stripped of `@` and
  // whitespace, capped at 30 chars (matches mobile validation in
  // coach-update-profile.js).
  document.getElementById('btn-save-ig-handle')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-ig-handle');
    const raw = document.getElementById('s-ig-handle')?.value || '';
    const cleaned = raw.trim().replace(/^@+/, '').replace(/\s+/g, '').slice(0, 30);
    // Email signature parts — "Clayton Nicholson, CPT" over "Coach & Founder,
    // Revolution Fitness" (email-render.js, sigRow). Capped here at the same
    // lengths coach-update-profile.js enforces for the mobile side, exactly
    // like the handle above. Empty saves as null so a coach who drops their
    // credential watches it disappear rather than sticking forever.
    const credential = (document.getElementById('s-credential')?.value || '').trim().slice(0, 40);
    const role       = (document.getElementById('s-role')?.value || '').trim().slice(0, 60);
    // The signed name itself (settings.coachName — same key the mobile app
    // writes). Before this field existed there was no way to sign with a full
    // name: the fallback is coaches.name, which holds whatever the coach
    // signed UP with, usually just a first name. Cap matches
    // coach-update-profile.js. Empty clears back to that fallback.
    const coachName  = (document.getElementById('s-coach-name')?.value || '').trim().slice(0, 80);
    // Street address for the email footer (settings.businessAddress) —
    // newlines are the line breaks, rendered under "Sent by" by
    // email-render.js. Empty clears the block entirely.
    const businessAddress = (document.getElementById('s-business-address')?.value || '').trim().slice(0, 200);
    // ⚠️ Normalise to E.164 or Twilio rejects it. A coach types
    // "507-412-8100"; stripping to digits alone gives "5074128100", which
    // looks saved, shows in the field, and silently never sends. Same rule as
    // public-optin-page.js's normalizePhone. Empty saves as null, which is
    // what leaves the Text switches inert rather than dead-but-on.
    const phoneRaw = document.getElementById('s-alert-phone')?.value || '';
    const phoneDigits = phoneRaw.replace(/\D/g, '');
    let alertPhone = null;
    if (phoneDigits.length === 10) alertPhone = '+1' + phoneDigits;
    else if (phoneDigits.length === 11 && phoneDigits.startsWith('1')) alertPhone = '+' + phoneDigits;
    else if (phoneDigits.length > 0) alertPhone = '+' + phoneDigits;
    try {
      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.access_token}` },
        // ⚠️ Every field rides ONE save button (the card has one), so they all
        // have to be sent together — posting only igHandle would silently
        // discard whatever the coach just typed into the others.
        body: JSON.stringify({ settings: { igHandle: cleaned || null, alertPhone: alertPhone, credential: credential || null, role: role || null, coachName: coachName || null, businessAddress: businessAddress || null } }),
      });
      if (!res.ok) throw new Error('Save failed');
      // Reflect canonical value back to the input + in-memory cache so
      // a re-render shows the same thing.
      const input = document.getElementById('s-ig-handle');
      if (input) input.value = cleaned ? '@' + cleaned : '';
      // ⚠️ The module-scoped cache, NOT window._appCoachSettings — app.js is
      // an ES module (index.html imports it), so the `let` at the top of STATE
      // is not a global and `window._appCoachSettings` is forever undefined.
      // The old guard therefore never ran; writing through it here would leave
      // the preview reading a stale blob after a save.
      _appCoachSettings.igHandle   = cleaned || null;
      _appCoachSettings.credential = credential || null;
      _appCoachSettings.role       = role || null;
      _appCoachSettings.coachName  = coachName || null;
      _appCoachSettings.businessAddress = businessAddress || null;
      // The business name rides the same Save, but through the coach app's
      // own save (coach-business-name.js says why), and only when it differs
      // from what coaches.settings holds (`saved`, set at load): a name the
      // coach app saved is never re-saved, while one that exists only from
      // signup is saved where emails and booking pages read it. After the save
      // above, never alongside it: both rewrite coaches.settings, and two at
      // once would lose one of them.
      const bizInput = document.getElementById('s-business-name');
      if (bizInput && !window._appMember) {
        const typedBiz = bizInput.value.trim().slice(0, 80);
        if (typedBiz !== (bizInput.dataset.saved || '')) {
          const savedBiz = await saveBusinessName(typedBiz, { callFunction, loadSettings, saveSettingsToStorage });
          bizInput.value = savedBiz;
          bizInput.dataset.saved = savedBiz;
          _appCoachSettings.businessName = savedBiz;
        }
      }
      window.renderSignaturePreview?.();
      showToast('Coach profile saved.', 'success');
      btn?._dirtyReset?.();
    } catch (err) {
      console.error('[ig-handle save]', err);
      showToast('Failed to save coach profile.', 'error');
    }
  });

  // ── Email signature preview ───────────────────────────────────
  // Composes the same two lines email-render.js's sigRow does, so the coach
  // sees where the comma lands and that the business name is appended for
  // them. Name and business fall back the way send-notification-email.js
  // resolves them (settings first, then the coaches row), because a coach who
  // never opened branding has neither settings.coachName nor
  // settings.businessName and would otherwise preview a signature nobody gets.
  function renderSignaturePreview() {
    const nameEl = document.getElementById('s-sig-preview-name');
    const roleEl = document.getElementById('s-sig-preview-role');
    if (!nameEl || !roleEl) return;
    const local = loadSettings();
    const cs = _appCoachSettings || {};
    // The name input previews LIVE like credential/role below — typing shows
    // the signature you'd get before Save is pressed.
    const typedName = (document.getElementById('s-coach-name')?.value || '').trim();
    const name = typedName || (cs.coachName || local.coachName || window._appCoachName || '').trim() || 'Your name';
    // A business name that differs from the saved one previews as it will
    // send once saved; otherwise, as it sends today.
    const bizInput = document.getElementById('s-business-name');
    const bizEdited = !!bizInput && bizInput.value.trim() !== (bizInput.dataset.saved || '');
    const business = ((bizEdited ? bizInput.value : (cs.businessName || local.businessName || '')).trim()
      || window._appCoachName || '').trim();
    const credential = (document.getElementById('s-credential')?.value || '').trim();
    const role = (document.getElementById('s-role')?.value || '').trim();
    nameEl.textContent = credential ? `${name}, ${credential}` : name;
    roleEl.textContent = [role, business].filter(Boolean).join(', ');
  }
  window.renderSignaturePreview = renderSignaturePreview;
  document.getElementById('s-coach-name')?.addEventListener('input', renderSignaturePreview);
  document.getElementById('s-business-name')?.addEventListener('input', renderSignaturePreview);
  document.getElementById('s-credential')?.addEventListener('input', renderSignaturePreview);
  document.getElementById('s-role')?.addEventListener('input', renderSignaturePreview);

  // Settings — save Workout intensity metric (RIR / RPE).
  // Mirrored to coaches.settings so workout-resolve and the program editor
  // can read the coach's default without an extra hop through localStorage.
  document.getElementById('btn-save-intensity-metric')?.addEventListener('click', async () => {
    const val = document.getElementById('s-intensity-metric')?.value === 'rpe' ? 'rpe' : 'rir';
    const settings = loadSettings();
    settings.intensityMetric = val;
    saveSettingsToStorage(settings);
    try {
      const supabase = _config.getSupabase();
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) {
        await fetch('/.netlify/functions/save-coach-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
          body: JSON.stringify({ settings: { intensityMetric: val } }),
        });
      }
    } catch {}
    showToast('Intensity metric saved.', 'success');
    document.getElementById('btn-save-intensity-metric')?._dirtyReset?.();
  });

  // Settings — save Form Check Videos (mode default + weekly cap).
  // Mirrored to coaches.settings.formCheckVideo so the gate
  // (_shared/form-video-gate.js) and client-get-profile resolve it.
  document.getElementById('btn-save-form-video')?.addEventListener('click', async () => {
    const mode = document.getElementById('s-form-video-mode')?.value;
    const defaultClientMode = ['on', 'request_only', 'off'].includes(mode) ? mode : 'on';
    const cap = Number(document.getElementById('s-form-video-cap')?.value);
    const weeklyCap = Number.isFinite(cap) && cap >= 1 ? Math.min(cap, 50) : 3;
    const settings = loadSettings();
    settings.formCheckVideo = { ...(settings.formCheckVideo || {}), defaultClientMode, weeklyCap };
    saveSettingsToStorage(settings);
    try {
      const supabase = _config.getSupabase();
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) {
        await fetch('/.netlify/functions/save-coach-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
          body: JSON.stringify({ settings: { formCheckVideo: settings.formCheckVideo } }),
        });
      }
    } catch {}
    showToast('Form check video settings saved.', 'success');
    document.getElementById('btn-save-form-video')?._dirtyReset?.();
  });

  // Settings — save Client app tabs (which bottom tabs clients see).
  // Moved to modules/client-app-tabs-settings.js (2026-10-02).
  wireClientAppTabsSave();

  // Protocols (supplements & PEDs) — coach-level master switch. Saves
  // immediately; gates the per-client protocols section in the profile.
  document.getElementById('s-protocols-enabled')?.addEventListener('change', async (e) => {
    const enabled = e.target.checked;
    try {
      const supabase = _config.getSupabase();
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) {
        const res = await fetch('/.netlify/functions/save-coach-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
          body: JSON.stringify({ settings: { protocolsEnabled: enabled } }),
        });
        if (!res.ok) throw new Error('Save failed');
      }
      _appCoachSettings.protocolsEnabled = enabled;
      showToast(enabled ? 'Protocol tracking enabled.' : 'Protocol tracking disabled.', 'success');
    } catch (err) {
      e.target.checked = !enabled;
      showToast('Could not save — try again', 'error');
    }
  });

  // Settings — save Time Zone. ⚠️ THE ONE TIME ZONE: it is also the zone
  // every booking runs in, so saveTimeZone asks the server first (it refuses a
  // change that would move clients' weekly standing sessions) and only then
  // keeps it for the CRM's dates. See modules/time-zone-setting.js.
  document.getElementById('btn-save-timezone')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-timezone');
    if (!btn || btn.dataset.saving) return;
    btn.dataset.saving = '1';
    btn.disabled = true;
    let ok = false;
    try { ok = await saveTimeZone(); }
    finally {
      delete btn.dataset.saving;
      btn.disabled = false;
      // ⚠️ AFTER the re-enable above: _dirtyReset() greys the button out.
      if (ok) btn._dirtyReset?.();
    }
  });

  // Settings — detect timezone
  document.getElementById('btn-detect-tz')?.addEventListener('click', () => {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const select = document.getElementById('s-timezone');
    if (select) {
      select.value = browserTz;
      select.dispatchEvent(new Event('change'));
      syncUpgradedSelects(select.parentElement); // custom dropdown shows the detected zone
    }
    const hint = document.getElementById('s-tz-hint');
    if (hint) hint.textContent = `Detected: ${browserTz}`;
  });

  document.getElementById('btn-save-sessions')?.addEventListener('click', async () => {
    const settings = loadSettings();
    // ⚠️ reschedCoach/ClientMonths RETIRED 2026-08-26 (ai.acquisition.com
    // ruling): a reschedule moves the session and touches no credit, so
    // there is no month window left to configure. Old saved values are
    // simply never read again.
    // ⚠️ Two stores. Every server consumer (session cancel, time off, standing
    // reservations, make-up approval) reads coaches.settings — the localStorage
    // write below only ever feeds this browser. Until 2026-08-20 this handler
    // stopped there, so the coach's expiry months silently never applied (same
    // trap as coach branding: user_config vs coaches.settings). The server
    // write is the one that counts — no success toast unless it lands.
    try {
      // The cancellation windows, read from the card that shows them
      // (modules/cancellation-settings.js). ⚠️ INSIDE THE TRY: it refuses
      // when the coach's own numbers never loaded (the save would drop the
      // ones the card no longer shows), and the catch below says so.
      const cancellation = readCancellationSettings();
      settings.cancellation = cancellation;
      saveSettingsToStorage(settings);
      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not signed in');
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + s.access_token },
        body: JSON.stringify({ settings: {
          // ⚠️ LONG FORM, NOT `cancellation,` SHORTHAND. settings-two-stores
          // reads these bodies to prove every server-read key actually gets
          // posted, and it can only see `key: value`. Shorthand is invisible
          // to it — which is how this very key first slipped past.
          cancellation: cancellation,
        } }),
      });
      if (!res.ok) throw new Error('Save failed');
      _appCoachSettings.cancellation = cancellation;
      showToast('Session settings saved.', 'success');
      document.getElementById('btn-save-sessions')?._dirtyReset?.();
    } catch (err) {
      showToast('Could not save session settings: ' + err.message, 'error');
    }
  });

  // The check-in submission window is no longer configurable — the check-in
  // day itself is the window (whole local day). The old Settings card and
  // its save path were removed with it; stale checkinWindowStart/End values
  // in coaches.settings are simply ignored.

  // ── Messaging settings — one-way messaging toggle ──────────────
  // Initialize the toggle from coach settings.
  // ⚠️ _appCoachSettings IS STILL {} HERE — bindEvents() runs before the
  // async coach-profile fetch resolves (see the coachProfile.settings block
  // further down), so this first call always paints the empty-object
  // default (off). applyMessagingSuspendSettings() runs a second time once
  // the real settings land, right next to where the IG handle / alert phone /
  // coach name fields get their own second pass — without it this control
  // would show "off" forever regardless of what is actually saved, while the
  // server keeps enforcing the real value.
  const oneWayCheck = document.getElementById('s-one-way-messaging');
  applyMessagingSuspendSettings({ oneWayMessaging: oneWayCheck }, _appCoachSettings);

  document.getElementById('btn-save-messaging')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-messaging');
    const enabled = document.getElementById('s-one-way-messaging')?.checked || false;

    btn.disabled = true;
    btn.textContent = 'Saving…';

    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) throw new Error('Not authenticated');

      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: JSON.stringify({ settings: { oneWayMessaging: enabled } }),
      });
      if (!res.ok) throw new Error('Save failed');

      _appCoachSettings.oneWayMessaging = enabled;
      showToast(enabled ? 'One-way messaging enabled.' : 'One-way messaging disabled.', 'success');
      btn.textContent = 'Save';
      btn._dirtyReset?.();
    } catch (err) {
      showToast('Failed to save: ' + err.message, 'error');
      btn.disabled = false;
      btn.textContent = 'Save';
    }
  });

  // The old auto-suspend-on-overdue Save handler was removed 2026-09-18 with
  // the card above it. Its stored settings are left in the database, unread.

  // ── Nutrition: Macro & Calorie Adherence Tolerance ────────────
  // The card's code lives in modules/nutrition-tolerance-card.js (moved out of app.js on 10/20, standing rule 3).
  // ⚠️ SAME SETUP-TIME-ONLY DEFECT AS THE MESSAGING/AUTO-SUSPEND CONTROLS
  // ABOVE, found in the same sweep (2026-09-18): this first call also reads
  // _appCoachSettings while it is still {}. Exposed on window so the
  // coachProfile.settings block can repaint it with the real, saved
  // nutritionTolerance once that fetch resolves — same pattern as
  // window._initCategoryUI just below.
  const _renderNutTol = initNutritionToleranceCard({
    getSettings: () => _appCoachSettings,
    getSupabase: () => _config.getSupabase(),
    showToast,
  });
  window._renderNutTol = _renderNutTol;

  // ── Streak Freezes (Effort v2) ────────────────────────────────
  // Coach toggles the freeze stockpile + sets the monthly refill count and
  // the max a client can bank. Stored on coaches.settings.freeze; the
  // nightly cron-freeze-roll spends banked freezes on missed days so the
  // home streak survives. Off by default.
  const FREEZE_DEFAULTS = { enabled: false, refillCount: 2, maxAccumulation: 5, cadence: 'monthly' };

  function _normalizeFreeze(raw) {
    const r = raw || {};
    const clampInt = (v, min, max, fb) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return fb;
      return Math.max(min, Math.min(max, Math.round(n)));
    };
    const refillCount = clampInt(r.refillCount, 0, 30, FREEZE_DEFAULTS.refillCount);
    const maxAccumulation = Math.max(refillCount, clampInt(r.maxAccumulation, 0, 60, FREEZE_DEFAULTS.maxAccumulation));
    return { enabled: r.enabled === true, refillCount, maxAccumulation, cadence: 'monthly' };
  }

  function _renderFreeze() {
    const f = _normalizeFreeze(_appCoachSettings?.freeze);
    document.querySelectorAll('#freeze-enabled-control [data-freeze-enabled]').forEach(btn => {
      btn.classList.toggle('active', (btn.getAttribute('data-freeze-enabled') === 'on') === f.enabled);
    });
    const block = document.getElementById('freeze-config-block');
    if (block) block.style.display = f.enabled ? '' : 'none';
    const inp = (id) => document.getElementById(id);
    if (inp('freeze-refill-count')) inp('freeze-refill-count').value = f.refillCount;
    if (inp('freeze-max-accumulation')) inp('freeze-max-accumulation').value = f.maxAccumulation;
  }

  document.querySelectorAll('#freeze-enabled-control [data-freeze-enabled]').forEach(btn => {
    btn.addEventListener('click', () => {
      const on = btn.getAttribute('data-freeze-enabled') === 'on';
      document.querySelectorAll('#freeze-enabled-control [data-freeze-enabled]').forEach(b => {
        b.classList.toggle('active', b === btn);
      });
      const block = document.getElementById('freeze-config-block');
      if (block) block.style.display = on ? '' : 'none';
    });
  });

  _renderFreeze();
  // Same reason as window._renderNutTol above: this first call also reads
  // _appCoachSettings while it is still {}. Exposed so the
  // coachProfile.settings block can repaint it once the real settings load.
  window._renderFreeze = _renderFreeze;

  document.getElementById('btn-save-freeze-config')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-freeze-config');
    const enabled = document.querySelector('#freeze-enabled-control [data-freeze-enabled].active')?.getAttribute('data-freeze-enabled') === 'on';
    const intv = (id, fallback) => {
      const v = parseInt(document.getElementById(id)?.value, 10);
      return Number.isFinite(v) && v >= 0 ? v : fallback;
    };
    const payload = _normalizeFreeze({
      enabled,
      refillCount: intv('freeze-refill-count', FREEZE_DEFAULTS.refillCount),
      maxAccumulation: intv('freeze-max-accumulation', FREEZE_DEFAULTS.maxAccumulation),
    });

    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: JSON.stringify({ settings: { freeze: payload } }),
      });
      if (!res.ok) throw new Error('Save failed');
      _appCoachSettings.freeze = payload;
      showToast('Streak freeze settings saved.', 'success');
      btn._dirtyReset?.();
    } catch (err) {
      showToast('Failed to save: ' + err.message, 'error');
      btn.disabled = false;
    }
    btn.textContent = 'Save';
  });

  // ── Check-In Category management ──────────────────────────────
  // The categories and each performance row's starting one: modules/checkin-category-settings.js.

  function _getAllCategories() {
    const custom = (_appCoachSettings?.checkinCategories?.custom || []).map(c => c.name);
    return [...CI_PREDEFINED_CATS, ...custom];
  }

  function _renderCategoryChips() {
    const list = document.getElementById('s-categories-list');
    if (!list) return;
    const custom = _appCoachSettings?.checkinCategories?.custom || [];
    let html = '';
    CI_PREDEFINED_CATS.forEach(name => {
      html += `<span style="display:inline-flex;align-items:center;gap:4px;padding:5px 12px;border-radius:6px;background:var(--surface-2);border:1px solid var(--border);font-size:12px;font-weight:600;color:var(--text);">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        ${escHtml(name)}</span>`;
    });
    custom.forEach((c, i) => {
      html += `<span style="display:inline-flex;align-items:center;gap:4px;padding:5px 12px;border-radius:6px;background:var(--surface-2);border:1px solid var(--border);font-size:12px;font-weight:600;color:var(--text);">
        ${escHtml(c.name)}
        <button onclick="window._removeCategory(${i})" style="background:none;border:none;cursor:pointer;color:var(--text-muted);padding:0;line-height:1;font-size:14px;">&times;</button>
      </span>`;
    });
    list.innerHTML = html;
  }

  function _renderAnalyticsOverrides() {
    const container = document.getElementById('s-analytics-overrides');
    if (!container) return;
    const overrides = _appCoachSettings?.checkinCategories?.analyticsOverrides || {};
    const allCats = _getAllCategories();
    let html = '';
    CI_ANALYTICS_ROWS.forEach(label => {
      const current = overrides[label] || CI_ANALYTICS_DEFAULTS[label] || '';
      html += `<div style="display:flex;align-items:center;gap:8px;">
        <span style="font-size:12px;color:var(--text-muted);flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHtml(label)}</span>
        <select class="ci-analytics-override" data-label="${escHtml(label)}" style="padding:4px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface);color:var(--text);font-size:12px;min-width:100px;">
          ${allCats.map(c => `<option value="${escHtml(c)}"${c === current ? ' selected' : ''}>${escHtml(c)}</option>`).join('')}
        </select>
      </div>`;
    });
    container.innerHTML = html;
  }

  window._getCheckinCategories = () => (_appCoachSettings?.checkinCategories?.custom || []).map(c => c.name);

  // ── Metric Registry (defaults + coach custom) ──────────────
  const DEFAULT_METRICS = [
    { id: 'weight', name: 'Weight' },
    { id: 'body_fat', name: 'Body Fat %' },
    { id: 'waist', name: 'Waist' },
    { id: 'hips', name: 'Hips' },
    { id: 'chest', name: 'Chest' },
    { id: 'arms', name: 'Arms' },
    { id: 'thighs', name: 'Thighs' },
    { id: 'calories', name: 'Calories' },
    { id: 'protein', name: 'Protein' },
    { id: 'carbs', name: 'Carbs' },
    { id: 'fats', name: 'Fats' },
    { id: 'water', name: 'Water Intake' },
    { id: 'steps', name: 'Steps' },
    { id: 'sleep', name: 'Sleep Hours' },
    { id: 'energy', name: 'Energy Level' },
    { id: 'stress', name: 'Stress Level' },
    { id: 'mood', name: 'Mood' },
    { id: 'training_intensity', name: 'Training Intensity' },
    { id: 'soreness', name: 'Soreness' },
    { id: 'adherence', name: 'Overall Adherence' },
  ];

  window._getCheckinMetrics = () => {
    const custom = (_appCoachSettings?.checkinMetrics || []);
    return [...DEFAULT_METRICS, ...custom];
  };

  window._addCheckinMetric = async (name) => {
    if (!name?.trim()) return null;
    if (!_appCoachSettings.checkinMetrics) _appCoachSettings.checkinMetrics = [];
    const id = 'custom_' + Date.now().toString(36);
    _appCoachSettings.checkinMetrics.push({ id, name: name.trim() });
    try {
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (await _config.getSupabase().auth.getSession()).data.session?.access_token },
        body: JSON.stringify({ settings: { checkinMetrics: _appCoachSettings.checkinMetrics } }),
      });
      if (!res.ok) throw new Error('Save failed');
    } catch (err) {
      // Roll back the optimistic add — an unsaved metric in the picker would lie.
      _appCoachSettings.checkinMetrics = _appCoachSettings.checkinMetrics.filter(m => m.id !== id);
      showToast('Could not save the metric: ' + err.message, 'error');
      return null;
    }
    return id;
  };

  window._initCategoryUI = function(settings) {
    if (settings) _appCoachSettings = settings;
    _renderCategoryChips();
    _renderAnalyticsOverrides();
    showReviewLayout(_appCoachSettings);
    _fillVoiceCard(_appCoachSettings?.briefVoice);
  };

  // Settings → Check-Ins → Your voice: how the brief's draft reply sounds.
  // Stored as coaches.settings.briefVoice; the brief reads it server-side.
  function _fillVoiceCard(v) {
    const r = v?.rules || {};
    const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
    const check = (id, on) => { const el = document.getElementById(id); if (el) el.checked = on; };
    set('s-voice-profile', v?.profile || '');
    check('s-voice-no-dashes', r.noDashes !== false);
    check('s-voice-no-exclamation', r.noExclamation !== false);
    set('s-voice-signoff', r.signOff || '');
    set('s-voice-avoid', Array.isArray(r.avoidWords) ? r.avoidWords.join(', ') : '');
    set('s-voice-max-words', Number.isFinite(Number(r.maxWords)) && Number(r.maxWords) > 0 ? String(r.maxWords) : '90');
    set('s-voice-samples', Array.isArray(v?.samples) ? v.samples.join('\n\n') : '');
  }
  function _readVoiceCard() {
    const val = (id) => document.getElementById(id)?.value ?? '';
    const on = (id) => document.getElementById(id)?.checked !== false;
    return {
      profile: val('s-voice-profile').trim(),
      rules: {
        noDashes: on('s-voice-no-dashes'),
        noExclamation: on('s-voice-no-exclamation'),
        signOff: val('s-voice-signoff').trim(),
        avoidWords: val('s-voice-avoid').split(',').map((w) => w.trim()).filter(Boolean),
        maxWords: Math.min(180, Math.max(40, parseInt(val('s-voice-max-words'), 10) || 90)),
      },
      samples: val('s-voice-samples').split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean).slice(0, 5),
    };
  }

  window._removeCategory = (idx) => {
    const custom = _appCoachSettings?.checkinCategories?.custom || [];
    custom.splice(idx, 1);
    if (!_appCoachSettings.checkinCategories) _appCoachSettings.checkinCategories = {};
    _appCoachSettings.checkinCategories.custom = custom;
    _renderCategoryChips();
    _renderAnalyticsOverrides();
  };

  document.getElementById('btn-add-category')?.addEventListener('click', () => {
    const input = document.getElementById('s-category-input');
    const name = (input?.value || '').trim();
    if (!name) return;
    if (_getAllCategories().some(c => c.toLowerCase() === name.toLowerCase())) {
      showToast('Category already exists', 'error');
      return;
    }
    if (!_appCoachSettings.checkinCategories) _appCoachSettings.checkinCategories = {};
    if (!_appCoachSettings.checkinCategories.custom) _appCoachSettings.checkinCategories.custom = [];
    _appCoachSettings.checkinCategories.custom.push({ id: 'cat_' + Date.now(), name });
    input.value = '';
    _renderCategoryChips();
    _renderAnalyticsOverrides();
  });

  document.getElementById('btn-save-categories')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-categories');
    btn.disabled = true;
    btn.textContent = 'Saving\u2026';
    try {
      // Collect analytics overrides from dropdowns
      const overrides = {};
      document.querySelectorAll('.ci-analytics-override').forEach(sel => {
        const label = sel.dataset.label;
        const val = sel.value;
        if (val !== CI_ANALYTICS_DEFAULTS[label]) overrides[label] = val;
      });
      if (!_appCoachSettings.checkinCategories) _appCoachSettings.checkinCategories = {};
      _appCoachSettings.checkinCategories.analyticsOverrides = overrides;
      // Review layout (2026-09-30): 'classic' cards or the new 'glance' list.
      _appCoachSettings.checkinReviewLayout = pickedReviewLayout();

      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: JSON.stringify({ settings: { checkinCategories: _appCoachSettings.checkinCategories, checkinReviewLayout: _appCoachSettings.checkinReviewLayout } }),
      });
      if (!res.ok) throw new Error('Save failed');
      setCheckinReviewCoachSettings(_appCoachSettings);
      showToast('Categories saved.', 'success');
    } catch (err) {
      showToast('Failed to save: ' + err.message, 'error');
    }
    btn.disabled = false;
    btn.textContent = 'Save';
  });

  document.getElementById('btn-save-voice')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-voice');
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const briefVoice = _readVoiceCard();
      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: JSON.stringify({ settings: { briefVoice: briefVoice } }),
      });
      if (!res.ok) throw new Error('Save failed');
      _appCoachSettings = { ...(_appCoachSettings || {}), briefVoice };
      setCheckinReviewCoachSettings(_appCoachSettings);
      _fillVoiceCard(briefVoice);
      // Saved: the button greys out until the next change (dirty-tracking), so
      // it is NOT re-enabled here. Only a failed save lights it again.
      btn.textContent = 'Save';
      btn._dirtyReset?.();
      showToast('Your voice is saved. The next brief writes with it.', 'success');
    } catch (err) {
      showToast('Failed to save: ' + err.message, 'error');
      btn.disabled = false;
      btn.textContent = 'Save';
    }
  });

  // "Write it from my replies": the model reads the coach's own replies and
  // describes the voice; the coach edits before saving. Nothing is stored here.
  document.getElementById('btn-voice-profile-draft')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-voice-profile-draft');
    const note = document.getElementById('s-voice-profile-note');
    btn.disabled = true;
    if (note) note.textContent = 'Reading your replies…';
    try {
      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/checkin-brief-voice-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: '{}',
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || 'Could not write the profile');
      const ta = document.getElementById('s-voice-profile');
      if (ta) {
        ta.value = out.profile || '';
        ta.dispatchEvent(new Event('input', { bubbles: true }));
      }
      const n = out.samples || {};
      if (note) note.textContent = `Written from ${n.notes || 0} typed ${n.notes === 1 ? 'reply' : 'replies'} and ${n.transcripts || 0} video ${n.transcripts === 1 ? 'transcript' : 'transcripts'}. Edit it, then Save.`;
    } catch (err) {
      if (note) note.textContent = err.message;
    }
    btn.disabled = false;
  });

  // "Preview a draft": one reply written for the coach's latest check-in with the
  // card's CURRENT values (saved or not). Shown in the box; never sent or stored.
  document.getElementById('btn-voice-preview')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-voice-preview');
    const box = document.getElementById('s-voice-preview');
    btn.disabled = true;
    btn.textContent = 'Writing…';
    if (box) { box.style.display = ''; box.textContent = 'Reading the latest check-in and writing a draft…'; }
    try {
      const { data: { session: s } } = await _config.getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/checkin-brief-voice-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: JSON.stringify({ briefVoice: _readVoiceCard() }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || 'Could not write the preview');
      if (box) box.textContent = `Written for ${out.client}'s check-in of ${formatDate(out.weekOf)}, ${out.words} words. Preview only, nothing is sent.\n\n${out.draft}`;
    } catch (err) {
      if (box) box.textContent = err.message;
    }
    btn.disabled = false;
    btn.textContent = 'Preview a draft';
  });

  // Pipeline note overlay — close on backdrop click
  document.getElementById('pipeline-note-overlay')?.addEventListener('click', e => {
    if (e.target.id === 'pipeline-note-overlay') window.closePipelineNote();
  });

  // Back button on client profile
  document.getElementById('btn-back-to-clients')?.addEventListener('click', () => showView('clients'));
}

// ============================================================
// EXPOSE ONCLICK HANDLERS TO WINDOW
// ============================================================
// ── Contact Info Inline Edit ──────────────────────────────────────────────────
window.formatContactPhone = function(el) {
  const digits = el.value.replace(/\D/g, '').substring(0, 10);
  if (digits.length === 0) { el.value = ''; return; }
  if (digits.length <= 3) { el.value = `(${digits}`; return; }
  if (digits.length <= 6) { el.value = `(${digits.slice(0,3)}) ${digits.slice(3)}`; return; }
  el.value = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
};

// ── Client Autocomplete Search ────────────────────────────────────────────────
window.switchLogoMode = function(mode) {
  const card = document.getElementById('logo-card');
  if (card) card.dataset.logoMode = mode;
  document.querySelectorAll('.logo-mode-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.mode === mode);
  });
};

window.handleClientSearch = function(input, dropdownId, hiddenId) {
  const query = input.value.trim().toLowerCase();
  const dropdown = document.getElementById(dropdownId);
  const hidden   = document.getElementById(hiddenId);
  // Clear stored ID whenever user edits the text — must re-select from dropdown
  if (hidden) hidden.value = '';
  if (!query) { if (dropdown) dropdown.classList.add('hidden'); return; }
  const matches = clients
    .filter(c => c.name.toLowerCase().includes(query) && c.id !== profileClientId)
    .slice(0, 6);
  if (!matches.length) { if (dropdown) dropdown.classList.add('hidden'); return; }
  dropdown.innerHTML = matches.map(c => `
    <div class="cs-item" onmousedown="window.selectClientSearch('${c.id}','${escHtml(c.name).replace(/'/g,"\\'")}','${dropdownId}','${hiddenId}')">
      <span class="cs-avatar">${escHtml(c.name.charAt(0).toUpperCase())}</span>
      <span class="cs-name">${escHtml(c.name)}</span>
      <span class="cs-badge badge-${c.status}" style="font-size:10px">${label(c.status)}</span>
    </div>`).join('');
  dropdown.classList.remove('hidden');
};

window.selectClientSearch = function(id, name, dropdownId, hiddenId) {
  const dropdown = document.getElementById(dropdownId);
  const hidden   = document.getElementById(hiddenId);
  const input    = dropdown?.parentElement.querySelector('input:not([type="hidden"])');
  if (input)    input.value  = name;
  if (hidden)   hidden.value = id;
  if (dropdown) dropdown.classList.add('hidden');
};

window.handleClientSearchBlur = function(dropdownId) {
  setTimeout(() => {
    const dropdown = document.getElementById(dropdownId);
    if (dropdown) dropdown.classList.add('hidden');
  }, 150);
};

window.openContactEdit = function(id) {
  document.getElementById(`contact-view-${id}`)?.classList.add('hidden');
  const editForm = document.getElementById(`contact-edit-form-${id}`);
  editForm?.classList.remove('hidden');
  // Where they came from: the list fresh, so a source added in Lead Engine since the profile opened is there.
  if (editForm) paintClientSourceFields(editForm, clients.find(x => x.id === id), escHtml, { fresh: true }).then(() => syncUpgradedSelects(editForm));
  const btn = document.getElementById(`contact-edit-btn-${id}`);
  if (btn) { btn.style.opacity = '0'; btn.style.pointerEvents = 'none'; }
};

window.closeContactEdit = function(id) {
  document.getElementById(`contact-view-${id}`)?.classList.remove('hidden');
  document.getElementById(`contact-edit-form-${id}`)?.classList.add('hidden');
  const btn = document.getElementById(`contact-edit-btn-${id}`);
  if (btn) { btn.style.opacity = ''; btn.style.pointerEvents = ''; }
};

window.saveContactEdit = async function(id) {
  const c = clients.find(x => x.id === id);
  if (!c) return;
  const form = document.getElementById(`contact-edit-form-${id}`);
  const get = n => form.querySelector(`[name="${n}"]`)?.value.trim() || '';
  const firstName = get('firstName');
  const lastName  = get('lastName');
  const name      = [firstName, lastName].filter(Boolean).join(' ') || c.name;
  try {
    await updateClient(id, {
      firstName, lastName, name,
      phone:           get('phone'),
      email:           get('email'),
      birthdate:       get('birthdate')       || null,
      gender:          form.querySelector('[name="gender"]')?.value || null,
      addressLine1:    get('addressLine1'),
      addressLine2:    get('addressLine2'),
      city:            get('city'),
      state:           form.querySelector('[name="state"]')?.value || '',
      zipCode:         get('zipCode'),
      instagramHandle: get('instagramHandle') || null,
      leadSourceId:    form.querySelector('[name="leadSourceId"]')?.value || null,
      referredByName:  get('referredByName')  || null,
      referredById:    form.querySelector('[name="referredById"]')?.value || null,
      // Surgery / injury / weight cap. The AI program builder reads this and
      // treats it as a hard filter on exercise selection, so it must survive
      // a contact-info save rather than being dropped on the floor.
      restrictions:    get('restrictions')    || null,
      // How Atlas should SAY this name aloud (Ruling J89) — never shown on
      // screen anywhere; jarvis-speak.js is the only reader.
      pronunciation:   get('pronunciation')   || null,
    });
    showToast('Contact info updated.', 'success');
    renderClientProfile();
  } catch (err) {
    showToast('Error saving contact info: ' + err.message, 'error');
  }
};

// ── Program Start Date — inline edit on client profile ──────────
window.editProgramStartDate = function(clientId, source) {
  const display = document.getElementById('cp-ps-display');
  const form    = document.getElementById('cp-ps-form');
  if (display) display.style.display = 'none';
  if (form)    { form.style.display = 'flex'; }
  const inp = document.getElementById('cp-ps-input');
  if (inp) inp.focus();
};

window.saveProgramStartDate = (clientId) => saveProgramStartDate(clientId, { render: renderClientProfile });

window.cancelProgramStartDateEdit = function() {
  const display = document.getElementById('cp-ps-display');
  const form    = document.getElementById('cp-ps-form');
  if (display) display.style.display = '';
  if (form)    form.style.display    = 'none';
};

window.confirmCheckinDay = function(clientId, newVal, dayName) {
  const label = newVal ? newVal.charAt(0).toUpperCase() + newVal.slice(1) : null;
  const msg = label ? `Set check-in day to ${label}?` : 'Clear the check-in day?';
  window.openBulkModal('Change Check-In Day', `<p style="margin:0">${msg}</p>`, () => {
    window.saveCheckinDay(clientId, newVal);
  }, label ? 'Set Day' : 'Clear', !label);
};

window.saveCheckinDay = async function(clientId, value) {
  try {
    // Optimistic local update for instant UI
    const c = clients.find(cl => cl.id === clientId);
    if (c) c.checkinDay = value || null;

    // Instantly update the day toggle buttons
    document.querySelectorAll('.ci-day-btn').forEach(btn => {
      const btnDay = btn.textContent.trim();
      const dayMap = { M: 'monday', T: 'tuesday', W: 'wednesday', Th: 'thursday', F: 'friday', Sa: 'saturday', Su: 'sunday' };
      btn.classList.toggle('active', dayMap[btnDay] === value);
    });

    // Instantly update calendar check-in markers
    const dayIdx = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].indexOf(value);
    document.querySelectorAll('.cal-checkin-marker').forEach(m => m.remove());
    if (dayIdx >= 0) {
      document.querySelectorAll('.cal-day:not(.cal-day--other)').forEach(cell => {
        const ds = cell.dataset.date;
        if (!ds) return;
        const cellDay = new Date(ds + 'T12:00:00').getDay();
        if (cellDay === dayIdx) {
          const marker = document.createElement('div');
          marker.className = 'cal-checkin-marker';
          marker.title = 'Check-in day';
          marker.textContent = 'Check-in';
          cell.appendChild(marker);
        }
      });
    }

    // Persist to backend (non-blocking)
    await updateClient(clientId, { checkinDay: value || null });
    showToast(value ? `Check-in day set to ${value.charAt(0).toUpperCase() + value.slice(1)}.` : 'Check-in day cleared.', 'success');
  } catch (err) {
    showToast('Error: ' + err.message, 'error');
  }
};

window.saveCheckinForm = async function(clientId, formId) {
  try {
    await updateClient(clientId, { checkinFormId: formId || null });
    showToast('Check-in form updated.', 'success');
  } catch (err) {
    showToast('Error: ' + err.message, 'error');
  }
};

// ============================================================
// MULTI-SELECT & BULK ACTIONS
// ============================================================

function renderBulkBar() {
  const bar     = document.getElementById('bulk-bar');
  const countEl = document.getElementById('bulk-count');
  if (!bar) return;
  const n = _selectedClients.size;
  if (n === 0) { bar.classList.add('hidden'); return; }
  bar.classList.remove('hidden');
  if (countEl) countEl.textContent = `${n} client${n === 1 ? '' : 's'} selected`;
}

window.toggleClientSelect = function(id, checked) {
  if (checked) _selectedClients.add(id);
  else _selectedClients.delete(id);
  // Toggle row-selected class directly to avoid full re-render
  const rows = document.querySelectorAll('#clients-tbody .client-row');
  rows.forEach(row => {
    const cb = row.querySelector('.row-check');
    if (cb && cb.getAttribute('onchange')?.includes(`'${id}'`)) {
      row.classList.toggle('row-selected', checked);
    }
  });
  // Sync select-all checkbox
  const filtered = getFiltered();
  const allCheck = document.getElementById('select-all-check');
  if (allCheck) {
    const allSel  = filtered.length > 0 && filtered.every(c => _selectedClients.has(c.id));
    const someSel = filtered.some(c => _selectedClients.has(c.id));
    allCheck.checked       = allSel;
    allCheck.indeterminate = !allSel && someSel;
  }
  renderBulkBar();
};

window.toggleSelectAll = function(checked) {
  const filtered = getFiltered();
  if (checked) filtered.forEach(c => _selectedClients.add(c.id));
  else         filtered.forEach(c => _selectedClients.delete(c.id));
  document.querySelectorAll('#clients-tbody .row-check').forEach(cb => { cb.checked = checked; });
  document.querySelectorAll('#clients-tbody .client-row').forEach(row => row.classList.toggle('row-selected', checked));
  const allCheck = document.getElementById('select-all-check');
  if (allCheck) { allCheck.checked = checked; allCheck.indeterminate = false; }
  renderBulkBar();
};

window.clearBulkSelection = function() {
  _selectedClients.clear();
  document.querySelectorAll('#clients-tbody .row-check').forEach(cb => { cb.checked = false; });
  document.querySelectorAll('#clients-tbody .client-row').forEach(row => row.classList.remove('row-selected'));
  const allCheck = document.getElementById('select-all-check');
  if (allCheck) { allCheck.checked = false; allCheck.indeterminate = false; }
  renderBulkBar();
};

window.openBulkModal = function(title, bodyHtml, onConfirm, okLabel = 'Apply', danger = false) {
  document.getElementById('bulk-modal-title').textContent = title;
  document.getElementById('bulk-modal-body').innerHTML    = bodyHtml;
  const okBtn = document.getElementById('bulk-modal-ok');
  okBtn.textContent = okLabel;
  okBtn.classList.toggle('btn-danger', danger);
  okBtn.classList.toggle('btn-primary', !danger);
  _bulkActionCallback = onConfirm;
  document.getElementById('bulk-modal-overlay').classList.remove('hidden');
};

window.closeBulkModal = function() {
  document.getElementById('bulk-modal-overlay').classList.add('hidden');
  _bulkActionCallback = null;
  window._bulkRemoveTag = null;
};

window.confirmBulkAction = async function() {
  if (_bulkActionCallback) await _bulkActionCallback();
  window.closeBulkModal();
};

// No bulk status change (Clayton, 2026-10-05): a cancel needs its reason and its when (today, or after the 30-day
// notice), and the label alone never stopped billing, filed a lead or locked the app. Cancel and undo are per client
// (cancel-client.js). Pinned by __tests__/the-clients-page-has-no-bulk-cancel.test.mjs.

window.bulkSetCheckinDay = function() {
  const ids = [..._selectedClients];
  if (!ids.length) return;
  window.openBulkModal(
    `Set Check-In Day — ${ids.length} client${ids.length === 1 ? '' : 's'}`,
    `<div class="bulk-form-group">
       <label>Check-in day</label>
       <select id="bulk-checkin-day-select" class="filter-select" style="width:100%">
         <option value="">— Clear Check-In Day —</option>
         <option value="monday">Monday</option>
         <option value="tuesday">Tuesday</option>
         <option value="wednesday">Wednesday</option>
         <option value="thursday">Thursday</option>
         <option value="friday" selected>Friday</option>
         <option value="saturday">Saturday</option>
         <option value="sunday">Sunday</option>
       </select>
     </div>`,
    async () => {
      const day = document.getElementById('bulk-checkin-day-select')?.value || null;
      window.closeBulkModal();
      const results = await Promise.allSettled(ids.map(id => updateClient(id, { checkinDay: day })));
      const count = results.filter(r => r.status === 'fulfilled').length;
      const dayLabel = day ? day.charAt(0).toUpperCase() + day.slice(1) : 'cleared';
      showToast(`Check-in day ${day ? 'set to ' + dayLabel : 'cleared'} for ${count} client${count === 1 ? '' : 's'}.`, 'success');
      window.clearBulkSelection();
    }
  );
};

window.bulkManageTags = function() {
  const ids = [..._selectedClients];
  if (!ids.length) return;

  // Tags that exist on at least one selected client (for remove section)
  const selectedClients = ids.map(id => clients.find(c => c.id === id)).filter(Boolean);
  const removableTags   = [...new Set(selectedClients.flatMap(c => c.tags || []))].sort();

  // All defined tags (for add section)
  const defs = loadTagDefs();
  const addPresetsHtml = defs.length
    ? defs.map(d => {
        const { bg, color, border } = getTagStyle(d.color);
        return `<button type="button" class="tag-preset bulk-tag-preset"
          style="background:${bg};color:${color};border-color:${border}"
          onclick="document.getElementById('bulk-tag-input').value='${escHtml(d.name)}';document.querySelectorAll('.bulk-tag-preset').forEach(b=>b.classList.remove('selected'));this.classList.add('selected')"
        >${escHtml(d.name)}</button>`;
      }).join('')
    : '';

  const removePresetsHtml = removableTags.length
    ? removableTags.map(t => {
        const def = defs.find(d => d.name.toLowerCase() === t.toLowerCase());
        const { bg, color, border } = def ? getTagStyle(def.color) : getTagStyle('#64748b');
        const countOnSelected = selectedClients.filter(c => (c.tags || []).includes(t)).length;
        return `<button type="button" class="tag-preset bulk-tag-remove-preset"
          style="background:${bg};color:${color};border-color:${border}"
          data-tag="${escHtml(t)}"
          onclick="document.querySelectorAll('.bulk-tag-remove-preset').forEach(b=>b.classList.remove('selected'));this.classList.add('selected');window._bulkRemoveTag='${escHtml(t)}'"
          title="On ${countOnSelected} of ${ids.length} selected client${ids.length===1?'':'s'}"
        >${escHtml(t)} <span style="opacity:0.65;font-weight:400;">(${countOnSelected})</span></button>`;
      }).join('')
    : '<span style="color:var(--text-subtle);font-size:13px;">No tags on selected clients.</span>';

  window.openBulkModal(
    `Tags — ${ids.length} client${ids.length === 1 ? '' : 's'}`,
    `<div class="bulk-tag-sections">
       <div class="bulk-tag-section">
         <div class="bulk-tag-section-label">Add tag</div>
         ${addPresetsHtml ? `<div class="bulk-tag-presets">${addPresetsHtml}</div>` : ''}
         <input type="text" id="bulk-tag-input" class="search-input" placeholder="Enter or select a tag…" style="width:100%;margin-top:${addPresetsHtml ? '8px' : '0'}" />
       </div>
       <div class="bulk-tag-divider"></div>
       <div class="bulk-tag-section">
         <div class="bulk-tag-section-label">Remove tag</div>
         <div class="bulk-tag-presets">${removePresetsHtml}</div>
       </div>
     </div>`,
    async () => {
      const addTag    = document.getElementById('bulk-tag-input')?.value.trim();
      const removeTag = window._bulkRemoveTag || null;

      if (!addTag && !removeTag) { showToast('Select or enter a tag to add, or select a tag to remove.', 'error'); return; }
      window.closeBulkModal();

      // Fetch fresh rows directly from Supabase — bypasses stale local cache
      const { data: freshRows, error: fetchErr } = await supabase
        .from('clients')
        .select('id, data')
        .in('id', ids);

      if (fetchErr || !freshRows) {
        showToast('Failed to load client data. Please try again.', 'error');
        return;
      }

      const now = new Date().toISOString();

      // Add tag
      if (addTag) {
        const currentDefs = loadTagDefs();
        if (!currentDefs.find(d => d.name.toLowerCase() === addTag.toLowerCase())) {
          currentDefs.push({ name: addTag, color: '#64748b' });
          saveTagDefs(currentDefs);
          renderTagSettings();
        }
        const toAdd = freshRows.filter(row => {
          const tags = Array.isArray(row.data?.tags) ? row.data.tags : [];
          return !tags.includes(addTag);
        });
        const addResults = await Promise.allSettled(toAdd.map(async row => {
          const tags    = Array.isArray(row.data?.tags) ? row.data.tags : [];
          const newData = { ...row.data, tags: [...tags, addTag], updatedAt: now };
          const { error } = await supabase.from('clients').update({ data: newData, updated_at: now }).eq('id', row.id);
          if (error) throw new Error(error.message);
          // Automation trigger — fire-and-forget, per-row, never fails the bulk tag op.
          callFunction('automation-fire-client-event', { clientId: row.id, eventType: 'tag_added', context: { tag: addTag } }).catch(() => {});
        }));
        const addCount  = addResults.filter(r => r.status === 'fulfilled').length;
        const addFailed = addResults.filter(r => r.status === 'rejected').length;
        if (addFailed) showToast(`Tag "${addTag}" added to ${addCount}, failed for ${addFailed}.`, 'error');
        else showToast(`Tag "${addTag}" added to ${addCount} client${addCount === 1 ? '' : 's'}.`, 'success');
      }

      // Remove tag
      if (removeTag) {
        const removeResults = await Promise.allSettled(freshRows.map(async row => {
          const tags    = Array.isArray(row.data?.tags) ? row.data.tags : [];
          const newData = { ...row.data, tags: tags.filter(t => t !== removeTag), updatedAt: now };
          const { error } = await supabase.from('clients').update({ data: newData, updated_at: now }).eq('id', row.id);
          if (error) throw new Error(error.message);
        }));
        const removeCount  = removeResults.filter(r => r.status === 'fulfilled').length;
        const removeFailed = removeResults.filter(r => r.status === 'rejected').length;
        if (removeFailed) showToast(`Tag "${removeTag}" removed from ${removeCount}, failed for ${removeFailed}.`, 'error');
        else if (removeCount) showToast(`Tag "${removeTag}" removed from ${removeCount} client${removeCount === 1 ? '' : 's'}.`, 'success');
      }

      window.clearBulkSelection();
    }
  );
};

window.bulkAppAccess = function() {
  const ids = [..._selectedClients];
  if (!ids.length) return;
  window.openBulkModal(
    `App Access — ${ids.length} client${ids.length === 1 ? '' : 's'}`,
    `<div class="bulk-form-group">
       <label>App access</label>
       <select id="bulk-app-access-select" class="filter-select" style="width:100%">
         <option value="enabled">Enabled — Full access</option>
         <option value="suspended">Suspended — Locked screen (late payment)</option>
         <option value="disabled">Disabled — Cannot log in (cancelled)</option>
       </select>
     </div>`,
    async () => {
      const val = document.getElementById('bulk-app-access-select')?.value;
      if (!val) return;
      window.closeBulkModal();
      const label = val === 'enabled' ? 'Enabled' : val === 'suspended' ? 'Suspended' : 'Disabled';

      const applyAccess = async () => {
        const results = await Promise.allSettled(ids.map(id => updateClient(id, { appAccess: val })));
        const count = results.filter(r => r.status === 'fulfilled').length;
        showToast(`App access set to "${label}" for ${count} client${count === 1 ? '' : 's'}.`, 'success');
        window.clearBulkSelection();
      };

      if (val === 'disabled') {
        showConfirmDialog({
          message: `You're about to disable app access for ${ids.length} client${ids.length === 1 ? '' : 's'}. They will not be able to log in. Continue?`,
          okLabel: 'Disable Access',
          okClass: 'btn-danger',
          onConfirm: applyAccess,
        });
      } else {
        await applyAccess();
      }
    }
  );
};

window.bulkExport = function() {
  const ids = [..._selectedClients];
  if (!ids.length) return;
  const subset = clients.filter(c => ids.includes(c.id));
  import('./modules/utils.js').then(utils => {
    const headers = [
      'First Name','Last Name','Phone','Email','Date of Birth',
      'Address Line 1','Address Line 2','City','State','Zip Code',
      'Status','Service Type','Billing','Payment Source',
      'Sign Up Date','Program Start Date','Agreement Length (mo)','Bonus Months',
      'Agreement Value','Collected Upfront','MRR Amount','Installment Count','Continuity Rate',
      'Months as Client','End Date','Renewal Needed','Inactive Date',
      'Cancellation Reason','Tags','Notes',
      'Stripe Customer ID','Stripe Subscription ID'
    ];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = subset.map(c => {
      const endDate = utils.getEndDate(c);
      return [
        c.firstName||'', c.lastName||'', c.phone||'', c.email||'', c.birthdate||'',
        c.addressLine1||c.streetAddress||'', c.addressLine2||'',
        c.city||'', c.state||'', c.zipCode||'',
        c.status, c.serviceType, c.billingStructure, c.paymentSource,
        c.signUpDate||'', c.programStartDate||'',
        c.lengthOfAgreement||0, c.bonusMonths||0,
        c.agreementValue||'', c.moneyCollectedUpfront||'',
        c.mrrAmount||'', c.installmentCount||'', continuityRateOf(c)||'',
        utils.getMonthsAsClient(c),
        endDate ? _config.localDateStr(endDate) : '',
        utils.isRenewalNeeded(c) ? 'Yes' : 'No',
        c.inactiveDate||'', c.cancellationReason||'',
        (c.tags||[]).join('; '), c.notes||'',
        c.stripeCustomerId||'', c.stripeSubscriptionId||''
      ].map(esc).join(',');
    });
    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `fitops-pro-clients-selected-${_config.localToday()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${subset.length} client${subset.length === 1 ? '' : 's'} to CSV.`, 'success');
  });
};

// ── Compose Message Modal ─────────────────────────────────────
let _composeMode = 'individual'; // 'individual' | 'group'

window.bulkSMS = function() {
  const ids = [..._selectedClients];
  if (!ids.length) return;
  const names = ids.map(id => { const c = clients.find(cl => cl.id === id); return c?.name || 'Unknown'; });
  const withPhone = ids.filter(id => { const c = clients.find(cl => cl.id === id); return !!c?.phone; });

  window.openBulkModal(
    `Send SMS to ${ids.length} Client${ids.length !== 1 ? 's' : ''}`,
    `<div style="margin-bottom:10px;">
      <div style="font-size:var(--fs-xs);color:var(--text-muted);margin-bottom:8px;">${withPhone.length} of ${ids.length} have phone numbers</div>
      <textarea id="bulk-sms-body" class="modal-input" rows="4" placeholder="Type your SMS message..." style="width:100%;resize:vertical;"></textarea>
    </div>`,
    async () => {
      const body = document.getElementById('bulk-sms-body')?.value?.trim();
      if (!body) { showToast('Message cannot be empty', 'error'); return; }
      let sent = 0, failed = 0;
      const errors = [];
      for (const id of withPhone) {
        try {
          // ⚠️ senderRole is REQUIRED by messages-send — without it every call
          // 400s before Twilio is ever reached, which is how this button sent
          // 0 of 20 and reported only a count. Every other caller passes it.
          await callFunction('messages-send', { clientId: id, body, senderRole: 'coach', channel: 'sms' });
          sent++;
        } catch (e) {
          failed++;
          // Keep the FIRST real reason. A bare count can't tell "two clients
          // opted out" from "nothing sent at all", and those need completely
          // different responses from the coach.
          if (errors.length < 2) errors.push(e?.message || String(e));
        }
      }
      const detail = failed && errors.length ? ` — ${errors[0]}` : '';
      showToast(`SMS sent to ${sent} client${sent !== 1 ? 's' : ''}${failed ? ` (${failed} failed)${detail}` : ''}`, sent > 0 ? 'success' : 'error');
    },
    'Send SMS',
    false
  );
};

window.bulkCompose = function() {
  const ids = [..._selectedClients];
  if (!ids.length) return;
  _composeMode = 'individual';

  // Populate recipient chips
  const container = document.getElementById('compose-recipients');
  container.innerHTML = ids.map(id => {
    const c = clients.find(cl => cl.id === id);
    const name = c ? escHtml(c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unknown') : 'Unknown';
    return `<span class="compose-chip">${name}</span>`;
  }).join('');

  // Reset form state
  document.getElementById('compose-body').value = '';
  document.getElementById('compose-group-name').value = '';
  document.getElementById('compose-group-name-section').classList.add('hidden');
  document.getElementById('compose-mode-individual').classList.add('active');
  document.getElementById('compose-mode-group').classList.remove('active');
  document.getElementById('compose-info').textContent = 'Each client receives this as a separate 1:1 message.';
  document.getElementById('compose-send-btn').disabled = false;
  document.getElementById('compose-send-btn').textContent = `Send to ${ids.length} Client${ids.length === 1 ? '' : 's'}`;
  document.getElementById('compose-overlay').classList.remove('hidden');
};

window.setComposeMode = function(mode) {
  _composeMode = mode;
  const indBtn = document.getElementById('compose-mode-individual');
  const grpBtn = document.getElementById('compose-mode-group');
  const grpSection = document.getElementById('compose-group-name-section');
  const info = document.getElementById('compose-info');

  indBtn.classList.toggle('active', mode === 'individual');
  grpBtn.classList.toggle('active', mode === 'group');
  grpSection.classList.toggle('hidden', mode !== 'group');

  if (mode === 'individual') {
    info.textContent = 'Each client receives this as a separate 1:1 message.';
  } else {
    info.textContent = 'Creates a group conversation. All members see all messages.';
  }
};

window.sendCompose = async function() {
  const ids = [..._selectedClients];
  const body = (document.getElementById('compose-body').value || '').trim();
  if (!ids.length || !body) {
    showToast('Please enter a message.', 'error');
    return;
  }

  const btn = document.getElementById('compose-send-btn');
  btn.disabled = true;
  btn.textContent = 'Sending...';

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    if (_composeMode === 'individual') {
      const res = await fetch('/.netlify/functions/messages-bulk-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ clientIds: ids, body }),
      });
      // ⚠️ A client whose package has no messaging is skipped, and the answer
      // names them (modules/bulk-send-outcome.js). Every client skipped is a
      // refusal: nothing went, so the window stays open.
      const json = await res.json().catch(() => ({}));
      const outcome = bulkSendOutcome(res.ok, json);
      if (!outcome) throw new Error(json.error || 'Failed to send');
      if (!outcome.sentSome) {
        showToast(outcome.text, outcome.type);
        btn.disabled = false;
        btn.textContent = `Send to ${ids.length} Client${ids.length === 1 ? '' : 's'}`;
        return;
      }
      window.closeCompose();
      window.clearBulkSelection();
      showToast(outcome.text, outcome.type);
    } else {
      const groupName = (document.getElementById('compose-group-name').value || '').trim();
      const res = await fetch('/.netlify/functions/messages-group-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ clientIds: ids, name: groupName || null, body }),
      });
      if (!res.ok) throw new Error('Failed to create group');
      window.closeCompose();
      window.clearBulkSelection();
      showToast('Group conversation created.', 'success');
    }
  } catch (err) {
    console.error('sendCompose error:', err);
    showToast('Failed to send message: ' + err.message, 'error');
    btn.disabled = false;
    btn.textContent = `Send to ${ids.length} Client${ids.length === 1 ? '' : 's'}`;
  }
};

window.closeCompose = function() {
  document.getElementById('compose-overlay').classList.add('hidden');
};

window.handleRowClick = function(e, id) {
  // Don't navigate if the click was on a button, .col-actions, or the checkbox cell
  if (e.target.closest('.col-actions') || e.target.closest('.col-check') || e.target.tagName === 'BUTTON') return;
  showClientProfile(id);
};

window.confirmDelete = confirmDelete;
window.confirmCancelClient = confirmCancelClient;
window.openSaveAttempt = openSaveAttempt;
window.renderRetentionFollowups = null; // set after DOMContentLoaded
window.showClientProfile = showClientProfile;
// The Scribe page's "Open lead" (its chunk is lazy and does not import navigation).
window.openLead = _navigation.openLead;

// ── Per-client app access toggle ────────────────────────────
window.setClientAppAccess = async function(clientId, value) {
  const label = value === 'enabled' ? 'Enabled' : value === 'suspended' ? 'Suspended' : 'Disabled';

  async function apply() {
    try {
      await updateClient(clientId, { appAccess: value });
      showToast(`App access set to "${label}".`, 'success');
    } catch (err) {
      showToast('Failed to update: ' + err.message, 'error');
      renderClientProfile();
    }
  }

  if (value === 'disabled') {
    showConfirmDialog({
      message: 'Disabling app access will prevent this client from logging in. Continue?',
      okLabel: 'Disable Access',
      okClass: 'btn-danger',
      onConfirm: apply,
    });
    // Reset dropdown in case they cancel
    const sel = document.getElementById('client-app-access-' + clientId);
    if (sel) sel.value = clients.find(cl => cl.id === clientId)?.appAccess || 'enabled';
  } else {
    await apply();
  }
};

// ── Per-client intensity metric override ───────────────────
// Empty string clears the override (falls back to program → coach default).
window.setClientIntensityMetric = async function(clientId, value) {
  const next = value === 'rir' || value === 'rpe' ? value : null;
  try {
    await updateClient(clientId, { intensityMetric: next });
    showToast(next ? `Intensity metric set to ${next.toUpperCase()}.` : 'Intensity metric reset to default.', 'success');
  } catch (err) {
    showToast('Failed to update: ' + err.message, 'error');
  }
};

// The per-client auto-suspend exemption toggle was removed 2026-09-18 with the
// setting it belonged to. The per-client control that replaces it is the "Keep
// this client's access on" switch on the Amount owed card.

// ── Per-client one-way messaging toggle ────────────────────
window.toggleClientOneWay = async function(clientId, checked) {
  try {
    await updateClient(clientId, { oneWayMessaging: checked });
    showToast(checked ? 'One-way messaging enabled for this client.' : 'One-way messaging disabled for this client.', 'success');
    renderClientProfile();
  } catch (err) {
    showToast('Failed to update: ' + err.message, 'error');
  }
};

window.resetClientOneWay = async function(clientId) {
  try {
    await updateClient(clientId, { oneWayMessaging: undefined });
    showToast('Reset to default messaging setting.', 'success');
    renderClientProfile();
  } catch (err) {
    showToast('Failed to reset: ' + err.message, 'error');
  }
};

// ── Per-client Do Not Text (DNC) ────────────────────────────
// Writes coach_opt_outs via the coach-opt-outs endpoint — the SAME list
// every send gate reads (messages-send, notify, scheduled emails, payment
// links, voicemail drops). Deliberately NOT updateClient/clients.data:
// nothing reads clients.data.smsOptedOut, so a flag there would look
// blocked while every text still sent.
window.setClientDnc = async function(clientId, phone, value) {
  if (!phone) { showToast('This client has no phone number on file.', 'error'); return; }
  try {
    if (value === 'blocked') {
      await callFunction('coach-opt-outs', { action: 'add', phoneNumber: phone, clientId, note: 'Blocked from client profile' });
      showToast(`No texts will be sent to ${phone}.`, 'success');
    } else {
      // The attestation is a server requirement (coach-opt-outs.js refuses
      // removal without it), not a UI nicety — same prompt as Settings.
      const attestation = prompt(
        `Allow texting ${phone} again?\n\n` +
        `Twilio compliance requires you to record HOW the recipient re-opted-in.\n` +
        `Type the source of re-consent (e.g. "verbal at consult 5/3", "signed waiver"). Min 10 chars:`
      );
      if (!attestation || attestation.trim().length < 10) {
        if (attestation !== null) showToast('Re-consent record must be at least 10 characters.', 'error');
        renderClientProfile(); // snap the select back to its real state
        return;
      }
      await callFunction('coach-opt-outs', { action: 'remove', phoneNumber: phone, attestation: attestation.trim() });
      showToast(`${phone} removed from the do-not-text list.`, 'success');
    }
    renderClientProfile();
  } catch (err) {
    showToast('Failed to update: ' + err.message, 'error');
    renderClientProfile();
  }
};

// ============================================================
// LOGOUT
// ============================================================
async function handleLogout() {
  try {
    stopMessaging();
    // ⚠️ THE MICROPHONE GOES OFF BEFORE THE SESSION DOES (brief W2 item 5).
    // The navigation below fires `pagehide`, which disarms the wake word on its
    // own — but that is milliseconds AFTER signOut(), and "signed out with a
    // live microphone" is not a state to leave lying around even briefly. A
    // no-op for every coach who never turned it on, and it cannot throw.
    try { (await import('./modules/jarvis/wake-controller.js')).disarmWake(); } catch (e) { /* never armed */ }
    await supabase.auth.signOut();
    window.location.href = 'login.html';
  } catch (err) {
    showToast('Error signing out.', 'error');
  }
}

window.handleLogout = handleLogout;

window.handleAddNote = async function(clientId) {
  const input = document.getElementById('note-input-' + clientId);
  if (!input || !input.value.trim()) return;
  const text = input.value.trim();
  const btn = document.getElementById('note-add-btn-' + clientId);
  if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
  try {
    await addNoteToClient(clientId, text);
    input.value = '';
    showToast('Note added.', 'success');
  } catch (err) {
    showToast('Error adding note: ' + err.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Add Note'; }
  }
};

window.handleDeleteNote = async function(clientId, noteId) {
  try {
    await deleteNoteFromClient(clientId, noteId);
    showToast('Note deleted.', 'success');
  } catch (err) {
    showToast('Error deleting note: ' + err.message, 'error');
  }
};

// ── Payment History's windows: Delete, the receipt, Resend, Edit ──
// MOVED to modules/payment-entry-windows.js (standing rule ③). It installs the same window.handleDeletePayment /
// viewStoredReceipt / resendStoredReceipt / handleEditPayment and exports showReceiptHtmlModal (Settings' receipt
// preview); setPaymentEntryDeps hands it THIS file's clients.

window.handleVoidAgreement = async function(clientId) {
  const c = clients.find(cl => cl.id === clientId);
  if (!c) return;
  const clientName = escHtml(c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'this client');

  window.openBulkModal(
    'Void Agreement',
    `<p style="margin:0 0 6px">Void the pending agreement for <strong>${clientName}</strong>?</p>
     <p style="margin:0;font-size:12px;color:var(--text-muted)">The signing link will be deactivated and the client will no longer be able to sign. This cannot be undone.</p>`,
    async () => {
      try {
        // Fetch the active agreement for this client
        const listRes = await callFunction('agreement-list', null, 'GET', { clientId });
        if (!listRes?.agreements?.length) throw new Error('No agreements found');

        // Find the first voidable agreement (sent or viewed)
        const voidable = listRes.agreements.find(a => ['sent', 'viewed'].includes(a.status));
        if (!voidable) throw new Error('No pending agreement to void');

        await callFunction('agreement-void', { agreementId: voidable.id });
        await updateClient(clientId, { agreementStatus: 'voided' });
        showToast('Agreement voided.', 'success');
      } catch (err) {
        showToast('Error voiding agreement: ' + err.message, 'error');
      }
    },
    'Void Agreement',
    true
  );
};

// ============================================================
// GLOBAL SEARCH (⌘K): modules/global-search.js (moved 2026-10-12)
// ============================================================
initGlobalSearch({ getClients: () => clients, getCheckins, openCheckinView });

// ============================================================
// SESSIONS OVERVIEW VIEW
// ============================================================
// MOVED to modules/sessions-overview.js (standing rule ③): the drawer, the client panel and their window.*
// handlers. app.js keeps window.logSessionForClient below (it sets this file's own profileClientId).

window.logSessionForClient = function(clientId) {
  // Set profileClientId so the modal reads the correct client
  profileClientId = clientId;
  _state.setProfileClientId(clientId);
  // ⚠️ LOG SESSION ADDS NO CREDITS (2026-10-02): the daily job issues a
  // paid-in-full client's month (see onProfileClientIdChanged).
  window.openLogSessionModal();
};

// ============================================================
// INIT — guarded by auth
// ============================================================
// Apply saved theme and brand colors immediately to avoid flash
const _bootSettings = loadSettings();
applyTheme(_bootSettings.theme);
applyCustomColors(_bootSettings);

// Auto-theme: listen for OS color scheme changes (instant) + poll fallback
function _checkAutoTheme() {
  const s = loadSettings();
  if (s.theme !== 'auto') return;
  const current = document.documentElement.getAttribute('data-theme');
  const next    = getAutoTheme();
  if (next === current) return;
  applyTheme('auto');
  applyLogoFromStorage();
  renderSettings();
}
// Instant reaction to OS dark/light switch
window.matchMedia?.('(prefers-color-scheme: dark)')?.addEventListener('change', _checkAutoTheme);
// Fallback poll every 60s
setInterval(_checkAutoTheme, 60000);

supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('[Auth] onAuthStateChange fired — event:', event, '| session:', session ? 'exists' : 'null');
  try {
    if (!session) {
      // loginUrl keeps an emailed client link (?client=<id>) through the sign-in.
      window.location.href = loginUrl();
      return;
    }
    // Prevent double-init on token refresh events
    if (_appInitialized) {
      currentUser = session.user;
      _state.setCurrentUser(currentUser);
      return;
    }
    _appInitialized = true;
    currentUser = session.user;
    // Somebody else's copy goes (see config.js); the owner's start-up reloads
    // once this person's copy has arrived, for what page load already read.
    const _copiesDropped = _config.claimAccountCopies(currentUser.id);

    // ── Wire cross-module callbacks ───────────────────────────
    _api.setApiCallbacks({
      renderAll, closeModal,
      addClient,
      onClientsUpdated: (data) => {
        // Hide auto-created white-label demo clients from the roster, counts,
        // check-in review + inbox. Self-clients stay — coaches train on those.
        const visible = (data || []).filter(c => !c.data?.isDemo);
        clients = visible; setCheckinReviewClients(visible); setInboxClients(visible); setCoachTasksClients(visible); loadChurnScores();
      },
    });
    _clients.setClientCallbacks({
      showClientProfile, closeModal, openRenewModal: window.openRenewModal,
      renderClientsTable,
    });
    _modals.setModalCallbacks({ renderClientProfile });
    setCalendarCallbacks({ renderClientProfile });
    setSaveAttemptCallbacks({ renderClientProfile });
    setProfileCallbacks({
      getForms,
      showView,
      renderClientProfile,
    });
    initSaveAttemptListeners();
    _navigation.setNavCallbacks({
      renderDashboard,
      renderClientsTable: () => { _clientsViewFresh = true; renderClientsTable(); },
      renderClientProfile,
      renderRenewals, renderSettings,
      renderSessionsView, renderCalendar, renderInbox, deactivateInbox,
      renderLeadEngine, deactivateLeadEngine,
      renderBuilder, renderCommunity, renderBooked, deactivateBooked, renderScribe, deactivateScribe, openLeadById, updatePipelineBadge, updateModeBadge,
      // ⚠️ OPENING A PROFILE ADDS NO CREDITS (2026-10-02). A paid-in-full client's
      // month used to be issued here, from the page's cache of session_credits,
      // and a profile opened twice, or on two screens, issued it twice. The daily
      // job (process-prepaid-pack-releases.js) issues it now, once, from the
      // database, for paid-in-full and installment clients alike.
      onProfileClientIdChanged: (id)   => {
        profileClientId = id;
      },
      onCurrentViewChanged:     (view) => { currentView = view; },
    });

    // Lead Engine — the Settings → Integrations card is wired when that tab is
    // actually opened, NOT here.
    //
    // ⚠️ THIS CALL USED TO RUN AT BOOT, AND IT WOULD HAVE MADE THE LAZY IMPORT
    // ABOVE POINTLESS: initLeadEngineSettingsCard lives in lead-engine/inbox.js,
    // so calling it on every boot fetches all 243 KB on every boot, for a card
    // inside a settings tab most sessions never open. It is idempotent and
    // refreshes its own connection status on call, so moving it to the tab's
    // open handler (and the saved-tab restore path) is behaviour-neutral —
    // the card is wired before it can be looked at, and not before.
    setSettingsCallbacks({ renderClientsTable, renderDashboard });
    setAutomationCallbacks({ callFunction, toast: showToast, showClientProfile, showView });
    setRosterFilterDeps({
      label, getMonthlyRecurring, getClientLifetimeSpend,
      getMonthsAsClient, getDaysUntilEnd, getEndDate, missedCheckin,
      hasOpenCancellationRequest,
    });
    // The extracted charge modal looks a client up in THIS file's roster —
    // the same array the code did before it moved out.
    setChargeOneoffDeps({ getClients: () => clients });
    setRecordPaymentDeps({ getClients: () => clients, periodLabel, getOverdueMonthCount, getCurrentUser: () => currentUser });
    setPaymentEntryDeps({ getClients: () => clients });
    setSessionsOverviewDeps({ getClients: () => clients });
    // ─────────────────────────────────────────────────────────

    _state.setCurrentUser(currentUser);
    console.log('[Auth] Booting app for user:', currentUser.id);

    // ── Boot UI immediately — nav links & events work right away ──
    const s0 = loadSettings();
    applyTheme(s0.theme);
    applyCustomColors(s0);
    // Stripe's card element mounts inside the Add Client modal. Warm the
    // script on idle for coaches who have a key configured, so the first
    // open is not slow — for everyone else it is never fetched at all. The
    // schedule-with-a-real-fallback logic lives in stripe.js itself (mirrors
    // chart-loader.js) rather than here, so this stays a one-line call site.
    preloadStripeAtIdle();
    refreshConnectedAccount();   // cache the coach's connected Stripe account so charges route to it
    loadGoogleMapsApi();
    setCheckinFormsUser(currentUser);
    setCheckinReviewUser(currentUser);
    // Builder chain (builder/agreement/workout/exercise) now initializes inside
    // its lazy wrapper on first use. We just cache the user here so the wrapper
    // can pass it along when the chain loads.
    _builderCachedUser = currentUser;
    initCheckinFormEvents();
    initCheckinReviewEvents();
    initCoachTasks();
    // ⚠️ currentClient DOES NOT EXIST IN THIS FILE. The client behind an open
    // profile is profileClientId, looked up in clients the same way the rest
    // of app.js already does (see line 6556) — showClientProfile() in
    // modules/navigation.js sets currentView to the literal string 'client'
    // via onCurrentViewChanged, which is what bar.js's screenFrom() matches.
    initJarvis(() => ({ view: currentView, clientName: clients.find(c => c.id === profileClientId)?.name }));
    // Expose showView for builder's Forms tab redirect
    window._navigation = { showView };

    // ── Onboarding actions (retry, resend, skip) ──────────────
    window._onboarding = {
      retry: async (clientId) => {
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        await fetch('/.netlify/functions/onboarding-advance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ clientId, action: 'retry' }),
        });
        showToast('Retrying onboarding step...', 'info');
        // The workflow advances through its steps server-side, so give it a beat
        // before re-reading. refreshClients() re-fetches and re-renders without
        // re-subscribing; the old loadClients() never existed and threw.
        setTimeout(() => { refreshClients().catch(e => console.warn("[onboarding] refresh failed:", e?.message)); }, 2000);
      },
      resend: async (clientId, step) => {
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const action = step === 'agreement' ? 'start' : 'agreement_completed';
        await fetch('/.netlify/functions/onboarding-advance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ clientId, action }),
        });
        showToast('Resending...', 'info');
      },
      skip: async (clientId, step) => {
        const supabase = _config.getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const action = step === 'agreement' ? 'agreement_completed' : 'onboarding_form_completed';
        await fetch('/.netlify/functions/onboarding-advance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ clientId, action }),
        });
        showToast('Step skipped, advancing...', 'info');
        // The workflow advances through its steps server-side, so give it a beat
        // before re-reading. refreshClients() re-fetches and re-renders without
        // re-subscribing; the old loadClients() never existed and threw.
        setTimeout(() => { refreshClients().catch(e => console.warn("[onboarding] refresh failed:", e?.message)); }, 2000);
      },
    };

    // ── Send Agreement Modal ─────────────────────────────────
    window._openSendAgreement = async (clientId) => {
      const templates = window._agreementBuilder?.getTemplates?.() || [];
      if (templates.length === 0) {
        await loadAgreementTemplates();
      }
      const tmplList = window._agreementBuilder?.getTemplates?.() || [];
      const select = document.getElementById('sa-template-select');
      if (select) {
        select.innerHTML = tmplList.length === 0
          ? '<option value="">No templates — create one in Builder > Agreements</option>'
          : tmplList.map(t => `<option value="${t.id}">${t.name || 'Untitled'}</option>`).join('');
      }
      document.getElementById('send-agreement-overlay')?.classList.remove('hidden');

      const sendBtn = document.getElementById('sa-send-btn');
      sendBtn.onclick = async () => {
        const templateId = select?.value;
        const sendVia = document.getElementById('sa-send-via')?.value || 'email';
        const subject = document.getElementById('sa-email-subject')?.value || '';
        if (!templateId) return;
        sendBtn.disabled = true;
        sendBtn.textContent = 'Sending...';
        const result = await sendAgreement(clientId, templateId);
        sendBtn.disabled = false;
        sendBtn.textContent = 'Send Agreement';
        if (result?.success) {
          document.getElementById('send-agreement-overlay')?.classList.add('hidden');
        }
      };
    };
    bindEvents();
    initSidebarResize();
    updateModeBadge();
    applyLogoFromStorage();
    applyCurrencyToUI(s0.currency || 'USD');
    setDateFormat(s0.dateFormat || 'MMM D, YYYY');
    console.log('[Auth] UI booted');

    // ── Booting as a team member ─────────────────────────────────
    // A member has no coaches row, so everything the owner path reads from it
    // has to come from team-me instead. Returns false when this login is not a
    // member of anything, which is the genuine "you have no account" case.
    //
    // ⚠️ THE NAV IS HIDDEN, NOT THE DATA. Every endpoint re-checks the
    // permission itself; this only stops a member being shown eleven screens
    // they hold no key for and landing on an empty client dashboard wondering
    // what broke. Same mechanism as review-mode, which already does exactly
    // this for the Meta review login.
    const _MEMBER_VIEW_KEYS = {
      'lead-engine': 'leads.view',
      booked:        'meetings.view',
      clients:       'clients.view',
      calendar:      'calendar.view',
      checkins:      'clients.checkins.review',
      // ⚠️ COMMUNITY IS DELIBERATELY ABSENT. Every one of its endpoints refuses
      // a team member — the viewer they all share recognises a client or a
      // coach and nothing else — so the button opened onto an error banner.
      //
      // It is not converted rather than half-converted because of the AUTHOR
      // model: a post is written either by a client or by "the coach", stored
      // as an empty author. A sub-coach posting would be saved as the owner and
      // shown to every client as the owner, which is a lie told to clients.
      // Fixing it properly means a third author case — a column, the hydrate,
      // and the display — and that is a build, not a permission.
      //
      // So the button is not offered, and community.post is gone from the
      // permission list with it: a tick box that controls nothing is the thing
      // this screen exists to not have.
      inbox:         'clients.message',
      // Settings always shows: it is where they change their own password, and
      // every tab inside it gates itself.
      settings:      null,
    };
    /** Paint a member's screen in the account's units, currency and colours. */
    async function _applyAccountSettings() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch('/.netlify/functions/team-account-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const { settings, logoUrls } = await res.json();
        if (!settings) return;
        // ⚠️ INTO THE SAME PLACE EVERY SCREEN READS. Applying the theme and
        // currency here painted the chrome and left loadSettings() — which the
        // calendar, nutrition, check-ins and the inbox all read for timezone,
        // units, date format and business name — on the platform defaults. So
        // a member's screen was in the right colours and the wrong kilos.
        // Written straight to storage: saveSettingsToStorage would sync it
        // back to user_config under the member's own id.
        try {
          const current = JSON.parse(localStorage.getItem('revfit_settings') || '{}');
          // ⚠️ RULING J83 — DROP IT BEFORE THE MERGE. atlasSpeakEnabled is the
          // one field in this blob that is not the account's — it's a
          // personal switch, not a display field, so team-account-settings.js's
          // SHAREABLE list deliberately excludes it and `settings` never
          // overwrites it. Left alone, `current` carries forward whatever the
          // LAST person signed into this browser had it set to: nothing else
          // clears revfit_settings for a member the way loadConfig() clears
          // and reseeds it for an owner. This member's own value is fetched
          // and applied separately, just below.
          delete current.atlasSpeakEnabled;
          localStorage.setItem('revfit_settings', JSON.stringify({ ...current, ...settings }));
        } catch (e) { console.warn('[member] settings storage:', e?.message); }
        // This member's own Atlas voice preference — a single key off their
        // own user_config row (loadMemberSpeakEnabled, modules/config.js),
        // never loadConfig()'s whole platform-default blob. Caught separately
        // from the block above: one failing must not take out the theme and
        // currency that already landed.
        try {
          const mySpeak = await loadMemberSpeakEnabled();
          const withSpeak = JSON.parse(localStorage.getItem('revfit_settings') || '{}');
          withSpeak.atlasSpeakEnabled = mySpeak;
          localStorage.setItem('revfit_settings', JSON.stringify(withSpeak));
        } catch (e) { console.warn('[member] voice setting:', e?.message); }
        // Same handful of calls the owner's own boot makes, in the same order.
        applyTheme(settings.theme);
        applyCustomColors(settings);
        if (settings.currency) applyCurrencyToUI(settings.currency);
        if (settings.dateFormat) setDateFormat(settings.dateFormat);
        window._appAccountSettings = { ...settings, logoUrls };
      } catch (e) {
        // Defaults are survivable; a blank screen is not.
        console.warn('[member] account settings:', e?.message);
      }
    }

    /**
     * Tell a refused member why, on the page, instead of moving them somewhere.
     *
     * ⚠️ NO REDIRECT. Every destination is wrong: signup.html offers to make
     * them a coach in their own right and locks them out of this account,
     * billing.html shows them somebody else's overdue invoice, and login.html
     * loops them straight back here. There is nowhere to send somebody whose
     * problem is that the account owner has to do something.
     */
    function _showMemberRefused(reason, serverMessage, ownerName) {
      const who = ownerName ? `${ownerName}'s` : 'this';
      const said = {
        'invite-not-accepted': {
          head: 'Your invitation is still waiting',
          body: `Ask the owner of ${who} account to check the invitation was sent to this email address.`,
        },
        suspended: {
          head: 'Your access has been paused',
          body: `The owner of ${who} account has paused your access. They can turn it back on from their Team settings.`,
        },
        'account-inactive': {
          head: 'This account is not active',
          body: `${ownerName || 'The account owner'} needs to sort out the subscription before anyone can work in here. Nothing you can do from your side, and nothing to pay.`,
        },
      }[reason] || { head: 'You cannot get in right now', body: serverMessage || 'Ask the account owner to check your access.' };

      document.body.innerHTML = `
        <div class="member-refused">
          <h1>${escHtml(said.head)}</h1>
          <p>${escHtml(said.body)}</p>
          <button class="btn-secondary" id="member-refused-out">Sign out</button>
        </div>`;
      document.getElementById('member-refused-out')?.addEventListener('click', async () => {
        try { await supabase.auth.signOut(); } catch { /* going to login either way */ }
        window.location.href = 'login.html';
      });
    }

    // Started by the owner boot path below, alongside the coaches gate (B1)
    // — before it is even known whether this login has a coaches row at all.
    // Declared here, not there, so a hand-off to _bootAsMember can reuse the
    // SAME in-flight fetch instead of opening a second 'clients-rt' channel.
    // ⚠️ _bootAsMember's roster wiring below is in scope for this fix round
    // under controller ruling PS10 (task-6-review.md Critical #1 / Important #2).
    let rosterReady;

    async function _bootAsMember() {
      let me;
      try {
        // Declared here, like every other call site in this file. Read at call
        // time rather than captured: Supabase rotates the access token roughly
        // hourly and a token grabbed at boot can already be dead.
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return false;
        const res = await fetch('/.netlify/functions/team-me', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        });
        // ⚠️ A REFUSAL IS NOT "NOT A MEMBER", AND TREATING IT AS ONE IS THE
        // WORST BUG IN THIS FILE. team-me answers 403 with a `denied` reason
        // for somebody suspended, or whose invitation has not been taken up.
        // Throwing that away made the caller send them to signup.html — where
        // completing the form creates them their OWN coaching account and locks
        // them out of this one for good, because a login can only be a coach or
        // a member, never both. They came here from an invitation email.
        if (res.status === 403) {
          const why = await res.json().catch(() => ({}));
          if (why?.denied) { _showMemberRefused(why.denied, why.error); return true; }
        }
        if (!res.ok) return false;
        me = await res.json();
      } catch { return false; }
      if (!me || !me.ownerId || me.isOwner) return false;   // not a member of anything

      // ⚠️ THE OWNER'S BILL, NOT THEIRS. A member sent to billing.html reads
      // "your subscription is past due" about a subscription that is not
      // theirs and which they cannot pay. Say whose it is.
      if (!me.accountActive) { _showMemberRefused('account-inactive', null, me.ownerName); return true; }

      window._appMember = me;
      window._appIsOwner = false;
      window._appHasScribe = false;
      // The sidebar's account row: who is signed in, on whose account.
      setSidebarAccount({ name: currentUser?.user_metadata?.full_name || currentUser?.email || '', business: me.ownerName || '' });
      document.body.classList.add('member-mode');

      const perms = me.permissions || {};
      const allowed = new Set(
        Object.entries(_MEMBER_VIEW_KEYS)
          .filter(([, key]) => key === null || memberHasPerm(perms, key))
          .map(([view]) => view),
      );
      document.querySelectorAll('.nav-btn[data-view]').forEach((btn) => {
        if (!allowed.has(btn.dataset.view)) btn.style.display = 'none';
      });
      // ⚠️ SOME NAV BUTTONS SHIP HIDDEN AND ARE UNHIDDEN ELSEWHERE. Booked's is
      // revealed inside the platform-super-admin branch of the owner's boot,
      // which a member never reaches — so "See booking pages" was granted by
      // two shipped roles, promised by its own tooltip and the guide, and the
      // button could not appear however the role was set up. Hiding by display
      // above does not undo a `hidden` class, so allowed views are un-hidden
      // explicitly here.
      for (const view of allowed) {
        document.querySelector(`.nav-btn[data-view="${view}"]`)?.classList.remove('hidden');
      }

      // ⚠️ THE SETTINGS TABS ARE THE OWNER'S BUSINESS. Appearance, branding,
      // payments, sessions, check-ins, nutrition, messaging, communications
      // and retention all configure the account; the pages rendered for a
      // member and their saves either went nowhere or under the member's own
      // id. Team stays (its own permission gates the cards), and Security is
      // their own login. The first tab left standing becomes the active one.
      const OWNER_TABS = ['appearance', 'app', 'integrations', 'payments', 'sessions', 'checkins', 'nutrition', 'messaging', 'communications', 'retention'];
      for (const tab of OWNER_TABS) {
        document.querySelector(`.settings-tab-btn[data-tab="${tab}"]`)?.setAttribute('data-owner-only', '');
        document.getElementById(`settings-tab-${tab}`)?.setAttribute('data-owner-only', '');
      }
      const firstTab = [...document.querySelectorAll('.settings-tab-btn')].find((b) => !b.hasAttribute('data-owner-only'));
      if (firstTab && !firstTab.classList.contains('active')) firstTab.click();

      // Land them somewhere they can actually use. Lead Engine first because
      // that is what a setter is for; otherwise the first thing they can see.
      const landing = allowed.has('lead-engine') ? 'lead-engine' : ([...allowed][0] || 'settings');
      try { showView(landing); } catch (e) { console.warn('[member] landing view:', e?.message); }

      // ── The screens they were just given have to be filled ────────────
      //
      // ⚠️ THIS FUNCTION USED TO END ON THE LINE ABOVE, AND THAT WAS THE WHOLE
      // FEATURE BROKEN. Its caller returns as soon as this returns true, so
      // everything the owner's boot path starts below it — the client roster,
      // the check-in list and its live feed, the inbox, the session listener —
      // never ran for a member at all. A sub-coach with every permission and a
      // client assigned to them would still have opened Clients and seen an
      // empty screen, and the obvious conclusion would have been that the
      // database rules were wrong. They were not; nothing had asked for the
      // data.
      //
      // ⚠️ EACH ONE IS BEHIND THE VIEW IT FEEDS. Starting a feed for a screen
      // this person cannot open would subscribe them to rows the database is
      // going to refuse anyway, which is noise at best and a confusing empty
      // realtime channel at worst.
      //
      // ⚠️ AND EACH ONE IS CAUGHT SEPARATELY. These are independent feeds; one
      // throwing must not take the rest of the member's boot with it. The
      // owner's path has the whole block inside one try, which is survivable
      // there because a failure lands on a coach who can see everything else.
      const start = (label, fn) => {
        try { fn(); } catch (e) { console.warn(`[member] ${label} did not start:`, e?.message); }
      };
      if (allowed.has('clients') || allowed.has('checkins') || allowed.has('inbox')) {
        // The roster feeds the client list, the check-in rows and the inbox
        // alike — all three render names out of it. Already STARTED by the
        // caller above (B1), before it knew this login would land here — but
        // not yet applied. This `if` is that decision: a member holding none
        // of these three permissions never reaches this line, so the fetch
        // settles into nothing (PS10) instead of landing in memory anyway.
        start('client roster', () => settleRosterFetch(rosterReady, true));
      }
      if (allowed.has('checkins')) {
        start('check-in forms', () => loadForms());
        start('check-ins', () => loadCheckins());
        start('check-in live feed', () => initCheckinsRealtime());
      }
      if (allowed.has('inbox')) {
        startMessaging(start);
      }
      // ⚠️ THE ACCOUNT'S SETTINGS, NOT THEIR OWN — AND NOT loadConfig(). A member
      // has no config row, so loadConfig would hand them platform defaults:
      // imperial units on a metric account, dollars on a pounds one, the wrong
      // date format, none of the branding. Every number on their screen would
      // be in the wrong shape.
      //
      // ⚠️ AND NOT loadConfig(me.ownerId) EITHER, which is the obvious version
      // and the dangerous one. The owner's config blob holds the account's API
      // keys — Anthropic, Google Maps, a Google refresh token, GHL, Whop, both
      // Stripe publishable keys. team-account-settings hands back an
      // allow-listed display subset and nothing else.
      start('account settings', () => _applyAccountSettings());

      start('sessions', () => _sessions.initSessionListener(() => {
        // ⚠️ ONLY REPAINT THE VIEW THE COACH IS ACTUALLY ON. #view-client is
        // hidden with a class, not removed, so client-profile-content still
        // resolves from anywhere and profileClientId survives navigation —
        // this callback used to rebuild the whole profile invisibly on every
        // session refresh, re-firing all seven Program-tab loaders. Measured
        // 2026-09-09: a repeat Sessions visit cost 12 requests / 2,153 ms.
        // ⚠️ AND ONLY THE SESSION TRACKER CARD (2026-10-14): a rebuilt profile lands on
        // Program, so every Add sessions sent the coach off the Account tab.
        if (currentView === 'client') _sessions.repaintSessionCard();
        if (currentView === 'sessions') repaintSessionsOverview();
      }));
      return true;
    }

    // ── Subscription gate + data load (async, non-blocking) ──────
    (async () => {
      // ⚠️ SET ONLY ONCE THIS LOGIN IS CONFIRMED TO BE THE OWNER (PS13,
      // task-6 fix round 2). The catch below reads this to decide whether it
      // may apply the roster: a `coaches` SELECT failure, or a throw inside
      // _bootAsMember before its own permission gate, both land in the catch
      // with this still false — identity was never resolved, so applying
      // would be Critical #1's own defect (the roster handed to a login
      // whose permission was never checked) reached through a different door.
      let ownerConfirmed = false;
      // The Scribe answer (modules/nav-access.js). The boot waits for it only
      // when it is about to restore Scribe, and the catch below reads it too.
      let scribeReady = Promise.resolve(false);
      try {
        // ⚠️ THE ROSTER GOES FIRST AND IS NOT AWAITED YET. The clients query
        // needs nothing from the coaches row, and running them back to back
        // cost a whole PostgREST round trip of dead time on every boot
        // (measured 2026-09-09: coaches 625→787 ms, clients not started until
        // 790 ms). Starting it here puts both on the wire at once.
        //
        // ⚠️ ASSIGNED, NOT DECLARED — rosterReady is the `let` _bootAsMember
        // closes over above. A login with no coaches row hands off there, and
        // it reuses this same fetch instead of starting a second one; calling
        // it twice would open a second 'clients-rt' channel.
        //
        // ⚠️ STARTING IS NOT APPLYING (PS10). rosterReady is only the fetch —
        // it has no side effects until something calls settleRosterFetch() on
        // it. On the redirect edge case below (not subscribed → billing.html)
        // nothing ever does, so the fetched page is genuinely dropped: no
        // state write, no render, no subscription. Caught immediately so a
        // failure on any of these paths is never an unhandled rejection.
        rosterReady = startRosterFetch();
        rosterReady.catch(() => {});

        const { data: coachProfile } = await supabase
          .from('coaches')
          .select('subscription_status, is_owner, review_mode, settings, name, business_name')
          .eq('id', currentUser.id)
          .single();

        // ⚠️ NO coaches ROW IS NOT THE SAME AS NO ACCOUNT ANY MORE. A team
        // member has a login and no coaches row of their own — that is what
        // being a member IS — and this used to bounce them straight to
        // signup.html. Every other fix in the team feature was unobservable
        // because of these six lines: a setter could not get through the front
        // door to see any of it.
        //
        // The subscription gate below has the same problem in reverse: it would
        // read the member's (nonexistent) status and send them to billing for
        // an account that is paid up. team-me answers both, server-side,
        // because a member cannot read the owner's row and should not be able
        // to.
        if (!coachProfile) {
          const member = await _bootAsMember();
          if (!member) { window.location.href = 'signup.html'; return; }
          return;   // _bootAsMember has taken it from here
        }
        if (!coachProfile.is_owner && !['active', 'trialing'].includes(coachProfile.subscription_status)) {
          window.location.href = 'billing.html'; return;
        }
        // Past both checks above: this login IS the owner. From here down,
        // if anything throws, the catch below may apply the roster (PS13).
        ownerConfirmed = true;

        // Review mode (scoped Meta App Review login): restrict this account to
        // the Lead Engine, the dashboard and Settings (the ads app's reviewer
        // needs the ads card there) — hide every other nav (CSS) + land on the
        // LE view. Default false, so real coaches are completely unaffected.
        if (coachProfile.review_mode) {
          window._reviewMode = true;
          document.body.classList.add('review-mode');
          try { showView('lead-engine'); } catch (e) { /* restoreLastView guard covers timing */ }
        }

        // The Zoom review login (FitOps Admin's Create coach, "Zoom review-only"): only Booked, the calendar and
        // Settings > Integrations, where its card is the one with Zoom (CSS body.review-zoom). It lands on that Zoom
        // row, where Zoom's test plan starts (Connect), and so does every reload (restoreLastView takes the first).
        // app_metadata is written only by the server, so the reviewer can never lift it.
        const zoomReview = currentUser?.app_metadata?.zoom_review === true;
        if (zoomReview) {
          window._reviewMode = true;
          window._reviewViews = ['settings', 'booked', 'calendar'];
          window._reviewSettingsTab = 'integrations';
          document.body.classList.add('review-zoom');
          try { showView('settings'); } catch (e) { /* restoreLastView guard covers timing */ }
        }

        // The owner's sidebar block (modules/nav-access.js): Booked for every
        // account owner, Scribe for whoever the Scribe answer says has it.
        scribeReady = applyOwnerNav({ isPlatformOwner: !!coachProfile.is_owner, reviewMode: !!coachProfile.review_mode || zoomReview, session, refreshMeetingsList, startOwnerHealthWatch: _startOwnerHealthWatch, fetchScribeAccess: () => callFunction('scribe-access', null, 'GET') });
        // All coaches (owner included) poll for platform broadcasts every
        // 90s. The owner needs to see their own message land so they can
        // verify it reached coaches.
        _startCoachBroadcastsWatch(session);

        // Platform Status page — available to ALL coaches (not owner-gated).
        // Safe to init here; module just attaches click listeners + waits
        // for view activation.
        initStatusPage({ supabase });

        // Signal House card visibility — DISABLED 2026-05-19.
        // We've committed to Twilio for SMS/Voice/WhatsApp. The SH
        // integration code (functions, scripts, platform_config keys)
        // stays in place as a kept-warm fallback, but the SH card is
        // hidden in the Integrations tab for ALL users including owners.
        // The HTML default is `hidden`, so leaving it alone keeps it hidden.
        // To re-enable for migration testing again, uncomment:
        // if (coachProfile.is_owner) {
        //   document.getElementById('a2p-sh-status-card')?.classList.remove('hidden');
        // }

        // Apply coach-specific check-in settings to the Settings UI
        const _coachSettings = coachProfile.settings || {};
        _appCoachSettings = _coachSettings;

        // Sync local email/identity settings to coaches.settings so serverless
        // functions can read them.
        //
        // ⚠️ ONLY WHEN SOMETHING ACTUALLY CHANGED. This used to POST on every
        // page load — a database write, 554 ms in boot wave 2 (measured
        // 2026-09-09) — because the trigger was "localStorage holds an email
        // domain or a business name", which is just the state of any configured
        // coach. _coachSettings is the stored copy, in hand two lines above.
        const localSettings = _config.loadSettings();
        const { payload: syncPayload, changed: syncNeeded } =
          buildCoachSettingsSync(localSettings, _coachSettings);
        if (syncNeeded) {
          console.log('[Auth] Syncing coach settings:', JSON.stringify(syncPayload));
          fetch('/.netlify/functions/save-coach-settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
            body: JSON.stringify({ settings: syncPayload }),
          }).then(r => {
            if (r.ok) console.log('[Auth] Coach settings synced successfully');
            else r.text().then(t => console.error('[Auth] Coach settings sync failed:', r.status, t));
          }).catch(err => console.error('[Auth] Coach settings sync error:', err.message));
        }

        setCheckinReviewCoachSettings(_coachSettings);
        if (window._initCategoryUI) window._initCategoryUI(_coachSettings);

        // Coach IG handle — used as the watermark on transformation
        // posts the mobile app generates. Stored canonical (no `@`)
        // server-side; rendered with leading `@` here.
        const igInput = document.getElementById('s-ig-handle');
        if (igInput) {
          igInput.value = _coachSettings.igHandle ? '@' + _coachSettings.igHandle : '';
        }

        // The coach's own mobile, for text alerts TO them. Shares the Coach
        // Profile card's single save button with the handle above, so it has
        // to be populated here too or saving the handle would blank it.
        const alertPhoneInput = document.getElementById('s-alert-phone');
        if (alertPhoneInput) {
          alertPhoneInput.value = _coachSettings.alertPhone || '';
        }

        // Email signature parts, same card and same save button again. The
        // coaches row's own `name` is what send-notification-email.js falls
        // back to for BOTH the signed name and the business line, so the
        // preview needs it to show the coach what clients will actually get.
        window._appCoachName = coachProfile.name || '';
        // The sidebar's account row: the name a send signs with, and the business.
        setSidebarAccount({
          name: _coachSettings.coachName || coachProfile.name || '',
          business: businessNameOnFile(localSettings, _coachSettings, coachProfile),
        });
        // The name field shows what a send would actually use: the saved
        // settings.coachName, else the coaches row's name — same resolution as
        // send-notification-email.js. Never left blank-looking when a send
        // would sign something.
        const coachNameInput = document.getElementById('s-coach-name');
        if (coachNameInput) coachNameInput.value = _coachSettings.coachName || coachProfile.name || '';
        const credentialInput = document.getElementById('s-credential');
        if (credentialInput) credentialInput.value = _coachSettings.credential || '';
        const roleInput = document.getElementById('s-role');
        if (roleInput) roleInput.value = _coachSettings.role || '';
        const addressInput = document.getElementById('s-business-address');
        if (addressInput) addressInput.value = _coachSettings.businessAddress || '';
        // The business name, as agreements print it (coach-business-name.js).
        // `saved` is what coaches.settings holds — the copy emails and booking
        // pages read, and the one the coach app writes — so Save writes the
        // name whenever the field differs from it: after the coach edits it,
        // or when the name exists only from signup.
        const bizInput = document.getElementById('s-business-name');
        if (bizInput) {
          bizInput.value = businessNameOnFile(localSettings, _coachSettings, coachProfile);
          bizInput.dataset.saved = (_coachSettings.businessName || '').trim();
        }
        window.renderSignaturePreview?.();

        // ⚠️ SAME RACE, ONE MORE CONTROL (found 2026-09-18 on real data).
        // bindEvents() already painted one-way messaging from
        // _appCoachSettings while it was still {} — this is the repaint with
        // the real, saved value, same idea as the six identity fields just
        // above. Without it a coach could see "off" while the server kept
        // enforcing whatever was actually saved.
        applyMessagingSuspendSettings(
          { oneWayMessaging: document.getElementById('s-one-way-messaging') },
          _coachSettings,
        );
        // Nutrition tolerance + streak freezes — same defect, found in the
        // same sweep: _renderNutTol()/_renderFreeze() (bindEvents) also run
        // exactly once, before this fetch resolves, and were never wired to
        // run again with the real settings.
        window._renderNutTol?.();
        window._renderFreeze?.();

        // ⚠️ Re-snapshot. initSettingsDirtyTracking() runs off renderSettings(),
        // so it normally fires well after this populate and snapshots the real
        // values. But it also fires on a theme switch and on opening Settings,
        // either of which can land while this coaches fetch is still in flight
        // — that snapshot is four empty strings, and filling them a moment
        // later leaves Save lit on a card nobody has edited. The four cards
        // just repainted above need the same treatment as IG handle, or a
        // theme switch mid-boot leaves THEIR Save lit for the same reason.
        document.getElementById('btn-save-ig-handle')?._dirtyReset?.();
        document.getElementById('btn-save-messaging')?._dirtyReset?.();
        document.getElementById('btn-save-nutrition-tolerance')?._dirtyReset?.();
        document.getElementById('btn-save-freeze-config')?._dirtyReset?.();

        // Subscription valid — the roster was started before the gate, and
        // this is the point the boot actually decides it wants it. Not
        // awaited: applying it is a promise callback that fires the moment
        // page 1 lands, so this stays exactly as fast as B1 made it.
        settleRosterFetch(rosterReady, true);
        loadForms();
        // loadExercises() + loadPrograms() moved into the lazy _loadBuilderChain()
        // wrapper — they now fire on the user's first Builder tab visit. Small
        // UX trade-off (~500ms delay on first Builder click) for a ~234KB cut
        // off initial page load.
        loadCheckins();
        initCheckinsRealtime();
        startMessaging();
        _sessions.initSessionListener(() => {
          // ⚠️ ONLY REPAINT THE VIEW THE COACH IS ACTUALLY ON. #view-client is
          // hidden with a class, not removed, so client-profile-content still
          // resolves from anywhere and profileClientId survives navigation —
          // this callback used to rebuild the whole profile invisibly on every
          // session refresh, re-firing all seven Program-tab loaders. Measured
          // 2026-09-09: a repeat Sessions visit cost 12 requests / 2,153 ms.
          // ⚠️ AND ONLY THE SESSION TRACKER CARD (2026-10-14): a rebuilt profile lands on
          // Program, so every Add sessions sent the coach off the Account tab.
          if (currentView === 'client') _sessions.repaintSessionCard();
          if (currentView === 'sessions') repaintSessionsOverview();
        });
        if (scribeIsTheRestoreTarget()) await scribeReady;
        restoreLastView();
        // ⚠️ THE DASHBOARD STARTS NOW, not after the client list loads: it reads none of it (10/16, a refresh waited
        // for the whole roster before asking for its numbers)
        if (!document.getElementById('view-dashboard')?.classList.contains('hidden')) renderDashboard();
        // ⚠️ A RESTORED SCRIBE IS DRAWN HERE (10/30): the client list can land during the wait above, and its renderAll
        // draws the view on screen then. restoreLastView only shows #view-scribe, so Scribe stayed blank.
        if (currentView === 'scribe') renderCurrentView();

        // Load cloud config in background
        loadConfig(currentUser.id).then(() => {
          console.log('[Auth] Cloud config loaded');
          if (_copiesDropped) { window.location.reload(); return; }
          const s = loadSettings();
          applyTheme(s.theme);
          applyCustomColors(s);
          // Cloud config can change the coach's configured key (or add one for
          // the first time) — initStripe() now fetches js.stripe.com itself,
          // so this call can reject on a network failure rather than assume
          // it is synchronous.
          initStripe().catch(err => console.warn('[stripe]', err));
          applyLogoFromStorage();
          // Sync unit system pill to loaded preference
          const unitSystem = s.unitSystem || 'imperial';
          document.querySelectorAll('#unit-system-control .theme-seg-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.unitSystem === unitSystem);
          });
        }).catch(err => {
          console.warn('[Auth] Cloud config load failed (using localStorage):', err.message);
        });

      } catch (err) {
        console.error('[Auth] Subscription check error:', err.message);
        // Fall through — still start data so UI isn't dead, but only when
        // this login was already confirmed to be the owner (PS13, task-6 fix
        // round 2). rosterReady already started at the top of the try — a
        // second startRosterFetch() call here would open a second
        // 'clients-rt' channel, so settleRosterFetch reuses the same
        // in-flight fetch instead. Passing ownerConfirmed rather than a
        // hardcoded true covers both problems the previous cut's
        // unconditional true had: reached BEFORE identity was ever known (a
        // `coaches` SELECT failure, or a throw inside _bootAsMember before
        // its own permission gate) it is still false here, so this drops —
        // no state write, no render, no subscription, same as a denied
        // member (New-1); reached AFTER the owner's own apply already fired
        // it is true, but settleRosterFetch's own once-only latch makes this
        // a no-op rather than a second subscription (New-2).
        settleRosterFetch(rosterReady, ownerConfirmed);
        _sessions.initSessionListener(() => {
          // ⚠️ ONLY REPAINT THE VIEW THE COACH IS ACTUALLY ON. #view-client is
          // hidden with a class, not removed, so client-profile-content still
          // resolves from anywhere and profileClientId survives navigation —
          // this callback used to rebuild the whole profile invisibly on every
          // session refresh, re-firing all seven Program-tab loaders. Measured
          // 2026-09-09: a repeat Sessions visit cost 12 requests / 2,153 ms.
          // ⚠️ AND ONLY THE SESSION TRACKER CARD (2026-10-14): a rebuilt profile lands on
          // Program, so every Add sessions sent the coach off the Account tab.
          if (currentView === 'client') _sessions.repaintSessionCard();
          if (currentView === 'sessions') repaintSessionsOverview();
        });
        if (scribeIsTheRestoreTarget()) await scribeReady;
        restoreLastView();
        // ⚠️ THE DASHBOARD STARTS NOW, not after the client list loads: it reads none of it (10/16, a refresh waited
        // for the whole roster before asking for its numbers)
        if (!document.getElementById('view-dashboard')?.classList.contains('hidden')) renderDashboard();
        // ⚠️ A RESTORED SCRIBE IS DRAWN HERE (10/30): the client list can land during the wait above, and its renderAll
        // draws the view on screen then. restoreLastView only shows #view-scribe, so Scribe stayed blank.
        if (currentView === 'scribe') renderCurrentView();
      }
    })();

  } catch (err) {
    console.error('[Auth] BOOT ERROR:', err);
  }
});

// ============================================================
// SESSION TRACKER — Window Functions
// ============================================================

const _sessOpenSections = { resched: false, credits: false, history: false };

window.toggleSessionSection = function(key) {
  _sessOpenSections[key] = !_sessOpenSections[key];
  const body = document.getElementById(`sess-body-${key}`);
  const chev = document.getElementById(`sess-chev-${key}`);
  if (body) body.classList.toggle('hidden', !_sessOpenSections[key]);
  if (chev) chev.textContent = _sessOpenSections[key] ? '▼' : '▶';
};

// The Log Session window, now in modules/sessions.js (moved 2026-10-06).
window.openLogSessionModal = _sessions.openLogSessionModal;
window.closeLogSessionModal = _sessions.closeLogSessionModal;
window.setSessOutcome = _sessions.setSessOutcome;
window.setSessWho = _sessions.setSessWho;
window.onSessDateChange = _sessions.onSessDateChange;
window.saveSessionLog = _sessions.saveSessionLog;

// ⚠️ THE EXPIRY IS ISSUE + creditLifeDays, NOT THE END OF THE BILLING MONTH.
// The server moved to that on 2026-08-24 and the CRM never followed, so the
// coach's own "A session credit lasts (days)" setting had no effect on anything
// he issued by hand. The PERIOD LABEL still names the billing period — it is
// display only — and the date stays editable so he can still override a batch.
//
// ⚠️ PAINTED FIRST, THEN CORRECTED. The saved number needs a round trip, and the
// modal must not sit blank waiting for it; the default IS what the server falls
// back to, so a failed fetch leaves the right answer on screen. The overwrite is
// skipped if the coach has already typed a date of his own.
window.openIssueCreditsModal = function(clientId) {
  const c = clients.find(x => x.id === clientId);
  if (!c) return;
  // ⚠️ SAME FIX AS logSessionForClient, a few hundred lines up, and for the
  // exact same reason: saveIssueCredits below reads profileClientId, which is
  // otherwise only set when the PROFILE view's own client changes
  // (onProfileClientIdChanged). This modal is ALSO opened from the Sessions
  // page detail (renderSessDetail's "Issue Credits" button,
  // window.openIssueCreditsModal(c.id)) for whichever client that page's own
  // drawer has selected — not necessarily whoever's profile was last viewed.
  // Without this, saving from the Sessions page silently issued credits (or
  // now, make-up sessions) against the wrong client.
  profileClientId = clientId;
  _state.setProfileClientId(clientId);
  // Always open on Regular credits — today's default, unchanged either way.
  window.setIssueCreditsKind('credits');
  const qtyEl    = document.getElementById('sess-issue-qty');
  const labelEl  = document.getElementById('sess-issue-label');
  const expiryEl = document.getElementById('sess-issue-expiry');
  const notesEl  = document.getElementById('sess-issue-notes');
  // ⚠️ R99 REVIEW GAP: nothing stopped a past date here, and the server
  // (coach-issue-makeup-credits.js) now refuses one for make-up sessions —
  // the picker should never let the coach reach that error in the first
  // place. Applies to both kinds; a regular credit dated in the past makes
  // as little sense as a make-up one.
  if (expiryEl) expiryEl.min = _config.localToday();
  const monthly  = _sessions.getMonthlySessionCount(c);
  if (qtyEl) qtyEl.value = monthly > 0 ? monthly : '';
  const anchor = c.billingStartDate || c.signUpDate;
  const period = anchor ? _sessions.getCurrentPeriod(anchor) : null;
  if (labelEl) labelEl.value = period ? _sessions.formatPeriodLabel(period.start, period.end) : '';
  // The default expiry lives with the rest of the credit rules (sessions.js).
  const painted = _sessions.issueCreditsDefaultExpiry(c);
  if (expiryEl) expiryEl.value = painted;
  _sessions.loadCreditLifeDays().then(() => {
    if (expiryEl && expiryEl.value === painted) {
      expiryEl.value = _sessions.issueCreditsDefaultExpiry(c);
    }
  });
  if (notesEl) notesEl.value = '';
  document.getElementById('sess-issue-modal')?.classList.remove('hidden');
};

window.closeIssueCreditsModal = function() {
  document.getElementById('sess-issue-modal')?.classList.add('hidden');
};

// ⚠️ REGULAR CREDITS (today's behaviour) / MAKE-UP SESSIONS — one modal,
// same fields, a different write underneath. Make-up mode hides the
// Period/Label field (a make-up has no billing period) and caps the Number
// field at 20, mirroring what the server actually accepts.
window.setIssueCreditsKind = function(kind) {
  const kindEl = document.getElementById('sess-issue-kind');
  if (kindEl) kindEl.value = kind;
  document.querySelectorAll('#sess-issue-modal .session-fmt-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.kind === kind);
  });
  document.getElementById('sess-issue-label-grp')?.classList.toggle('hidden', kind === 'makeup');
  const qtyEl = document.getElementById('sess-issue-qty');
  if (qtyEl) qtyEl.max = kind === 'makeup' ? '20' : '999';
};

window.saveIssueCredits = async function() {
  const qty    = parseInt(document.getElementById('sess-issue-qty')?.value || '0');
  const label  = document.getElementById('sess-issue-label')?.value?.trim() || '';
  const expiry = document.getElementById('sess-issue-expiry')?.value || '';
  const notes  = document.getElementById('sess-issue-notes')?.value?.trim() || '';
  if (!qty || qty < 1) { showToast('Enter a valid number of credits.', 'error'); return; }

  const kind = document.getElementById('sess-issue-kind')?.value || 'credits';
  if (kind === 'makeup') {
    try {
      // ⚠️ NEVER session_credits. The browser does not insert a reschedit
      // itself either — issueMakeupCredits calls coach-issue-makeup-credits.js,
      // the same phone rule coach-issue-session-credits.js already follows.
      await _sessions.issueMakeupCredits(profileClientId, qty, expiry || _sessions.expiryForNewCredit(), notes);
      // The three session tables are a login-time snapshot until they join
      // the realtime publication (refreshSessionData's own header) — without
      // this, the Makeup count and the Makeup Credits list would not move
      // until the next reload, which is the exact complaint that started R99.
      await _sessions.refreshSessionData();
      window.closeIssueCreditsModal();
      showToast(`${qty} make-up session${qty !== 1 ? 's' : ''} issued.`, 'success');
    } catch (err) {
      showToast(err.message || 'Failed to issue make-up sessions.', 'error');
    }
    return;
  }

  try {
    await _sessions.issueSessionCredits(profileClientId, currentUser?.id, qty, label, notes, expiry || undefined);
    window.closeIssueCreditsModal();
    showToast(`${qty} credit${qty !== 1 ? 's' : ''} issued.`, 'success');
  } catch (err) {
    showToast(err.message || 'Failed to issue credits.', 'error');
  }
};

// ── Standing slot modal ───────────────────────────────────────
// Coach-side assign (Booked Phase 6, multi-slot design 2026-08-23): a
// client can hold up to 3 standing slots. This modal is the SECONDARY
// surface (the calendar drag is primary — coach-calendar/index.js) but it
// must walk the exact same add/move/over-capacity dialog chain, not a
// second hand-rolled one: `resolveStandingSlot` (standing-slot-flow.js)
// owns that conversation end to end — which server call to make, which
// dialog to render for its answer — and is unit-tested on its own. Setting
// a slot books nothing here — the daily cron owns the actual booking. The
// clients-rt realtime listener repaints the card once the server write
// lands.
//
// ⚠️ Found in pre-merge review 2026-08-24: this used to build the
// coach-standing-reservation-set body itself, with no `action` field — the
// server now 400s any body without one, so Save silently did nothing.
// Delegating the whole flow to resolveStandingSlot means the request is
// always built by the one place that already gets this right, so the bug
// cannot come back through this file.
// ⚠️ WHICH SLOT "Change" IS CHANGING (fix, 2026-08-24). The button passed a
// slotId all along and nothing kept it: openStandingSlotModal used it to
// prefill the form and dropped it, so Save ran the generic ADD assessment
// whose PRIMARY button reads "Add {day} as a 2nd time". Pressing Change and
// hitting the default button ADDED a slot — the most likely route to the
// second standing slot the product owner found on the original day and time.
// Cleared on every open, so "+ Add another standing slot" can never inherit
// the last slot he changed and silently retime it instead.
let _standingSlotEditingId = null;

window.openStandingSlotModal = function(clientId, slotId) {
  const c = clients.find(x => x.id === clientId);
  if (!c) return;
  _standingSlotEditingId = slotId || null;
  // A client can hold up to 3 slots; clientStandingSlots tolerates both the
  // plural and legacy singular shape. With no slotId (adding a new slot,
  // possibly the first) the form starts from sensible defaults — the
  // dialog chain below decides whether that's an add, a move, or over
  // capacity once Save is pressed.
  const sr     = slotId ? clientStandingSlots(c).find(s => s.id === slotId) : null;
  const active = sr && sr.active;
  const dowEl  = document.getElementById('sess-standing-dow');
  const timeEl = document.getElementById('sess-standing-time');
  const durEl  = document.getElementById('sess-standing-duration');
  if (dowEl)  dowEl.value  = active ? String(sr.dow) : '1';
  // <input type="time"> requires HH:MM with a 2-digit hour; the stored
  // value may be e.g. "9:00" (the server accepts 1-2 hour digits).
  if (timeEl) timeEl.value = active ? String(sr.time).padStart(5, '0') : '09:00';
  if (durEl)  durEl.value  = active ? (sr.durationMin || 60) : 60;
  document.getElementById('sess-standing-modal')?.classList.remove('hidden');
};

window.closeStandingSlotModal = function() {
  document.getElementById('sess-standing-modal')?.classList.add('hidden');
};

window.saveStandingSlot = async function() {
  const dow  = parseInt(document.getElementById('sess-standing-dow')?.value ?? '', 10);
  const time = document.getElementById('sess-standing-time')?.value || '';
  const durationMin = parseInt(document.getElementById('sess-standing-duration')?.value || '60', 10);
  if (!Number.isFinite(dow)) { showToast('Pick a day of the week.', 'error'); return; }
  if (!time)                 { showToast('Pick a time.', 'error'); return; }

  const c = clients.find(x => x.id === profileClientId);
  const clientName = c ? (c.name || [c.firstName, c.lastName].filter(Boolean).join(' ')) : '';
  const held = clientStandingSlots(c);
  const hasHeldSlots = held.length > 0;
  const editing = _standingSlotEditingId ? held.find(s => s.id === _standingSlotEditingId) : null;

  // The dialog chain (add / move / over-capacity, and — for a move — what
  // happens to weeks already booked) is asked by resolveStandingSlot
  // itself, on top of this modal, exactly as it is from the calendar drag.
  window.closeStandingSlotModal();
  try {
    // ⚠️ CHANGE RUNS A MOVE. The coach came here from a specific slot's
    // Change button, so which slot he means is not in question and the
    // add-or-move dialog would be noise. runStandingSlotMove is the same
    // implementation the calendar uses, so both surfaces ask the "what
    // happens to the weeks already booked" question the same way.
    if (editing) {
      const out = await runStandingSlotMove({
        clientId: profileClientId, clientName, slot: editing, dow, time, durationMin,
      });
      if (!out) return;                  // backed out of the follow-up question
      // Ruling R102 (2026-09-11): the move lands and the paid period fills
      // immediately at the new time.
      const fillMsg = standingFillToastMessage(out);
      showToast(fillMsg ? `Standing slot saved. ${fillMsg}` : 'Standing slot saved.', 'success');
      renderCurrentView();
      return;
    }
    let filled = null;
    const outcome = await resolveStandingSlot({
      clientId: profileClientId, clientName, dow, time, durationMin, hasHeldSlots,
    }, { onPosted: (res) => { filled = res; } });
    if (outcome === 'declined') return; // coach backed out of a dialog — not an error
    // ⚠️ REFUSED, NOT SILENTLY DOUBLED. This used to come back as 'done' and
    // show the green toast below while nothing had been written at all
    // (checklist step a19 would have read as passing).
    if (outcome === 'duplicate') {
      showToast(`${clientName || 'This client'} already has a standing slot at that day and time.`, 'error');
      return;
    }
    // Ruling R102 (2026-09-11): the rule lands and the paid period fills
    // immediately.
    const fillMsg = filled ? standingFillToastMessage(filled) : null;
    showToast(fillMsg ? `Standing slot saved. ${fillMsg}` : 'Standing slot saved.', 'success');
    renderCurrentView();
  } catch (err) {
    showToast(err.message || 'Failed to save standing slot.', 'error');
  }
};

// The profile's ✕ on one weekly time, now in modules/standing-slot-remove.js.
window.removeStandingSlot = removeStandingSlot;

window.undoSession = function(sessId) {
  showConfirmDialog({
    message: 'This will restore the consumed credit. If a makeup rescheduled credit was issued, it will also be removed.',
    okLabel: 'Undo Session',
    onConfirm: async () => {
      try {
        await _sessions.undoSessionEntry(sessId);
        showToast('Session undone.', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to undo session.', 'error');
      }
    }
  });
};

// ── Settings dirty-state tracking ─────────────────────────────
// Moved to modules/settings-dirty-tracking.js (10/19); settings.js calls it through the window.
window.initSettingsDirtyTracking = initSettingsDirtyTracking;

window.copyWithTooltip = function(el, text) {
  navigator.clipboard.writeText(text).then(() => {
    el.classList.add('copied');
    setTimeout(() => el.classList.remove('copied'), 1800);
  });
};

window.deleteSessionCredit = function(creditId) {
  showConfirmDialog({
    message: 'This credit package will be permanently removed. Session history is preserved.',
    okLabel: 'Delete',
    onConfirm: async () => {
      try {
        await _sessions.deleteSessionCredit(creditId);
        showToast('Credits deleted.', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to delete credits.', 'error');
      }
    }
  });
};

window.deleteSessionResched = function(reschedId) {
  showConfirmDialog({
    message: 'This rescheduled credit will be permanently removed.',
    okLabel: 'Remove',
    onConfirm: async () => {
      try {
        await _sessions.deleteSessionResched(reschedId);
        showToast('Rescheduled credit removed.', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to remove credit.', 'error');
      }
    }
  });
};

// ── Owner-only system health watcher ────────────────────────
// Polls admin-system-events every 90s. If there's been a warning or
// error in the last hour, a red badge appears on the Admin Panel
// button in the topbar (counts the number of distinct events). Click
// through to the admin panel → System Health tab to investigate.
let _healthWatchInterval = null;
let _healthLastKnownCount = 0;

async function _startOwnerHealthWatch(session) {
  if (_healthWatchInterval) return;
  await _checkOwnerHealth(session);
  await _checkOwnerOpsBanner();
  _healthWatchInterval = setInterval(() => {
    _checkOwnerHealth(session);
    _checkOwnerOpsBanner();
  }, 90_000);
  _startOpsRealtimeWatch();
}

// ── Realtime: instant banner updates when a poller or webhook writes ──
// Subscribes to platform_health_snapshot inserts so the banner refreshes
// the moment a new attention-worthy snapshot lands, instead of waiting
// up to 90s for the next poll. Cron-poll-vendors writes 5 rows in rapid
// succession, so we debounce by 500ms — one refresh per batch.
let _opsRtChannel = null;
let _opsRtDebounce = null;

function _startOpsRealtimeWatch() {
  if (_opsRtChannel) return;
  try {
    _opsRtChannel = supabase.channel('ops-health-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'platform_health_snapshot' }, () => {
        if (_opsRtDebounce) clearTimeout(_opsRtDebounce);
        _opsRtDebounce = setTimeout(() => { _checkOwnerOpsBanner(); }, 500);
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[ops-rt] subscription status:', status);
        }
      });
  } catch (e) {
    console.warn('[ops-rt] start failed:', e.message);
  }
}

// ── Owner-only Operations banner ─────────────────────────────
// Polls admin-platform-health alongside the existing system-health
// watcher. If there's an active warn/incident attention item NOT
// dismissed this session, renders the banner at the top of the CRM
// with a link into the Operations admin tab.
let _opsBannerDismissedKey = null;
try { _opsBannerDismissedKey = sessionStorage.getItem('opsBannerDismissed'); } catch {}

async function _checkOwnerOpsBanner() {
  const el = document.getElementById('ops-admin-banner');
  if (!el) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;

    const res = await fetch('/.netlify/functions/admin-platform-health', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      el.classList.add('hidden');
      return;
    }
    const data = await res.json();
    const attention = data.attention || [];
    if (attention.length === 0) {
      el.classList.add('hidden');
      return;
    }

    // Pick the worst item — incidents first, then warns, freshest first.
    const top = attention[0];
    if (_opsBannerDismissedKey === top.alertKey) {
      el.classList.add('hidden');
      return;
    }

    const isIncident = top.status === 'incident';
    el.className = 'ops-admin-banner' + (isIncident ? ' danger' : '');
    const otherCount = attention.length - 1;
    const subText = otherCount > 0
      ? `${top.source} · ${otherCount} more item${otherCount === 1 ? '' : 's'}`
      : top.source;
    el.innerHTML = `
      <span class="glyph">${isIncident ? '⛔' : '⚠'}</span>
      <div class="body">
        <strong>${_opsBannerEsc(top.message || top.source)}</strong>
        <div class="sub">${_opsBannerEsc(subText)}</div>
      </div>
      <a class="ops-banner-link" href="admin.html#operations">Open Operations →</a>
      <button class="ops-banner-dismiss" onclick="window._dismissOpsBanner('${_opsBannerEsc(top.alertKey)}')" title="Dismiss for this session">×</button>
    `;
  } catch {
    el.classList.add('hidden');
  }
}

function _opsBannerEsc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c]);
}

window._dismissOpsBanner = function(alertKey) {
  _opsBannerDismissedKey = alertKey;
  try { sessionStorage.setItem('opsBannerDismissed', alertKey); } catch {}
  const el = document.getElementById('ops-admin-banner');
  if (el) el.classList.add('hidden');
};

// ── Coach broadcast banner stack (Phase 3.1 Session 4) ──────────
// Non-owner coaches poll coach-active-broadcasts every 90s and render
// one banner per active platform broadcast above .app-layout. Owners
// see the inline composer preview instead — they don't need a public
// banner of their own message stacking on top of #ops-admin-banner.
let _coachBroadcastsInterval = null;
const _coachBroadcastsRendered = new Set();
const COACH_BROADCAST_POLL_MS = 90_000;

function _renderBroadcastBody(raw) {
  if (!raw) return '';
  let s = String(raw)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  s = s.replace(/\n/g, '<br>');
  return s;
}

function _broadcastEsc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c]);
}

// Phosphor filled icons (info-fill, warning-fill, warning-octagon-fill).
// Path data fetched directly from phosphor-icons/core@main. viewBox is
// Phosphor's native 0 0 256 256. fill="currentColor" so the icon picks
// up the white banner text color.
const _BROADCAST_ICONS = {
  info: '<svg width="18" height="18" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm-4,48a12,12,0,1,1-12,12A12,12,0,0,1,124,72Zm12,112a16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40a8,8,0,0,1,0,16Z"/></svg>',
  warn: '<svg width="18" height="18" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M236.8,188.09,149.35,36.22h0a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"/></svg>',
  incident: '<svg width="18" height="18" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M227.31,80.23,175.77,28.69A16.13,16.13,0,0,0,164.45,24H91.55a16.13,16.13,0,0,0-11.32,4.69L28.69,80.23A16.13,16.13,0,0,0,24,91.55v72.9a16.13,16.13,0,0,0,4.69,11.32l51.54,51.54A16.13,16.13,0,0,0,91.55,232h72.9a16.13,16.13,0,0,0,11.32-4.69l51.54-51.54A16.13,16.13,0,0,0,232,164.45V91.55A16.13,16.13,0,0,0,227.31,80.23ZM120,80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm8,104a12,12,0,1,1,12-12A12,12,0,0,1,128,184Z"/></svg>',
};

function _setCoachBroadcastHeight() {
  const stack = document.getElementById('coach-broadcasts-banner-stack');
  if (!stack || stack.classList.contains('hidden')) {
    document.documentElement.style.setProperty('--coach-broadcast-h', '0px');
    return;
  }
  // Wait one frame so the browser has laid out the new innerHTML before
  // we measure offsetHeight — otherwise it returns 0 on first paint.
  requestAnimationFrame(() => {
    const h = stack.offsetHeight || 0;
    document.documentElement.style.setProperty('--coach-broadcast-h', `${h}px`);
  });
}

function _renderCoachBroadcastStack(broadcasts) {
  const stack = document.getElementById('coach-broadcasts-banner-stack');
  if (!stack) return;
  if (!Array.isArray(broadcasts) || broadcasts.length === 0) {
    stack.innerHTML = '';
    stack.classList.add('hidden');
    _coachBroadcastsRendered.clear();
    _setCoachBroadcastHeight();
    return;
  }

  const html = broadcasts.map(b => {
    const sev = (b.severity === 'warn' || b.severity === 'incident') ? b.severity : '';
    const iconKey = (b.severity === 'warn' || b.severity === 'incident') ? b.severity : 'info';
    const glyph = _BROADCAST_ICONS[iconKey] || _BROADCAST_ICONS.info;
    const titleHtml = _broadcastEsc(b.title);
    const bodyHtml = _renderBroadcastBody(b.body);
    const ctaHtml = (b.ctaLabel && b.ctaUrl)
      ? `<a class="cta" href="${_broadcastEsc(b.ctaUrl)}" target="_blank" rel="noopener noreferrer">${_broadcastEsc(b.ctaLabel)}</a>`
      : '';
    const dismissHtml = b.allowDismiss !== false
      ? `<button class="dismiss" onclick="window._dismissCoachBroadcast('${_broadcastEsc(b.id)}')" title="Dismiss">×</button>`
      : '';
    const bodyBlock = bodyHtml ? `<div class="body">${bodyHtml}</div>` : '';
    return `
      <div class="coach-broadcast-banner ${sev}" data-broadcast-id="${_broadcastEsc(b.id)}">
        <span class="glyph">${glyph}</span>
        <div class="content">
          <div class="title">${titleHtml}</div>
          ${bodyBlock}
        </div>
        ${ctaHtml}
        ${dismissHtml}
      </div>`;
  }).join('');

  stack.innerHTML = html;
  stack.classList.remove('hidden');
  _coachBroadcastsRendered.clear();
  broadcasts.forEach(b => _coachBroadcastsRendered.add(b.id));
  _setCoachBroadcastHeight();
}

async function _pollCoachBroadcasts() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;
    const res = await fetch('/.netlify/functions/coach-active-broadcasts', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    _renderCoachBroadcastStack(json.broadcasts || []);
  } catch (_e) {
    // Silent — banner just won't update. Next poll will retry.
  }
}

function _startCoachBroadcastsWatch(_initialSession) {
  if (_coachBroadcastsInterval) return; // idempotent
  _pollCoachBroadcasts();
  _coachBroadcastsInterval = setInterval(_pollCoachBroadcasts, COACH_BROADCAST_POLL_MS);
}

window._dismissCoachBroadcast = async function(broadcastId) {
  // Optimistically remove from DOM so the click feels instant.
  const stack = document.getElementById('coach-broadcasts-banner-stack');
  if (stack) {
    const node = stack.querySelector(`[data-broadcast-id="${CSS.escape(broadcastId)}"]`);
    if (node) node.remove();
    if (!stack.children.length) stack.classList.add('hidden');
  }
  _coachBroadcastsRendered.delete(broadcastId);
  _setCoachBroadcastHeight();

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;
    await fetch('/.netlify/functions/coach-dismiss-broadcast', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ broadcastId }),
    });
  } catch (_e) {
    // If the persist fails, the banner returns on next poll — acceptable.
  }
};

async function _checkOwnerHealth(_initialSession) {
  try {
    // Always pull a fresh session — Supabase access tokens expire after
    // ~1 hour, but this watcher polls indefinitely. The previous version
    // cached the page-load session token and silently 401'd forever once
    // it expired. supabase.auth.getSession() returns a refreshed token
    // automatically when the current one is past expiry.
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;

    const res = await fetch('/.netlify/functions/admin-system-events?hours=1&limit=50', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    const events = (json.events || []).filter(e => e.severity === 'warning' || e.severity === 'error');
    _updateAdminBadge(events);
    _healthLastKnownCount = events.length;
  } catch {}
}

function _updateAdminBadge(events) {
  const btn = document.getElementById('btn-admin-panel');
  if (!btn) return;

  // Remove any existing badge first
  const existingBadge = btn.querySelector('.admin-health-badge');
  if (existingBadge) existingBadge.remove();
  btn.classList.remove('admin-health-active');

  if (!events || events.length === 0) return;

  const hasError = events.some(e => e.severity === 'error');
  const count = events.length;
  const label = count > 99 ? '99+' : String(count);
  const tooltip = `${count} system health event${count !== 1 ? 's' : ''} in the last hour — click to review`;

  const badge = document.createElement('span');
  badge.className = 'admin-health-badge' + (hasError ? ' admin-health-badge--error' : '');
  badge.textContent = label;
  badge.setAttribute('aria-label', tooltip);
  btn.appendChild(badge);
  btn.classList.add('admin-health-active');
  btn.setAttribute('data-tooltip', tooltip);
}


// ── App branding wizard (Settings → App) ──────────────────────
// Guided setup for a coach's white-label app: square icon, color, store
// description (+ AI draft), and privacy policy. Saves to user_config.config
// (appBranding + brandColor) where App Studio + the mobile app read it.
(function () {
  const $ = (id) => document.getElementById(id);
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  // Adaptive step model — the active list is derived from the coach's triage
  // answers (do they already have Apple/Google accounts), so the container is a
  // DYNAMIC list of step KEYS, not a fixed 0..8 index. The D-U-N-S step only
  // appears when an account still needs creating; Apple + Google share one
  // "connect" step. Each step carries a stable key so a changing list (D-U-N-S
  // coming and going) never strands the coach on the wrong pane.
  const STEP_LABELS = { triage: 'Accounts', name: 'Name', icon: 'Icon', color: 'Color', copy: 'Copy', privacy: 'Privacy', duns: 'D-U-N-S', connect: 'Connect', submit: 'Submit' };
  let _awStepKey = 'triage';
  let _awBranding = {};            // { appName, appIconUrl, description, privacyPolicyUrl, iconMode, iconBackgroundColor, storeSetup }
  let _awColor = '#2557d6';
  let _awIconMode = 'generate';    // 'generate' (from logo) | 'upload'
  let _awIconBg = '';              // '' → fall back to the brand color
  let _awLogoUrl = '';             // brand logo (for the generate-mode preview)
  let _awExamplesLoaded = false;
  let _awWired = false;
  let _awLoaded = false;
  // The operator connects Apple/Google on the coach's behalf (wl.<coach>.* keys);
  // the coach's app-request state drives the Submit step across reloads.
  const _awConn = { apple: false, google: false };
  let _awRequestStatus = 'none';   // none | requested | accepted | declined
  let _awRequestNote = null;       // a decline reason the coach can act on
  let _awLaunch = null;            // per-platform launch progress once accepted ({ios:{build,store}, android:{…}})
  let _awPollTimer = null;         // polls connection status while parked on the Connect step

  const _awUrlOk = (u) => /^https?:\/\/[^"'()\s]+$/.test(u || '');
  const _awHex = (h) => (/^#[0-9a-fA-F]{6}$/.test(h || '') ? h : '#000000');
  const _awEffIconBg = () => _awHex(_awIconBg || _awColor);
  // DUNS displays as XX-XXX-XXXX (2-3-4). We store the raw 9 digits and only
  // dash them for display, so the dashes are presentation, never stored.
  const _formatDuns = (v) => { const d = String(v || '').replace(/\D/g, '').slice(0, 9); return d.length <= 2 ? d : d.length <= 5 ? `${d.slice(0, 2)}-${d.slice(2)}` : `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`; };

  function _awAppName() { const s = (typeof loadSettings === 'function' ? loadSettings() : _config.loadSettings()) || {}; return (s.businessName || s.coachName || '').trim() || 'Your App'; }
  // The name to show in the live preview: the app name the coach typed in step 0
  // if set, else their CRM business/coach name (so the preview matches the
  // review + submit screens, not the unrelated business name).
  function _awDisplayName() { return (_awBranding.appName || '').trim() || _awAppName(); }
  function _awInitials() { const n = _awDisplayName(); return (n.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()) || 'RF'; }
  function _awCount() { const n = ($('appwiz-desc')?.value || '').length; if ($('appwiz-desc-count')) $('appwiz-desc-count').textContent = `${n} characters`; }

  // Standard, best-practice store copy (ASO-researched) we pre-fill so the coach
  // sees how complete it can be. {App Name} = their store name, {Brand} = their
  // brand (reads "your {Brand} coach"). They can edit any line or redraft via AI.
  const STANDARD_DESC = `{App Name} is where you train with your {Brand} coach — your workouts, nutrition, check-ins, and progress, all in one place, with your coach in your corner every step.

This isn't a generic fitness app. It's your private training home with {Brand}: the plan built for you, the daily habits that move the needle, and a direct line to your coach so you're never doing it alone. Open the app, see exactly what to do today, and watch the work add up week after week.

Your account is set up by your {Brand} coach. Just log in with the invite they sent you and everything is ready and waiting.

EVERYTHING YOU NEED, IN ONE PLACE
• Your workouts — Follow the exact plan your coach built for you. Log every set, rep, and weight, watch demo videos, and see your numbers climb over time.
• Nutrition made simple — Hit your daily targets with clear macro and calorie guidance. Log meals, track water, and stay on plan without the guesswork.
• Weekly check-ins — Send your {Brand} coach your progress, wins, and struggles each week and get personal feedback that keeps you moving forward.
• Progress photos & stats — Capture consistent before-and-afters and track your body weight and measurements to see how far you've come.
• Message your coach — They're one tap away, right inside the app.
• Habits & health tracking — Build the daily habits that stick and connect your wearable to bring steps, sleep, and activity into one clear picture.

HOW IT WORKS
1. Download and open {App Name}.
2. Log in with the invite from your {Brand} coach.
3. See today's plan and start training — your coach handles the rest.

WHY CLIENTS STICK WITH IT
Real coaching beats another app full of generic plans. With your {Brand} coach guiding every workout, meal, and check-in, you get a plan that adapts to you, accountability that keeps you consistent, and someone in your corner who knows your goals. Stay on track, beat last week, and reach goals you didn't think were possible.

Your coach. Your plan. Your results — let's get to work.`;
  const STANDARD_SUBTITLE = 'Coaching, workouts & nutrition';
  const STANDARD_KEYWORDS = 'personal training,coaching,workout,nutrition,fitness,checkin,progress,habit,macros,accountability';
  function _awBrandName() {
    // The BRAND (coaching business) name ONLY — never the app name. {Brand} in
    // the store copy must read "your Revolution Fitness coach", not the app name.
    const ls = (typeof loadSettings === 'function' ? loadSettings() : null) || {};
    return (ls.businessName || ls.emailBusinessName || '').trim();
  }
  function _awFillTemplate(tpl) {
    const brand = _awBrandName();
    const appName = (_awBranding.appName || brand).trim();
    return tpl.replace(/\{App Name\}/g, appName).replace(/\{Brand\}/g, brand);
  }
  // Detect a description that's still the STANDARD template — with ANY app name /
  // brand filled in — so a name change can re-sync the app name WITHOUT clobbering
  // a coach edit or an AI redraft (neither matches this exact shape).
  let _awStdRe = null;
  function _awIsStandardDesc(desc) {
    if (!_awStdRe) {
      const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      _awStdRe = new RegExp('^' + STANDARD_DESC.split(/\{App Name\}|\{Brand\}/g).map(esc).join('[^\\n]{1,40}') + '$');
    }
    return _awStdRe.test(String(desc || ''));
  }
  function _awSave() { syncConfig({ appBranding: { ..._awBranding } }); }

  function _awUpdatePreview() {
    const box = $('appwiz-live-icon');
    const ini = $('appwiz-live-initials');
    if (box) {
      // Validate URLs before putting them into a CSS value (coach-controlled in
      // the DB) so they can't break out of url("…") and inject CSS.
      const safeIcon = _awUrlOk(_awBranding.appIconUrl) ? _awBranding.appIconUrl : '';
      const safeLogo = _awUrlOk(_awLogoUrl) ? _awLogoUrl : '';
      box.style.backgroundRepeat = 'no-repeat';
      if (_awIconMode === 'upload' && safeIcon) {
        box.style.backgroundColor = '#fff';
        box.style.backgroundImage = `url("${safeIcon}")`;
        box.style.backgroundSize = 'cover';
        box.style.backgroundPosition = 'center';
        if (ini) ini.style.display = 'none';
      } else if (_awIconMode === 'generate' && safeLogo) {
        box.style.backgroundColor = _awEffIconBg();
        box.style.backgroundImage = `url("${safeLogo}")`;
        box.style.backgroundSize = '70%';
        box.style.backgroundPosition = 'center';
        if (ini) ini.style.display = 'none';
      } else {
        box.style.backgroundImage = 'none';
        box.style.backgroundColor = _awColor;
        if (ini) { ini.style.display = ''; ini.textContent = _awInitials(); }
      }
    }
    if ($('appwiz-live-name')) $('appwiz-live-name').textContent = _awDisplayName();
  }

  // ── Icon: mode (generate-from-logo vs upload) + background + previews ──
  function _awSetIconMode(mode) {
    _awIconMode = mode === 'upload' ? 'upload' : 'generate';
    _awBranding.iconMode = _awIconMode;
    document.querySelectorAll('#appwiz-iconmode .appwiz-mode-btn').forEach((b) => b.classList.toggle('appwiz-mode-on', b.dataset.iconmode === _awIconMode));
    $('appwiz-gen')?.classList.toggle('hidden', _awIconMode !== 'generate');
    $('appwiz-upl')?.classList.toggle('hidden', _awIconMode !== 'upload');
    $('appwiz-guide-upload')?.classList.toggle('hidden', _awIconMode !== 'upload'); // icon design tips: upload mode only
    _awSave();
    _awRenderRealsize();
    _awUpdatePreview();
    _awRender(); // icon gate depends on mode (generate→logo vs upload→file)
  }

  function _awApplyIconBg(hex) {
    _awIconBg = hex;
    _awBranding.iconBackgroundColor = hex;
    if ($('appwiz-iconbg')) $('appwiz-iconbg').value = hex;
    if ($('appwiz-iconbg-hex')) $('appwiz-iconbg-hex').value = hex.toUpperCase();
    _awSave();
    _awRenderRealsize();
    _awUpdatePreview();
  }

  // Render the icon at real device sizes with the iOS rounded-corner mask, from
  // the current source (uploaded icon, or the logo centered on the background).
  function _awRenderRealsize() {
    const row = $('appwiz-realsize');
    if (!row) return;
    const sizes = [[112, 'App Store'], [60, 'Home screen'], [40, 'Spotlight'], [29, 'Settings']];
    const safeIcon = _awUrlOk(_awBranding.appIconUrl) ? _awBranding.appIconUrl : '';
    const safeLogo = _awUrlOk(_awLogoUrl) ? _awLogoUrl : '';
    const bg = _awEffIconBg();
    const upload = _awIconMode === 'upload' && safeIcon;
    row.innerHTML = sizes.map(([s, label]) => {
      const r = Math.round(s * 0.225);
      const style = upload
        // Single quotes inside the double-quoted style="" attribute — double
        // quotes would terminate the attribute early and break the image.
        // (safeIcon is _awUrlOk-validated, so it carries no quotes itself.)
        ? `background-image:url('${safeIcon}');background-size:cover;background-position:center;`
        : `background-color:${bg};`;
      const logo = (!upload && safeLogo) ? `<img src="${safeLogo}" alt="" style="width:70%;height:70%;object-fit:contain;"/>` : '';
      return `<div class="appwiz-rs-cell"><div class="appwiz-rs-icon" style="width:${s}px;height:${s}px;border-radius:${r}px;${style}">${logo}</div><div class="appwiz-rs-label">${label}</div></div>`;
    }).join('');
  }

  async function _awLoadExamples() {
    if (_awExamplesLoaded) return;
    _awExamplesLoaded = true;
    const box = $('appwiz-examples');
    if (!box) return;
    try {
      const token = (await _config.getSupabase().auth.getSession())?.data?.session?.access_token;
      if (!token) { _awExamplesLoaded = false; return; }
      const res = await fetch('/.netlify/functions/coach-app-icon-examples', { headers: { Authorization: 'Bearer ' + token } });
      const d = await res.json().catch(() => ({}));
      const ex = (d.examples || []).filter((e) => /^https:\/\/[^"'()\s]+$/.test(e.artworkUrl || ''));
      box.innerHTML = ex.length
        ? ex.map((e) => `<div class="appwiz-ex"><img src="${e.artworkUrl}" alt="${esc(e.name)}" loading="lazy"/><span>${esc(e.name)}</span></div>`).join('')
        : '<span class="appwiz-muted">Examples unavailable right now.</span>';
    } catch (e) { box.innerHTML = ''; _awExamplesLoaded = false; }
  }

  // ── Adaptive step list ────────────────────────────────────────
  // Triage answers decide whether the D-U-N-S step appears — it's only needed
  // when a store account still has to be created, and one D-U-N-S serves both
  // stores. Everything else is constant.
  function _awTriaged() { const ss = _awBranding.storeSetup || {}; return (ss.hasApple === 'yes' || ss.hasApple === 'no') && (ss.hasGoogle === 'yes' || ss.hasGoogle === 'no'); }
  function _awNeedAccount() { const ss = _awBranding.storeSetup || {}; return ss.hasApple === 'no' || ss.hasGoogle === 'no'; }
  function _awSteps() {
    const s = ['triage', 'name', 'icon', 'color', 'copy', 'privacy'];
    if (_awNeedAccount()) s.push('duns');
    s.push('connect', 'submit');
    return s;
  }

  // ── Gates ─────────────────────────────────────────────────────
  // Navigation gates guard only the quick CREATIVE steps (name/icon/copy) so a
  // coach can't skip ahead with nothing entered. The slow account work is
  // deliberately NON-blocking: color/privacy/D-U-N-S/connect are freely
  // navigable, and the self-confirm checkboxes gate SUBMIT (via _awReady), not
  // navigation — so the creative is never trapped behind weeks of store waiting.
  function _awHasName() { return !!(_awBranding.appName || '').trim(); }
  // Mode-agnostic, mirroring the server's hasIcon (an uploaded icon OR a usable
  // brand logo qualifies) — so toggling generate/upload can't strand a coach
  // whose icon the server would actually accept.
  function _awIconOk() { return !!_awBranding.appIconUrl || _awUrlOk(_awLogoUrl); }
  function _awHasDesc() { return !!(_awBranding.description || '').trim(); }
  // Apple/Google are CONNECTED by us (the operator), which can take a day or two.
  // Once the coach confirms they've done their part (added the Admin / invited
  // the service account), the request can go through; we connect + verify on our
  // end during fulfillment (or decline with a note).
  function _awAttest(prov) { const ss = _awBranding.storeSetup || {}; return prov === 'apple' ? !!ss.attestApple : !!ss.attestGoogle; }
  function _awGateMet(key) {
    switch (key) {
      case 'triage': return _awTriaged();
      case 'name': return _awHasName();
      case 'icon': return _awIconOk();
      case 'copy': return _awHasDesc();
      default: return true; // color · privacy · duns · connect · submit — non-blocking
    }
  }
  // Whether a step is actually COMPLETE (the rail's green ✓) — stricter than the
  // navigation gate for the non-blocking steps, so "Connect" isn't shown done
  // until the accounts are connected/confirmed.
  function _awStepDone(key) {
    if (key === 'connect') return (_awConn.apple || _awAttest('apple')) && (_awConn.google || _awAttest('google'));
    if (key === 'duns') { const d = ((_awBranding.storeSetup || {}).dunsNumber || '').replace(/\D/g, ''); return d.length === 9; }
    return _awGateMet(key);
  }
  // The furthest step reachable: you can sit ON the first unmet gate (to satisfy
  // it) but never advance past it.
  function _awMaxReachableIdx() {
    const steps = _awSteps();
    for (let i = 0; i < steps.length; i++) if (!_awGateMet(steps[i])) return i;
    return steps.length - 1;
  }

  // Persist the current step (by KEY) so a reload lands the coach back where they
  // were. Legacy numeric values (pre-adaptive) aren't known keys → fall back to
  // triage.
  function _awReadSavedStep() { const k = localStorage.getItem('revfit_appwiz_step') || ''; return STEP_LABELS[k] ? k : 'triage'; }

  function _awSetStep(key) {
    const steps = _awSteps();
    const maxIdx = _awMaxReachableIdx();
    let idx = steps.indexOf(key);
    if (idx < 0) idx = 0;                       // unknown/removed step → start of the flow
    idx = Math.max(0, Math.min(maxIdx, idx));   // never past the first unmet gate
    _awStepKey = steps[idx];
    try { localStorage.setItem('revfit_appwiz_step', _awStepKey); } catch (e) { /* storage off — non-fatal */ }
    _awRender();
  }

  // Paint panes / dots / nav from the current step + gate state. Safe to call on
  // any state change (name typed, icon set, connection loaded) — it never moves
  // the step, only re-derives what's reachable + the nav's enabled state.
  // Paint the rail / panes / nav from the current step + gate state. Safe to call
  // on any change (name typed, triage answered, connection loaded) — it re-derives
  // the active list + what's reachable, and only clamps the step (via the guard
  // below), never jumping it. The rail is rebuilt from the active keys so it
  // grows/shrinks with the adaptive list.
  function _awRender() {
    const steps = _awSteps();
    const maxIdx = _awMaxReachableIdx();
    let curIdx = steps.indexOf(_awStepKey);
    if (curIdx < 0) { _awStepKey = steps[0]; curIdx = 0; }   // current step fell out of the list

    // Rail — a numbered dot per active step (Submit gets ★). Rebuilt each render;
    // clicks are handled by delegation on the container (see _awWire).
    const rail = $('appwiz-steps');
    if (rail) rail.innerHTML = steps.map((key, i) => {
      const ic = key === 'submit' ? '★' : String(i + 1);
      const cls = 'onb-step appwiz-step'
        + (i === curIdx ? ' appwiz-step--active' : '')
        + (i < curIdx && _awStepDone(key) ? ' appwiz-step--done' : '')
        + (i > maxIdx ? ' appwiz-step--locked' : '');
      return `<div class="${cls}" data-key="${key}"><div class="onb-step-icon">${ic}</div><span class="onb-step-label">${STEP_LABELS[key]}</span></div>`;
    }).join('');

    document.querySelectorAll('#appwiz .appwiz-pane').forEach((p) => p.classList.toggle('hidden', p.dataset.key !== _awStepKey));

    $('appwiz-back')?.classList.toggle('hidden', curIdx === 0);
    const onSubmit = _awStepKey === 'submit';
    const next = $('appwiz-next');
    if (next) {
      next.classList.toggle('hidden', onSubmit);   // the Submit step has its own button
      next.disabled = !_awGateMet(_awStepKey);      // can't advance until this step's gate is met
      next.textContent = 'Next →';
    }
    // Once WE'VE verified a connection (green ✓), the self-confirm box is moot.
    $('appwiz-attest-apple-wrap')?.classList.toggle('hidden', _awConn.apple);
    $('appwiz-attest-google-wrap')?.classList.toggle('hidden', _awConn.google);
    _awRenderTriage();
    _awRenderConnect();
    if (onSubmit) _awRenderSubmit();
    _awManagePoll();
  }

  // Reflect the triage answers on the segmented buttons + the outcome note.
  function _awRenderTriage() {
    const ss = _awBranding.storeSetup || {};
    document.querySelectorAll('#appwiz-triage-apple .appwiz-mode-btn').forEach((b) => b.classList.toggle('appwiz-mode-on', b.dataset.has === ss.hasApple));
    document.querySelectorAll('#appwiz-triage-google .appwiz-mode-btn').forEach((b) => b.classList.toggle('appwiz-mode-on', b.dataset.has === ss.hasGoogle));
    const note = $('appwiz-triage-note');
    if (!note) return;
    if (!_awTriaged()) { note.className = 'appwiz-triage-note hidden'; note.textContent = ''; return; }
    if (ss.hasApple === 'yes' && ss.hasGoogle === 'yes') {
      note.className = 'appwiz-triage-note ok';
      note.textContent = 'Perfect — you already have both accounts, so this is the short path: add your branding, add us as a team member, and submit.';
    } else {
      note.className = 'appwiz-triage-note';
      note.textContent = "No problem — we'll walk you through creating what you're missing (the store's side can take a couple of weeks). You can do everything else right now and add the rest whenever it's ready.";
    }
  }

  // Connect step — show each store's "create the account first" line only when
  // the coach said they don't have it, and update the recoverable flip link.
  function _awRenderConnect() {
    const ss = _awBranding.storeSetup || {};
    [['apple', 'Apple', ss.hasApple], ['google', 'Google', ss.hasGoogle]].forEach(([prov, label, has]) => {
      const need = has === 'no';
      document.querySelectorAll(`#appwiz .appwiz-conn-need[data-need="${prov}"]`).forEach((el) => el.classList.toggle('hidden', !need));
      const flip = $(`appwiz-flip-${prov}`);
      if (flip) flip.textContent = need ? `Actually, I already have a ${label} account →` : `I don't have a ${label} account yet →`;
    });
  }

  // While the coach is parked on the Connect step waiting for us to connect an
  // account on their behalf — or on the Submit step watching an accepted app's
  // launch progress — poll so the status advances without a reload.
  // Self-managing: stops once connected / both stores are live.
  function _awManagePoll() {
    const connectWaiting = _awStepKey === 'connect' && (!_awConn.apple || !_awConn.google);
    const l = _awLaunch || {};
    const bothLive = (l.ios || {}).store === 'live' && (l.android || {}).store === 'live';
    const launchWaiting = _awStepKey === 'submit' && _awRequestStatus === 'accepted' && !bothLive;
    const waiting = connectWaiting || launchWaiting;
    if (waiting && !_awPollTimer) _awPollTimer = setInterval(_storesLoadCredStatus, 45000);
    else if (!waiting && _awPollTimer) { clearInterval(_awPollTimer); _awPollTimer = null; }
  }

  // Whether the request is ready to send — the exact 5 things the server
  // re-checks in coach-app-request (name + icon + description + Apple + Google).
  function _awReady() { return _awHasName() && _awIconOk() && _awHasDesc() && (_awConn.apple || _awAttest('apple')) && (_awConn.google || _awAttest('google')); }

  function _awRenderReview() {
    const name = (_awBranding.appName || '').trim();
    const desc = (_awBranding.description || '').trim();
    const priv = (_awBranding.privacyPolicyUrl || '').trim();
    const duns = ((_awBranding.storeSetup || {}).dunsNumber || '').replace(/\D/g, '');
    const hasLogo = _awUrlOk(_awLogoUrl);
    const iconOk = !!_awBranding.appIconUrl || hasLogo;
    const iconDetail = _awIconMode === 'upload'
      ? (_awBranding.appIconUrl ? 'Your uploaded icon' : 'No icon uploaded yet')
      : (hasLogo ? 'Generated from your logo' : 'Add a logo in Settings → Appearance');
    const items = [
      ['App name', !!name, name || 'Not set yet'],
      ['App icon', iconOk, iconDetail],
      ['Brand color', true, _awColor.toUpperCase()],
      ['Store description', !!desc, desc ? 'Written' : 'Not written yet'],
      ['Privacy policy', true, priv ? 'Your own URL' : 'FitOps Pro (shared)'],
    ];
    // D-U-N-S only matters when a store account still needs creating.
    if (_awNeedAccount()) items.push(['D-U-N-S number', true, duns.length === 9 ? 'Saved' : (duns ? 'In progress' : 'Add it when it arrives')]);
    items.push(
      ['Apple account', _awConn.apple || _awAttest('apple'), _awConn.apple ? 'Connected' : (_awAttest('apple') ? 'You confirmed it — we’ll finish connecting' : 'Add apps@fitopspro.com as Admin')],
      ['Google account', _awConn.google || _awAttest('google'), _awConn.google ? 'Connected' : (_awAttest('google') ? 'You confirmed it — we’ll finish connecting' : 'Invite the service account')],
    );
    const ul = $('appwiz-checklist');
    if (ul) ul.innerHTML = items.map(([label, ok, detail]) =>
      `<li class="appwiz-check ${ok ? 'ok' : 'warn'}"><span class="appwiz-check-ic">${ok ? '✓' : '!'}</span><span class="appwiz-check-label">${esc(label)}</span><span class="appwiz-check-detail">${esc(detail)}</span></li>`).join('');
    const ready = _awReady();
    const banner = $('appwiz-ready');
    if (banner) {
      banner.className = 'appwiz-ready ' + (ready ? 'ok' : 'warn');
      banner.textContent = ready
        ? '✓ Everything’s ready — send us the request and we’ll build your app.'
        : 'Almost there — finish the items marked above, then submit.';
    }
  }

  // One coach-voice line per store from the coarse launch statuses. Calm on
  // purpose: rejections read as "we're on it" — the operator handles them.
  function _awLaunchLine(p) {
    const store = p.store || 'not_started';
    const build = p.build || 'none';
    if (store === 'live') return ['ok', 'Live! 🎉'];
    if (store === 'in_review') return ['ok', 'Submitted — in store review'];
    if (store === 'building') return ['ok', 'Uploading to the store'];
    if (store === 'rejected') return ['warn', 'The store asked for a change — we’re on it'];
    if (build === 'finished') return ['ok', 'Built — getting the store listing ready'];
    if (build === 'queued' || build === 'in_progress') return ['ok', 'Building your app'];
    if (build === 'errored' || build === 'canceled') return ['warn', 'Build hit a snag — we’re on it'];
    return ['ok', 'In our build queue'];
  }
  function _awLaunchRowsHtml() {
    const l = _awLaunch || {};
    const rows = [['App Store (iPhone)', l.ios || {}], ['Google Play (Android)', l.android || {}]].map(([label, p]) => {
      const [cls, text] = _awLaunchLine(p);
      return `<li class="appwiz-check ${cls}"><span class="appwiz-check-ic">${cls === 'ok' ? '✓' : '!'}</span><span class="appwiz-check-label">${esc(label)}</span><span class="appwiz-check-detail">${esc(text)}</span></li>`;
    }).join('');
    return `<ul class="appwiz-checklist" style="margin:10px 0 0;">${rows}</ul>`;
  }

  // Drive the Submit step's button vs. confirmation from the request state.
  function _awRenderSubmit() {
    _awRenderReview();
    const sent = _awRequestStatus === 'requested' || _awRequestStatus === 'accepted';
    const declined = _awRequestStatus === 'declined';
    const submitted = $('appwiz-submitted');
    if (submitted) {
      if (_awRequestStatus === 'accepted') {
        // Accepted → live per-platform progress (updates itself while open).
        submitted.className = 'appwiz-submitted ok';
        submitted.innerHTML = '✓ <strong>We’re building your app!</strong> Here’s where each store is right now — this updates on its own.' + _awLaunchRowsHtml();
      } else if (sent) {
        submitted.className = 'appwiz-submitted ok';
        submitted.innerHTML = '✓ <strong>Request sent.</strong> Your app is in our build queue — we’ll be in touch as we get it ready. You can keep updating your branding above anytime.';
      } else if (declined) {
        submitted.className = 'appwiz-submitted warn';
        submitted.innerHTML = 'We couldn’t start your app yet' + (_awRequestNote ? ': ' + esc(_awRequestNote) : '') + '. Fix the items above and resubmit.';
      } else {
        submitted.className = 'appwiz-submitted hidden';
        submitted.innerHTML = '';
      }
    }
    const btn = $('appwiz-submit');
    if (btn) {
      btn.classList.toggle('hidden', sent);
      btn.disabled = !_awReady();
      btn.textContent = declined ? 'Resubmit app request' : 'Submit app request';
    }
    $('appwiz-ready')?.classList.toggle('hidden', sent); // the confirmation replaces the readiness banner
  }

  async function _awSubmitRequest() {
    const btn = $('appwiz-submit');
    if (!btn || btn.disabled) return;
    const orig = btn.textContent;
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      const token = await _storesToken();
      if (!token) { showToast('Your session expired — refresh and try again.', 'error'); btn.disabled = false; btn.textContent = orig; return; }
      const res = await fetch('/.netlify/functions/coach-app-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        _awRequestStatus = d.request_status || 'requested';
        _awRequestNote = null;
        _awRenderSubmit();
        showToast('Request sent — your app is in the build queue.', 'success');
      } else {
        showToast(d.error || 'Could not send your request.', 'error');
        btn.disabled = false; btn.textContent = orig;
      }
    } catch (e) {
      showToast('Could not send your request right now.', 'error');
      btn.disabled = false; btn.textContent = orig;
    }
  }

  function _awApplyColor(hex) {
    _awColor = hex;
    if ($('appwiz-color')) $('appwiz-color').value = hex;
    if ($('appwiz-color-hex')) $('appwiz-color-hex').value = hex.toUpperCase();
    syncConfig({ brandColor: hex });   // top-level — App Studio + client-login read this first
    // ...and the canonical Settings field, so the App tab + normal Settings show
    // ONE brand color (single source of truth — edit either, both reflect it).
    try {
      const s = (typeof loadSettings === 'function' ? loadSettings() : (_config.loadSettings ? _config.loadSettings() : null)) || {};
      if (!s.customColors) s.customColors = {};
      s.customColors.brand = hex;
      if (typeof saveSettingsToStorage === 'function') saveSettingsToStorage(s);
    } catch (e) { /* brandColor already saved above */ }
    _awUpdatePreview();
  }

  function _awWire() {
    if (_awWired) return;
    _awWired = true;

    $('appwiz-icon-btn')?.addEventListener('click', () => $('appwiz-icon-input')?.click());
    $('appwiz-icon-input')?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!['image/png', 'image/jpeg'].includes(file.type)) { showToast('Use a PNG or JPG image.', 'error'); return; }
      if (file.size > 2 * 1024 * 1024) { showToast('Image must be under 2 MB.', 'error'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUri = ev.target.result;
        const probe = new Image();
        probe.onload = async () => {
          // Square is a hard store requirement; sub-1024 + transparency are warnings.
          if (probe.width !== probe.height) { showToast('Your icon must be square — equal width and height.', 'error'); return; }
          if (probe.width < 1024) showToast(`Tip: 1024×1024 looks sharpest (yours is ${probe.width}px).`, 'info');
          try {
            const cw = Math.min(probe.width, 64);
            const cv = document.createElement('canvas'); cv.width = cw; cv.height = cw;
            const ctx = cv.getContext('2d'); ctx.drawImage(probe, 0, 0, cw, cw);
            const px = ctx.getImageData(0, 0, cw, cw).data;
            let alpha = false; for (let i = 3; i < px.length; i += 4) { if (px[i] < 250) { alpha = true; break; } }
            if (alpha) showToast('Heads up: transparent areas will be filled with your brand color.', 'info');
          } catch (_) { /* canvas may be blocked — skip the transparency check */ }
          try {
            const url = await _uploadLogoToStorage(currentUser.id, 'app_icon', dataUri);
            const busted = `${url}?t=${Date.now()}`;
            _awBranding.appIconUrl = busted;
            if (_awIconMode !== 'upload') _awSetIconMode('upload');
            _awSave();
            const img = $('appwiz-icon-preview');
            if (img) { img.src = busted; img.classList.remove('hidden'); }
            $('appwiz-icon-ph')?.classList.add('hidden');
            $('appwiz-icon-remove')?.classList.remove('hidden');
            _awUpdatePreview(); _awRenderRealsize(); _awRender();
            showToast('App icon saved.', 'success');
          } catch (err) { showToast('Upload failed: ' + err.message, 'error'); }
        };
        probe.onerror = () => showToast('Could not read that image.', 'error');
        probe.src = dataUri;
      };
      reader.readAsDataURL(file);
    });
    $('appwiz-icon-remove')?.addEventListener('click', async () => {
      try { await _deleteLogoFromStorage(currentUser.id, 'app_icon'); } catch (e) { /* best effort */ }
      _awBranding.appIconUrl = '';
      _awSave();
      const img = $('appwiz-icon-preview');
      if (img) { img.src = ''; img.classList.add('hidden'); }
      $('appwiz-icon-ph')?.classList.remove('hidden');
      $('appwiz-icon-remove')?.classList.add('hidden');
      _awUpdatePreview(); _awRenderRealsize(); _awRender();
    });

    document.querySelectorAll('#appwiz-iconmode .appwiz-mode-btn').forEach((b) => b.addEventListener('click', () => _awSetIconMode(b.dataset.iconmode)));
    $('appwiz-iconbg')?.addEventListener('input', function () { _awApplyIconBg(this.value); });
    $('appwiz-iconbg-hex')?.addEventListener('change', function () { const v = this.value.trim(); if (/^#[0-9a-fA-F]{6}$/.test(v)) _awApplyIconBg(v.toLowerCase()); });

    $('appwiz-color')?.addEventListener('input', function () { _awApplyColor(this.value); });
    $('appwiz-color-hex')?.addEventListener('change', function () { const v = this.value.trim(); if (/^#[0-9a-fA-F]{6}$/.test(v)) _awApplyColor(v.toLowerCase()); });

    $('appwiz-desc')?.addEventListener('input', () => { _awBranding.description = $('appwiz-desc').value; _awCount(); _awRender(); });
    $('appwiz-desc')?.addEventListener('blur', _awSave);
    $('appwiz-subtitle')?.addEventListener('input', () => { _awBranding.appSubtitle = $('appwiz-subtitle').value; });
    $('appwiz-subtitle')?.addEventListener('blur', _awSave);
    $('appwiz-keywords')?.addEventListener('input', () => { _awBranding.appKeywords = $('appwiz-keywords').value; });
    $('appwiz-keywords')?.addEventListener('blur', _awSave);
    $('appwiz-desc-ai')?.addEventListener('click', async () => {
      const btn = $('appwiz-desc-ai');
      const orig = btn.textContent;
      btn.disabled = true; btn.textContent = 'Writing…';
      try {
        const token = (await _config.getSupabase().auth.getSession())?.data?.session?.access_token;
        if (!token) { showToast('Your session expired — refresh and try again.', 'error'); return; }
        const res = await fetch('/.netlify/functions/coach-app-description', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({ context: $('appwiz-desc-context')?.value || '' }),
        });
        const d = await res.json().catch(() => ({}));
        if (res.ok && d.description) {
          $('appwiz-desc').value = d.description;
          _awBranding.description = d.description;
          _awSave(); _awCount(); _awRender();
        } else { showToast(d.error || 'Could not write a description.', 'error'); }
      } catch (e) { showToast('Could not write a description right now.', 'error'); }
      finally { btn.disabled = false; btn.textContent = orig; }
    });

    // Persist the coach's own privacy URL only when it's well-formed — a
    // malformed string must never reach the store listing. Blank/invalid falls
    // back to the shared FitOps policy; an invalid non-blank value shows a hint.
    const _awSetPrivacyUrl = () => {
      const own = document.querySelector('input[name="appwiz-priv"]:checked')?.value === 'own';
      const raw = ($('appwiz-priv-url')?.value || '').trim();
      _awBranding.privacyPolicyUrl = (own && _awUrlOk(raw)) ? raw : '';
      const hint = $('appwiz-priv-hint');
      if (hint) { const bad = own && !!raw && !_awUrlOk(raw); hint.classList.toggle('hidden', !bad); hint.textContent = bad ? 'Enter a full URL starting with https://' : ''; }
      _awSave();
    };
    document.querySelectorAll('input[name="appwiz-priv"]').forEach((r) => r.addEventListener('change', () => {
      const own = document.querySelector('input[name="appwiz-priv"]:checked')?.value === 'own';
      $('appwiz-priv-own-wrap')?.classList.toggle('hidden', !own);
      _awSetPrivacyUrl();
    }));
    $('appwiz-priv-url')?.addEventListener('change', _awSetPrivacyUrl);

    // Next/Back walk the ACTIVE list by key; rail clicks use delegation (the rail
    // is rebuilt each render, so per-dot listeners wouldn't survive).
    $('appwiz-next')?.addEventListener('click', () => { const steps = _awSteps(); const i = steps.indexOf(_awStepKey); _awSave(); _awSetStep(steps[Math.min(steps.length - 1, i + 1)]); });
    $('appwiz-back')?.addEventListener('click', () => { const steps = _awSteps(); const i = steps.indexOf(_awStepKey); _awSetStep(steps[Math.max(0, i - 1)]); });
    $('appwiz-steps')?.addEventListener('click', (e) => { const dot = e.target.closest('.appwiz-step'); if (dot?.dataset.key) _awSetStep(dot.dataset.key); });

    // Triage — the account fork. Answering re-derives the step list (D-U-N-S in/out).
    document.querySelectorAll('#appwiz-triage-apple .appwiz-mode-btn').forEach((b) => b.addEventListener('click', () => { _awBranding.storeSetup = { ...(_awBranding.storeSetup || {}), hasApple: b.dataset.has }; _awSave(); _awRender(); }));
    document.querySelectorAll('#appwiz-triage-google .appwiz-mode-btn').forEach((b) => b.addEventListener('click', () => { _awBranding.storeSetup = { ...(_awBranding.storeSetup || {}), hasGoogle: b.dataset.has }; _awSave(); _awRender(); }));

    // Recoverable "actually I do / don't have this" — flips the answer in place
    // (and adds/removes the D-U-N-S step accordingly).
    $('appwiz-flip-apple')?.addEventListener('click', () => { const ss = _awBranding.storeSetup || {}; _awBranding.storeSetup = { ...ss, hasApple: ss.hasApple === 'no' ? 'yes' : 'no' }; _awSave(); _awRender(); });
    $('appwiz-flip-google')?.addEventListener('click', () => { const ss = _awBranding.storeSetup || {}; _awBranding.storeSetup = { ...ss, hasGoogle: ss.hasGoogle === 'no' ? 'yes' : 'no' }; _awSave(); _awRender(); });

    // ── Store-accounts checklist ──
    let _nameTimer = null;
    $('stores-appname')?.addEventListener('input', () => {
      _awBranding.appName = $('stores-appname').value.trim();
      // Keep the store description's {App Name} in sync while it's still the
      // STANDARD template (any name/brand filled in) — a coach edit or AI redraft
      // won't match _awIsStandardDesc, so their copy is never clobbered.
      if (_awBrandName() && _awIsStandardDesc(_awBranding.description)) {
        _awBranding.description = _awFillTemplate(STANDARD_DESC);
        if ($('appwiz-desc')) $('appwiz-desc').value = _awBranding.description;
        _awCount();
      }
      _awSave();
      _awRender(); // name gate (step 0) just changed
      _awUpdatePreview(); // live preview reflects the typed app name
      _storesSetDot('name', _awBranding.appName ? 'saved' : '');
      clearTimeout(_nameTimer);
      _nameTimer = setTimeout(_storesNameCheck, 450);
    });
    $('stores-duns-input')?.addEventListener('input', (e) => {
      const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 9);
      e.target.value = _formatDuns(digits); // live-dash as they type (cursor lands at end — typed left-to-right)
      const valid = digits.length === 9;
      _awBranding.storeSetup = { ...(_awBranding.storeSetup || {}), dunsNumber: digits };
      _awSave();
      _storesSetDot('duns', valid ? 'ok' : (digits ? 'saved' : ''));
      const hint = $('stores-duns-hint');
      if (hint) { hint.textContent = digits ? (valid ? '✓ saved' : `${digits.length} / 9 digits`) : ''; hint.className = 'stores-namehint ' + (valid ? 'ok' : digits ? 'warn' : ''); }
    });
    document.querySelectorAll('#appwiz .stores-copy').forEach((b) => b.addEventListener('click', () => _storesCopy(b)));

    $('appwiz-submit')?.addEventListener('click', _awSubmitRequest);

    $('appwiz-attest-apple')?.addEventListener('change', function () {
      _awBranding.storeSetup = { ...(_awBranding.storeSetup || {}), attestApple: this.checked };
      _awSave(); _awRender();
    });
    $('appwiz-attest-google')?.addEventListener('change', function () {
      _awBranding.storeSetup = { ...(_awBranding.storeSetup || {}), attestGoogle: this.checked };
      _awSave(); _awRender();
    });

    // Returning to the tab refreshes connection/request status, so an operator
    // connection (or accept/decline) that landed while the coach was away shows
    // up without a full page reload.
    document.addEventListener('visibilitychange', () => { if (!document.hidden && _awLoaded) _storesLoadCredStatus(); });
  }

  async function _awInit() {
    if (_awLoaded) return;
    _awLoaded = true;
    try {
      const sb = _config.getSupabase();
      // maybeSingle: a first-time coach has no user_config row yet (0 rows) —
      // that returns { data: null } cleanly instead of a PGRST116 error.
      const { data } = await sb.from('user_config').select('config').eq('user_id', currentUser.id).maybeSingle();
      const cfg = data?.config || {};
      _awBranding = cfg.appBranding || {};
      _awColor = _awHex(cfg.brandColor || cfg.settings?.customColors?.brand || cfg.customColors?.brand
        || (typeof loadSettings === 'function' ? loadSettings()?.customColors?.brand : null) || '#2557d6');
      _awLogoUrl = cfg.logoUrls?.revfit_logo_full || cfg.logoUrls?.revfit_logo || cfg.logoUrl || '';
    } catch (e) { _awBranding = {}; }
    _awIconMode = _awBranding.iconMode || (_awBranding.appIconUrl ? 'upload' : 'generate');
    _awIconBg = _awBranding.iconBackgroundColor || '';
    if (_awBranding.appIconUrl) {
      const img = $('appwiz-icon-preview');
      if (img) { img.src = _awBranding.appIconUrl; img.classList.remove('hidden'); }
      $('appwiz-icon-ph')?.classList.add('hidden');
      $('appwiz-icon-remove')?.classList.remove('hidden');
    }
    // Icon mode toggle + background pickers
    document.querySelectorAll('#appwiz-iconmode .appwiz-mode-btn').forEach((b) => b.classList.toggle('appwiz-mode-on', b.dataset.iconmode === _awIconMode));
    $('appwiz-gen')?.classList.toggle('hidden', _awIconMode !== 'generate');
    $('appwiz-upl')?.classList.toggle('hidden', _awIconMode !== 'upload');
    $('appwiz-guide-upload')?.classList.toggle('hidden', _awIconMode !== 'upload'); // icon design tips: upload mode only
    const _awEffBg = _awEffIconBg();
    if ($('appwiz-iconbg')) $('appwiz-iconbg').value = _awEffBg;
    if ($('appwiz-iconbg-hex')) $('appwiz-iconbg-hex').value = _awEffBg.toUpperCase();
    if ($('appwiz-color')) $('appwiz-color').value = _awColor;
    if ($('appwiz-color-hex')) $('appwiz-color-hex').value = _awColor.toUpperCase();
    // Pre-fill the standard, brand-injected store copy so the coach sees how
    // complete it can be — only when we have a brand name and they haven't
    // written their own yet. They can edit any line or redraft with the AI.
    if (_awBrandName()) {
      if (!(_awBranding.description || '').trim()) _awBranding.description = _awFillTemplate(STANDARD_DESC);
      if (!(_awBranding.appSubtitle || '').trim()) _awBranding.appSubtitle = STANDARD_SUBTITLE;
      if (!(_awBranding.appKeywords || '').trim()) _awBranding.appKeywords = STANDARD_KEYWORDS;
      _awSave();
    }
    if ($('appwiz-desc')) $('appwiz-desc').value = _awBranding.description || '';
    if ($('appwiz-subtitle')) $('appwiz-subtitle').value = _awBranding.appSubtitle || '';
    if ($('appwiz-keywords')) $('appwiz-keywords').value = _awBranding.appKeywords || '';
    _awCount();
    if ((_awBranding.privacyPolicyUrl || '').trim()) {
      const own = document.querySelector('input[name="appwiz-priv"][value="own"]');
      if (own) own.checked = true;
      $('appwiz-priv-own-wrap')?.classList.remove('hidden');
      if ($('appwiz-priv-url')) $('appwiz-priv-url').value = _awBranding.privacyPolicyUrl;
    }
    _awUpdatePreview();
    _awRenderRealsize();
    _awLoadExamples();
    _awSetStep(_awReadSavedStep()); // restore last step, clamped to what's currently reachable
    _storesLoad();
  }

  // ── Store-accounts checklist helpers ──
  // state: true|'ok' → green (done/verified) · 'saved' → amber (saved, not yet
  // verified) · false|'' → grey (not done).
  function _storesSetDot(key, state) {
    const dot = $(`stores-dot-${key}`);
    if (!dot) return;
    const cls = (state === true || state === 'ok') ? 'ok' : (state === 'saved' ? 'saved' : '');
    // The status dots are vestigial in the stepper — the step rail + the inline
    // "✓ looks available" / "Connected ✓" text carry the signal, so keep the dots
    // HIDDEN. Still apply the state class so the walkthrough pill's verifyDot
    // check (reads stores-dot-<prov>.ok → "Done ✓ — replay") keeps working.
    dot.className = 'stores-dot hidden' + (cls ? ' ' + cls : '');
  }
  function _storesToken() { return _config.getSupabase().auth.getSession().then((r) => r?.data?.session?.access_token); }

  // App-store name rules (both stores) — surface violations before the
  // availability check, since a rule break matters more than availability.
  function _storesNameRule(name) {
    if (name.length > 30) return 'Keep it to 30 characters or fewer';
    if (/\p{Emoji_Presentation}/u.test(name)) return 'No emoji in app names';
    if (name.length >= 4 && name === name.toUpperCase() && /[A-Z]/.test(name)) return 'Avoid ALL CAPS — it can flag review';
    if (/(^|\s)(free|sale|best|cheap|discount)(\s|$)|#\s?1|\bno\.?\s?1\b|\d+%\s*off/i.test(name)) return 'Avoid promo words (free, best, #1, sale…)';
    return null;
  }

  async function _storesNameCheck() {
    const name = ($('stores-appname')?.value || '').trim();
    const hint = $('stores-namehint');
    if (!hint) return;
    const disc = $('stores-namedisclaimer');
    disc?.classList.add('hidden');
    const rule = _storesNameRule(name);
    if (rule) { hint.textContent = `⚠ ${rule}`; hint.className = 'stores-namehint warn'; return; }
    if (name.length < 2) { hint.textContent = ''; hint.className = 'stores-namehint'; return; }
    hint.textContent = 'checking…'; hint.className = 'stores-namehint';
    try {
      const token = await _storesToken();
      if (!token) { hint.textContent = ''; hint.className = 'stores-namehint'; return; }
      const res = await fetch(`/.netlify/functions/coach-app-name-check?name=${encodeURIComponent(name)}`, { headers: { Authorization: 'Bearer ' + token } });
      const d = await res.json().catch(() => ({}));
      if (d.available === true) { hint.textContent = '✓ looks available'; hint.className = 'stores-namehint ok'; _storesSetDot('name', 'ok'); disc?.classList.remove('hidden'); }
      else if (d.available === false) { hint.textContent = `⚠ an app called "${(d.exactMatches[0] || {}).name || name}" already exists`; hint.className = 'stores-namehint warn'; _storesSetDot('name', 'saved'); }
      else { hint.textContent = "couldn't check right now"; hint.className = 'stores-namehint'; _storesSetDot('name', 'saved'); }
    } catch (e) { hint.textContent = ''; }
  }

  async function _storesLoadCredStatus() {
    try {
      const token = await _storesToken();
      if (!token) return;
      const res = await fetch('/.netlify/functions/coach-store-credentials-status', { headers: { Authorization: 'Bearer ' + token } });
      const d = await res.json().catch(() => ({}));
      // The operator generates + saves the credentials in App Studio, landing in
      // the same wl.<coach>.* keys this reads — so presence here means we're
      // connected on the coach's behalf. Green ✓ = connected.
      _storesSetConn('apple', !!d.apple);
      _storesSetConn('google', !!d.google);
      _awRequestStatus = d.requestStatus || 'none';
      _awRequestNote = d.requestNote || null;
      _awLaunch = d.launch || null;
      _awRender(); // connection status + the Submit readiness just resolved
    } catch (e) { /* leave grey */ }
  }
  function _storesSetConn(prov, connected) {
    _awConn[prov] = !!connected;
    _storesSetDot(prov, connected ? 'ok' : '');
    const el = document.querySelector(`.stores-connstatus[data-prov="${prov}"]`);
    if (el) { el.textContent = connected ? 'Connected ✓' : 'Not connected yet'; el.classList.toggle('ok', connected); }
  }
  function _storesCopy(btn) {
    const t = btn.getAttribute('data-copy') || '';
    if (!t) return;
    navigator.clipboard?.writeText(t);
    const orig = btn.textContent;
    btn.textContent = 'Copied ✓';
    setTimeout(() => { btn.textContent = orig; }, 1200);
  }

  // Lazy-load the in-app walkthrough player + inject "Show me how" pills next to
  // the store test buttons. Author mode (click-to-place coords) is gated to the
  // operator + a ?wtAuthor=1 flag; inert for coaches.
  let _wtMod;
  async function _storesMountGuides() {
    try {
      _wtMod ??= await import('./modules/walkthrough.js');
      const isAuthor = currentUser?.email === 'clayton@revfitcoaching.com'
        && new URLSearchParams(location.search).has('wtAuthor');
      await _wtMod.mountWalkthroughs({ author: isAuthor });
    } catch (e) { /* guides not shipped / fetch failed — non-fatal */ }
  }

  function _storesLoad() {
    const ss = _awBranding.storeSetup || {};
    if ($('stores-appname')) $('stores-appname').value = _awBranding.appName || '';
    _storesSetDot('name', (_awBranding.appName || '').trim() ? 'saved' : '');
    const duns = (ss.dunsNumber || '').replace(/\D/g, '');
    if ($('stores-duns-input')) $('stores-duns-input').value = _formatDuns(duns);
    _storesSetDot('duns', duns.length === 9 ? 'ok' : (duns ? 'saved' : ''));
    if ($('appwiz-attest-apple')) $('appwiz-attest-apple').checked = !!ss.attestApple;
    if ($('appwiz-attest-google')) $('appwiz-attest-google').checked = !!ss.attestGoogle;
    const dh = $('stores-duns-hint');
    if (dh && duns) { const ok = duns.length === 9; dh.textContent = ok ? '✓ saved' : `${duns.length} / 9 digits`; dh.className = 'stores-namehint ' + (ok ? 'ok' : 'warn'); }
    if ((_awBranding.appName || '').trim()) _storesNameCheck();
    _storesLoadCredStatus();
    _storesMountGuides();
  }

  document.querySelector('[data-tab="app"]')?.addEventListener('click', () => { _awWire(); _awInit(); });
  // ⚠️ THE BOOT TIMER THAT USED TO BE HERE IS GONE. It read
  // revfit_settings_tab and fired _awWire()/_awInit() 400 ms into every load,
  // whichever view was being restored. _initActiveSettingsTab (in bindEvents)
  // now runs it from the Settings entry path instead. Expose the two so it can.
  window._awWire = _awWire;
  window._awInit = _awInit;
})();
